import { LOINC, HTTP_TERMINOLOGY_HL7_ORG, HTTP_HL7_ORG } from '@medplum/core';
import { kebabCase } from 'change-case';

/**
 * FHIR-related URLs used throughout the application
 */ // HL7 FHIR Base URLs
const HL7_FHIR_BASE = "http://hl7.org/fhir";
const HL7_TERMINOLOGY_BASE = "http://terminology.hl7.org";
const HL7_US_CORE_BASE = "http://hl7.org/fhir/us/core";
const HL7_US_CDMH_BASE = "http://hl7.org/fhir/us/cdmh";
// Organization Base URLs
const ORGANIZATION_BASE = "http://benchmarkhealth.com/fhir";
const MEDPLUM_BASE = "http://medplum.com";
/**
 * Base URL for Benchmark Health custom FHIR extensions
 *
 * All custom StructureDefinition URLs should be derived from this base.
 * Use this constant to ensure consistency across the codebase.
 *
 * @example
 * ```ts
 * const myExtension = `${EXTENSION_BASE}/my-extension-name`;
 * // => "http://benchmarkhealth.com/fhir/StructureDefinition/my-extension-name"
 * ```
 */ const EXTENSION_BASE = `${ORGANIZATION_BASE}/StructureDefinition`;
// Example URLs (for development/testing)
const EXAMPLE = {
    BASE: "http://example.com",
    BOOK_APPOINTMENT: "book-appointment",
    SET_AVAILABILITY: "set-availability",
    BLOCK_AVAILABILITY: "block-availability",
    CANCEL_APPOINTMENT: "cancel-appointment",
    APPOINTMENT_SERVICE_TYPES: "appointment-service-types"
};
// Profile URLs
const PROFILE_URLS = {
    AllergyIntolerance: `${HL7_US_CORE_BASE}/StructureDefinition/us-core-allergyintolerance`,
    CareTeam: `${HL7_US_CORE_BASE}/StructureDefinition/us-core-careteam`,
    Coverage: `${HL7_US_CORE_BASE}/StructureDefinition/us-core-coverage`,
    Immunization: `${HL7_US_CORE_BASE}/StructureDefinition/us-core-immunization`,
    MedicationRequest: `${HL7_US_CORE_BASE}/StructureDefinition/us-core-medicationrequest`,
    Patient: `${HL7_US_CORE_BASE}/StructureDefinition/us-core-patient`,
    SmokingStatus: `${HL7_US_CORE_BASE}/StructureDefinition/us-core-smokingstatus`,
    ServiceRequest: `${MEDPLUM_BASE}/StructureDefinition/medplum-provider-lab-procedure-servicerequest`,
    Device: `${HL7_US_CORE_BASE}/StructureDefinition/us-core-implantable-device`
};
// Extension URLs
const EXTENSION_URLS = {
    encounterNote: `${ORGANIZATION_BASE}/StructureDefinition/encounter-note`,
    sexualOrientation: `${ORGANIZATION_BASE}/StructureDefinition/sexual-orientation`,
    race: `${HL7_US_CORE_BASE}/StructureDefinition/us-core-race`,
    ethnicity: `${HL7_US_CORE_BASE}/StructureDefinition/us-core-ethnicity`,
    veteran: `${HL7_FHIR_BASE}/us/military-service/StructureDefinition/military-service-veteran-status`
};
// Patient Summary Extension
const PATIENT_SUMMARY_EXTENSION_URL = "http://benchmarkhealth.com/fhir/StructureDefinition/patient-summary";
// CoCM Consent Extension
const COCM_CONSENT_EXTENSION_URL = `${EXTENSION_BASE}/cocm-consent`;
/**
 * HL7 extension for personal pronouns (complex extension; value slice uses CodeableConcept).
 * @agentic @benchmark/core
 */ const INDIVIDUAL_PRONOUNS_EXTENSION_URL = `${HL7_FHIR_BASE}/StructureDefinition/individual-pronouns`;
// StructureDefinition URLs
const STRUCTURE_DEFINITION = {
    QUESTIONNAIRE_ITEM_CONTROL: `${HL7_FHIR_BASE}/StructureDefinition/questionnaire-itemControl`,
    QUESTIONNAIRE_REFERENCE_RESOURCE: `${HL7_FHIR_BASE}/StructureDefinition/questionnaire-referenceResource`,
    APPLICABLE_CHARGE_DEFINITION: `${ORGANIZATION_BASE}/StructureDefinition/applicable-charge-definition`,
    CHARGEITEM_MODIFIER: `${HL7_FHIR_BASE}/StructureDefinition/chargeitem-modifier`
};
// ValueSet URLs
const VALUE_SET = {
    // Generics
    SERVICE_TYPE: `${HL7_FHIR_BASE}/ValueSet/service-type`,
    SERVICE_CATEGORY: `${HL7_FHIR_BASE}/ValueSet/service-category`,
    SPECIALTY: `${HL7_FHIR_BASE}/ValueSet/c80-practice-codes`,
    // Tasks
    TASK_STATUS: `${HL7_FHIR_BASE}/ValueSet/task-status`,
    TASK_BUSINESS_STATUS: `${ORGANIZATION_BASE}/ValueSet/medplum-provider-task-business-status-valueset`,
    PRACTITIONER_ROLE: `${ORGANIZATION_BASE}/ValueSet/medplum-provider-practitioner-role-valueset`,
    // Appointments
    APPOINTMENT_STATUS: `${HL7_FHIR_BASE}/ValueSet/appointmentstatus`,
    APPOINTMENT_TYPE: `${HL7_TERMINOLOGY_BASE}/ValueSet/v2-0276`,
    // Encounters
    ENCOUNTER_CODE: `${HL7_TERMINOLOGY_BASE}/ValueSet/v3-ActEncounterCode`,
    ENCOUNTER_STATUS: `${HL7_FHIR_BASE}/ValueSet/encounter-status`,
    ENCOUNTER_TYPE: `${ORGANIZATION_BASE}/ValueSet/encounter-type`,
    ENCOUNTER_METHOD: `${ORGANIZATION_BASE}/ValueSet/encounter-method`,
    ENCOUNTER_INOUT: `${ORGANIZATION_BASE}/ValueSet/encounter-inout`,
    ENCOUNTER_SUCCESS: `${ORGANIZATION_BASE}/ValueSet/encounter-success`,
    ENCOUNTER_BILLABLE: `${ORGANIZATION_BASE}/ValueSet/encounter-billable`,
    // Provenance stamp for form-created encounters (activity-completion vs
    // free-form). Mixed-era contract: absence means legacy or system-generated
    // (API/scribe pipeline) — never infer free-form from absence.
    ENCOUNTER_ORIGIN: `${ORGANIZATION_BASE}/ValueSet/encounter-origin`,
    ENCOUNTER_REASON: `${HL7_FHIR_BASE}/ValueSet/encounter-reason`,
    ICD_10: `${HL7_FHIR_BASE}/ValueSet/icd-10`,
    // Patients
    LANGUAGES: `${HL7_FHIR_BASE}/ValueSet/languages`,
    USPS_STATE: `${HL7_US_CORE_BASE}/ValueSet/us-core-usps-state`,
    SUBSCRIBER_RELATIONSHIP: `${HL7_FHIR_BASE}/ValueSet/subscriber-relationship`,
    GENDER_IDENTITY: `${HL7_FHIR_BASE}/ValueSet/gender-identity`,
    RACE_CATEGORY: `${HL7_US_CORE_BASE}/ValueSet/omb-race-category`,
    ETHNICITY_CATEGORY: `${HL7_US_CORE_BASE}/ValueSet/omb-ethnicity-category`,
    // TODO(#985): Add custom EMR value set, currently using example
    EMR: `${HL7_FHIR_BASE}/ValueSet/emr`,
    // Claims
    CLAIM_MODIFIERS: `${HL7_FHIR_BASE}/ValueSet/claim-modifiers`
};
// CodeSystem URLs
const CODE_SYSTEM = {
    // General
    FHIR_TYPES: `${HL7_FHIR_BASE}/fhir-types`,
    DAYS_OF_WEEK: `${HL7_FHIR_BASE}/days-of-week`,
    QUESTIONNAIRE_ITEM_CONTROL: `${HL7_FHIR_BASE}/questionnaire-item-control`,
    // Patient-related
    SEXUAL_ORIENTATION: `${HL7_US_CDMH_BASE}/CodeSystem/pcornet-sexual-orientation`,
    // Identifier-related
    V2_0203: `${HL7_TERMINOLOGY_BASE}/CodeSystem/v2-0203`,
    V2_0131: `${HL7_TERMINOLOGY_BASE}/CodeSystem/v2-0131`,
    // Role-related
    V3_ACT_CODE: `${HL7_TERMINOLOGY_BASE}/CodeSystem/v3-ActCode`,
    V3_ROLE_CODE: `${HL7_TERMINOLOGY_BASE}/CodeSystem/v3-RoleCode`,
    V3_PARTICIPATION_TYPE: `${HL7_TERMINOLOGY_BASE}/CodeSystem/v3-ParticipationType`,
    // External
    CPT: "http://www.ama-assn.org/go/cpt",
    LOINC: "http://loinc.org",
    SNOMED: "http://snomed.info/sct",
    ICD10_CM: "http://hl7.org/fhir/sid/icd-10-cm"
};
// Questionnaire URLs
const QUESTIONNAIRE = {
    PATIENT_INTAKE: "https://medplum.com/Questionnaire/patient-intake-questionnaire"
};
// Identifier System URLs
const IDENTIFIER_SYSTEM = {
    /**
   * Pipeline Composition Identifier (legacy)
   *
   * Used to identify Composition resources created by the clinical call pipeline.
   * Format: "call-{callId}" for TypeScript pipeline
   * Format: "legacy-{correlationId}" for legacy Python pipeline
   *
   * @deprecated Use CALL_COMPOSITION for new call compositions
   */ PIPELINE_COMPOSITION: `${ORGANIZATION_BASE}/identifier/pipeline-composition`,
    /**
   * Call Composition Identifier
   *
   * Used to identify Composition resources created from Dialpad call transcripts.
   * Format: "{callId}" (the Dialpad call ID)
   */ CALL_COMPOSITION: `${ORGANIZATION_BASE}/identifier-system/call-id`,
    /**
   * Dialpad Message ID
   *
   * Used to identify Communication resources created from Dialpad SMS events.
   */ DIALPAD_MESSAGE_ID: "dialpad:messageId",
    /**
   * Dialpad Conversation ID
   *
   * Used to link related SMS messages in a conversation thread.
   */ DIALPAD_CONVERSATION_ID: "dialpad:conversationId",
    /**
   * Call Record Communication Identifier
   *
   * Used to identify Communication resources created from Dialpad call events.
   * Uses transcript hash for deduplication (same strategy as Composition).
   * Format: "transcript-{hash}" where hash is first 16 chars of SHA256
   */ CALL_RECORD: `${ORGANIZATION_BASE}/identifier-system/call-record`,
    /**
   * Dialpad Call ID
   *
   * Secondary identifier for call Communications, useful for lookup/debugging.
   * Not used for deduplication (transcript hash is used instead).
   */ DIALPAD_CALL_ID: "dialpad:callId"
};
// Consent-related URLs
const CONSENT = {
    SCOPE: {
        ADR: {
            coding: [
                {
                    system: `${HL7_TERMINOLOGY_BASE}/CodeSystem/consentscope`,
                    code: "adr",
                    display: "Advanced Care Directive"
                }
            ]
        },
        PATIENT_PRIVACY: {
            coding: [
                {
                    system: `${HL7_TERMINOLOGY_BASE}/CodeSystem/consentscope`,
                    code: "patient-privacy",
                    display: "Patient Privacy"
                }
            ]
        },
        TREATMENT: {
            coding: [
                {
                    system: `${HL7_TERMINOLOGY_BASE}/CodeSystem/consentscope`,
                    code: "treatment",
                    display: "Treatment"
                }
            ]
        }
    },
    CATEGORY: {
        ACD: {
            coding: [
                {
                    system: `${HL7_TERMINOLOGY_BASE}/CodeSystem/consentcategorycodes`,
                    code: "acd",
                    display: "Advanced Care Directive"
                }
            ]
        },
        NOPP: {
            coding: [
                {
                    system: `${HL7_TERMINOLOGY_BASE}/CodeSystem/v3-ActCode`,
                    code: "nopp",
                    display: "Notice of Privacy Practices"
                }
            ]
        },
        PAY: {
            coding: [
                {
                    system: `${HL7_TERMINOLOGY_BASE}/CodeSystem/v3-ActCode`,
                    code: "pay",
                    display: "Payment"
                }
            ]
        },
        MED: {
            coding: [
                {
                    system: `${HL7_TERMINOLOGY_BASE}/CodeSystem/v3-ActCode`,
                    code: "med",
                    display: "Medical"
                }
            ]
        }
    },
    POLICY_RULE: {
        HIPAA_NPP: {
            coding: [
                {
                    system: `${HL7_TERMINOLOGY_BASE}/CodeSystem/consentpolicycodes`,
                    code: "hipaa-npp",
                    display: "HIPAA Notice of Privacy Practices"
                }
            ]
        },
        HIPAA_SELF_PAY: {
            coding: [
                {
                    system: `${HL7_TERMINOLOGY_BASE}/CodeSystem/consentpolicycodes`,
                    code: "hipaa-self-pay",
                    display: "HIPAA Self-Pay Restriction"
                }
            ]
        },
        CRIC: {
            coding: [
                {
                    system: `${HL7_TERMINOLOGY_BASE}/CodeSystem/consentpolicycodes`,
                    code: "cric",
                    display: "Common Rule Informed Consent"
                }
            ]
        },
        ADR: {
            coding: [
                {
                    system: "http://medplum.com",
                    code: "BasicADR",
                    display: "Advanced Care Directive"
                }
            ]
        }
    }
};

// Resource Profile URLs mapping
const RESOURCE_PROFILE_URLS = {
    Patient: PROFILE_URLS.Patient,
    ServiceRequest: PROFILE_URLS.ServiceRequest,
    Device: PROFILE_URLS.Device
};
// Patient Reference Elements mapping
const PATIENT_REFERENCE_ELEMENTS = {
    Appointment: [
        "participant.actor"
    ],
    CarePlan: [
        "subject"
    ],
    Device: [
        "patient"
    ],
    DiagnosticReport: [
        "subject"
    ],
    DocumentReference: [
        "subject"
    ],
    MedicationRequest: [
        "subject"
    ],
    ServiceRequest: [
        "subject"
    ],
    Task: [
        "for"
    ]
};
// Observation Categories
const OBSERVATION_CATEGORIES = {
    socialHistory: {
        coding: [
            {
                system: `${HTTP_TERMINOLOGY_HL7_ORG}/CodeSystem/observation-category`,
                code: "social-history",
                display: "Social History"
            }
        ]
    },
    survey: {
        coding: [
            {
                system: `${HTTP_TERMINOLOGY_HL7_ORG}/CodeSystem/observation-category`,
                code: "survey",
                display: "Survey"
            }
        ]
    },
    sdoh: {
        coding: [
            {
                system: `${HTTP_HL7_ORG}/fhir/us/core/CodeSystem/us-core-tags`,
                code: "sdoh",
                display: "SDOH"
            }
        ]
    }
};
// Observation Code Mappings
const OBSERVATION_CODE_MAPPINGS = {
    housingStatus: {
        coding: [
            {
                code: "71802-3",
                system: LOINC,
                display: "Housing Status"
            }
        ]
    },
    educationLevel: {
        coding: [
            {
                code: "82589-3",
                system: LOINC,
                display: "Highest Level of Education"
            }
        ]
    },
    smokingStatus: {
        coding: [
            {
                code: "72166-2",
                system: LOINC,
                display: "Tobacco Smoking Status"
            }
        ]
    },
    sexualOrientation: {
        coding: [
            {
                code: "76690-7",
                system: LOINC,
                display: "Sexual Orientation"
            }
        ]
    },
    pregnancyStatus: {
        coding: [
            {
                code: "82810-3",
                system: LOINC,
                display: "Pregnancy Status"
            }
        ]
    },
    estimatedDeliveryDate: {
        coding: [
            {
                code: "11778-8",
                system: LOINC,
                display: "Estimated Date of Delivery"
            }
        ]
    }
};
// Consent Scope Mappings
const CONSENT_SCOPE_MAPPINGS = {
    adr: {
        coding: [
            {
                system: `${HTTP_TERMINOLOGY_HL7_ORG}/CodeSystem/consentscope`,
                code: "adr",
                display: "Advanced Care Directive"
            }
        ]
    },
    patientPrivacy: {
        coding: [
            {
                system: `${HTTP_TERMINOLOGY_HL7_ORG}/CodeSystem/consentscope`,
                code: "patient-privacy",
                display: "Patient Privacy"
            }
        ]
    },
    treatment: {
        coding: [
            {
                system: `${HTTP_TERMINOLOGY_HL7_ORG}/CodeSystem/consentscope`,
                code: "treatment",
                display: "Treatment"
            }
        ]
    }
};
// Consent Category Mappings
const CONSENT_CATEGORY_MAPPINGS = {
    acd: {
        coding: [
            {
                system: `${HTTP_TERMINOLOGY_HL7_ORG}/CodeSystem/consentcategorycodes`,
                code: "acd",
                display: "Advanced Care Directive"
            }
        ]
    },
    nopp: {
        coding: [
            {
                system: `${HTTP_TERMINOLOGY_HL7_ORG}/CodeSystem/v3-ActCode`,
                code: "nopp",
                display: "Notice of Privacy Practices"
            }
        ]
    },
    pay: {
        coding: [
            {
                system: `${HTTP_TERMINOLOGY_HL7_ORG}/CodeSystem/v3-ActCode`,
                code: "pay",
                display: "Payment"
            }
        ]
    },
    med: {
        coding: [
            {
                system: `${HTTP_TERMINOLOGY_HL7_ORG}/CodeSystem/v3-ActCode`,
                code: "med",
                display: "Medical"
            }
        ]
    }
};
// Consent Policy Rule Mappings
const CONSENT_POLICY_RULE_MAPPINGS = {
    hipaaNpp: {
        coding: [
            {
                system: `${HTTP_TERMINOLOGY_HL7_ORG}/CodeSystem/consentpolicycodes`,
                code: "hipaa-npp",
                display: "HIPAA Notice of Privacy Practices"
            }
        ]
    },
    hipaaSelfPay: {
        coding: [
            {
                system: `${HTTP_TERMINOLOGY_HL7_ORG}/CodeSystem/consentpolicycodes`,
                code: "hipaa-self-pay",
                display: "HIPAA Self-Pay Restriction"
            }
        ]
    },
    cric: {
        coding: [
            {
                system: `${HTTP_TERMINOLOGY_HL7_ORG}/CodeSystem/consentpolicycodes`,
                code: "cric",
                display: "Common Rule Informed Consent"
            }
        ]
    },
    adr: {
        coding: [
            {
                system: "http://medplum.com",
                code: "BasicADR",
                display: "Advanced Care Directive"
            }
        ]
    }
};
// Clinical Impression Code Map
// TODO(#987): Confirm additional code mappings in LOINC
const CLINICAL_IMPRESSION_CODE_MAPPINGS = {
    comment: {
        coding: [
            {
                system: LOINC,
                code: "11329-0",
                display: "General Note"
            }
        ]
    },
    chiefComplaint: {
        coding: [
            {
                system: LOINC,
                code: "10154-3",
                display: "Chief Complaint"
            }
        ]
    },
    hpiCurrentSymptoms: {
        coding: [
            {
                system: LOINC,
                code: "10164-2",
                display: "HPI Current Symptoms"
            }
        ]
    },
    mentalHealthHistory: {
        coding: [
            {
                system: LOINC,
                code: "45420-7",
                display: "Mental Health History"
            }
        ]
    },
    "mental-status-appearance": {
        coding: [
            {
                system: LOINC,
                code: "10190-7",
                display: "Mental Status - Appearance"
            }
        ]
    },
    "mental-status-behavior": {
        coding: [
            {
                system: LOINC,
                code: "10190-7",
                display: "Mental Status - Behavior"
            }
        ]
    },
    "mental-status-speech": {
        coding: [
            {
                system: LOINC,
                code: "10190-7",
                display: "Mental Status - Speech"
            }
        ]
    },
    "mental-status-mood-affect": {
        coding: [
            {
                system: LOINC,
                code: "10190-7",
                display: "Mental Status - Mood/Affect"
            }
        ]
    },
    "mental-status-thought-process-content": {
        coding: [
            {
                system: LOINC,
                code: "10190-7",
                display: "Mental Status - Thought Process/Content"
            }
        ]
    },
    "mental-status-cognition": {
        coding: [
            {
                system: LOINC,
                code: "10190-7",
                display: "Mental Status - Cognition"
            }
        ]
    },
    "mental-status-perception": {
        coding: [
            {
                system: LOINC,
                code: "10190-7",
                display: "Mental Status - Perception"
            }
        ]
    },
    "mental-status-insight-judgment": {
        coding: [
            {
                system: LOINC,
                code: "10190-7",
                display: "Mental Status - Insight/Judgment"
            }
        ]
    },
    traumaHistory: {
        coding: [
            {
                system: LOINC,
                code: "74198-3",
                display: "Trauma History"
            }
        ]
    },
    substanceUseHistory: {
        coding: [
            {
                system: LOINC,
                code: "87259-8",
                display: "Substance Use History"
            }
        ]
    },
    medicalHistory: {
        coding: [
            {
                system: LOINC,
                code: "11348-0",
                display: "Medical History"
            }
        ]
    },
    medicationHistory: {
        coding: [
            {
                system: LOINC,
                code: "10160-0",
                display: "Medication History"
            }
        ]
    },
    socialHistory: {
        coding: [
            {
                system: LOINC,
                code: "29762-2",
                display: "Social History"
            }
        ]
    },
    familyHistory: {
        coding: [
            {
                system: LOINC,
                code: "10157-6",
                display: "Family History"
            }
        ]
    },
    assessmentPlan: {
        coding: [
            {
                system: LOINC,
                code: "51847-2",
                display: "Assessment & Plan"
            }
        ]
    },
    // Does not appear to be used in any forms currently - nov 12 2025
    // caseManagementSummary: {
    //   coding: [
    //     {
    //       system: LOINC,
    //       code: "46210-1",
    //       display: "Case Management Summary",
    //     },
    //   ],
    // },
    currentOutpatientTreaters: {
        coding: [
            {
                system: LOINC,
                code: "62387-6",
                display: "Current Outpatient Treaters"
            }
        ]
    },
    // --- Universal Intake subsection fields (added for the single-entry overhaul).
    // Reuse the parent-section LOINC with a unique display; the toForm reverse
    // mapping disambiguates by (code, display), mirroring the mental-status-* set.
    hospitalizations: {
        coding: [
            {
                system: LOINC,
                code: "45420-7",
                display: "Hospitalizations"
            }
        ]
    },
    safety: {
        coding: [
            {
                system: LOINC,
                code: "45420-7",
                display: "Safety and Risk History"
            }
        ]
    },
    "existing-treaters": {
        coding: [
            {
                system: LOINC,
                code: "62387-6",
                display: "Existing Treaters"
            }
        ]
    },
    "outpatient-care-history": {
        coding: [
            {
                system: LOINC,
                code: "62387-6",
                display: "History of Outpatient Care"
            }
        ]
    },
    "substance-current-use": {
        coding: [
            {
                system: LOINC,
                code: "87259-8",
                display: "Substance Use - Current or Past Use"
            }
        ]
    },
    "substance-treatment-history": {
        coding: [
            {
                system: LOINC,
                code: "87259-8",
                display: "Substance Use - Treatment History"
            }
        ]
    },
    "chronic-conditions": {
        coding: [
            {
                system: LOINC,
                code: "11348-0",
                display: "Known Chronic Conditions"
            }
        ]
    },
    "other-physical-symptoms": {
        coding: [
            {
                system: LOINC,
                code: "11348-0",
                display: "Other Physical Symptoms"
            }
        ]
    },
    "living-situation": {
        coding: [
            {
                system: LOINC,
                code: "29762-2",
                display: "Living Situation"
            }
        ]
    },
    "social-supports": {
        coding: [
            {
                system: LOINC,
                code: "29762-2",
                display: "Social Supports"
            }
        ]
    },
    "occupation-education": {
        coding: [
            {
                system: LOINC,
                code: "29762-2",
                display: "Occupation and Education"
            }
        ]
    },
    "peds-home-life": {
        coding: [
            {
                system: LOINC,
                code: "29762-2",
                display: "Pediatric - Home Life"
            }
        ]
    },
    "peds-school-education": {
        coding: [
            {
                system: LOINC,
                code: "29762-2",
                display: "Pediatric - School and Education"
            }
        ]
    },
    "peds-activities-strengths": {
        coding: [
            {
                system: LOINC,
                code: "29762-2",
                display: "Pediatric - Activities and Strengths"
            }
        ]
    },
    "peds-peers-social": {
        coding: [
            {
                system: LOINC,
                code: "29762-2",
                display: "Pediatric - Peers and Social"
            }
        ]
    },
    "peds-birth-complications": {
        coding: [
            {
                system: LOINC,
                code: "45420-7",
                display: "Pediatric - Birth Complications"
            }
        ]
    },
    "peds-developmental-milestones": {
        coding: [
            {
                system: LOINC,
                code: "45420-7",
                display: "Pediatric - Developmental Milestones"
            }
        ]
    },
    "peds-family-history": {
        coding: [
            {
                system: LOINC,
                code: "10157-6",
                display: "Pediatric - Family History"
            }
        ]
    }
};
/**
 * Clinical Impression Form Field Definitions
 *
 * Array of form field definitions derived from CLINICAL_IMPRESSION_CODE_MAPPINGS.
 * Used for rendering form fields in encounter forms.
 * Excludes "comment" field which is handled separately.
 */ const CLINICAL_IMPRESSION_FORM_FIELDS = Object.entries(CLINICAL_IMPRESSION_CODE_MAPPINGS).filter(([key])=>key !== "comment") // Exclude comment field
.map(([key, codeableConcept])=>({
        key: kebabCase(key),
        label: codeableConcept.coding?.[0]?.display || key
    }));
/**
 * Clinical fields retired from authoring, retained for read-back.
 *
 * Each described the same content as the subsections that replaced it, so the
 * AICD pipeline documented it twice. They are no longer in the AI schema, the
 * Composition emitters, the prefill mapping, the scribe overlay, or the intake
 * form's authored field list — but encounters documented before the split still
 * carry them, so their codings, hydration, and persistence stay.
 *
 * Because they are no longer authored, their value is frequently absent from
 * `formData` on save while their id is present in `_clinicalImpressionIds`
 * (every path that seeds the form from something other than a full
 * `encounterToForm` hydration — the scribe-encounter load in
 * `useIntakeInteractionSubmit`, for one). That combination is a DELETE in
 * `buildUpsertEncounterBundle`, so the builder special-cases these keys: a
 * retired field's ClinicalImpression is never deleted by an intake save.
 * @agentic @benchmark/core
 */ const RETIRED_CLINICAL_IMPRESSION_FIELD_KEYS = [
    "substance-use-history",
    "medical-history",
    "social-history",
    "current-outpatient-treaters"
];
/**
 * Default text seeded into MSE fields that a clinician on a phone call
 * cannot directly observe. Treated as "empty" by the scribe-prefill pipeline
 * so a real LLM-extracted value will overwrite it on a video encounter.
 * @agentic @benchmark/core
 */ const MSE_PHONE_NOT_INFERABLE_DEFAULT = "Not inferable from a phone call";
/**
 * Form field keys that are seeded with `MSE_PHONE_NOT_INFERABLE_DEFAULT`
 * on intake forms (telephonic encounters cannot observe these).
 * @agentic @benchmark/core
 */ const MSE_PHONE_DEFAULTED_FIELD_KEYS = [
    "mental-status-appearance",
    "mental-status-perception"
];
// Patient Tag Map
const ENCOUNTER_TAG_MAPPINGS = {
    method: {
        system: "http://terminology.hl7.org/CodeSystem/tag-encounter-method",
        code: "encounter-method",
        display: "Encounter Method"
    },
    type: {
        system: "http://terminology.hl7.org/CodeSystem/tag-encounter-type",
        code: "encounter-type",
        display: "Encounter Type"
    },
    inboundOutbound: {
        system: "http://terminology.hl7.org/CodeSystem/tag-encounter-inbound-outbound",
        code: "inbound-outbound",
        display: "Inbound/Outbound"
    }
};
const PATIENT_TAG_MAPPINGS = {
    engagementStatus: {
        system: "http://terminology.hl7.org/CodeSystem/patient-engagement",
        code: "engagement-status",
        display: "Engagement Status"
    }
};
/**
 * Extension URL for distinguishing practitioner role types in generalPractitioner
 * Shortened to GP_ prefix for readability
 */ const GP_ROLE_TYPE_EXTENSION = "http://benchmarkhealth.com/fhir/StructureDefinition/gp-role-type";
/**
 * Role type codes for generalPractitioner entries
 */ const GP_ROLE_TYPES = {
    REFERRING_PROVIDER: "referring-provider",
    BENCHMARK_PRIMARY_ADVOCATE: "benchmark-advocate",
    // Legacy code retained for backwards compatibility with existing Patient data.
    BENCHMARK_LCSW: "benchmark-lcsw"
};

/**
 * OCPE Phase 1: Taxonomy Compatibility Mapping
 *
 * Central mapping module for legacy-to-v2 activity/task taxonomy translation.
 * Supports display preference (v2 labels wherever possible) and deprecation checks.
 */ /**
 * Legacy activity code → v2 activity code mapping
 */ const ACTIVITY_CODE_MAP = {
    "initial-contact": "schedule-intake",
    "intake-assessment": "complete-intake",
    "care-plan-selection": "finalize-care-plan-enrollment",
    "enroll-in-care-plan": "finalize-care-plan-enrollment",
    "resource-offering": "offer-services",
    "appointment-scheduling": "schedule-services",
    "appointment-completion": "follow-up-on-services",
    "mirah-enrollment": "enroll-in-mirah",
    "cocm-intake": "complete-intake",
    "patient-check-in": "follow-up-on-cocm",
    "psychiatric-consultation": "consult-psych",
    "pcp-consultation": "consult-pcp",
    discharge: "discharge-member",
    // Old v2 codes → new v2 codes (safety net for staging/branch data)
    "select-services-for-referral": "select-services",
    "offer-referral": "offer-services",
    "confirm-referral-services": "confirm-services",
    "initiate-referral": "schedule-services",
    "follow-up-on-referral": "follow-up-on-services"
};
/**
 * Deprecated activity codes (no v2 equivalent, should not be created)
 * Display overrides for legacy data rendering
 */ /**
 * Deprecated plan-backed ad-hoc activity; use standalone custom tasks instead.
 * @agentic @benchmark/core
 */ const DEPRECATED_CUSTOM_CARE_PLAN_ACTIVITY_CODE = "custom-activity";
const DEPRECATED_ACTIVITY_CODES = [
    DEPRECATED_CUSTOM_CARE_PLAN_ACTIVITY_CODE,
    "resource-acceptance",
    "needs-evaluation",
    "ehr-documentation",
    "time-logging",
    "chart-review"
];
const DEPRECATED_ACTIVITY_DISPLAYS = {
    [DEPRECATED_CUSTOM_CARE_PLAN_ACTIVITY_CODE]: "Custom Activity (deprecated)",
    "resource-acceptance": "Resource Acceptance",
    "needs-evaluation": "Needs Evaluation",
    "ehr-documentation": "EHR Documentation",
    "time-logging": "Time Logging",
    "chart-review": "Chart Review"
};
/**
 * Standalone task type migration
 * - deprecated: Stop creating; maps to plan-backed activities or is superseded
 * - no-change: Remains standalone (custom, fax workflow tasks)
 */ const STANDALONE_TASK_MIGRATION_MAP = {
    "new-member-intake": {
        action: "deprecated",
        display: "Complete New Member Intake"
    },
    "offer-member-resources": {
        action: "deprecated",
        display: "Offer Resources to Member"
    },
    "schedule-member-appointment": {
        action: "deprecated",
        display: "Schedule Appointment for Member"
    },
    "member-check-in": {
        action: "deprecated",
        newActivityCode: "check-in-on-member"
    },
    custom: {
        action: "no-change",
        display: "Custom Task"
    },
    "review-new-referral": {
        action: "no-change",
        display: "🔔 NEW REFERRAL"
    },
    "potential-duplicate-review": {
        action: "no-change",
        display: "Review New Referral: Potential Duplicate"
    },
    "manual-review-fax": {
        action: "no-change",
        display: "Review New Referral: Complete Insurance & Verification"
    },
    "review-unmatched-fax": {
        action: "no-change",
        display: "Review Unmatched Fax: No Patient Found"
    },
    "post-care-followup": {
        action: "deprecated",
        display: "Complete Post-Care Followup"
    },
    "cadenced-followup": {
        action: "deprecated",
        display: "Complete Cadenced Followup"
    },
    "send-nps-survey": {
        action: "deprecated",
        display: "Send NPS Survey"
    }
};
/**
 * V2 activity code → display label
 * Canonical display strings for all v2 activity codes
 */ const V2_ACTIVITY_DISPLAYS = {
    // Intake
    "schedule-intake": "Schedule Intake",
    "complete-intake": "Complete Intake",
    "finalize-care-plan-enrollment": "Finalize Care Plan Enrollment",
    "check-in-on-member": "Check In on Member",
    // Triage
    "select-services": "Select Services",
    "approve-selected-services": "Approve Selected Services",
    "request-approval": "Approve Selected Services",
    "offer-services": "Offer Services to Member",
    "confirm-services": "Confirm Service Selections",
    "schedule-services": "Schedule Services",
    "follow-up-on-services": "Follow Up on Services",
    "complete-cadenced-followup": "Complete Cadenced Followup",
    // CoCM
    "enroll-in-mirah": "Confirm Mirah Enrollment",
    "build-cocm-care-plan": "Build CoCM Care Plan",
    "follow-up-on-cocm": "Follow Up on CoCM Progress",
    "consult-psych": "Consult Psychiatrist",
    "consult-pcp": "Consult PCP",
    "discharge-member": "Discharge Member"
};
/**
 * Resolve legacy or standalone task code to v2 activity code
 */ const resolveActivityCode = (code)=>ACTIVITY_CODE_MAP[code] ?? code;
/**
 * Resolve any code (activity or standalone task type) to v2 display label.
 * Prefer v2 labels wherever possible for consistent UI rendering.
 */ const resolveActivityDisplay = (code)=>{
    // Standalone task type with explicit newActivityCode → use v2 activity display
    const standaloneEntry = STANDALONE_TASK_MIGRATION_MAP[code];
    if (standaloneEntry?.newActivityCode) {
        return V2_ACTIVITY_DISPLAYS[standaloneEntry.newActivityCode] ?? code;
    }
    // Standalone task type with display override (no-change or deprecated fallback)
    if (standaloneEntry?.display) {
        return standaloneEntry.display;
    }
    // Deprecated activity codes: use legacy display for rendering
    if (DEPRECATED_ACTIVITY_DISPLAYS[code]) {
        return DEPRECATED_ACTIVITY_DISPLAYS[code];
    }
    // Activity code: resolve legacy → v2, then get display
    const v2Code = resolveActivityCode(code);
    return V2_ACTIVITY_DISPLAYS[v2Code] ?? code;
};
/**
 * Check if an activity code is deprecated (should not be created for new plans)
 */ const isDeprecatedActivityCode = (code)=>DEPRECATED_ACTIVITY_CODES.includes(code);
/**
 * Check if a standalone task type is deprecated (should not be created)
 */ const isDeprecatedStandaloneTaskType = (code)=>STANDALONE_TASK_MIGRATION_MAP[code]?.action === "deprecated";

/**
 * ActivityDefinition Type System
 * Custom system for activity definition codes
 */ const ACTIVITY_DEFINITION_TYPE_SYSTEM = "http://benchmarkhealth.com/fhir/CodeSystem/activity-type";
/**
 * ActivityDefinition Options (v2 canonical taxonomy)
 * Defines all available activity definitions for NEW CarePlans.
 * Legacy codes remain in taxonomy-mapping.ts for backwards-compatible display.
 */ const ACTIVITY_DEFINITION_OPTIONS = [
    // Intake Protocol Activities
    {
        code: "schedule-intake",
        display: "Schedule Intake",
        system: ACTIVITY_DEFINITION_TYPE_SYSTEM,
        description: "Make initial contact with the member to schedule an intake session.",
        canonical: "ActivityDefinition/schedule-intake",
        protocol: "intake"
    },
    {
        code: "complete-intake",
        display: "Complete Intake",
        system: ACTIVITY_DEFINITION_TYPE_SYSTEM,
        description: "Complete the initial clinical intake session.",
        canonical: "ActivityDefinition/complete-intake",
        protocol: "intake"
    },
    {
        code: "finalize-care-plan-enrollment",
        display: "Finalize Care Plan Enrollment",
        system: ACTIVITY_DEFINITION_TYPE_SYSTEM,
        description: "Finalize care plan enrollment after clinical intake session is completed.",
        canonical: "ActivityDefinition/finalize-care-plan-enrollment",
        protocol: "intake"
    },
    {
        code: "check-in-on-member",
        display: "Check In on Member",
        system: ACTIVITY_DEFINITION_TYPE_SYSTEM,
        description: "Check in on member to assess progress or needs.",
        canonical: "ActivityDefinition/check-in-on-member",
        protocol: "intake"
    },
    // Triage Protocol Activities
    {
        code: "select-services",
        display: "Select Services",
        system: ACTIVITY_DEFINITION_TYPE_SYSTEM,
        description: "Select external services that match the member's documented service needs.",
        canonical: "ActivityDefinition/select-services",
        protocol: "triage"
    },
    {
        code: "approve-selected-services",
        display: "Approve Selected Services",
        system: ACTIVITY_DEFINITION_TYPE_SYSTEM,
        description: "Review the services selected by SpecOps for your member.",
        canonical: "ActivityDefinition/approve-selected-services",
        protocol: "triage"
    },
    {
        code: "offer-services",
        display: "Offer Services to Member",
        system: ACTIVITY_DEFINITION_TYPE_SYSTEM,
        description: "Present the selected services to the member and discuss options.",
        canonical: "ActivityDefinition/offer-services",
        protocol: "triage"
    },
    {
        code: "confirm-services",
        display: "Confirm Service Selections",
        system: ACTIVITY_DEFINITION_TYPE_SYSTEM,
        description: "Confirm the member's acceptance of offered services before scheduling.",
        canonical: "ActivityDefinition/confirm-services",
        protocol: "triage"
    },
    {
        code: "schedule-services",
        display: "Schedule Services",
        system: ACTIVITY_DEFINITION_TYPE_SYSTEM,
        description: "Schedule appointment(s) with external service(s) on behalf of the member.",
        canonical: "ActivityDefinition/schedule-services",
        protocol: "triage"
    },
    {
        code: "follow-up-on-services",
        display: "Follow Up on Services",
        system: ACTIVITY_DEFINITION_TYPE_SYSTEM,
        description: "Reach out to the member to gauge appointment completion and satisfaction.",
        canonical: "ActivityDefinition/follow-up-on-services",
        protocol: "triage"
    },
    {
        code: "complete-cadenced-followup",
        display: "Complete Cadenced Followup",
        system: ACTIVITY_DEFINITION_TYPE_SYSTEM,
        description: "Check-in on member at 6 week intervals to assess ongoing progress or needs.",
        canonical: "ActivityDefinition/complete-cadenced-followup",
        protocol: "triage"
    },
    // CoCM Protocol Activities
    // Note: `enroll-in-mirah` was retired when the Benchmark/Mirah partnership
    // ended. The display label is still resolvable via `taxonomy-mapping.ts`
    // (`V2_ACTIVITY_DISPLAYS`) so existing tasks render correctly. New CoCM
    // plans no longer include this activity.
    {
        code: "build-cocm-care-plan",
        display: "Build CoCM Care Plan",
        system: ACTIVITY_DEFINITION_TYPE_SYSTEM,
        description: "Select and schedule the CoCM activities for this member's care plan.",
        canonical: "ActivityDefinition/build-cocm-care-plan",
        protocol: "cocm"
    },
    {
        code: "follow-up-on-cocm",
        display: "Follow Up on CoCM Progress",
        system: ACTIVITY_DEFINITION_TYPE_SYSTEM,
        description: "Check in on member to assess ongoing CoCM progress or needs.",
        canonical: "ActivityDefinition/follow-up-on-cocm",
        protocol: "cocm"
    },
    {
        code: "consult-psych",
        display: "Consult Psychiatrist",
        system: ACTIVITY_DEFINITION_TYPE_SYSTEM,
        description: "Schedule a consultation with a psychiatric provider to assess the member's mental health needs.",
        canonical: "ActivityDefinition/consult-psych",
        protocol: "cocm"
    },
    {
        code: "consult-pcp",
        display: "Consult PCP",
        system: ACTIVITY_DEFINITION_TYPE_SYSTEM,
        description: "Schedule a consultation with a primary care provider to assess the member's physical health needs.",
        canonical: "ActivityDefinition/consult-pcp",
        protocol: "cocm"
    },
    {
        code: "discharge-member",
        display: "Discharge Member",
        system: ACTIVITY_DEFINITION_TYPE_SYSTEM,
        description: "Discharge the member from the CoCM program.",
        canonical: "ActivityDefinition/discharge-member",
        protocol: "cocm"
    }
];
/**
 * Helper Functions
 * getActivityDefinitionDisplay uses taxonomy mapping for legacy code support
 */ const getActivityDefinitionDisplay = (code)=>resolveActivityDisplay(code);
const getActivityDefinitionDescription = (code)=>{
    const v2Code = resolveActivityCode(code);
    const option = ACTIVITY_DEFINITION_OPTIONS.find((o)=>o.code === v2Code);
    return option?.description ?? "";
};
const getActivityDefinitionCanonical = (code)=>{
    const v2Code = resolveActivityCode(code);
    const option = ACTIVITY_DEFINITION_OPTIONS.find((o)=>o.code === v2Code);
    return option?.canonical ?? `ActivityDefinition/${code}`;
};
/**
 * Returns activity definitions for a protocol.
 * @agentic @benchmark/core
 */ const getActivityDefinitionsByProtocol = (protocol)=>{
    return ACTIVITY_DEFINITION_OPTIONS.filter((o)=>o.protocol === protocol);
};
const getActivityDefinitionByCanonical = (canonical)=>ACTIVITY_DEFINITION_OPTIONS.find((o)=>o.canonical === canonical);
/**
 * UI-ready select options
 */ const ACTIVITY_DEFINITION_SELECT_OPTIONS = ACTIVITY_DEFINITION_OPTIONS.map((option)=>({
        value: option.code,
        label: option.display,
        description: option.description
    }));
const INTAKE_ACTIVITY_DEFINITION_SELECT_OPTIONS = ACTIVITY_DEFINITION_OPTIONS.filter((o)=>o.protocol === "intake").map((option)=>({
        value: option.code,
        label: option.display,
        description: option.description
    }));
const TRIAGE_ACTIVITY_DEFINITION_SELECT_OPTIONS = ACTIVITY_DEFINITION_OPTIONS.filter((o)=>o.protocol === "triage").map((option)=>({
        value: option.code,
        label: option.display,
        description: option.description
    }));
const COCM_ACTIVITY_DEFINITION_SELECT_OPTIONS = ACTIVITY_DEFINITION_OPTIONS.filter((o)=>o.protocol === "cocm").map((option)=>({
        value: option.code,
        label: option.display,
        description: option.description
    }));

/**
 * FHIR-Compliant Constants for Appointment Domain
 *
 * This file contains all constants used in Appointment forms, utils, and related components.
 * All codes follow FHIR R4 value sets and include proper system/code/display structures.
 */ // FHIR R4-compliant specialty options from Practice Setting Code Value Set
// http://hl7.org/fhir/ValueSet/c80-practice-codes
// Based on SNOMED CT International edition
const APPOINTMENT_SPECIALTY_OPTIONS = [
    {
        code: "394576009",
        display: "Accident & emergency",
        system: "http://snomed.info/sct"
    },
    {
        code: "408467006",
        display: "Adult mental illness",
        system: "http://snomed.info/sct"
    },
    {
        code: "394577000",
        display: "Anesthetics",
        system: "http://snomed.info/sct"
    },
    {
        code: "394579002",
        display: "Cardiology",
        system: "http://snomed.info/sct"
    },
    {
        code: "394588006",
        display: "Child and adolescent psychiatry",
        system: "http://snomed.info/sct"
    },
    {
        code: "394803006",
        display: "Clinical hematology",
        system: "http://snomed.info/sct"
    },
    {
        code: "408454008",
        display: "Clinical microbiology",
        system: "http://snomed.info/sct"
    },
    {
        code: "408478003",
        display: "Critical care medicine",
        system: "http://snomed.info/sct"
    },
    {
        code: "394582007",
        display: "Dermatology",
        system: "http://snomed.info/sct"
    },
    {
        code: "394583002",
        display: "Endocrinology",
        system: "http://snomed.info/sct"
    },
    {
        code: "419772000",
        display: "Family practice",
        system: "http://snomed.info/sct"
    },
    {
        code: "394584008",
        display: "Gastroenterology",
        system: "http://snomed.info/sct"
    },
    {
        code: "408444009",
        display: "General dental practice",
        system: "http://snomed.info/sct"
    },
    {
        code: "394802001",
        display: "General medicine",
        system: "http://snomed.info/sct"
    },
    {
        code: "394915009",
        display: "General pathology",
        system: "http://snomed.info/sct"
    },
    {
        code: "394814009",
        display: "General practice",
        system: "http://snomed.info/sct"
    },
    {
        code: "394811001",
        display: "Geriatric medicine",
        system: "http://snomed.info/sct"
    },
    {
        code: "394586005",
        display: "Gynecology",
        system: "http://snomed.info/sct"
    },
    {
        code: "394597005",
        display: "Histopathology",
        system: "http://snomed.info/sct"
    },
    {
        code: "394807007",
        display: "Infectious diseases",
        system: "http://snomed.info/sct"
    },
    {
        code: "419192003",
        display: "Internal medicine",
        system: "http://snomed.info/sct"
    },
    {
        code: "394593009",
        display: "Medical oncology",
        system: "http://snomed.info/sct"
    },
    {
        code: "394733009",
        display: "Medical specialty - Other",
        system: "http://snomed.info/sct"
    },
    {
        code: "394589003",
        display: "Nephrology",
        system: "http://snomed.info/sct"
    },
    {
        code: "394591006",
        display: "Neurology",
        system: "http://snomed.info/sct"
    },
    {
        code: "394649004",
        display: "Nuclear medicine",
        system: "http://snomed.info/sct"
    },
    {
        code: "394585009",
        display: "Obstetrics and gynecology",
        system: "http://snomed.info/sct"
    },
    {
        code: "408470005",
        display: "Obstetrics",
        system: "http://snomed.info/sct"
    },
    {
        code: "394821009",
        display: "Occupational medicine",
        system: "http://snomed.info/sct"
    },
    {
        code: "394594003",
        display: "Ophthalmology",
        system: "http://snomed.info/sct"
    },
    {
        code: "394605001",
        display: "Oral surgery",
        system: "http://snomed.info/sct"
    },
    {
        code: "394608004",
        display: "Orthodontics",
        system: "http://snomed.info/sct"
    },
    {
        code: "394882004",
        display: "Pain management",
        system: "http://snomed.info/sct"
    },
    {
        code: "394806003",
        display: "Palliative medicine",
        system: "http://snomed.info/sct"
    },
    {
        code: "408459003",
        display: "Pediatric cardiology",
        system: "http://snomed.info/sct"
    },
    {
        code: "394607009",
        display: "Pediatric dentistry",
        system: "http://snomed.info/sct"
    },
    {
        code: "419610006",
        display: "Pediatric endocrinology",
        system: "http://snomed.info/sct"
    },
    {
        code: "418058008",
        display: "Pediatric gastroenterology",
        system: "http://snomed.info/sct"
    },
    {
        code: "419365004",
        display: "Pediatric nephrology",
        system: "http://snomed.info/sct"
    },
    {
        code: "418002000",
        display: "Pediatric oncology",
        system: "http://snomed.info/sct"
    },
    {
        code: "394539006",
        display: "Pediatric surgery",
        system: "http://snomed.info/sct"
    },
    {
        code: "409968004",
        display: "Preventive medicine",
        system: "http://snomed.info/sct"
    },
    {
        code: "394587001",
        display: "Psychiatry",
        system: "http://snomed.info/sct"
    },
    {
        code: "394913002",
        display: "Psychotherapy",
        system: "http://snomed.info/sct"
    },
    {
        code: "408440000",
        display: "Public health medicine",
        system: "http://snomed.info/sct"
    },
    {
        code: "419815003",
        display: "Radiation oncology",
        system: "http://snomed.info/sct"
    },
    {
        code: "394914008",
        display: "Radiology",
        system: "http://snomed.info/sct"
    },
    {
        code: "408455009",
        display: "Radiology - Interventional radiology",
        system: "http://snomed.info/sct"
    },
    {
        code: "394602003",
        display: "Rehabilitation",
        system: "http://snomed.info/sct"
    },
    {
        code: "394810000",
        display: "Rheumatology",
        system: "http://snomed.info/sct"
    },
    {
        code: "408466002",
        display: "Surgery - Cardiac surgery",
        system: "http://snomed.info/sct"
    },
    {
        code: "394609007",
        display: "Surgery - General",
        system: "http://snomed.info/sct"
    },
    {
        code: "394610002",
        display: "Surgery - Neurosurgery",
        system: "http://snomed.info/sct"
    },
    {
        code: "394611003",
        display: "Surgery - Plastic surgery",
        system: "http://snomed.info/sct"
    },
    {
        code: "394801008",
        display: "Surgery - Trauma and orthopedics",
        system: "http://snomed.info/sct"
    },
    {
        code: "408463005",
        display: "Surgery - Vascular",
        system: "http://snomed.info/sct"
    },
    {
        code: "394732004",
        display: "Surgical specialty - Other",
        system: "http://snomed.info/sct"
    },
    {
        code: "394590007",
        display: "Thoracic medicine",
        system: "http://snomed.info/sct"
    },
    {
        code: "394612005",
        display: "Urology",
        system: "http://snomed.info/sct"
    }
];
// FHIR R4-compliant appointment status options
// http://hl7.org/fhir/R4/valueset-appointmentstatus.html
const APPOINTMENT_STATUS_OPTIONS = [
    {
        code: "pending",
        display: "Pending",
        system: "http://hl7.org/fhir/appointmentstatus",
        description: "Some or all of the participant(s) have not/did not appear for the appointment (usually the patient)."
    },
    {
        code: "booked",
        display: "Booked",
        system: "http://hl7.org/fhir/appointmentstatus",
        description: "All participant(s) have been considered and the appointment is confirmed to go ahead at the date/times specified."
    },
    {
        code: "fulfilled",
        display: "Fulfilled",
        system: "http://hl7.org/fhir/appointmentstatus",
        description: "The appointment has been completed and resulted in a clinical encounter."
    },
    {
        code: "cancelled",
        display: "Cancelled",
        system: "http://hl7.org/fhir/appointmentstatus",
        description: "The appointment has been cancelled."
    },
    {
        code: "noshow",
        display: "No Show",
        system: "http://hl7.org/fhir/appointmentstatus",
        description: "Some or all of the participant(s) have not/did not appear for the appointment (usually the patient)."
    },
    {
        code: "waitlist",
        display: "Waitlisted",
        system: "http://hl7.org/fhir/appointmentstatus",
        description: "The appointment has been placed on a waitlist, to be scheduled/confirmed in the future when a slot/service is available."
    }
];
// UI-specific mappings for Select components
const APPOINTMENT_SPECIALTY_SELECT_OPTIONS = APPOINTMENT_SPECIALTY_OPTIONS.map((specialty)=>({
        value: specialty.code,
        label: specialty.display
    }));
const APPOINTMENT_STATUS_SELECT_OPTIONS = APPOINTMENT_STATUS_OPTIONS.map((status)=>({
        value: status.code,
        label: status.display
    }));
// Helper function to get specialty display name from code
function getSpecialtyDisplay(code) {
    const specialty = APPOINTMENT_SPECIALTY_OPTIONS.find((s)=>s.code === code);
    return specialty?.display || "Unknown specialty";
}
// Helper function to get status display name from code
function getStatusDisplay(code) {
    const status = APPOINTMENT_STATUS_OPTIONS.find((s)=>s.code === code);
    return status?.display || "Unknown status";
}

/**
 * Assignment Mode Constants
 *
 * Defines how Task.owner should be determined for a given activity.
 * Used by ACTIVITY_METADATA to configure per-activity assignment.
 * @agentic @benchmark/core
 */ const ASSIGNMENT_MODE_OPTIONS = [
    {
        code: "unassigned-queue",
        display: "Unassigned Queue",
        description: "Task.owner is absent until claimed from a shared queue"
    },
    {
        code: "current-user",
        display: "Current User",
        description: "Task.owner set to the currently logged-in user's PractitionerRole"
    },
    {
        code: "primary-advocate",
        display: "Primary Advocate",
        description: "Task.owner set to the patient's primary advocate (Patient.generalPractitioner with benchmark-advocate extension)"
    },
    {
        code: "role-restricted",
        display: "Role",
        description: "Task.owner set to a PractitionerRole matching a specific role code"
    },
    {
        code: "care-team-assigned",
        display: "Care Team",
        description: "Task.owner set to the patient's designated care team member"
    }
];
/**
 * CarePlan Category System
 * Custom Benchmark Health system for care plan categorization
 */ const CARE_PLAN_CATEGORY_SYSTEM = "http://benchmarkhealth.com/fhir/CodeSystem/care-plan-type";
/**
 * CarePlan Category Options
 * Defines the types of care plans available
 */ const CARE_PLAN_CATEGORY_OPTIONS = [
    {
        code: "intake",
        display: "Intake",
        system: CARE_PLAN_CATEGORY_SYSTEM,
        description: "Initial Member Intake & Onboarding"
    },
    {
        code: "triage",
        display: "Triage",
        system: CARE_PLAN_CATEGORY_SYSTEM,
        description: "Benchmark Care Triage & Navigation"
    },
    {
        code: "cocm",
        display: "CoCM",
        system: CARE_PLAN_CATEGORY_SYSTEM,
        description: "Benchmark CoCM"
    }
];
/**
 * CarePlan Activity Code System
 * Custom system for activity phases within care plans
 * Note: Activity definitions come from ActivityDefinition constants.
 * This system is used for CarePlan.activity.code codings.
 */ const CARE_PLAN_ACTIVITY_SYSTEM = "http://benchmarkhealth.com/fhir/CodeSystem/care-plan-activity";
/**
 * Metadata mapping for all v2 activities
 * Legacy codes resolve via taxonomy-mapping for backwards compatibility
 */ const ACTIVITY_METADATA = {
    // Intake
    "schedule-intake": {
        order: 1,
        category: "sequential",
        isFlexible: false,
        assignmentMode: "primary-advocate",
        assignmentGeneralPractitionerIndex: 1
    },
    "complete-intake": {
        order: 2,
        category: "sequential",
        isFlexible: false,
        assignmentMode: "primary-advocate",
        assignmentGeneralPractitionerIndex: 1
    },
    "finalize-care-plan-enrollment": {
        order: 3,
        category: "sequential",
        isFlexible: false,
        assignmentMode: "primary-advocate",
        assignmentGeneralPractitionerIndex: 1
    },
    "check-in-on-member": {
        order: 4,
        category: "flexible",
        isFlexible: true,
        assignmentMode: "current-user"
    },
    // Triage — tasks start unassigned in the shared queue
    "select-services": {
        order: 1,
        category: "sequential",
        isFlexible: false,
        assignmentMode: "unassigned-queue"
    },
    "approve-selected-services": {
        order: 2,
        category: "as-needed",
        isFlexible: true,
        assignmentMode: "primary-advocate",
        assignmentGeneralPractitionerIndex: 1
    },
    "offer-services": {
        order: 3,
        category: "sequential",
        isFlexible: false,
        assignmentMode: "unassigned-queue"
    },
    "confirm-services": {
        order: 4,
        category: "sequential",
        isFlexible: false,
        assignmentMode: "unassigned-queue"
    },
    "schedule-services": {
        order: 5,
        category: "sequential",
        isFlexible: false,
        assignmentMode: "unassigned-queue"
    },
    "follow-up-on-services": {
        order: 6,
        category: "sequential",
        isFlexible: false,
        assignmentMode: "unassigned-queue"
    },
    "complete-cadenced-followup": {
        order: 7,
        category: "sequential",
        isFlexible: false,
        maxOccurrences: 3,
        slaDurationHours: 1008,
        assignmentMode: "unassigned-queue"
    },
    // CoCM — all tasks assigned to patient's primary advocate (benchmark-advocate extension)
    // Note: `enroll-in-mirah` (order 1) was retired with the Benchmark/Mirah
    // partnership wind-down. `build-cocm-care-plan` is now the first CoCM
    // sequential activity. Historical task displays still resolve via taxonomy
    // mapping.
    "build-cocm-care-plan": {
        order: 1,
        category: "sequential",
        isFlexible: false,
        assignmentMode: "primary-advocate",
        assignmentGeneralPractitionerIndex: 1
    },
    "follow-up-on-cocm": {
        order: 3,
        category: "flexible",
        isFlexible: true,
        assignmentMode: "primary-advocate",
        assignmentGeneralPractitionerIndex: 1
    },
    "consult-psych": {
        order: 4,
        category: "flexible",
        isFlexible: true,
        assignmentMode: "primary-advocate",
        assignmentGeneralPractitionerIndex: 1
    },
    "consult-pcp": {
        order: 5,
        category: "flexible",
        isFlexible: true,
        assignmentMode: "primary-advocate",
        assignmentGeneralPractitionerIndex: 1
    },
    "discharge-member": {
        order: 50,
        category: "flexible",
        isFlexible: true,
        assignmentMode: "primary-advocate",
        assignmentGeneralPractitionerIndex: 1
    }
};
/**
 * Get activity metadata for a given activity code.
 * Resolves legacy codes to v2 for backwards compatibility.
 */ const getActivityMetadata = (code)=>{
    const v2Code = resolveActivityCode(code);
    return ACTIVITY_METADATA[v2Code];
};
/**
 * Create CarePlan activity option from ActivityDefinition code
 * Combines ActivityDefinition data with CarePlan metadata
 */ const createCarePlanActivityOption = (code)=>{
    const activityDef = ACTIVITY_DEFINITION_OPTIONS.find((a)=>a.code === code);
    const metadata = getActivityMetadata(code);
    if (!activityDef || !metadata) {
        return undefined;
    }
    return {
        code: activityDef.code,
        display: activityDef.display,
        system: CARE_PLAN_ACTIVITY_SYSTEM,
        description: activityDef.description,
        order: metadata.order,
        isFlexible: metadata.isFlexible,
        category: metadata.category
    };
};
/**
 * Get activities for a care plan type
 * Returns CarePlan activity options with metadata
 */ const getActivitiesForCarePlanType = (type)=>{
    const protocol = type === "cocm" ? "cocm" : type;
    const activityDefs = getActivityDefinitionsByProtocol(protocol);
    return activityDefs.map((def)=>createCarePlanActivityOption(def.code)).filter((option)=>option !== undefined);
};
/**
 * Get all CarePlan activity options
 * Useful for lookups and validation
 */ const getAllCarePlanActivityOptions = ()=>{
    return ACTIVITY_DEFINITION_OPTIONS.map((def)=>createCarePlanActivityOption(def.code)).filter((option)=>option !== undefined);
};
/**
 * Helper Functions
 */ const getCarePlanCategoryDisplay = (code)=>{
    const option = CARE_PLAN_CATEGORY_OPTIONS.find((o)=>o.code === code);
    return option?.display || code;
};
const getCarePlanActivityDisplay = (code)=>getActivityDefinitionDisplay(code);
const getCarePlanActivityDescription = (code)=>getActivityDefinitionDescription(code);
const isActivityFlexible = (code)=>{
    const metadata = getActivityMetadata(code);
    return metadata?.isFlexible ?? false;
};
/**
 * Get the SLA duration in hours for an activity code.
 * Returns undefined for flexible/as-needed activities or codes without SLAs.
 */ const getActivitySlaDurationHours = (code)=>{
    const metadata = getActivityMetadata(code);
    return metadata?.slaDurationHours;
};
/**
 * Get the number of occurrences for a multi-occurrence activity (e.g. cadenced-followup: 3).
 * Returns 1 for single-occurrence activities.
 */ const getActivityMaxOccurrences = (code)=>{
    const metadata = getActivityMetadata(code);
    return metadata?.maxOccurrences ?? 1;
};
/**
 * Get the cadence interval in weeks for multi-occurrence activities.
 * Derived from slaDurationHours (e.g. 1008 hours = 6 weeks).
 * Returns undefined for single-occurrence activities.
 */ const getActivityCadenceWeeks = (code)=>{
    const metadata = getActivityMetadata(code);
    if (!metadata?.maxOccurrences || metadata.maxOccurrences <= 1) return undefined;
    if (!metadata.slaDurationHours) return undefined;
    return Math.round(metadata.slaDurationHours / 168);
};
/**
 * Backwards-compatible exports for activity options
 * These now derive from ActivityDefinition constants with metadata
 */ const INTAKE_ACTIVITY_OPTIONS = getActivitiesForCarePlanType("intake");
const TRIAGE_ACTIVITY_OPTIONS = getActivitiesForCarePlanType("triage");
/**
 * Set of all triage activity codes for efficient lookup.
 * @agentic @benchmark/core
 */ const TRIAGE_ACTIVITY_CODES = new Set(TRIAGE_ACTIVITY_OPTIONS.map((opt)=>opt.code));
const COCM_ACTIVITY_OPTIONS = getActivitiesForCarePlanType("cocm");
/**
 * Get sequential activities for any care plan type.
 * Only sequential activities are auto-assigned at creation time.
 * Flexible/as-needed activities can be added manually later.
 */ const getSequentialActivitiesForType = (type)=>getActivitiesForCarePlanType(type).filter((activity)=>!activity.isFlexible);
/**
 * Get CoCM sequential activities.
 * These are automatically assigned when creating a CoCM care plan.
 */ const getCoCMSequentialActivities = ()=>{
    const sequentialCodes = [
        "build-cocm-care-plan"
    ];
    return getActivitiesForCarePlanType("cocm").filter((activity)=>sequentialCodes.includes(activity.code));
};
/**
 * Get flexible activities for any care plan type.
 * Returns activities that can be manually added after care plan creation.
 */ const getFlexibleActivitiesForType = (type)=>{
    const flexibleActivities = getActivitiesForCarePlanType(type).filter((activity)=>activity.isFlexible);
    const ensureActivity = (code, fallback)=>{
        if (flexibleActivities.some((activity)=>activity.code === code)) {
            return;
        }
        const maxOrder = Math.max(0, ...flexibleActivities.map((a)=>a.order));
        flexibleActivities.push({
            ...fallback,
            order: maxOrder + 1
        });
    };
    if (type === "intake" || type === "triage") {
        ensureActivity("check-in-on-member", {
            code: "check-in-on-member",
            display: getCarePlanActivityDisplay("check-in-on-member"),
            system: CARE_PLAN_ACTIVITY_SYSTEM,
            description: getCarePlanActivityDescription("check-in-on-member"),
            isFlexible: true,
            category: "flexible"
        });
    }
    return flexibleActivities;
};
/**
 * Get CoCM flexible activities (can be added manually after care plan creation)
 */ const getCoCMFlexibleActivities = ()=>{
    const flexibleCodes = [
        "follow-up-on-cocm",
        "consult-psych",
        "consult-pcp",
        "discharge-member"
    ];
    return getActivitiesForCarePlanType("cocm").filter((activity)=>flexibleCodes.includes(activity.code));
};
/**
 * UI-ready select options
 */ const CARE_PLAN_CATEGORY_SELECT_OPTIONS = CARE_PLAN_CATEGORY_OPTIONS.map((option)=>({
        value: option.code,
        label: option.display,
        description: option.description
    }));
const INTAKE_ACTIVITY_SELECT_OPTIONS = getActivitiesForCarePlanType("intake").map((option)=>({
        value: option.code,
        label: option.display,
        description: option.description,
        isFlexible: option.isFlexible
    }));
const TRIAGE_ACTIVITY_SELECT_OPTIONS = getActivitiesForCarePlanType("triage").map((option)=>({
        value: option.code,
        label: option.display,
        description: option.description,
        isFlexible: option.isFlexible
    }));
const COCM_ACTIVITY_SELECT_OPTIONS = getActivitiesForCarePlanType("cocm").map((option)=>({
        value: option.code,
        label: option.display,
        description: option.description,
        isFlexible: option.isFlexible
    }));
/**
 * Get the assignment mode for an activity code. Defaults to "current-user".
 * @agentic @benchmark/core
 */ const getActivityAssignmentMode = (code)=>{
    const metadata = getActivityMetadata(code);
    return metadata?.assignmentMode ?? "current-user";
};
/**
 * Get the role code for role-restricted activities. Returns undefined for other modes.
 * @agentic @benchmark/core
 */ const getActivityAssignmentRoleCode = (code)=>{
    const metadata = getActivityMetadata(code);
    return metadata?.assignmentRoleCode;
};
/**
 * Get the Patient.generalPractitioner index for GP-assigned activities.
 * @agentic @benchmark/core
 */ const getActivityAssignmentGPIndex = (code)=>{
    const metadata = getActivityMetadata(code);
    return metadata?.assignmentGeneralPractitionerIndex;
};
/**
 * CoCM Consent Date Extension URL
 * FHIR extension for storing the date when a patient consented to receive CoCM services
 */ const COCM_CONSENT_DATE_EXTENSION_URL = "http://benchmarkhealth.com/fhir/StructureDefinition/cocm-consent-date";
/**
 * Pre-pause CarePlan status extension URL.
 *
 * Holding a CarePlan for a patient pause overwrites `CarePlan.status`, which is
 * otherwise the only record of what the plan was doing beforehand. Resuming
 * without this marker has to guess, and guessing `active` promotes a `draft`
 * plan that was never started. The pause bundle stamps the pre-pause status
 * here; the unpause bundle restores from it and removes the extension.
 * @agentic @benchmark/core
 */ const CARE_PLAN_PRE_PAUSE_STATUS_EXTENSION_URL = "http://benchmarkhealth.com/fhir/StructureDefinition/care-plan-pre-pause-status";

/**
 * FHIR-Compliant Constants for Observation Domain
 *
 * This file contains all constants used in Observation forms, utils, and related components.
 * All codes follow FHIR R4 value sets and include proper system/code/display structures.
 *
 * IMPORTANT: This is the SINGLE SOURCE OF TRUTH for all observation-related constants:
 * - LOINC codes for questionnaire total scores
 * - Risk level classifications and scoring
 * - Questionnaire scale configurations
 * - Observation type definitions
 *
 * These codes should NOT be used for Questionnaire or QuestionnaireResponse resources.
 *
 * Validation: ✅ All codes verified against official LOINC database (2025-01-08)
 * See: packages/medplum/LOINC_CODE_MAPPINGS.md for detailed mapping documentation
 */ /**
 * LEGACY observation LOINC codes - deprecated but maintained for backwards compatibility
 * These codes may exist in historical data and should be supported for viewing/editing
 * but should NOT be used for creating new resources
 */ const LEGACY_OBSERVATION_LOINC_CODES = {
    /** GAD-2 Total Score - LOINC 89204-7 ✅ VALIDATED - DEPRECATED: Use GAD-7 or PHQ-4 instead */ GAD2_TOTAL_SCORE: "89204-7",
    /** PHQ-2 Total Score - LOINC 55758-7 ✅ VALIDATED - DEPRECATED: Use PHQ-4 instead */ PHQ2_TOTAL_SCORE: "55758-7"
};
/**
 * Current observation LOINC codes for assessment scores
 * Used for creating new Observation resources from questionnaire responses
 *
 * IMPORTANT: These codes should ONLY be used for Observation resources that contain
 * calculated total scores. For questionnaire definitions, use QUESTIONNAIRE_LOINC_CODES
 * from questionnaire.ts which contains the panel codes.
 *
 * Validation: ✅ All codes verified against official LOINC database (2025-01-08)
 * See: packages/medplum/LOINC_CODE_MAPPINGS.md for detailed mapping documentation
 */ const OBSERVATION_LOINC_CODES = {
    /** ASRS Adult ADHD Self-Report Scale Total Score - Organizational code (no official LOINC) */ ASRS_TOTAL_SCORE: "https://benchmarkhealth.org/fhir/CodeSystem/asrs-total-score",
    /** AUDIT-C Total Score - LOINC 75626-2 ✅ VALIDATED */ AUDIT_C_TOTAL_SCORE: "75626-2",
    /** C-SSRS Columbia Suicide Severity Rating Scale Risk Level - Organizational code (no official LOINC) */ C_SSRS_SEVERITY: "https://benchmarkhealth.org/fhir/CodeSystem/c-ssrs-severity",
    /** GAD-7 Total Score - LOINC 70274-6 ✅ VALIDATED */ GAD7_TOTAL_SCORE: "70274-6",
    /** GAD-2 Total Score - Organizational code (the legacy LOINC 89204-7 is deprecated; the adaptive short check uses an org code) */ GAD2_TOTAL_SCORE: "https://benchmarkhealth.org/fhir/CodeSystem/gad-2-total-score",
    /** UCLA-3 Three-Item Loneliness Scale Total Score - Organizational code (no official LOINC) */ UCLA3_TOTAL_SCORE: "https://benchmarkhealth.org/fhir/CodeSystem/ucla-3-total-score",
    /** WSAS Work and Social Adjustment Scale Total Score - Organizational code (no official LOINC) */ WSAS_TOTAL_SCORE: "https://benchmarkhealth.org/fhir/CodeSystem/wsas-total-score",
    /** OCI-4 Obsessive-Compulsive Inventory Total Score - Organizational code (no official LOINC) */ OCI_4_TOTAL_SCORE: "https://benchmarkhealth.org/fhir/CodeSystem/oci-4-total-score",
    /** PC-PTSD-5 Total Score - LOINC 102017-1 ✅ VALIDATED */ PC_PTSD_5_TOTAL_SCORE: "102017-1",
    /** PSC-17 Pediatric Symptom Checklist Total Score - Organizational code (no official LOINC) */ PSC_17_TOTAL_SCORE: "https://benchmarkhealth.org/fhir/CodeSystem/psc-17-total-score",
    /** PHQ-9 Total Score - LOINC 44261-6 ✅ VALIDATED */ PHQ9_TOTAL_SCORE: "44261-6",
    /** PHQ-9A (Adolescent) Total Score - LOINC 89204-2 ✅ VALIDATED */ PHQ9A_TOTAL_SCORE: "89204-2",
    /** PHQ-4 Total Score - LOINC 70272-0 ✅ VALIDATED */ PHQ4_TOTAL_SCORE: "70272-0",
    /** PRAPARE Social Determinants Risk Score - Organizational code (no official LOINC) */ PRAPARE_TOTAL_SCORE: "https://benchmarkhealth.org/fhir/CodeSystem/prapare-risk-score",
    /** SBIRT-ED Eating Disorder Screening Score - Organizational code (no official LOINC) */ SBIRT_ED_TOTAL_SCORE: "https://benchmarkhealth.org/fhir/CodeSystem/sbirt-ed-score",
    /** TAPS-1 Substance Use Screening Score - Organizational code (no official LOINC) */ TAPS_1_TOTAL_SCORE: "https://benchmarkhealth.org/fhir/CodeSystem/taps-1-score",
    /** TAPS-2 Substance Use Assessment Score - Organizational code (no official LOINC) */ TAPS_2_TOTAL_SCORE: "https://benchmarkhealth.org/fhir/CodeSystem/taps-2-score",
    /** WPAI Work Productivity and Activity Impairment Score - Organizational code (no official LOINC) */ WPAI_TOTAL_SCORE: "https://benchmarkhealth.org/fhir/CodeSystem/wpai-shp-score",
    /** WHO-5 Well-Being Index Total Score - Organizational code (no official LOINC) */ WHO5_TOTAL_SCORE: "https://benchmarkhealth.org/fhir/CodeSystem/who-5-total-score",
    /** Health Leads Social Needs Screening Toolkit Score - Organizational code (no official LOINC) */ SDOH_TOTAL_SCORE: "https://benchmarkhealth.org/fhir/CodeSystem/sdoh-health-leads-score",
    /** Mirah Complexity composite score - Mirah-proprietary (no official LOINC) */ MIRAH_COMPLEXITY_SCORE: "https://benchmarkhealth.org/fhir/CodeSystem/mirah-complexity-score",
    /** Mirah Severity composite score - Mirah-proprietary (no official LOINC) */ MIRAH_SEVERITY_SCORE: "https://benchmarkhealth.org/fhir/CodeSystem/mirah-severity-score",
    /** NPS 0–10 likelihood score — organizational code (no official LOINC) */ NPS_SCORE: "https://benchmarkhealth.org/fhir/CodeSystem/nps-score"
};
/**
 * Risk level classifications for observations
 * Used for interpreting observation scores and providing clinical context
 */ const OBSERVATION_RISK_LEVELS = {
    MINIMAL: {
        value: "minimal",
        display: "Minimal",
        color: "green"
    },
    MILD: {
        value: "mild",
        display: "Mild",
        color: "yellow"
    },
    MODERATE: {
        value: "moderate",
        display: "Moderate",
        color: "orange"
    },
    MODERATELY_SEVERE: {
        value: "moderately-severe",
        display: "Moderately Severe",
        color: "red"
    },
    SEVERE: {
        value: "severe",
        display: "Severe",
        color: "red"
    },
    HIGH: {
        value: "high",
        display: "High",
        color: "red"
    },
    LOW: {
        value: "low",
        display: "Low",
        color: "green"
    },
    NEGATIVE: {
        value: "negative",
        display: "Negative",
        color: "green"
    },
    POSITIVE: {
        value: "positive",
        display: "Positive",
        color: "orange"
    },
    CLINICAL: {
        value: "clinical",
        display: "Clinical",
        color: "red"
    },
    SUBCLINICAL: {
        value: "subclinical",
        display: "Subclinical",
        color: "yellow"
    }
};
/**
 * LEGACY observation types - deprecated but maintained for backwards compatibility
 * These types may exist in historical data and should be supported for viewing/editing
 * but should NOT be used for creating new resources
 */ const LEGACY_OBSERVATION_TYPES = [
    {
        code: "89204-7",
        display: "GAD-2",
        system: "http://loinc.org",
        fieldKey: "gad-2",
        description: "Generalized Anxiety Disorder-2",
        minValue: 0,
        maxValue: 6,
        deprecated: true,
        deprecationReason: "Replaced by GAD-7 or PHQ-4 for combined screening"
    },
    {
        code: "55758-7",
        display: "PHQ-2",
        system: "http://loinc.org",
        fieldKey: "phq-2",
        description: "Patient Health Questionnaire-2",
        minValue: 0,
        maxValue: 6,
        deprecated: true,
        deprecationReason: "Replaced by PHQ-4 for combined depression/anxiety screening"
    }
];
/**
 * Current observation types for assessment scores
 * FHIR R4-compliant observation types from LOINC
 * http://hl7.org/fhir/ValueSet/observation-codes
 * These are TOTAL SCORE codes for questionnaire observations
 * This is the UNIFIED source that replaces the previous duplicated constants
 */ const OBSERVATION_TYPES = [
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/asrs-total-score",
        display: "ASRS",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "asrs",
        description: "Adult ADHD Self-Report Scale",
        minValue: 0,
        maxValue: 24
    },
    {
        code: "75626-2",
        display: "AUDIT-C",
        system: "http://loinc.org",
        fieldKey: "audit-c",
        description: "Alcohol Use Disorders Identification Test - Consumption",
        minValue: 0,
        maxValue: 12
    },
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/gad-2-total-score",
        display: "GAD-2",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "gad-2",
        description: "Generalized Anxiety Disorder 2-item short check",
        minValue: 0,
        maxValue: 6
    },
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/ucla-3-total-score",
        display: "UCLA-3",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "ucla-3",
        description: "Three-Item Loneliness Scale (UCLA)",
        minValue: 3,
        maxValue: 9
    },
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/wsas-total-score",
        display: "WSAS",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "wsas",
        description: "Work and Social Adjustment Scale",
        minValue: 0,
        maxValue: 40
    },
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/c-ssrs-severity",
        display: "C-SSRS",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "c-ssrs",
        description: "Columbia Suicide Severity Rating Scale",
        minValue: 0,
        maxValue: 5
    },
    {
        code: "70274-6",
        display: "GAD-7",
        system: "http://loinc.org",
        fieldKey: "gad-7",
        description: "Generalized Anxiety Disorder-7",
        minValue: 0,
        maxValue: 21
    },
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/oci-4-total-score",
        display: "OCI-4",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "oci-4",
        description: "Obsessive-Compulsive Inventory-4",
        minValue: 0,
        maxValue: 16
    },
    {
        code: "102017-1",
        display: "PC-PTSD-5",
        system: "http://loinc.org",
        fieldKey: "pc-ptsd-5",
        description: "Primary Care PTSD Screen for DSM-5",
        minValue: 0,
        maxValue: 5
    },
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/psc-17-total-score",
        display: "PSC-17",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "psc-17",
        description: "Pediatric Symptom Checklist-17",
        minValue: 0,
        maxValue: 34
    },
    {
        code: "44261-6",
        display: "PHQ-9",
        system: "http://loinc.org",
        fieldKey: "phq-9",
        description: "Patient Health Questionnaire-9",
        minValue: 0,
        maxValue: 27
    },
    {
        code: "89204-2",
        display: "PHQ-9A",
        system: "http://loinc.org",
        fieldKey: "phq-9a",
        description: "Patient Health Questionnaire-9 Modified for Teens",
        minValue: 0,
        maxValue: 27
    },
    {
        code: "70272-0",
        display: "PHQ-4",
        system: "http://loinc.org",
        fieldKey: "phq-4",
        description: "Patient Health Questionnaire-4",
        minValue: 0,
        maxValue: 12
    },
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/prapare-risk-score",
        display: "PRAPARE",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "prapare",
        description: "Protocol for Responding to and Assessing Patients' Assets, Risks, and Experiences",
        minValue: 0,
        maxValue: 100
    },
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/sbirt-ed-score",
        display: "SBIRT-ED",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "sbirt-ed",
        description: "SBIRT Eating Disorder Screener",
        minValue: 0,
        maxValue: 5
    },
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/taps-1-score",
        display: "TAPS-1",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "taps-1",
        description: "TAPS Screener (Part 1)",
        minValue: 0,
        maxValue: 16
    },
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/taps-2-score",
        display: "TAPS-2",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "taps-2",
        description: "TAPS Assessment (Part 2)",
        minValue: 0,
        maxValue: 100
    },
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/wpai-impairment-score",
        display: "WPAI",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "wpai",
        description: "Work Productivity and Activity Impairment",
        minValue: 0,
        maxValue: 100
    },
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/who-5-total-score",
        display: "WHO-5",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "who-5",
        description: "World Health Organization Well-Being Index",
        minValue: 0,
        maxValue: 25
    },
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/sdoh-health-leads-score",
        display: "SDOH",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "sdoh",
        description: "Social Needs Screening Toolkit (Health Leads)",
        minValue: 0,
        maxValue: 100
    },
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/mirah-complexity-score",
        display: "Mirah Complexity",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "mirah-complexity",
        description: "Mirah Complexity Composite Score",
        minValue: 0,
        maxValue: 100
    },
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/mirah-severity-score",
        display: "Mirah Severity",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "mirah-severity",
        description: "Mirah Severity Composite Score",
        minValue: 0,
        maxValue: 100
    },
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/nps-score",
        display: "NPS",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "nps",
        description: "Net Promoter Score (0–10)",
        minValue: 0,
        maxValue: 10
    }
];
// Select options for forms
const OBSERVATION_TYPE_SELECT_OPTIONS = OBSERVATION_TYPES.map((type)=>({
        value: type.code,
        label: type.display
    }));
/**
 * FHIR-compliant category for survey-type observations
 * All questionnaire scores and clinical assessments use this category
 */ const SURVEY_OBSERVATION_CATEGORY = {
    coding: [
        {
            system: "http://terminology.hl7.org/CodeSystem/observation-category",
            code: "survey",
            display: "Survey"
        }
    ]
};
/**
 * Get display name for observation type code
 * Maps LOINC codes to display names
 */ function getObservationTypeDisplay(code) {
    const observationType = OBSERVATION_TYPES.find((type)=>type.code === code);
    return observationType?.display || "Unknown observation type";
}
/**
 * Get observation display name by LOINC code
 * Returns the short display name (e.g., "PHQ-4", "GAD-7") for a given LOINC code
 * Falls back to the code itself if not found
 *
 * @param loincCode - The LOINC code to look up
 * @returns The display name or the code itself if not found
 */ function getObservationDisplayByLoincCode(loincCode) {
    const type = OBSERVATION_TYPES.find((t)=>t.code === loincCode);
    return type?.display ?? loincCode;
}
/**
 * Validate that a LOINC code is appropriate for Observation resources
 * Helps prevent mixing up questionnaire panel codes with observation score codes
 */ function isValidObservationLoincCode(code) {
    return OBSERVATION_TYPES.some((type)=>type.code === code);
}
/**
 * Get the complete observation coding for a given LOINC code
 * Returns undefined if the code is not valid for observations
 */ function getObservationCoding(code) {
    const observationType = OBSERVATION_TYPES.find((type)=>type.code === code);
    return observationType ? {
        system: observationType.system,
        code: observationType.code,
        display: observationType.display
    } : undefined;
}
/**
 * Get observation coding for a specific questionnaire field
 * Returns the proper LOINC coding for creating observations from form fields
 * Derived from the unified OBSERVATION_TYPES array
 */ function getObservationCodingForField(field) {
    // First try current observation types
    let observationType = OBSERVATION_TYPES.find((type)=>type.fieldKey === field);
    // If not found, try legacy observation types
    if (!observationType) {
        observationType = LEGACY_OBSERVATION_TYPES.find((type)=>type.fieldKey === field);
    }
    return observationType ? {
        system: observationType.system,
        code: observationType.code,
        display: observationType.display
    } : undefined;
}
/**
 * Get questionnaire scale information by field key
 * Returns the scale configuration including valid score ranges
 * Derived from the unified OBSERVATION_TYPES array
 */ function getQuestionnaireScaleInfoByField(field) {
    // First try current observation types
    let observationType = OBSERVATION_TYPES.find((type)=>type.fieldKey === field);
    // If not found, try legacy observation types
    if (!observationType) {
        observationType = LEGACY_OBSERVATION_TYPES.find((type)=>type.fieldKey === field);
    }
    return observationType ? {
        code: observationType.fieldKey,
        display: observationType.display,
        description: observationType.description,
        minValue: observationType.minValue,
        maxValue: observationType.maxValue,
        system: observationType.system,
        deprecated: "deprecated" in observationType ? observationType.deprecated : undefined,
        deprecationReason: "deprecationReason" in observationType ? observationType.deprecationReason : undefined
    } : undefined;
}

/**
 * FHIR extension URL marking a care-time entry as excluded from CoCM billing.
 * Absent or `valueBoolean: false` means the entry is billable (backward
 * compatible — no historical data carries the extension). See the care-time
 * non-billable toggle plan (2026-07-23).
 * @agentic @benchmark/core
 */ const CARE_TIME_NON_BILLABLE_EXTENSION_URL = `${EXTENSION_BASE}/care-time-non-billable`;
/**
 * True only when the entry carries the non-billable extension set to `true`.
 * Any other shape (missing extension, `false`, absent `valueBoolean`) is
 * treated as billable.
 * @agentic @benchmark/core
 */ const isCareTimeNonBillable = (observation)=>(observation.extension ?? []).some((ext)=>ext.url === CARE_TIME_NON_BILLABLE_EXTENSION_URL && ext.valueBoolean === true);
/**
 * Care-time observation code system (Benchmark-minted).
 * Holds the single `care-management-minutes` code (D3).
 * @agentic @benchmark/core
 */ const CARE_TIME_CODE_SYSTEM = "https://benchmarkhealth.org/fhir/CodeSystem/care-time-type";
/**
 * Benchmark observation category system used as a sub-classification
 * alongside the HL7 `activity` category.
 * @agentic @benchmark/core
 */ const CARE_TIME_CATEGORY_SYSTEM = "https://benchmarkhealth.org/fhir/CodeSystem/observation-category";
/**
 * Care-time method system encoding the source surface that produced
 * the entry (`task-completion`, `progress-update`, `call`, ...).
 * @agentic @benchmark/core
 */ const CARE_TIME_METHOD_SYSTEM = "https://benchmarkhealth.org/fhir/CodeSystem/care-time-method";
/**
 * UCUM system URL — used on `valueQuantity.system` for the `min` unit.
 * @agentic @benchmark/core
 */ const UCUM_SYSTEM = "http://unitsofmeasure.org";
/**
 * HL7 observation-category code used as the primary care-time category.
 * Pairs with the Benchmark `care-time` sub-category coding.
 * @agentic @benchmark/core
 */ const ACTIVITY_OBSERVATION_CATEGORY = {
    coding: [
        {
            system: "http://terminology.hl7.org/CodeSystem/observation-category",
            code: "activity",
            display: "Activity"
        }
    ]
};
/**
 * Benchmark `care-time` sub-category coding paired with the HL7
 * `activity` category on every care-time Observation.
 * @agentic @benchmark/core
 */ const CARE_TIME_OBSERVATION_CATEGORY = {
    coding: [
        {
            system: CARE_TIME_CATEGORY_SYSTEM,
            code: "care-time",
            display: "Care Time"
        }
    ]
};
/**
 * Token search expression for FHIR queries that target the Benchmark
 * `care-time` category (e.g. `category={CARE_TIME_CATEGORY_TOKEN}`).
 * @agentic @benchmark/core
 */ const CARE_TIME_CATEGORY_TOKEN = `${CARE_TIME_CATEGORY_SYSTEM}|care-time`;
/**
 * Single observation code for all care-time entries.
 * @agentic @benchmark/core
 */ const CARE_TIME_OBSERVATION_CODE_VALUE = "care-management-minutes";
/**
 * `Observation.code` payload for every care-time entry (D3 LOCKED).
 * @agentic @benchmark/core
 */ const CARE_TIME_OBSERVATION_CODE = {
    coding: [
        {
            system: CARE_TIME_CODE_SYSTEM,
            code: CARE_TIME_OBSERVATION_CODE_VALUE,
            display: "Care management minutes"
        }
    ],
    text: "Care management minutes"
};
/**
 * Source-surface method options carried on `Observation.method` (D3 LOCKED).
 * @agentic @benchmark/core
 */ const CARE_TIME_METHOD_OPTIONS = [
    {
        code: "task-completion",
        display: "Task Completion"
    },
    {
        code: "progress-update",
        display: "Progress Update"
    },
    {
        code: "action-list-quick-add",
        display: "Action List Quick Add"
    },
    {
        code: "patient-profile-quick-add",
        display: "Patient Profile Quick Add"
    },
    {
        code: "intake-call",
        display: "Intake Call"
    },
    {
        code: "intake-documentation",
        display: "Intake Documentation"
    },
    {
        code: "cocm-followup",
        display: "CoCM Follow-Up"
    },
    {
        code: "general-interaction",
        display: "General Interaction"
    },
    {
        code: "call",
        display: "Call"
    },
    {
        code: "sms",
        display: "SMS"
    }
];
/**
 * Set of all valid care-time methods for fast membership checks.
 * @agentic @benchmark/core
 */ const CARE_TIME_METHOD_CODES = new Set(CARE_TIME_METHOD_OPTIONS.map((o)=>o.code));
/**
 * Build the FHIR CodeableConcept payload for an `Observation.method` value.
 * @agentic @benchmark/core
 */ const getCareTimeMethodCoding = (method)=>{
    const option = CARE_TIME_METHOD_OPTIONS.find((o)=>o.code === method);
    const display = option?.display ?? method;
    return {
        coding: [
            {
                system: CARE_TIME_METHOD_SYSTEM,
                code: method,
                display
            }
        ],
        text: display
    };
};
/**
 * UCUM `min` quantity coding used on every care-time Observation's
 * `valueQuantity`. Helper centralizes the unit shape.
 * @agentic @benchmark/core
 */ const buildCareTimeMinutesQuantity = (value)=>({
        value,
        unit: "min",
        system: UCUM_SYSTEM,
        code: "min"
    });
/**
 * Complete roster of every assessment instrument that can enter a patient's
 * chart, mapped to the code its score Observation carries. This is the ONE
 * place that enumerates all instruments; billing eligibility is derived from it
 * as a denylist (see {@link BILLING_INELIGIBLE_ASSESSMENTS}) so a newly added
 * instrument is never silently billable — it must be classified here.
 *
 * Observations are clinical chart data first; billing eligibility is a separate
 * decision this module owns. A `code: ""` entry is a categorical instrument
 * (PGI-S, PGI-C, Safety Plan, Drug Use) that emits NO numeric score
 * Observation; every such instrument is billing-ineligible and must appear in
 * {@link BILLING_INELIGIBLE_ASSESSMENTS}.
 *
 * This list is source-agnostic: eligibility reads Observations by code, so a
 * matching score unlocks billing whether it was written by the in-app
 * clinician encounter, a fixed remote survey link, or the adaptive screener.
 *
 * Two eligibility nuances the code-based list CANNOT enforce on its own:
 *   - AUDIT-C: only a FULLY administered AUDIT-C is billing-valid (2026-09-30).
 *     A gated "Never" first item is a genuine complete 0/12 and counts; an
 *     abandoned/partial administration must NOT emit a score Observation. Both
 *     share the one AUDIT-C score code, so completeness has to be enforced
 *     where the Observation is written, not here. Follow-up tracked on the
 *     billing-parity ticket.
 *   - Legacy PHQ-2 (deprecated LOINC 55758-7) is never billing-valid. It is not
 *     an administered instrument today, so it is intentionally absent from this
 *     roster; the lock test guards that its code never appears in the eligible
 *     list.
 * @agentic @benchmark/core
 */ const ALL_ASSESSMENTS = {
    PHQ4: OBSERVATION_LOINC_CODES.PHQ4_TOTAL_SCORE,
    PHQ9: OBSERVATION_LOINC_CODES.PHQ9_TOTAL_SCORE,
    PHQ9A: OBSERVATION_LOINC_CODES.PHQ9A_TOTAL_SCORE,
    GAD7: OBSERVATION_LOINC_CODES.GAD7_TOTAL_SCORE,
    GAD2: OBSERVATION_LOINC_CODES.GAD2_TOTAL_SCORE,
    PC_PTSD_5: OBSERVATION_LOINC_CODES.PC_PTSD_5_TOTAL_SCORE,
    PSC_17: OBSERVATION_LOINC_CODES.PSC_17_TOTAL_SCORE,
    C_SSRS: OBSERVATION_LOINC_CODES.C_SSRS_SEVERITY,
    ASRS: OBSERVATION_LOINC_CODES.ASRS_TOTAL_SCORE,
    OCI_4: OBSERVATION_LOINC_CODES.OCI_4_TOTAL_SCORE,
    TAPS_1: OBSERVATION_LOINC_CODES.TAPS_1_TOTAL_SCORE,
    TAPS_2: OBSERVATION_LOINC_CODES.TAPS_2_TOTAL_SCORE,
    SBIRT_ED: OBSERVATION_LOINC_CODES.SBIRT_ED_TOTAL_SCORE,
    AUDIT_C: OBSERVATION_LOINC_CODES.AUDIT_C_TOTAL_SCORE,
    UCLA3: OBSERVATION_LOINC_CODES.UCLA3_TOTAL_SCORE,
    WSAS: OBSERVATION_LOINC_CODES.WSAS_TOTAL_SCORE,
    WPAI: OBSERVATION_LOINC_CODES.WPAI_TOTAL_SCORE,
    WHO5: OBSERVATION_LOINC_CODES.WHO5_TOTAL_SCORE,
    SDOH: OBSERVATION_LOINC_CODES.SDOH_TOTAL_SCORE,
    MIRAH_COMPLEXITY: OBSERVATION_LOINC_CODES.MIRAH_COMPLEXITY_SCORE,
    MIRAH_SEVERITY: OBSERVATION_LOINC_CODES.MIRAH_SEVERITY_SCORE,
    NPS: OBSERVATION_LOINC_CODES.NPS_SCORE,
    PGI_S: "",
    PGI_C: "",
    SAFETY_PLAN: "",
    DRUG_USE: ""
};
/**
 * Instruments that are administered but are NOT billing-valid for CoCM
 * eligibility (D18). This is the denylist over {@link ALL_ASSESSMENTS}: every
 * key here is subtracted to produce {@link BILLING_ELIGIBLE_ASSESSMENTS}. Each
 * entry names WHY it is excluded so the decision stays auditable. Confirmed
 * with clinical/compliance (2026-06-22, refined 2026-09-30).
 *
 * Note: an instrument absent from BOTH this denylist and the eligible set can
 * only happen if a new key is added to ALL_ASSESSMENTS without classifying it;
 * the exact-set lock test in `care-time.constants.test.ts` fails loudly in that
 * case rather than letting an unreviewed instrument become billable.
 * @agentic @benchmark/core
 */ const BILLING_INELIGIBLE_ASSESSMENTS = {
    PHQ4: "Ultra-brief combined screener; not a standalone severity measure.",
    GAD2: "Two-item anxiety short check; not billing-valid (opens GAD-7, which is).",
    UCLA3: "Loneliness scale; outside the billing-valid clinical set.",
    WSAS: "Everyday-functioning scale; not a clinical severity measure.",
    WPAI: "Work productivity/activity; functional, not clinical severity.",
    WHO5: "Well-being index; not a billing-valid severity measure.",
    SDOH: "Social-needs screen; not a clinical severity measure.",
    MIRAH_COMPLEXITY: "Vendor composite score; not a billing-valid instrument.",
    MIRAH_SEVERITY: "Vendor composite score; not a billing-valid instrument.",
    NPS: "Member-feedback likelihood score; not clinical.",
    PGI_S: "Categorical patient self-rating; no numeric score Observation.",
    PGI_C: "Categorical patient self-rating; no numeric score Observation.",
    SAFETY_PLAN: "Care-planning artifact; no score Observation.",
    DRUG_USE: "House-modified categorical drug screen; not billing-valid (2026-09-30) and no numeric score Observation."
};
// Eligible-by-policy = every instrument NOT on the denylist. Confirmed valid
// (2026-09-30): PHQ-9, PHQ-9A, GAD-7, PC-PTSD-5, PSC-17, C-SSRS, ASRS, OCI-4,
// TAPS-1, TAPS-2, SBIRT-ED, and AUDIT-C (full administration only). Every one
// carries a real score code; the `.filter` below is a guard, and the lock test
// asserts no eligible-by-policy instrument is categorical (`code: ""`) — a
// categorical instrument must be denied above, never silently dropped here.
const BILLING_ELIGIBLE_POLICY_KEYS = Object.keys(ALL_ASSESSMENTS).filter((key)=>!(key in BILLING_INELIGIBLE_ASSESSMENTS));
/**
 * The single billing-eligible assessment list, honored regardless of which
 * entry surface (in-app encounter, remote survey, adaptive screener) wrote the
 * score. Derived as {@link ALL_ASSESSMENTS} minus
 * {@link BILLING_INELIGIBLE_ASSESSMENTS}, then narrowed to instruments that
 * actually emit a score Observation (non-empty code). Each entry's system is
 * resolved the same way `buildObservationFromScore` stamps the score
 * Observation (`getObservationCoding(code)?.system ?? "http://loinc.org"`), so
 * the eligibility search always matches what questionnaire completion writes.
 * Within the 6-week freshness window these unlock 99492 / 99493 / 99494.
 * @agentic @benchmark/core
 */ const BILLING_ELIGIBLE_ASSESSMENTS = BILLING_ELIGIBLE_POLICY_KEYS.map((key)=>ALL_ASSESSMENTS[key]).filter((code)=>code !== "").map((code)=>({
        system: getObservationCoding(code)?.system ?? "http://loinc.org",
        code
    }));
/**
 * Codes-only view of {@link BILLING_ELIGIBLE_ASSESSMENTS} for value comparisons
 * and tests.
 * @agentic @benchmark/core
 */ const COCM_ELIGIBLE_ASSESSMENT_CODES = BILLING_ELIGIBLE_ASSESSMENTS.map((e)=>e.code);
/**
 * System-aware comma-delimited token search expression for the eligible
 * assessments (FHIR uses comma for OR within a single search parameter, and
 * `system|code` for the system-qualified token). Supports mixed LOINC and
 * organizational systems. Used by the eligibility service and the registry hook.
 * @agentic @benchmark/core
 */ const COCM_ELIGIBLE_ASSESSMENT_CODES_TOKEN = BILLING_ELIGIBLE_ASSESSMENTS.map((e)=>`${e.system}|${e.code}`).join(",");
/**
 * Assessment freshness window for CoCM billing eligibility (D18 — 6 weeks).
 * Applied uniformly to every rule that requires an assessment.
 * @agentic @benchmark/core
 */ const COCM_ASSESSMENT_FRESHNESS_WEEKS = 6;

/** HCPCS Level II system (G-codes such as G2214). @agentic @benchmark/core */ const HCPCS_SYSTEM = "https://bluebutton.cms.gov/resources/codesystem/hcpcs";
/**
 * The code system for a billed CoCM code. Numeric CPT codes (99492/99493/99494)
 * use the AMA CPT system; G-codes (G2214) are HCPCS Level II.
 * @agentic @benchmark/core
 */ const billingCodeSystem = (code)=>code.startsWith("G") ? HCPCS_SYSTEM : CODE_SYSTEM.CPT;
/** `Composition.type` value for a CoCM monthly billing packet. @agentic @benchmark/core */ const COCM_BILLING_PACKET_CODE_SYSTEM = `${ORGANIZATION_BASE}/CodeSystem/cocm-billing-packet`;
/** `Composition.type` value token. @agentic @benchmark/core */ const COCM_BILLING_PACKET_TYPE_CODE = "cocm-monthly-billing-packet";
/** `Composition.type` payload. @agentic @benchmark/core */ const COCM_BILLING_PACKET_TYPE = {
    coding: [
        {
            system: COCM_BILLING_PACKET_CODE_SYSTEM,
            code: COCM_BILLING_PACKET_TYPE_CODE,
            display: "CoCM Monthly Billing Packet"
        }
    ],
    text: "CoCM Monthly Billing Packet"
};
/** `Composition.category` payload (billing). @agentic @benchmark/core */ const COCM_BILLING_PACKET_CATEGORY = {
    coding: [
        {
            system: COCM_BILLING_PACKET_CODE_SYSTEM,
            code: "billing",
            display: "Billing"
        }
    ]
};
/** Token for a `type` search targeting the billing-packet Composition. @agentic @benchmark/core */ const COCM_BILLING_PACKET_TYPE_TOKEN = `${COCM_BILLING_PACKET_CODE_SYSTEM}|${COCM_BILLING_PACKET_TYPE_CODE}`;
/** Identifier system for the `{patientId}|{YYYY-MM}` composite key. @agentic @benchmark/core */ const COCM_BILLING_PACKET_IDENTIFIER_SYSTEM = `${ORGANIZATION_BASE}/identifier-system/cocm-billing-packet`;
/** Identifier system for the ChargeItem `{patientId}|{YYYY-MM}|{code}|{n}` key. @agentic @benchmark/core */ const COCM_CHARGE_ITEM_IDENTIFIER_SYSTEM = `${ORGANIZATION_BASE}/identifier-system/cocm-charge-item`;
/**
 * Extension URL carrying the packet + rules version on each ChargeItem, so a
 * line-item is self-describing (which rules produced it) for audit + dbt.
 * @agentic @benchmark/core
 */ const COCM_BILLING_PACKET_VERSION_EXTENSION_URL = `${EXTENSION_BASE}/cocm-billing-packet-version`;
/**
 * Extension URL carrying the FULL serialized `BillingCodePacket` (JSON) on the
 * attested Composition, so every criterion that produced the codes — assessments
 * used, enrollment window, attribution, episode flags, versions, timestamp — is
 * reconstructible from the persisted resource (D19). Human-readable sections +
 * ChargeItems remain for querying; this is the complete audit snapshot.
 * @agentic @benchmark/core
 */ const COCM_BILLING_PACKET_SNAPSHOT_EXTENSION_URL = `${EXTENSION_BASE}/cocm-billing-packet-snapshot`;
/**
 * `Composition.status` values that model the packet lock (mirrors the summary
 * Observation semantics): open→closed→reopened.
 *  - `preliminary` = open (draft, not yet locked)
 *  - `final`       = closed / locked (submitted)
 *  - `amended`     = reopened for correction
 * @agentic @benchmark/core
 */ const COCM_BILLING_PACKET_STATUS = {
    open: "preliminary",
    closed: "final",
    reopened: "amended"
};
/** Build the composite identifier value for a patient-month packet. @agentic @benchmark/core */ const buildCoCMBillingPacketIdentifierValue = (patientId, billingMonth)=>`${patientId}|${billingMonth}`;
/** `system|value` token for a packet identifier search. @agentic @benchmark/core */ const buildCoCMBillingPacketIdentifierToken = (patientId, billingMonth)=>`${COCM_BILLING_PACKET_IDENTIFIER_SYSTEM}|${buildCoCMBillingPacketIdentifierValue(patientId, billingMonth)}`;
/**
 * Build the identifier value for one billed-code ChargeItem occurrence
 * (`{patientId}|{YYYY-MM}|{code}|{n}`). Shared by the packet builder and the
 * submit/reopen reconciliation so both agree on line-item identity.
 * @agentic @benchmark/core
 */ const buildCoCMChargeItemIdentifierValue = (patientId, billingMonth, code, index)=>`${buildCoCMBillingPacketIdentifierValue(patientId, billingMonth)}|${code}|${index}`;
/**
 * Identifier-value prefix (`{patientId}|{YYYY-MM}|`) shared by every ChargeItem
 * of a patient-month, used to scope a reconciliation search to that month.
 * @agentic @benchmark/core
 */ const cocmChargeItemMonthPrefix = (patientId, billingMonth)=>`${buildCoCMBillingPacketIdentifierValue(patientId, billingMonth)}|`;

/** Benchmark `billing-summary` category coding (distinct from `care-time`). @agentic @benchmark/core */ const COCM_BILLING_SUMMARY_CATEGORY = {
    coding: [
        {
            system: CARE_TIME_CATEGORY_SYSTEM,
            code: "billing-summary",
            display: "Billing Summary"
        }
    ]
};
/** Token for a `category` search targeting the billing-summary category. @agentic @benchmark/core */ const COCM_BILLING_SUMMARY_CATEGORY_TOKEN = `${CARE_TIME_CATEGORY_SYSTEM}|billing-summary`;
/** `Observation.code` value for a CoCM monthly billing summary. @agentic @benchmark/core */ const COCM_BILLING_SUMMARY_CODE_VALUE = "cocm-monthly-billing-summary";
/** `Observation.code` payload. @agentic @benchmark/core */ const COCM_BILLING_SUMMARY_CODE = {
    coding: [
        {
            system: CARE_TIME_CODE_SYSTEM,
            code: COCM_BILLING_SUMMARY_CODE_VALUE,
            display: "CoCM Monthly Billing Summary"
        }
    ],
    text: "CoCM Monthly Billing Summary"
};
/** Token for a `code` search. @agentic @benchmark/core */ const COCM_BILLING_SUMMARY_CODE_TOKEN = `${CARE_TIME_CODE_SYSTEM}|${COCM_BILLING_SUMMARY_CODE_VALUE}`;
/** Identifier system for the `{patientId}|{YYYY-MM}` composite key. @agentic @benchmark/core */ const COCM_BILLING_SUMMARY_IDENTIFIER_SYSTEM = `${ORGANIZATION_BASE}/identifier-system/cocm-billing-summary`;
/** Root complex-extension URL carrying submission + reopen audit. @agentic @benchmark/core */ const COCM_BILLING_SUMMARY_EXTENSION_URL = `${EXTENSION_BASE}/cocm-billing-summary`;
/**
 * Relative sub-extension keys nested under
 * {@link COCM_BILLING_SUMMARY_EXTENSION_URL}.
 * @agentic @benchmark/core
 */ const COCM_BILLING_SUMMARY_EXT = {
    billingMonth: "billingMonth",
    submitted: "submitted",
    submittedAt: "submittedAt",
    submittedBy: "submittedBy",
    reopen: "reopen",
    reopenAt: "at",
    reopenReason: "reason",
    reopenBy: "by"
};
/**
 * `Observation.status` values that model the month lock:
 *  - `preliminary` = open (live, editable)
 *  - `final`       = closed / locked
 *  - `amended`     = reopened for correction
 * @agentic @benchmark/core
 */ const COCM_BILLING_SUMMARY_STATUS = {
    open: "preliminary",
    closed: "final",
    reopened: "amended"
};
/** Build the composite identifier value for a patient-month. @agentic @benchmark/core */ const buildCoCMBillingSummaryIdentifierValue = (patientId, billingMonth)=>`${patientId}|${billingMonth}`;
/** `system|value` token for an identifier search. @agentic @benchmark/core */ const buildCoCMBillingSummaryIdentifierToken = (patientId, billingMonth)=>`${COCM_BILLING_SUMMARY_IDENTIFIER_SYSTEM}|${buildCoCMBillingSummaryIdentifierValue(patientId, billingMonth)}`;

/**
 * FHIR-Compliant Constants for ClinicalImpression Domain
 *
 * This file contains all constants used in ClinicalImpression forms, utils, and related components.
 * All codes follow FHIR R4 value sets and include proper system/code/display structures.
 */ // FHIR-compliant clinical impression type options
const CLINICAL_IMPRESSION_TYPE_OPTIONS = [
    {
        code: "assessmentPlan",
        display: "Assessment and Plan",
        system: "http://benchmarkhealth.com/fhir/ValueSet/clinical-impression-types"
    },
    {
        code: "caseManagementSummary",
        display: "Case Management Summary",
        system: "http://benchmarkhealth.com/fhir/ValueSet/clinical-impression-types"
    },
    {
        code: "currentOutpatientTreaters",
        display: "Current Outpatient Treaters",
        system: "http://benchmarkhealth.com/fhir/ValueSet/clinical-impression-types"
    },
    {
        code: "chiefComplaint",
        display: "Chief Complaint",
        system: "http://benchmarkhealth.com/fhir/ValueSet/clinical-impression-types"
    },
    {
        code: "familyHistory",
        display: "Family History",
        system: "http://benchmarkhealth.com/fhir/ValueSet/clinical-impression-types"
    },
    {
        code: "generalNote",
        display: "General Note",
        system: "http://benchmarkhealth.com/fhir/ValueSet/clinical-impression-types"
    },
    {
        code: "hpiCurrentSymptoms",
        display: "HPI Current Symptoms",
        system: "http://benchmarkhealth.com/fhir/ValueSet/clinical-impression-types"
    },
    {
        code: "medicalHistory",
        display: "Medical History",
        system: "http://benchmarkhealth.com/fhir/ValueSet/clinical-impression-types"
    },
    {
        code: "mentalHealthHistory",
        display: "Mental Health History",
        system: "http://benchmarkhealth.com/fhir/ValueSet/clinical-impression-types"
    },
    {
        code: "mentalStatusAppearance",
        display: "Mental Status - Appearance",
        system: "http://benchmarkhealth.com/fhir/ValueSet/clinical-impression-types"
    },
    {
        code: "mentalStatusBehavior",
        display: "Mental Status - Behavior",
        system: "http://benchmarkhealth.com/fhir/ValueSet/clinical-impression-types"
    },
    {
        code: "mentalStatusSpeech",
        display: "Mental Status - Speech",
        system: "http://benchmarkhealth.com/fhir/ValueSet/clinical-impression-types"
    },
    {
        code: "mentalStatusMoodAffect",
        display: "Mental Status - Mood/Affect",
        system: "http://benchmarkhealth.com/fhir/ValueSet/clinical-impression-types"
    },
    {
        code: "mentalStatusThoughtProcessContent",
        display: "Mental Status - Thought Process/Content",
        system: "http://benchmarkhealth.com/fhir/ValueSet/clinical-impression-types"
    },
    {
        code: "mentalStatusCognition",
        display: "Mental Status - Cognition",
        system: "http://benchmarkhealth.com/fhir/ValueSet/clinical-impression-types"
    },
    {
        code: "mentalStatusPerception",
        display: "Mental Status - Perception",
        system: "http://benchmarkhealth.com/fhir/ValueSet/clinical-impression-types"
    },
    {
        code: "mentalStatusInsightJudgment",
        display: "Mental Status - Insight/Judgment",
        system: "http://benchmarkhealth.com/fhir/ValueSet/clinical-impression-types"
    },
    {
        code: "socialHistory",
        display: "Social History",
        system: "http://benchmarkhealth.com/fhir/ValueSet/clinical-impression-types"
    },
    {
        code: "substanceUseHistory",
        display: "Substance Use History",
        system: "http://benchmarkhealth.com/fhir/ValueSet/clinical-impression-types"
    },
    {
        code: "traumaHistory",
        display: "Trauma History",
        system: "http://benchmarkhealth.com/fhir/ValueSet/clinical-impression-types"
    }
];
// FHIR-compliant clinical impression status options
// http://hl7.org/fhir/R4/valueset-clinicalimpression-status.html
const CLINICAL_IMPRESSION_STATUS_OPTIONS = [
    {
        code: "in-progress",
        display: "In Progress",
        system: "http://hl7.org/fhir/clinicalimpression-status"
    },
    {
        code: "completed",
        display: "Completed",
        system: "http://hl7.org/fhir/clinicalimpression-status"
    },
    {
        code: "entered-in-error",
        display: "Entered in Error",
        system: "http://hl7.org/fhir/clinicalimpression-status"
    }
];
// UI-specific mappings for Select components
const CLINICAL_IMPRESSION_TYPE_SELECT_OPTIONS = CLINICAL_IMPRESSION_TYPE_OPTIONS.map((type)=>({
        value: type.code,
        label: type.display
    }));
const CLINICAL_IMPRESSION_STATUS_SELECT_OPTIONS = CLINICAL_IMPRESSION_STATUS_OPTIONS.map((status)=>({
        value: status.code,
        label: status.display
    }));
// Helper functions to get display names from codes
function getClinicalImpressionTypeDisplay(code) {
    const type = CLINICAL_IMPRESSION_TYPE_OPTIONS.find((t)=>t.code === code);
    return type?.display || "Unknown impression type";
}
function getClinicalImpressionStatusDisplay(code) {
    const status = CLINICAL_IMPRESSION_STATUS_OPTIONS.find((s)=>s.code === code);
    return status?.display || "Unknown status";
}
// Helper function to resolve display values from various coding systems
function getClinicalImpressionDisplayFromCoding(coding) {
    if (!coding || coding.length === 0) {
        return "Unknown Clinical Impression";
    }
    const firstCoding = coding[0];
    // If display is already provided, use it
    if (firstCoding.display) {
        return firstCoding.display;
    }
    // If it's a LOINC code, check our known mappings
    if (firstCoding.system === "http://loinc.org" && firstCoding.code) {
        const loincMappings = {
            "11329-0": "General Note",
            "10154-3": "Chief Complaint",
            "10164-2": "HPI Current Symptoms",
            "45420-7": "Mental Health History",
            "10190-7": "Mental Status",
            "74198-3": "Trauma History",
            "87259-8": "Substance Use History",
            "11348-0": "Medical History",
            "29762-2": "Social History",
            "10157-6": "Family History",
            "51847-2": "Assessment & Plan",
            "46210-1": "Case Management Summary",
            "62387-6": "Current Outpatient Treaters"
        };
        const display = loincMappings[firstCoding.code];
        if (display) {
            return display;
        }
    }
    // If it's our custom system, check our type options
    if (firstCoding.system === "http://benchmarkhealth.com/fhir/ValueSet/clinical-impression-types" && firstCoding.code) {
        return getClinicalImpressionTypeDisplay(firstCoding.code);
    }
    // Fallback to formatting the code
    if (firstCoding.code) {
        return firstCoding.code.replace(/[-_]/g, " ").replace(/\b\w/g, (char)=>char.toUpperCase());
    }
    return "Unknown Clinical Impression";
}
// Backward compatibility: Create a lookup object for existing code
const CLINICAL_IMPRESSION_TYPES = CLINICAL_IMPRESSION_TYPE_OPTIONS.reduce((acc, type)=>{
    acc[type.code] = type.display;
    return acc;
}, {});

/**
 * FHIR-Compliant Constants for Communication Domain
 *
 * This file contains all constants used in Communication forms, utils, and related components.
 * All codes follow FHIR R4 value sets and include proper system/code/display structures.
 *
 * ── Sacred Communication model (ratified 2026-06-08, Decision R1: multi-system) ──
 *
 * Every creation path and every backfill must satisfy this single contract.
 * Channel and interaction type are deliberately separate axes; the category
 * systems are purpose-specific and are NOT collapsed into one enum.
 *
 *   medium    → CHANNEL ("how"). Always HL7 `v3-ParticipationMode`
 *               (`COMMUNICATION_MEDIUM_SYSTEM`): phone | text | email |
 *               secure-message | fax | portal. Set whenever a real channel
 *               exists; omitted only for genuinely channel-less records.
 *   category  → TYPE/PURPOSE ("what"). One coding per cohesive question, each on
 *               its own canonical Benchmark host:
 *                 • `COMMUNICATION_TYPE_SYSTEM`        patient-message |
 *                                                      manual-outreach | nps-4-week |
 *                                                      nps-onboarding | nps-ongoing-care
 *                 • `CALL_STATE_SYSTEM` (calls only)   completed | missed | voicemail
 *                 • `PROGRESS_LOG_CATEGORY_SYSTEM`     outreach-attempt |
 *                                                      provider-outreach |
 *                                                      administrative-note
 *                 • `AVAILABILITY_UPDATE_CATEGORY_SYSTEM`  availability-update
 *   reasonCode → OUTCOME/FINDING (progress-log-outcome, availability-finding).
 *   statusReason → NPS only (`NPS_STATUS_REASON_SYSTEM`).
 *   sender/recipient/subject → `ResourceType/id` WITH `display`, semantically
 *               correct per archetype (no patient-as-recipient on provider
 *               outreach).
 *   status    → per-archetype policy. Hardcoded `completed` on the staff/log
 *               paths (interaction occurred); NPS is the only path that varies
 *               it; SMS/call lifecycle status is deferred (Decision D7).
 *
 * Deprecated, read-only, target = 0 rows after backfill (legacy detection +
 * read-side fallback ONLY): `LEGACY_COMMUNICATION_CHANNEL_SYSTEM`,
 * `LEGACY_CALL_STATE_SYSTEM`. Channel now lives exclusively in `medium`.
 *
 * Conscious exception: call extensions (`call-duration-ms`, `call-direction`)
 * still use the `benchmark.health/fhir/StructureDefinition/...` host. They are
 * extension URLs, not category systems, so they are out of the canonical-host
 * migration (Decision D3) scope.
 */ /** HL7 v3-ParticipationMode system URL for communication medium codings. */ const COMMUNICATION_MEDIUM_SYSTEM = "http://terminology.hl7.org/CodeSystem/v3-ParticipationMode";
/** All communication medium options (member + provider facing). */ const COMMUNICATION_MEDIUM_OPTIONS = [
    {
        code: "phone",
        display: "Phone",
        system: COMMUNICATION_MEDIUM_SYSTEM
    },
    {
        code: "text",
        display: "Text",
        system: COMMUNICATION_MEDIUM_SYSTEM
    },
    {
        code: "email",
        display: "Email",
        system: COMMUNICATION_MEDIUM_SYSTEM
    },
    {
        code: "secure-message",
        display: "Secure Message",
        system: COMMUNICATION_MEDIUM_SYSTEM
    },
    {
        code: "fax",
        display: "Fax",
        system: COMMUNICATION_MEDIUM_SYSTEM
    },
    {
        code: "portal",
        display: "Portal",
        system: COMMUNICATION_MEDIUM_SYSTEM
    }
];
/** Member-facing medium select options (phone, text, email, secure-message). */ const COMMUNICATION_MEDIUM_SELECT_OPTIONS = COMMUNICATION_MEDIUM_OPTIONS.filter((o)=>o.code !== "fax" && o.code !== "portal").map((option)=>({
        value: option.code,
        label: option.display
    }));
/** Provider-facing medium select options (phone, fax, email, portal). */ const PROVIDER_COMMUNICATION_MEDIUM_SELECT_OPTIONS = COMMUNICATION_MEDIUM_OPTIONS.filter((o)=>o.code === "phone" || o.code === "fax" || o.code === "email" || o.code === "portal").map((option)=>({
        value: option.code,
        label: option.display
    }));
// ── Canonical category (interaction type/purpose) ──────────────────────
/**
 * Canonical Benchmark system for the Communication interaction type/purpose.
 * Channel lives in `medium`; this system answers "what kind of interaction".
 * @agentic @benchmark/core
 */ const COMMUNICATION_TYPE_SYSTEM = "http://benchmarkhealth.com/fhir/CodeSystem/communication-type";
/**
 * Interaction-type codes on `COMMUNICATION_TYPE_SYSTEM`. `patient-message` is
 * reserved for the Dialpad conversational channel (SMS/call webhooks +
 * optimistic UI); `manual-outreach` is the staff manual outreach form;
 * `nps-4-week` is historical NPS; new automated reads use purpose-specific
 * onboarding and ongoing-care codes.
 * @agentic @benchmark/core
 */ const COMMUNICATION_TYPE_OPTIONS = [
    {
        code: "patient-message",
        display: "Patient Message",
        system: COMMUNICATION_TYPE_SYSTEM
    },
    {
        code: "manual-outreach",
        display: "Manual Outreach",
        system: COMMUNICATION_TYPE_SYSTEM
    },
    {
        code: "nps-4-week",
        display: "NPS Survey (4-week)",
        system: COMMUNICATION_TYPE_SYSTEM
    },
    {
        code: "nps-onboarding",
        display: "NPS Survey (Onboarding)",
        system: COMMUNICATION_TYPE_SYSTEM
    },
    {
        code: "nps-ongoing-care",
        display: "NPS Survey (Ongoing care)",
        system: COMMUNICATION_TYPE_SYSTEM
    }
];
// ── Call-state category (calls only) ───────────────────────────────────
/**
 * Canonical Benchmark system for the call-state category coding on call
 * Communications (migrated off the legacy `benchmark.health/codes` host).
 * @agentic @benchmark/core
 */ const CALL_STATE_SYSTEM = "http://benchmarkhealth.com/fhir/CodeSystem/call-state";
/** Call-state codes (how a call ended). @agentic @benchmark/core */ const CALL_STATE_OPTIONS = [
    {
        code: "completed",
        display: "Completed Call",
        system: CALL_STATE_SYSTEM
    },
    {
        code: "missed",
        display: "Missed Call",
        system: CALL_STATE_SYSTEM
    },
    {
        code: "voicemail",
        display: "Voicemail",
        system: CALL_STATE_SYSTEM
    }
];
// ── NPS Communication systems (single source of truth) ─────────────────
/** Category code identifying historical NPS Communications. @agentic @benchmark/core */ const NPS_COMMUNICATION_CATEGORY_CODE = "nps-4-week";
/** Category coding for each purpose-specific automated NPS read. @agentic @benchmark/core */ const NPS_READ_COMMUNICATION_CATEGORIES = {
    onboarding: {
        code: "nps-onboarding",
        display: "NPS Survey (Onboarding)"
    },
    "ongoing-care": {
        code: "nps-ongoing-care",
        display: "NPS Survey (Ongoing care)"
    }
};
/** Identifier system for the per-anchor/read at-most-once claim. @agentic @benchmark/core */ const NPS_READ_KEY_IDENTIFIER_SYSTEM = "https://benchmarkhealth.com/fhir/nps-read-key";
/** System for the NPS Communication `statusReason` coding. @agentic @benchmark/core */ const NPS_STATUS_REASON_SYSTEM = "http://benchmarkhealth.com/fhir/CodeSystem/nps-status-reason";
/** Complex extension URL holding NPS send metadata on a Communication. @agentic @benchmark/core */ const NPS_METADATA_EXTENSION_URL = "http://benchmarkhealth.com/fhir/StructureDefinition/nps-metadata";
// ── Legacy systems (backfill detection + read-side fallback) ────────────
/**
 * Deprecated channel category host. Channel now lives in `medium`; retained
 * only so the backfill can detect and the read side can recognize legacy data.
 * @agentic @benchmark/core
 */ const LEGACY_COMMUNICATION_CHANNEL_SYSTEM = "https://benchmark.health/codes/communication-channel";
/**
 * Deprecated call-state category host, superseded by `CALL_STATE_SYSTEM`.
 * @agentic @benchmark/core
 */ const LEGACY_CALL_STATE_SYSTEM = "https://benchmark.health/codes/call-state";
/**
 * Builds a `medium` CodeableConcept (HL7 v3-ParticipationMode) for a channel
 * code. Unknown codes fall back to the code as its own display (no throw).
 * @agentic @benchmark/core
 */ const buildMediumCoding = (channelCode)=>{
    const option = COMMUNICATION_MEDIUM_OPTIONS.find((o)=>o.code === channelCode);
    return [
        {
            coding: [
                {
                    system: COMMUNICATION_MEDIUM_SYSTEM,
                    code: channelCode,
                    display: option?.display ?? channelCode
                }
            ]
        }
    ];
};
/**
 * Maps a legacy `communication-channel` category code to its canonical
 * `v3-ParticipationMode` medium code (`sms`→`text`, `phone`→`phone`). Returns
 * `undefined` for unrecognized channel codes.
 * @agentic @benchmark/core
 */ const channelCategoryCodeToMediumCode = (channelCategoryCode)=>{
    if (channelCategoryCode === "sms") return "text";
    if (channelCategoryCode === "phone") return "phone";
    return undefined;
};
/**
 * Builds a `category` CodeableConcept on `COMMUNICATION_TYPE_SYSTEM` for an
 * interaction-type code (e.g. `patient-message`, `manual-outreach`).
 * @agentic @benchmark/core
 */ const buildCommunicationTypeCategory = (code)=>{
    const option = COMMUNICATION_TYPE_OPTIONS.find((o)=>o.code === code);
    return [
        {
            coding: [
                {
                    system: COMMUNICATION_TYPE_SYSTEM,
                    code,
                    display: option?.display ?? code
                }
            ]
        }
    ];
};

/**
 * FHIR-Compliant Constants for Coverage Domain
 *
 * This file contains all constants used in Coverage forms, utils, and related components.
 * All codes follow FHIR R4 value sets and include proper system/code/display structures.
 */ // FHIR R4-compliant coverage type options
// http://hl7.org/fhir/R4/valueset-coverage-type.html
const COVERAGE_TYPE_OPTIONS = [
    {
        code: "EHCPOL",
        display: "Extended Healthcare",
        system: "http://terminology.hl7.org/CodeSystem/v3-ActCode"
    },
    {
        code: "PUBLICPOL",
        display: "Public Healthcare",
        system: "http://terminology.hl7.org/CodeSystem/v3-ActCode"
    },
    {
        code: "DENTPRG",
        display: "Dental Program",
        system: "http://terminology.hl7.org/CodeSystem/v3-ActCode"
    },
    {
        code: "MENTPRG",
        display: "Mental Health Program",
        system: "http://terminology.hl7.org/CodeSystem/v3-ActCode"
    },
    {
        code: "SUBPRG",
        display: "Substance Use Program",
        system: "http://terminology.hl7.org/CodeSystem/v3-ActCode"
    },
    {
        code: "VISPOL",
        display: "Vision Care Policy",
        system: "http://terminology.hl7.org/CodeSystem/v3-ActCode"
    }
];
// FHIR R4-compliant coverage status options
// http://hl7.org/fhir/R4/valueset-fm-status.html
const COVERAGE_STATUS_OPTIONS = [
    {
        code: "active",
        display: "Active",
        system: "http://hl7.org/fhir/fm-status",
        description: "The coverage is currently in-force and providing benefits."
    },
    {
        code: "cancelled",
        display: "Cancelled",
        system: "http://hl7.org/fhir/fm-status",
        description: "The coverage has been cancelled and is no longer in effect."
    },
    {
        code: "draft",
        display: "Draft",
        system: "http://hl7.org/fhir/fm-status",
        description: "The coverage is being set up but not yet active."
    },
    {
        code: "entered-in-error",
        display: "Entered in Error",
        system: "http://hl7.org/fhir/fm-status",
        description: "The coverage was entered in error and should be ignored."
    }
];
// FHIR R4-compliant relationship options
// http://hl7.org/fhir/R4/valueset-subscriber-relationship.html
const COVERAGE_RELATIONSHIP_OPTIONS = [
    {
        code: "self",
        display: "Self",
        system: "http://terminology.hl7.org/CodeSystem/subscriber-relationship",
        description: "The beneficiary is the subscriber."
    },
    {
        code: "spouse",
        display: "Spouse",
        system: "http://terminology.hl7.org/CodeSystem/subscriber-relationship",
        description: "The beneficiary is the spouse or equivalent of the subscriber."
    },
    {
        code: "child",
        display: "Child",
        system: "http://terminology.hl7.org/CodeSystem/subscriber-relationship",
        description: "The beneficiary is a child of the subscriber."
    },
    {
        code: "parent",
        display: "Parent",
        system: "http://terminology.hl7.org/CodeSystem/subscriber-relationship",
        description: "The beneficiary is a parent of the subscriber."
    },
    {
        code: "other",
        display: "Other",
        system: "http://terminology.hl7.org/CodeSystem/subscriber-relationship",
        description: "The beneficiary has some other relationship to the subscriber."
    }
];
// UI-specific mappings for Select components
const COVERAGE_TYPE_SELECT_OPTIONS = COVERAGE_TYPE_OPTIONS.map((type)=>({
        value: type.code,
        label: type.display
    }));
const COVERAGE_STATUS_SELECT_OPTIONS = COVERAGE_STATUS_OPTIONS.map((status)=>({
        value: status.code,
        label: status.display
    }));
const COVERAGE_RELATIONSHIP_SELECT_OPTIONS = COVERAGE_RELATIONSHIP_OPTIONS.map((relationship)=>({
        value: relationship.code,
        label: relationship.display
    }));
// Helper functions to get display names from codes
function getCoverageTypeDisplay(code) {
    const type = COVERAGE_TYPE_OPTIONS.find((t)=>t.code === code);
    return type?.display || "Unknown coverage type";
}
function getCoverageStatusDisplay(code) {
    const status = COVERAGE_STATUS_OPTIONS.find((s)=>s.code === code);
    return status?.display || "Unknown status";
}
function getCoverageRelationshipDisplay(code) {
    const relationship = COVERAGE_RELATIONSHIP_OPTIONS.find((r)=>r.code === code);
    return relationship?.display || "Unknown relationship";
}

/**
 * FHIR-Compliant Constants for Patient Domain
 *
 * This file contains all constants used in Patient forms, utils, and related components.
 * All codes follow FHIR R4 value sets and include proper system/code/display structures.
 */ /**
 * US States with FHIR-compliant codes
 * Based on USPS state codes as per FHIR US Core
 */ const US_STATES = [
    {
        code: "AK",
        display: "Alaska"
    },
    {
        code: "AL",
        display: "Alabama"
    },
    {
        code: "AR",
        display: "Arkansas"
    },
    {
        code: "AZ",
        display: "Arizona"
    },
    {
        code: "CA",
        display: "California"
    },
    {
        code: "CO",
        display: "Colorado"
    },
    {
        code: "CT",
        display: "Connecticut"
    },
    {
        code: "DC",
        display: "District of Columbia"
    },
    {
        code: "DE",
        display: "Delaware"
    },
    {
        code: "FL",
        display: "Florida"
    },
    {
        code: "GA",
        display: "Georgia"
    },
    {
        code: "HI",
        display: "Hawaii"
    },
    {
        code: "IA",
        display: "Iowa"
    },
    {
        code: "ID",
        display: "Idaho"
    },
    {
        code: "IL",
        display: "Illinois"
    },
    {
        code: "IN",
        display: "Indiana"
    },
    {
        code: "KS",
        display: "Kansas"
    },
    {
        code: "KY",
        display: "Kentucky"
    },
    {
        code: "LA",
        display: "Louisiana"
    },
    {
        code: "MA",
        display: "Massachusetts"
    },
    {
        code: "MD",
        display: "Maryland"
    },
    {
        code: "ME",
        display: "Maine"
    },
    {
        code: "MI",
        display: "Michigan"
    },
    {
        code: "MN",
        display: "Minnesota"
    },
    {
        code: "MO",
        display: "Missouri"
    },
    {
        code: "MS",
        display: "Mississippi"
    },
    {
        code: "MT",
        display: "Montana"
    },
    {
        code: "NC",
        display: "North Carolina"
    },
    {
        code: "ND",
        display: "North Dakota"
    },
    {
        code: "NE",
        display: "Nebraska"
    },
    {
        code: "NH",
        display: "New Hampshire"
    },
    {
        code: "NJ",
        display: "New Jersey"
    },
    {
        code: "NM",
        display: "New Mexico"
    },
    {
        code: "NV",
        display: "Nevada"
    },
    {
        code: "NY",
        display: "New York"
    },
    {
        code: "OH",
        display: "Ohio"
    },
    {
        code: "OK",
        display: "Oklahoma"
    },
    {
        code: "OR",
        display: "Oregon"
    },
    {
        code: "PA",
        display: "Pennsylvania"
    },
    {
        code: "RI",
        display: "Rhode Island"
    },
    {
        code: "SC",
        display: "South Carolina"
    },
    {
        code: "SD",
        display: "South Dakota"
    },
    {
        code: "TN",
        display: "Tennessee"
    },
    {
        code: "TX",
        display: "Texas"
    },
    {
        code: "UT",
        display: "Utah"
    },
    {
        code: "VA",
        display: "Virginia"
    },
    {
        code: "VT",
        display: "Vermont"
    },
    {
        code: "WA",
        display: "Washington"
    },
    {
        code: "WI",
        display: "Wisconsin"
    },
    {
        code: "WV",
        display: "West Virginia"
    },
    {
        code: "WY",
        display: "Wyoming"
    }
];
/**
 * Common languages with ISO 639-1 codes
 * Following FHIR R4 language binding
 */ const LANGUAGES = [
    {
        code: "en",
        display: "English"
    },
    {
        code: "es",
        display: "Spanish"
    },
    {
        code: "fr",
        display: "French"
    },
    {
        code: "de",
        display: "German"
    },
    {
        code: "it",
        display: "Italian"
    },
    {
        code: "pt",
        display: "Portuguese"
    },
    {
        code: "ru",
        display: "Russian"
    },
    {
        code: "zh",
        display: "Chinese"
    },
    {
        code: "ja",
        display: "Japanese"
    },
    {
        code: "ko",
        display: "Korean"
    },
    {
        code: "ar",
        display: "Arabic"
    },
    {
        code: "hi",
        display: "Hindi"
    }
];
/**
 * Race categories per US Core Race Extension
 * http://hl7.org/fhir/us/core/StructureDefinition/us-core-race
 */ const RACE_OPTIONS = [
    {
        code: "1002-5",
        display: "American Indian or Alaska Native"
    },
    {
        code: "2028-9",
        display: "Asian"
    },
    {
        code: "2054-5",
        display: "Black or African American"
    },
    {
        code: "2076-8",
        display: "Native Hawaiian or Other Pacific Islander"
    },
    {
        code: "2106-3",
        display: "White"
    },
    {
        code: "UNK",
        display: "Unknown"
    },
    {
        code: "ASKU",
        display: "Asked but no answer"
    }
];
/**
 * Ethnicity categories per US Core Ethnicity Extension
 * http://hl7.org/fhir/us/core/StructureDefinition/us-core-ethnicity
 */ const ETHNICITY_OPTIONS = [
    {
        code: "2135-2",
        display: "Hispanic or Latino"
    },
    {
        code: "2186-5",
        display: "Not Hispanic or Latino"
    },
    {
        code: "UNK",
        display: "Unknown"
    },
    {
        code: "ASKU",
        display: "Asked but no answer"
    }
];
/**
 * Gender identity options per FHIR R4 administrative gender
 * http://hl7.org/fhir/administrative-gender
 */ const GENDER_IDENTITY_OPTIONS = [
    {
        code: "male",
        display: "Male"
    },
    {
        code: "female",
        display: "Female"
    },
    {
        code: "other",
        display: "Other"
    },
    {
        code: "unknown",
        display: "Unknown"
    }
];
/**
 * Sexual orientation options
 * Using SNOMED CT codes as per FHIR guidance
 */ const SEXUAL_ORIENTATION_OPTIONS = [
    {
        code: "38628009",
        display: "Lesbian"
    },
    {
        code: "20430005",
        display: "Gay"
    },
    {
        code: "42035005",
        display: "Bisexual"
    },
    {
        code: "72552008",
        display: "Heterosexual"
    },
    {
        code: "65433006",
        display: "Questioning"
    },
    {
        code: "74964007",
        display: "Other"
    }
];
/**
 * Relationship to subscriber options for insurance
 * http://terminology.hl7.org/CodeSystem/subscriber-relationship
 */ const RELATIONSHIP_TO_SUBSCRIBER_OPTIONS = [
    {
        code: "self",
        display: "Self"
    },
    {
        code: "spouse",
        display: "Spouse"
    },
    {
        code: "child",
        display: "Child"
    },
    {
        code: "parent",
        display: "Parent"
    },
    {
        code: "other",
        display: "Other"
    }
];
/**
 * EMR/EHR system options
 * Custom value set for this implementation
 */ const EMR_SYSTEM_OPTIONS = [
    {
        code: "allscripts",
        display: "Allscripts"
    },
    {
        code: "athena",
        display: "athenahealth"
    },
    {
        code: "cerner",
        display: "Cerner"
    },
    {
        code: "eclinicalworks",
        display: "eClinicalWorks"
    },
    {
        code: "epic",
        display: "Epic"
    },
    {
        code: "meditech",
        display: "MEDITECH"
    },
    {
        code: "nextgen",
        display: "NextGen"
    },
    {
        code: "practice-fusion",
        display: "Practice Fusion"
    },
    {
        code: "other",
        display: "Other"
    }
];
/**
 * Helper functions to get select options for forms
 */ const getStateOptions = ()=>US_STATES.map((state)=>({
            value: state.code,
            label: state.display
        }));
const getLanguageOptions = ()=>LANGUAGES.map((lang)=>({
            value: lang.code,
            label: lang.display
        }));
const getRaceOptions = ()=>RACE_OPTIONS.map((race)=>({
            value: race.code,
            label: race.display
        }));
const getEthnicityOptions = ()=>ETHNICITY_OPTIONS.map((ethnicity)=>({
            value: ethnicity.code,
            label: ethnicity.display
        }));
const getGenderIdentityOptions = ()=>GENDER_IDENTITY_OPTIONS.map((gender)=>({
            value: gender.code,
            label: gender.display
        }));
const getSexualOrientationOptions = ()=>SEXUAL_ORIENTATION_OPTIONS.map((orientation)=>({
            value: orientation.code,
            label: orientation.display
        }));
const getRelationshipToSubscriberOptions = ()=>RELATIONSHIP_TO_SUBSCRIBER_OPTIONS.map((rel)=>({
            value: rel.code,
            label: rel.display
        }));
const getEmrSystemOptions = ()=>EMR_SYSTEM_OPTIONS.map((emr)=>({
            value: emr.code,
            label: emr.display
        }));
/**
 * Legacy Patient Engagement Status (V1)
 * @deprecated Use PATIENT_ENGAGEMENT_STATUS_OPTIONS instead
 * Maintained for backward compatibility only
 */ const LEGACY_PATIENT_ENGAGEMENT_STATUS_SYSTEM = "http://terminology.hl7.org/CodeSystem/patient-engagement";
const LEGACY_PATIENT_ENGAGEMENT_STATUS_OPTIONS = [
    {
        code: "initial-referral",
        display: "Initial Referral",
        system: LEGACY_PATIENT_ENGAGEMENT_STATUS_SYSTEM,
        version: "v1",
        deprecated: true
    },
    {
        code: "engaged",
        display: "Engaged",
        system: LEGACY_PATIENT_ENGAGEMENT_STATUS_SYSTEM,
        version: "v1",
        deprecated: false
    },
    {
        code: "activated",
        display: "Activated",
        system: LEGACY_PATIENT_ENGAGEMENT_STATUS_SYSTEM,
        version: "v1",
        deprecated: false
    },
    {
        code: "unsure",
        display: "Unsure",
        system: LEGACY_PATIENT_ENGAGEMENT_STATUS_SYSTEM,
        version: "v1",
        deprecated: true
    },
    {
        code: "declined",
        display: "Declined Services",
        system: LEGACY_PATIENT_ENGAGEMENT_STATUS_SYSTEM,
        version: "v1",
        deprecated: false
    },
    {
        code: "unable-to-reach",
        display: "Unable to Reach",
        system: LEGACY_PATIENT_ENGAGEMENT_STATUS_SYSTEM,
        version: "v1",
        deprecated: true
    }
];
/**
 * Patient Engagement Status (V2 - Current)
 * Enhanced engagement status codes following FHIR best practices
 */ const PATIENT_ENGAGEMENT_STATUS_SYSTEM = "http://benchmarkhealth.com/fhir/CodeSystem/patient-engagement-v2";
const PATIENT_ENGAGEMENT_STATUS_OPTIONS = [
    {
        code: "referred",
        display: "Referred",
        system: PATIENT_ENGAGEMENT_STATUS_SYSTEM,
        version: "v2",
        description: "Initial state when ServiceRequest created"
    },
    {
        code: "engaged",
        display: "Engaged",
        system: PATIENT_ENGAGEMENT_STATUS_SYSTEM,
        version: "v2",
        description: "Consented to services, completed intake"
    },
    {
        code: "activated",
        display: "Activated",
        system: PATIENT_ENGAGEMENT_STATUS_SYSTEM,
        version: "v2",
        description: "Patient activated in system (backwards compatibility)"
    },
    {
        code: "graduated",
        display: "Graduated",
        system: PATIENT_ENGAGEMENT_STATUS_SYSTEM,
        version: "v2",
        description: "Successfully completed program"
    },
    {
        code: "unreachable",
        display: "Unreachable",
        system: PATIENT_ENGAGEMENT_STATUS_SYSTEM,
        version: "v2",
        description: "Unable to contact after multiple attempts"
    },
    {
        code: "declined",
        display: "Declined",
        system: PATIENT_ENGAGEMENT_STATUS_SYSTEM,
        version: "v2",
        description: "Explicitly declined services"
    },
    {
        code: "discharged",
        display: "Discharged",
        system: PATIENT_ENGAGEMENT_STATUS_SYSTEM,
        version: "v2",
        description: "Administratively discharged from program"
    }
];
/**
 * All Patient Engagement Status Options (V1 + V2)
 * For display/editing existing patient statuses
 */ const ALL_PATIENT_ENGAGEMENT_STATUS_OPTIONS = [
    ...LEGACY_PATIENT_ENGAGEMENT_STATUS_OPTIONS,
    ...PATIENT_ENGAGEMENT_STATUS_OPTIONS
];
/**
 * Patient Engagement Status V1 → V2 Migration Map
 * Maps legacy V1 codes to current V2 codes
 */ const PATIENT_ENGAGEMENT_STATUS_MIGRATION_MAP = {
    "initial-referral": "referred",
    engaged: "engaged",
    activated: "activated",
    unsure: "referred",
    declined: "declined",
    "unable-to-reach": "unreachable"
};
/**
 * Helper functions for engagement status
 */ const getPatientEngagementStatusDisplay = (code)=>{
    // Prefer V2 entries over V1 when duplicates exist (e.g., "engaged", "declined", "activated")
    const v2Status = PATIENT_ENGAGEMENT_STATUS_OPTIONS.find((s)=>s.code === code);
    if (v2Status) return v2Status.display;
    // Fall back to V1 entries
    const v1Status = LEGACY_PATIENT_ENGAGEMENT_STATUS_OPTIONS.find((s)=>s.code === code);
    return v1Status?.display || code;
};
const getPatientEngagementStatusDescription = (code)=>{
    const status = PATIENT_ENGAGEMENT_STATUS_OPTIONS.find((s)=>s.code === code);
    return status?.description || "";
};
const migratePatientEngagementStatusCode = (v1Code)=>{
    return PATIENT_ENGAGEMENT_STATUS_MIGRATION_MAP[v1Code] || "referred";
};
const isLegacyPatientEngagementStatus = (code)=>{
    return LEGACY_PATIENT_ENGAGEMENT_STATUS_OPTIONS.some((s)=>s.code === code);
};
/**
 * UI-ready select options for engagement status
 */ const PATIENT_ENGAGEMENT_STATUS_SELECT_OPTIONS = PATIENT_ENGAGEMENT_STATUS_OPTIONS.map((status)=>({
        value: status.code,
        label: status.display,
        description: status.description
    }));
const ALL_PATIENT_ENGAGEMENT_STATUS_SELECT_OPTIONS = (()=>{
    // Deduplicate by preferring V2 entries over V1 when codes match
    const seenCodes = new Set();
    const result = [];
    // Add V2 entries first (preferred)
    for (const status of PATIENT_ENGAGEMENT_STATUS_OPTIONS){
        if (!seenCodes.has(status.code)) {
            seenCodes.add(status.code);
            result.push({
                value: status.code,
                label: status.display,
                deprecated: false,
                version: status.version
            });
        }
    }
    // Add V1 entries that don't exist in V2
    for (const status of LEGACY_PATIENT_ENGAGEMENT_STATUS_OPTIONS){
        if (!seenCodes.has(status.code)) {
            seenCodes.add(status.code);
            result.push({
                value: status.code,
                label: status.display,
                deprecated: "deprecated" in status ? status.deprecated : false,
                version: status.version
            });
        }
    }
    return result;
})();
/**
 * Terminating Engagement Statuses
 *
 * These statuses indicate the patient is no longer receiving active care,
 * and all open tasks should be cancelled when transitioning to these statuses.
 */ const TERMINATING_ENGAGEMENT_STATUSES = [
    "declined",
    "discharged",
    "unreachable"
];
/**
 * Check if an engagement status is a terminating status
 *
 * @param code - Engagement status code to check
 * @returns true if the status indicates patient is no longer receiving active care
 */ const isTerminatingEngagementStatus = (code)=>{
    return TERMINATING_ENGAGEMENT_STATUSES.includes(code);
};
// Patient Summary Extension Sub-URLs
const PATIENT_SUMMARY_TEXT_URL = "text";
const PATIENT_SUMMARY_LAST_UPDATED_URL = "lastUpdated";
const PATIENT_SUMMARY_AUTHOR_URL = "author";

/**
 * CodeSystem URL for coverage-area state codings on the canonical
 * `Location` resource. Reserved for future per-state CodeableConcepts.
 * @agentic @benchmark/core
 */ const COVERAGE_AREA_STATE_CODESYSTEM = "https://benchmarkhealth.com/fhir/CodeSystem/coverage-area-state";
/**
 * Identifier system used to look up the canonical state-coverage
 * `Location` resource. The identifier value is the two-letter state
 * code (e.g. "MA", "CA").
 * @agentic @benchmark/core
 */ const COVERAGE_AREA_STATE_IDENTIFIER_SYSTEM = "https://benchmarkhealth.com/fhir/sid/coverage-area-state";
/**
 * State codes for which Connections currently authors and queries
 * coverage areas. Adding a new state is a one-line change here.
 * @agentic @benchmark/core
 */ const SUPPORTED_COVERAGE_AREA_STATE_CODES = [
    "MA",
    "CA"
];
const SUPPORTED_STATE_CODE_SET = new Set(SUPPORTED_COVERAGE_AREA_STATE_CODES);
/**
 * Returns true when the given string is one of the supported coverage
 * area state codes.
 * @agentic @benchmark/core
 */ const isCoverageAreaStateCode = (value)=>typeof value === "string" && SUPPORTED_STATE_CODE_SET.has(value);
/**
 * Returns the human display label for a supported state code (e.g.
 * "Massachusetts" for "MA"). Falls back to the code itself when the
 * state is somehow not in the US_STATES catalogue.
 * @agentic @benchmark/core
 */ const getCoverageAreaStateDisplay = (code)=>{
    const entry = US_STATES.find((state)=>state.code === code);
    return entry?.display ?? code;
};
/**
 * Returns the canonical Location.name for a supported state coverage
 * area, e.g. "Massachusetts Coverage Area".
 * @agentic @benchmark/core
 */ const getCoverageAreaLocationName = (code)=>`${getCoverageAreaStateDisplay(code)} Coverage Area`;
/**
 * Returns Mantine-friendly select options for the coverage-area state
 * picker (one entry per supported state).
 * @agentic @benchmark/core
 */ const getCoverageAreaStateOptions = ()=>SUPPORTED_COVERAGE_AREA_STATE_CODES.map((code)=>({
            value: code,
            label: getCoverageAreaStateDisplay(code)
        }));
/**
 * Type-narrowing helper that re-asserts a CoverageAreaStateCode as a
 * StateCode from US_STATES (true by construction).
 * @agentic @benchmark/core
 */ const coverageAreaStateCodeToStateCode = (code)=>code;

/**
 * FHIR-Compliant Constants for Encounter Domain
 *
 * This file contains all constants used in Encounter forms, utils, and related components.
 * All codes follow FHIR R4 value sets and include proper system/code/display structures.
 *
 * ARCHITECTURE NOTE:
 * These constants replace the JSON ValueSets in packages/medplum/src/data/valuesets/.
 * While the JSON approach offers better FHIR compliance and versioning support,
 * the TypeScript constants provide immediate developer experience benefits:
 * - Type safety and IntelliSense
 * - Direct UI mappings for Select components
 * - Helper functions for display lookups
 *
 * VERSIONING STRATEGY:
 * - LEGACY_ENCOUNTER_TYPE_OPTIONS: Deprecated v1 types (for backwards compatibility)
 * - ENCOUNTER_TYPE_OPTIONS: Current v2 types (for new forms)
 * - ALL_ENCOUNTER_TYPE_OPTIONS: Combined for display/lookup functions
 *
 * Future consideration: Migrate to versioned JSON ValueSet system for better
 * FHIR server integration and centralized management.
 */ // =============================================================================
// LEGACY ENCOUNTER TYPES (v1) - DEPRECATED
// =============================================================================
// These are maintained for backwards compatibility with existing encounters.
// DO NOT use these in new forms - use ENCOUNTER_TYPE_OPTIONS instead.
const CLINICAL_INTAKE_ENCOUNTER_TYPE_CODE = "clinical-intake-interaction";
const COCM_INTAKE_ENCOUNTER_TYPE_CODE = "cocm-intake-interaction";
/** Wellness-track follow-up code. Mirrors the clinical (90791) intake track. */ const CLINICAL_FOLLOW_UP_ENCOUNTER_TYPE_CODE = "clinical-follow-up-interaction";
/** CoCM-track follow-up code. Minutes roll into the month-end CoCM packet. */ const COCM_FOLLOW_UP_ENCOUNTER_TYPE_CODE = "cocm-follow-up-interaction";
/**
 * No-bill intake code. Written when the Schedule Intake billing flag is
 * "No bill"; parity with the billed intake codes so unbilled intakes are a
 * first-class (non-billable) intake type rather than an untagged encounter.
 */ const UNBILLED_INTAKE_ENCOUNTER_TYPE_CODE = "unbilled-intake-interaction";
/**
 * No-bill follow-up code. Mirrors {@link UNBILLED_INTAKE_ENCOUNTER_TYPE_CODE}
 * for follow-up encounters whose originating intake was unbilled.
 */ const UNBILLED_FOLLOW_UP_ENCOUNTER_TYPE_CODE = "unbilled-follow-up-interaction";
/**
 * @deprecated Use ENCOUNTER_TYPE_OPTIONS for new forms.
 * This constant is maintained only for backwards compatibility with existing encounters.
 */ const LEGACY_ENCOUNTER_TYPE_OPTIONS = [
    {
        code: "outreach",
        display: "Outreach",
        system: "http://www.benchmarkhealth.com/fhir/ValueSet/encounter-type",
        version: "v1",
        deprecated: true,
        deprecationReason: "Outreach interactions are now handled by Communication resources"
    },
    {
        code: "initial",
        display: "Initial Assessment",
        system: "http://www.benchmarkhealth.com/fhir/ValueSet/encounter-type",
        version: "v1",
        deprecated: true,
        deprecationReason: "Replaced by more specific interaction types in v2"
    },
    {
        code: "check-in-evaluation",
        display: "Check-In/Evaluation",
        system: "http://www.benchmarkhealth.com/fhir/ValueSet/encounter-type",
        version: "v1",
        deprecated: true,
        deprecationReason: "Replaced by more specific interaction types in v2"
    },
    {
        code: "follow-up",
        display: "Follow-Up Assessment",
        system: "http://www.benchmarkhealth.com/fhir/ValueSet/encounter-type",
        version: "v1",
        deprecated: true,
        deprecationReason: "Replaced by more specific interaction types in v2"
    }
];
// =============================================================================
// CURRENT ENCOUNTER TYPES (v2) - ACTIVE
// =============================================================================
// These are the current encounter types for use in new forms and encounters.
const ENCOUNTER_TYPE_OPTIONS = [
    {
        code: "general-interaction",
        display: "General Interaction",
        system: "http://www.benchmarkhealth.com/fhir/ValueSet/encounter-type",
        version: "v2",
        description: "General visit for information, coordination, or support"
    },
    {
        code: CLINICAL_INTAKE_ENCOUNTER_TYPE_CODE,
        display: "Clinical Intake Interaction",
        system: "http://www.benchmarkhealth.com/fhir/ValueSet/encounter-type",
        version: "v2",
        description: "Initial clinical intake visit (90791) for assessment and planning"
    },
    {
        code: COCM_INTAKE_ENCOUNTER_TYPE_CODE,
        display: "CoCM Intake Interaction",
        system: "http://www.benchmarkhealth.com/fhir/ValueSet/encounter-type",
        version: "v2",
        description: "Initial Collaborative Care visit (99492) with expanded clinical history and goal setting"
    },
    {
        code: CLINICAL_FOLLOW_UP_ENCOUNTER_TYPE_CODE,
        display: "Clinical Follow-Up Interaction",
        uiLabel: "Followup",
        system: "http://www.benchmarkhealth.com/fhir/ValueSet/encounter-type",
        version: "v2",
        description: "Follow-up visit with an enrolled member. The billing track is confirmed on the Details step."
    },
    {
        code: "case-management-interaction",
        display: "Case Management Interaction",
        system: "http://www.benchmarkhealth.com/fhir/ValueSet/encounter-type",
        version: "v2",
        description: "Case management visit for coordination and support"
    },
    {
        code: COCM_FOLLOW_UP_ENCOUNTER_TYPE_CODE,
        display: "CoCM Follow-Up Interaction",
        uiLabel: "CoCM Followup",
        system: "http://www.benchmarkhealth.com/fhir/ValueSet/encounter-type",
        version: "v2",
        description: "Follow-up Collaborative Care visit with ongoing care coordination, goal progress tracking, and monitoring"
    },
    {
        code: "discharge-interaction",
        display: "Discharge Interaction",
        system: "http://www.benchmarkhealth.com/fhir/ValueSet/encounter-type",
        version: "v2",
        description: "Final member interaction documenting discharge reason, follow-up plan, and care transition"
    },
    {
        code: UNBILLED_INTAKE_ENCOUNTER_TYPE_CODE,
        display: "Unbilled Intake Interaction",
        system: "http://www.benchmarkhealth.com/fhir/ValueSet/encounter-type",
        version: "v2",
        description: "Initial intake visit documented but not billed (schedule-intake billing flag = No bill)"
    },
    {
        code: UNBILLED_FOLLOW_UP_ENCOUNTER_TYPE_CODE,
        display: "Unbilled Follow-Up Interaction",
        uiLabel: "Unbilled Followup",
        system: "http://www.benchmarkhealth.com/fhir/ValueSet/encounter-type",
        version: "v2",
        description: "Follow-up visit for a member whose intake was unbilled; documented but not billed"
    }
];
// =============================================================================
// COMBINED OPTIONS FOR BACKWARDS COMPATIBILITY
// =============================================================================
// This includes both legacy and current options for display/lookup functions
const ALL_ENCOUNTER_TYPE_OPTIONS = [
    ...LEGACY_ENCOUNTER_TYPE_OPTIONS,
    ...ENCOUNTER_TYPE_OPTIONS
];
const ENCOUNTER_METHOD_OPTIONS = [
    {
        code: "phone",
        display: "Phone",
        system: "http://www.benchmarkhealth.com/fhir/ValueSet/encounter-method",
        description: "Voice conversation with patient"
    },
    {
        code: "text",
        display: "Text",
        system: "http://www.benchmarkhealth.com/fhir/ValueSet/encounter-method",
        description: "SMS-based communication"
    },
    {
        code: "email",
        display: "Email",
        system: "http://www.benchmarkhealth.com/fhir/ValueSet/encounter-method",
        description: "Email-based communication"
    },
    {
        code: "emr-secure-message",
        display: "Secure Message",
        system: "http://www.benchmarkhealth.com/fhir/ValueSet/encounter-method",
        description: "Secure messaging through an EMR"
    },
    {
        code: "video",
        display: "Video Chat",
        system: "http://www.benchmarkhealth.com/fhir/ValueSet/encounter-method",
        description: "Video-based telehealth session"
    },
    {
        code: "in-person",
        display: "In-Person",
        system: "http://www.benchmarkhealth.com/fhir/ValueSet/encounter-method",
        description: "Face-to-face meeting"
    }
];
// Encounter direction options
const ENCOUNTER_DIRECTION_OPTIONS = [
    {
        code: "outbound",
        display: "Outbound",
        system: "http://www.benchmarkhealth.com/fhir/ValueSet/encounter-direction",
        description: "We initiated this interaction"
    },
    {
        code: "inbound",
        display: "Inbound",
        system: "http://www.benchmarkhealth.com/fhir/ValueSet/encounter-direction",
        description: "Member initiated this interaction"
    }
];
// Encounter success status options
/**
 * @deprecated This constant is deprecated by Communication resource support.
 * This constant is maintained only for backwards compatibility with existing encounters.
 */ const ENCOUNTER_SUCCESS_OPTIONS = [
    {
        code: "pending",
        display: "Pending",
        system: "http://www.benchmarkhealth.com/fhir/ValueSet/encounter-outcome"
    },
    {
        code: "successful",
        display: "Successful",
        system: "http://www.benchmarkhealth.com/fhir/ValueSet/encounter-outcome"
    },
    {
        code: "unsuccessful",
        display: "Unsuccessful",
        system: "http://www.benchmarkhealth.com/fhir/ValueSet/encounter-outcome"
    }
];
// Encounter billable status options
const ENCOUNTER_BILLABLE_OPTIONS = [
    {
        code: "billable",
        display: "Billable",
        system: "http://www.benchmarkhealth.com/fhir/ValueSet/encounter-billing",
        description: "This interaction meets the criteria for billing"
    },
    {
        code: "non-billable",
        display: "Non-Billable",
        system: "http://www.benchmarkhealth.com/fhir/ValueSet/encounter-billing",
        description: "This interaction does not meet the criteria for billing"
    }
];
/**
 * @deprecated Import PATIENT_ENGAGEMENT_STATUS_OPTIONS (V2) or LEGACY_PATIENT_ENGAGEMENT_STATUS_OPTIONS (V1) directly from patient.ts instead
 * This re-export is removed to avoid export conflicts in the domain constants index.
 *
 * For V1 statuses: import { LEGACY_PATIENT_ENGAGEMENT_STATUS_OPTIONS } from "./patient"
 * For V2 statuses: import { PATIENT_ENGAGEMENT_STATUS_OPTIONS } from "./patient"
 */ // =============================================================================
// UI-SPECIFIC MAPPINGS FOR SELECT COMPONENTS
// =============================================================================
/**
 * The label a clinician reads for an encounter type. `display` is the FHIR
 * `meta.tag.display` written on save and the join key for dbt's
 * `encounter_category`, so it is frozen; `uiLabel` lets a surface read
 * differently without touching stored data or analytics (D18/D21).
 */ const getEncounterTypeUiLabel = (type)=>type.uiLabel ?? type.display;
// Current (non-deprecated) types offered in new-form pickers. Excludes the
// unbilled tracks: those codes are derived from the Schedule Intake billing
// flag (intake) or the member's care plan / originating intake (follow-up) and
// are never chosen directly from this picker, so they must not appear as
// selectable options. Use ALL_ENCOUNTER_TYPE_SELECT_OPTIONS for
// display/editing of existing encounters (which must still resolve the
// unbilled labels).
const ENCOUNTER_TYPE_SELECT_OPTIONS = ENCOUNTER_TYPE_OPTIONS.filter((type)=>type.code !== UNBILLED_INTAKE_ENCOUNTER_TYPE_CODE && type.code !== UNBILLED_FOLLOW_UP_ENCOUNTER_TYPE_CODE).map((type)=>({
        value: type.code,
        label: getEncounterTypeUiLabel(type),
        description: type.description
    }));
// All types (including legacy) for display/editing existing encounters
const ALL_ENCOUNTER_TYPE_SELECT_OPTIONS = ALL_ENCOUNTER_TYPE_OPTIONS.map((type)=>({
        value: type.code,
        label: getEncounterTypeUiLabel(type),
        deprecated: "deprecated" in type ? type.deprecated : false,
        version: "version" in type ? type.version : undefined
    }));
// Standard select options (backward compatible)
const ENCOUNTER_METHOD_SELECT_OPTIONS = ENCOUNTER_METHOD_OPTIONS.map((method)=>({
        value: method.code,
        label: method.display
    }));
// Enhanced options with descriptions for CardSelect component
const ENCOUNTER_METHOD_CARD_OPTIONS = ENCOUNTER_METHOD_OPTIONS.map((method)=>({
        value: method.code,
        label: method.display,
        description: method.description
    }));
const ENCOUNTER_DIRECTION_CARD_OPTIONS = ENCOUNTER_DIRECTION_OPTIONS.map((direction)=>({
        value: direction.code,
        label: direction.display,
        description: direction.description
    }));
const ENCOUNTER_BILLABLE_CARD_OPTIONS = ENCOUNTER_BILLABLE_OPTIONS.map((billable)=>({
        value: billable.code,
        label: billable.display,
        description: billable.description
    }));
const ENCOUNTER_DIRECTION_SELECT_OPTIONS = ENCOUNTER_DIRECTION_OPTIONS.map((direction)=>({
        value: direction.code,
        label: direction.display
    }));
const ENCOUNTER_SUCCESS_SELECT_OPTIONS = ENCOUNTER_SUCCESS_OPTIONS.map((success)=>({
        value: success.code,
        label: success.display
    }));
const ENCOUNTER_BILLABLE_SELECT_OPTIONS = ENCOUNTER_BILLABLE_OPTIONS.map((billable)=>({
        value: billable.code,
        label: billable.display
    }));
/**
 * @deprecated Import PATIENT_ENGAGEMENT_STATUS_SELECT_OPTIONS directly from patient.ts instead
 * This re-export is removed to avoid export conflicts in the domain constants index.
 */ // =============================================================================
// HELPER FUNCTIONS FOR DISPLAY AND VALIDATION
// =============================================================================
// Enhanced helper function that works with both legacy and current types
function getEncounterTypeDisplay(code) {
    const type = ALL_ENCOUNTER_TYPE_OPTIONS.find((t)=>t.code === code);
    if (type) {
        const deprecatedSuffix = "deprecated" in type && type.deprecated ? " (Legacy)" : "";
        return type.display + deprecatedSuffix;
    }
    return "Unknown encounter type";
}
// Helper to check if an encounter type is deprecated
function isEncounterTypeDeprecated(code) {
    const type = ALL_ENCOUNTER_TYPE_OPTIONS.find((t)=>t.code === code);
    return type && "deprecated" in type ? type.deprecated === true : false;
}
// Helper to get encounter type version
function getEncounterTypeVersion(code) {
    const type = ALL_ENCOUNTER_TYPE_OPTIONS.find((t)=>t.code === code);
    return type && "version" in type ? type.version : undefined;
}
// Legacy mapping helper for migration purposes
function mapLegacyEncounterType(legacyCode) {
    const mappings = {
        initial: CLINICAL_INTAKE_ENCOUNTER_TYPE_CODE,
        "check-in-evaluation": "general-interaction",
        "follow-up": CLINICAL_FOLLOW_UP_ENCOUNTER_TYPE_CODE
    };
    return mappings[legacyCode] || null;
}
// Other helper functions (unchanged)
function getEncounterMethodDisplay(code) {
    const method = ENCOUNTER_METHOD_OPTIONS.find((m)=>m.code === code);
    return method?.display || "Unknown method";
}
function getEncounterDirectionDisplay(code) {
    const direction = ENCOUNTER_DIRECTION_OPTIONS.find((d)=>d.code === code);
    return direction?.display || "Unknown direction";
}
function getEncounterSuccessDisplay(code) {
    const success = ENCOUNTER_SUCCESS_OPTIONS.find((s)=>s.code === code);
    return success?.display || "Unknown success status";
}
function getEncounterBillableDisplay(code) {
    const billable = ENCOUNTER_BILLABLE_OPTIONS.find((b)=>b.code === code);
    return billable?.display || "Unknown billable status";
}
// =============================================================================
// ACTIVITY → ENCOUNTER TYPE MAPPING
// =============================================================================
/**
 * Maps care plan activity codes to their corresponding encounter types.
 * When the encounter form is opened via activity context, this determines
 * which encounter type is created — the user does not select one.
 * @agentic @benchmark/core
 */ /**
 * Maps care plan activity codes to their corresponding encounter types.
 * When the encounter form is opened via activity context, this determines
 * which encounter type is created. A string value auto-selects the type;
 * an array value lets the user choose from the valid types.
 * @agentic @benchmark/core
 */ const ACTIVITY_ENCOUNTER_TYPE_MAP = {
    "complete-intake": [
        CLINICAL_INTAKE_ENCOUNTER_TYPE_CODE,
        COCM_INTAKE_ENCOUNTER_TYPE_CODE,
        UNBILLED_INTAKE_ENCOUNTER_TYPE_CODE
    ],
    "check-in-on-member": "general-interaction",
    // Not a clinical follow-up: non-licensed associates send the member a
    // self-report survey link, so it must never open the clinical follow-up
    // form (assessments / adaptive screener / billing track).
    "complete-cadenced-followup": "general-interaction",
    "follow-up-on-cocm": COCM_FOLLOW_UP_ENCOUNTER_TYPE_CODE,
    "discharge-member": "discharge-interaction"
};

/**
 * Encounter Constants
 *
 * Constants used for encounter resource construction and processing.
 * @agentic @benchmark/core
 */ /**
 * Extension URL for encounter scratch/draft data.
 * @agentic @benchmark/core
 */ const ENCOUNTER_SCRATCH_EXTENSION_URL = "http://www.benchmarkhealth.com/fhir/StructureDefinition/encounter-scratch";
/**
 * Extension URL for draft form state JSON (non-bundled fields like goals, enrollment, referrals).
 * @agentic @benchmark/core
 */ const ENCOUNTER_DRAFT_FORM_STATE_EXTENSION_URL = "http://www.benchmarkhealth.com/fhir/StructureDefinition/encounter-draft-form-state";
/**
 * Extension URL for per-section intake scratch notes (call-guide notes keyed by
 * section id). Unlike the draft-form-state extension — which is stripped on
 * finalize — this survives finalize so the full note context is restored when a
 * clinician re-opens a completed intake to edit it. Scratch is clinical context
 * for the author; it is never part of the exported note (the copy formatter
 * reads ClinicalImpressions, not this extension).
 * @agentic @benchmark/core
 */ const ENCOUNTER_SECTION_SCRATCH_EXTENSION_URL = "http://www.benchmarkhealth.com/fhir/StructureDefinition/encounter-section-scratch";
/**
 * Code system URL for AI-generated content tags.
 * @agentic @benchmark/core
 */ const AI_GENERATED_TAG_SYSTEM = "http://benchmarkhealth.com/fhir/CodeSystem/ai-generated";
/**
 * Set of AI-generated tag codes (Aurora).
 * @agentic @benchmark/core
 */ const AI_GENERATED_TAG_CODES = new Set([
    "generated-by-aurora",
    "generated_by_aurora"
]);

/**
 * All legacy (v1) encounter codes. Stable ordering matches
 * `LEGACY_ENCOUNTER_TYPE_OPTIONS`.
 * @agentic @benchmark/core
 */ const LEGACY_ENCOUNTER_CODES = LEGACY_ENCOUNTER_TYPE_OPTIONS.map((t)=>t.code);
/**
 * Whether the encounter type's Notes step shows the General Note textarea.
 * @agentic @benchmark/core
 */ const showsGeneralNotes = (encounterType)=>[
        CLINICAL_FOLLOW_UP_ENCOUNTER_TYPE_CODE,
        "case-management-interaction",
        "general-interaction",
        COCM_FOLLOW_UP_ENCOUNTER_TYPE_CODE,
        UNBILLED_FOLLOW_UP_ENCOUNTER_TYPE_CODE,
        ...LEGACY_ENCOUNTER_CODES
    ].includes(encounterType);
/**
 * Whether the encounter type's Notes step shows the structured clinical
 * impression fields.
 * @agentic @benchmark/core
 */ const showsClinicalNotes = (encounterType)=>[
        CLINICAL_INTAKE_ENCOUNTER_TYPE_CODE,
        COCM_INTAKE_ENCOUNTER_TYPE_CODE,
        UNBILLED_INTAKE_ENCOUNTER_TYPE_CODE,
        ...LEGACY_ENCOUNTER_CODES
    ].includes(encounterType);
/**
 * Whether the encounter type requires the billable status selector on Details.
 * @agentic @benchmark/core
 */ const requiresBillableStatus = (encounterType)=>[
        CLINICAL_INTAKE_ENCOUNTER_TYPE_CODE,
        ...LEGACY_ENCOUNTER_CODES
    ].includes(encounterType);
/**
 * All v2 intake encounter codes (Clinical, CoCM, Unbilled).
 * @agentic @benchmark/core
 */ const INTAKE_ENCOUNTER_CODES = [
    CLINICAL_INTAKE_ENCOUNTER_TYPE_CODE,
    COCM_INTAKE_ENCOUNTER_TYPE_CODE,
    UNBILLED_INTAKE_ENCOUNTER_TYPE_CODE
];
/**
 * All v2 follow-up encounter codes (Clinical, CoCM, Unbilled).
 * @agentic @benchmark/core
 */ const FOLLOW_UP_ENCOUNTER_CODES = [
    CLINICAL_FOLLOW_UP_ENCOUNTER_TYPE_CODE,
    COCM_FOLLOW_UP_ENCOUNTER_TYPE_CODE,
    UNBILLED_FOLLOW_UP_ENCOUNTER_TYPE_CODE
];
/**
 * Whether the code identifies a v2 intake encounter (Clinical, CoCM, Unbilled).
 * @agentic @benchmark/core
 */ const isIntakeEncounterType = (encounterType)=>encounterType !== undefined && INTAKE_ENCOUNTER_CODES.includes(encounterType);
/**
 * Whether the code identifies a v2 follow-up encounter (Clinical, CoCM, Unbilled).
 * @agentic @benchmark/core
 */ const isFollowUpEncounterType = (encounterType)=>encounterType !== undefined && FOLLOW_UP_ENCOUNTER_CODES.includes(encounterType);
/**
 * Resolve the persisted intake encounter-type code from the Schedule Intake
 * billing flag. Undefined (missing decision) defaults to the unbilled code.
 * @agentic @benchmark/core
 */ const resolveIntakeCodeFromBillingFlag = (flag)=>{
    switch(flag){
        case "cocm":
            return COCM_INTAKE_ENCOUNTER_TYPE_CODE;
        case "wellness":
            return CLINICAL_INTAKE_ENCOUNTER_TYPE_CODE;
        case "no-bill":
        case undefined:
        default:
            return UNBILLED_INTAKE_ENCOUNTER_TYPE_CODE;
    }
};
/**
 * Resolve the `encounter-billable` tag value from the billing flag. CoCM and
 * Wellness are billable; No bill (and a missing decision) are non-billable.
 * @agentic @benchmark/core
 */ const resolveBillableFromBillingFlag = (flag)=>flag === "cocm" || flag === "wellness" ? "billable" : "non-billable";
/**
 * Resolve the follow-up encounter-type code from a completed intake's code,
 * mirroring the intake billing track onto the follow-up.
 * @agentic @benchmark/core
 */ const resolveFollowUpCodeFromIntakeCode = (intakeCode)=>{
    switch(intakeCode){
        case COCM_INTAKE_ENCOUNTER_TYPE_CODE:
            return COCM_FOLLOW_UP_ENCOUNTER_TYPE_CODE;
        case UNBILLED_INTAKE_ENCOUNTER_TYPE_CODE:
            return UNBILLED_FOLLOW_UP_ENCOUNTER_TYPE_CODE;
        case CLINICAL_INTAKE_ENCOUNTER_TYPE_CODE:
        default:
            return CLINICAL_FOLLOW_UP_ENCOUNTER_TYPE_CODE;
    }
};
/**
 * Resolve the PREFILLED follow-up track for a free-created follow-up.
 *
 * The member's active care plan is authoritative for the CoCM axis; the
 * originating intake only resolves Wellness vs Unbilled when CoCM does not
 * apply. A member whose CoCM plan has ended is therefore demoted off the CoCM
 * track even when their intake was a CoCM intake — time logged against a
 * closed CoCM episode bills nothing, so mirroring the intake there would
 * misattribute it.
 *
 * This is a prefill, not a constraint: the clinician confirms or changes the
 * track in the follow-up form's Billing section.
 * @agentic @benchmark/core
 */ const resolveFollowUpCodePrefill = ({ hasActiveCoCMCarePlan, intakeCode })=>{
    if (hasActiveCoCMCarePlan) return COCM_FOLLOW_UP_ENCOUNTER_TYPE_CODE;
    const mirrored = resolveFollowUpCodeFromIntakeCode(intakeCode);
    return mirrored === COCM_FOLLOW_UP_ENCOUNTER_TYPE_CODE ? CLINICAL_FOLLOW_UP_ENCOUNTER_TYPE_CODE : mirrored;
};
/**
 * Whether the code identifies a deprecated (v1) encounter type.
 * @agentic @benchmark/core
 */ const isLegacyEncounterType = (encounterType)=>ALL_ENCOUNTER_TYPE_OPTIONS.some((option)=>option.code === encounterType && "deprecated" in option && option.deprecated === true);
/**
 * Encounter codes available for free-creation surfaces (no activity context).
 * Excludes activity-only types (`discharge-interaction`) and the unbilled
 * codes, which are never picked directly — they are derived from the intake
 * billing flag (intake) or the originating intake's code (follow-up).
 * @agentic @benchmark/core
 */ const getFreeCreationEncounterTypes = ()=>ENCOUNTER_TYPE_OPTIONS.filter((t)=>t.code !== "discharge-interaction" && t.code !== UNBILLED_INTAKE_ENCOUNTER_TYPE_CODE && t.code !== UNBILLED_FOLLOW_UP_ENCOUNTER_TYPE_CODE).map((t)=>t.code);
/**
 * Free-creation encounter codes excluding the v2 intake types. Used by
 * surfaces that intentionally omit intake (e.g. PatientFloatingActionButton's
 * "Log Interaction" shortcut, where intake is reached only through the
 * `complete-intake` activity).
 * @agentic @benchmark/core
 */ const getFreeCreationNonIntakeEncounterTypes = ()=>getFreeCreationEncounterTypes().filter((code)=>code !== CLINICAL_INTAKE_ENCOUNTER_TYPE_CODE && code !== COCM_INTAKE_ENCOUNTER_TYPE_CODE && // Follow-up tracks share one form, so the picker offers a single
        // "Followup" option (the clinical code is its representative). The
        // unified form prefills the real track from the member's care plan and
        // intake, and the clinician confirms it on the Details step.
        code !== COCM_FOLLOW_UP_ENCOUNTER_TYPE_CODE);

/**
 * Flag Domain Constants (FHIR R4)
 *
 * Defines controlled value sets for Flag.category and Flag.code.
 * - category: HL7-defined categories for flags
 * - code: Curated SNOMED CT clinical findings relevant to safety/behavioral risk flags
 */ // Flag.category uses HL7 flag-category codes
const FLAG_CATEGORY_SYSTEM = "http://terminology.hl7.org/CodeSystem/flag-category";
const FLAG_CATEGORY_OPTIONS = [
    {
        code: "admin",
        display: "Administrative",
        system: FLAG_CATEGORY_SYSTEM
    },
    {
        code: "advance-directive",
        display: "Advance Directive",
        system: FLAG_CATEGORY_SYSTEM
    },
    {
        code: "behavioral",
        display: "Behavioral",
        system: FLAG_CATEGORY_SYSTEM
    },
    {
        code: "contact",
        display: "Subject Contact",
        system: FLAG_CATEGORY_SYSTEM
    },
    {
        code: "clinical",
        display: "Clinical",
        system: FLAG_CATEGORY_SYSTEM
    },
    {
        code: "drug",
        display: "Drug",
        system: FLAG_CATEGORY_SYSTEM
    },
    {
        code: "safety",
        display: "Safety",
        system: FLAG_CATEGORY_SYSTEM
    }
];
function getFlagCategoryDisplay(code) {
    return FLAG_CATEGORY_OPTIONS.find((o)=>o.code === code)?.display || code;
}
// Flag.code is a SNOMED CT clinical finding; constrain to relevant risk/safety findings
const FLAG_CODE_SYSTEM = "http://snomed.info/sct";
// Organization-specific codes for operational/policy flags
const FLAG_LOCAL_CODE_SYSTEM = "https://benchmark.health/fhir/CodeSystem/flag-code";
const FLAG_CODE_OPTIONS = [
    // Suicide risk and self-harm
    {
        code: "161901003",
        display: "Self-Harm",
        system: FLAG_CODE_SYSTEM,
        category: "safety",
        description: "History of self-harming behavior"
    },
    {
        code: "225444004",
        display: "Suicidality",
        system: FLAG_CODE_SYSTEM,
        category: "safety",
        description: "History of suicidal ideation or behavior"
    },
    {
        code: "275939002",
        display: "Suicide Attempt(s)",
        system: FLAG_CODE_SYSTEM,
        category: "safety",
        description: "History of suicide attempts"
    },
    // Violence/aggression risk
    {
        code: "248062006",
        display: "Aggression",
        system: FLAG_CODE_SYSTEM,
        category: "safety",
        description: "History of aggression"
    },
    {
        code: "110483000",
        display: "Violence",
        system: FLAG_CODE_SYSTEM,
        category: "safety",
        description: "History of violence"
    },
    // Operational/policy flags (local codes)
    {
        code: "clinician-contact-only",
        display: "Clinician Contact Only",
        system: FLAG_LOCAL_CODE_SYSTEM,
        category: "contact",
        description: "Member may only be contacted by a clinician"
    },
    {
        code: "cocm-enrolled",
        display: "Enrolled in COCM",
        system: FLAG_LOCAL_CODE_SYSTEM,
        category: "admin",
        description: "Member is enrolled in COCM"
    },
    {
        code: "patient-under-18",
        display: "Minor (Under 18)",
        system: FLAG_LOCAL_CODE_SYSTEM,
        category: "admin",
        description: "Member is a minor"
    },
    {
        code: "patient-paused",
        display: "Paused",
        system: FLAG_LOCAL_CODE_SYSTEM,
        category: "admin",
        description: "Member is temporarily paused"
    }
];
function getFlagCodeDisplay(code) {
    return FLAG_CODE_OPTIONS.find((o)=>o.code === code)?.display || code;
}
function getFlagCodingByCode(code) {
    const match = FLAG_CODE_OPTIONS.find((o)=>o.code === code);
    if (match) {
        return {
            system: match.system,
            display: match.display
        };
    }
    return {
        system: FLAG_CODE_SYSTEM,
        display: code
    };
}
/**
 * Get the FHIR flag category code for a given flag code.
 * This ensures consistent category assignment across the application.
 *
 * @param code - The flag code (e.g., "cocm-enrolled", "161901003")
 * @returns The category code (e.g., "admin", "safety") or undefined if not found
 */ function getFlagCategoryByCode(code) {
    const match = FLAG_CODE_OPTIONS.find((o)=>o.code === code);
    return match?.category;
}
// UI-ready options for CardSelect with descriptions
const FLAG_CODE_CARD_OPTIONS = FLAG_CODE_OPTIONS.map((o)=>({
        value: o.code,
        label: o.display,
        description: o.description
    }));
const PAUSE_FLAG_REASON_EXTENSION_URL = "http://benchmarkhealth.com/fhir/StructureDefinition/pause-reason";

/**
 * Healthcare Service Constants
 * Centralized constants for FHIR Healthcare Service resources
 * Implements FHIR R4-compliant dimension + value coding pattern
 * with consistent service- prefixed CodeSystem URLs
 */ /**
 * CodeSystem URLs for HealthcareService resources
 * All CodeSystems follow the service- prefix naming convention
 */ const HEALTHCARE_SERVICE_CODESYSTEMS = {
    // Core CodeSystems
    SERVICE_CATEGORY: "https://benchmarkhealth.com/fhir/CodeSystem/service-category",
    SERVICE_TYPE: "https://benchmarkhealth.com/fhir/CodeSystem/service-type",
    SERVICE_SPECIALTY: "https://benchmarkhealth.com/fhir/CodeSystem/service-specialty",
    SERVICE_SPECIALTY_DIMENSION: "https://benchmarkhealth.com/fhir/CodeSystem/service-specialty-dimension",
    SERVICE_SPECIALTY_TREATMENT_MODALITY: "https://benchmarkhealth.com/fhir/CodeSystem/service-specialty-treatment-modality",
    SERVICE_SPECIALTY_TREATMENT_CONDITION: "https://benchmarkhealth.com/fhir/CodeSystem/service-specialty-treatment-condition",
    SERVICE_PROVISION: "https://benchmarkhealth.com/fhir/CodeSystem/service-provision",
    // Eligibility Dimension CodeSystems
    SERVICE_ELIGIBILITY_DIMENSION: "https://benchmarkhealth.com/fhir/CodeSystem/service-eligibility-dimension",
    SERVICE_ELIGIBILITY_AGE_GROUP: "https://benchmarkhealth.com/fhir/CodeSystem/service-eligibility-age-group",
    SERVICE_ELIGIBILITY_GENDER: "https://benchmarkhealth.com/fhir/CodeSystem/service-eligibility-gender",
    SERVICE_ELIGIBILITY_VETERAN_STATUS: "https://benchmarkhealth.com/fhir/CodeSystem/service-eligibility-veteran-status",
    SERVICE_ELIGIBILITY_STUDENT_STATUS: "https://benchmarkhealth.com/fhir/CodeSystem/service-eligibility-student-status",
    SERVICE_ELIGIBILITY_PREGNANCY_STATUS: "https://benchmarkhealth.com/fhir/CodeSystem/service-eligibility-pregnancy-status",
    SERVICE_ELIGIBILITY_LEGAL_STATUS: "https://benchmarkhealth.com/fhir/CodeSystem/service-eligibility-legal-status",
    SERVICE_ELIGIBILITY_INCOME_BAND: "https://benchmarkhealth.com/fhir/CodeSystem/service-eligibility-income-band",
    // Characteristic Dimension CodeSystems
    SERVICE_CHARACTERISTIC_DIMENSION: "https://benchmarkhealth.com/fhir/CodeSystem/service-characteristic-dimension",
    SERVICE_CHARACTERISTIC_VISIT_MODE: "https://benchmarkhealth.com/fhir/CodeSystem/service-characteristic-visit-mode",
    SERVICE_CHARACTERISTIC_PARTNERSHIP: "https://benchmarkhealth.com/fhir/CodeSystem/service-characteristic-partnership",
    SERVICE_CHARACTERISTIC_ACCESSIBILITY: "https://benchmarkhealth.com/fhir/CodeSystem/service-characteristic-accessibility",
    SERVICE_CHARACTERISTIC_SCHEDULING: "https://benchmarkhealth.com/fhir/CodeSystem/service-characteristic-scheduling",
    SERVICE_CHARACTERISTIC_WAIT_TIME: "https://benchmarkhealth.com/fhir/CodeSystem/service-characteristic-wait-time",
    SERVICE_CHARACTERISTIC_SESSION_LENGTH: "https://benchmarkhealth.com/fhir/CodeSystem/service-characteristic-session-length",
    SERVICE_CHARACTERISTIC_EVIDENCE_TIER: "https://benchmarkhealth.com/fhir/CodeSystem/service-characteristic-evidence-tier",
    SERVICE_CHARACTERISTIC_CULTURAL_FOCUS: "https://benchmarkhealth.com/fhir/CodeSystem/service-characteristic-cultural-focus",
    SERVICE_CHARACTERISTIC_CLINICIAN_CADENCE: "https://benchmarkhealth.com/fhir/CodeSystem/service-characteristic-clinician-cadence",
    SERVICE_CHARACTERISTIC_ACCESS: "https://benchmarkhealth.com/fhir/CodeSystem/service-characteristic-access",
    SERVICE_CHARACTERISTIC_AVAILABILITY: "https://benchmarkhealth.com/fhir/CodeSystem/service-characteristic-availability",
    SERVICE_CHARACTERISTIC_AGE_GROUP: "https://benchmarkhealth.com/fhir/CodeSystem/service-characteristic-age-group",
    // Program Dimension CodeSystems
    SERVICE_PROGRAM_DIMENSION: "https://benchmarkhealth.com/fhir/CodeSystem/service-program-dimension",
    SERVICE_PROGRAM_PAYER_NETWORK: "https://benchmarkhealth.com/fhir/CodeSystem/service-program-payer-network",
    SERVICE_PROGRAM_STATE: "https://benchmarkhealth.com/fhir/CodeSystem/service-program-state",
    SERVICE_PROGRAM_INSURANCE_PLAN: "https://benchmarkhealth.com/fhir/CodeSystem/service-program-insurance-plan",
    // ReferralMethod Dimension CodeSystems
    SERVICE_REFERRAL_METHOD_DIMENSION: "https://benchmarkhealth.com/fhir/CodeSystem/service-referralMethod-dimension",
    SERVICE_REFERRAL_METHOD_INTAKE_CHANNEL: "https://benchmarkhealth.com/fhir/CodeSystem/service-referralMethod-intakeChannel",
    SERVICE_REFERRAL_METHOD_REFERRAL_TYPE: "https://benchmarkhealth.com/fhir/CodeSystem/service-referralMethod-referralType",
    SERVICE_REFERRAL_METHOD_REFERRAL_FORMAT: "https://benchmarkhealth.com/fhir/CodeSystem/service-referralMethod-referralFormat"
};
// ============================================================================
// CORE SERVICE CONSTANTS
// ============================================================================
/**
 * Service Category options (service-category CodeSystem)
 * Exactly one category required per HealthcareService
 */ const SERVICE_CATEGORY_OPTIONS = [
    {
        value: "mental-health",
        label: "Mental Health"
    },
    {
        value: "substance-use",
        label: "Substance Use"
    },
    {
        value: "crisis-services",
        label: "Crisis Services"
    },
    {
        value: "social-services",
        label: "Social Services"
    }
];
/**
 * Service Type options (service-type CodeSystem)
 * Exactly one type required per HealthcareService
 */ const SERVICE_TYPE_OPTIONS = [
    // Mental Health - Assessment & Outpatient
    {
        value: "psychological-testing",
        label: "Psychological Testing & Assessment"
    },
    {
        value: "self-guided-program",
        label: "Self-Guided Program"
    },
    {
        value: "individual-coaching",
        label: "Individual Coaching"
    },
    {
        value: "individual-therapy",
        label: "Individual Therapy"
    },
    {
        value: "collaborative-care",
        label: "Collaborative Care"
    },
    {
        value: "family-couples-therapy",
        label: "Family & Couples Therapy"
    },
    {
        value: "peer-support-group-therapy",
        label: "Peer Support & Group Therapy"
    },
    {
        value: "psychiatry-medication-management",
        label: "Psychiatry & Medication Management"
    },
    // Mental Health - Intensive & Inpatient
    {
        value: "intensive-outpatient-program",
        label: "Intensive Outpatient (IOP)"
    },
    {
        value: "partial-hospitalization",
        label: "Partial Hospitalization (PHP)"
    },
    {
        value: "crisis-stabilization",
        label: "Crisis Stabilization"
    },
    {
        value: "inpatient",
        label: "Inpatient"
    },
    {
        value: "residential",
        label: "Residential"
    },
    // Mental Health - Crisis Services
    {
        value: "warm-line",
        label: "Warm Line"
    },
    {
        value: "hotline",
        label: "Hotline"
    },
    {
        value: "mobile-crisis-team",
        label: "Mobile Crisis Team"
    },
    // Substance Use - Harm Reduction & Outpatient
    {
        value: "substance-use-hotlines",
        label: "Substance Use Hotlines"
    },
    {
        value: "harm-reduction",
        label: "Harm Reduction"
    },
    {
        value: "substance-use-case-management",
        label: "Substance Use Case Management"
    },
    {
        value: "individual-substance-use-counseling",
        label: "Individual Substance Use Counseling"
    },
    {
        value: "peer-support-substance-use-groups",
        label: "Peer Support & Recovery Groups"
    },
    {
        value: "medication-assisted-treatment",
        label: "Medication-Assisted Treatment"
    },
    // Substance Use - Intensive & Inpatient
    {
        value: "detoxification",
        label: "Detoxification"
    },
    {
        value: "intensive-outpatient-rehabilitation",
        label: "Intensive Outpatient Rehabilitation"
    },
    {
        value: "residential-rehabilitation",
        label: "Residential Rehabilitation"
    },
    // Social Services
    {
        value: "domestic-violence-sexual-assault",
        label: "Domestic Violence & Sexual Assault Services"
    },
    {
        value: "homeless-outreach-case-management",
        label: "Homeless Outreach & Case Management"
    },
    {
        value: "housing-assistance",
        label: "Housing Assistance"
    },
    {
        value: "food-nutrition-assistance",
        label: "Food & Nutrition Assistance"
    },
    {
        value: "employment-vocational-support",
        label: "Employment & Vocational Support"
    },
    {
        value: "financial-benefits-counseling",
        label: "Financial & Benefits Counseling"
    },
    {
        value: "caregiver-support-respite",
        label: "Caregiver Support & Respite"
    },
    {
        value: "legal-aid-advocacy",
        label: "Legal Aid & Advocacy"
    }
];
/**
 * Mapping of service categories to their valid service types
 * This enables tight coupling between category and type selections
 */ const SERVICE_TYPE_BY_CATEGORY = {
    "mental-health": [
        "psychological-testing",
        "self-guided-program",
        "individual-coaching",
        "individual-therapy",
        "collaborative-care",
        "family-couples-therapy",
        "peer-support-group-therapy",
        "psychiatry-medication-management",
        "intensive-outpatient-program",
        "partial-hospitalization",
        "crisis-stabilization",
        "inpatient",
        "residential"
    ],
    "crisis-services": [
        "warm-line",
        "hotline",
        "mobile-crisis-team"
    ],
    "substance-use": [
        "substance-use-hotlines",
        "substance-use-case-management",
        "individual-substance-use-counseling",
        "peer-support-substance-use-groups",
        "medication-assisted-treatment",
        "detoxification",
        "intensive-outpatient-rehabilitation",
        "residential-rehabilitation",
        "harm-reduction"
    ],
    "social-services": [
        "domestic-violence-sexual-assault",
        "homeless-outreach-case-management",
        "housing-assistance",
        "food-nutrition-assistance",
        "employment-vocational-support",
        "financial-benefits-counseling",
        "caregiver-support-respite",
        "legal-aid-advocacy"
    ]
};
/**
 * Get service type options filtered by category
 * @param category - The selected category value
 * @returns Array of type options that belong to the category, or all types if no category selected
 */ const getServiceTypesByCategory = (category)=>{
    if (!category || !SERVICE_TYPE_BY_CATEGORY[category]) {
        return [
            ...SERVICE_TYPE_OPTIONS
        ];
    }
    const validTypes = SERVICE_TYPE_BY_CATEGORY[category];
    return SERVICE_TYPE_OPTIONS.filter((type)=>validTypes.includes(type.value));
};
// ============================================================================
// SPECIALTY DIMENSION CONSTANTS (NEW - Multi-dimensional specialty system)
// ============================================================================
/**
 * Specialty Dimension options (service-specialty-dimension CodeSystem)
 * Multi-dimensional specialty system for organizing service specialties
 */ const SERVICE_SPECIALTY_DIMENSION_OPTIONS = [
    {
        value: "treatmentModality",
        label: "Treatment Modality"
    },
    {
        value: "treatmentCondition",
        label: "Treatment Condition"
    }
];
/**
 * Specialty Treatment Modality options (service-specialty-treatment-modality CodeSystem)
 * Therapeutic approaches and intervention methods
 */ const SERVICE_SPECIALTY_TREATMENT_MODALITY_OPTIONS = [
    // Individual Psychotherapies
    {
        value: "cbt",
        label: "Cognitive Behavioral Therapy (CBT)"
    },
    {
        value: "dbt",
        label: "Dialectical Behavior Therapy (DBT)"
    },
    {
        value: "act",
        label: "Acceptance and Commitment Therapy (ACT)"
    },
    {
        value: "emdr",
        label: "EMDR Therapy"
    },
    {
        value: "trauma-focused-cbt",
        label: "Trauma-Focused CBT"
    },
    {
        value: "exposure-therapy",
        label: "Exposure Therapy"
    },
    {
        value: "psychodynamic",
        label: "Psychodynamic Therapy"
    },
    {
        value: "psychoanalytic",
        label: "Psychoanalytic Therapy"
    },
    {
        value: "gestalt",
        label: "Gestalt Therapy"
    },
    {
        value: "narrative-therapy",
        label: "Narrative Therapy"
    },
    {
        value: "schema-therapy",
        label: "Schema Therapy"
    },
    {
        value: "interpersonal-therapy",
        label: "Interpersonal Therapy (IPT)"
    },
    {
        value: "solution-focused",
        label: "Solution-Focused Brief Therapy"
    },
    {
        value: "emotionally-focused",
        label: "Emotionally Focused Therapy (EFT)"
    },
    {
        value: "internal-family-systems",
        label: "Internal Family Systems (IFS)"
    },
    // Group & Systemic Therapies
    {
        value: "group-psychotherapy",
        label: "Group Psychotherapy"
    },
    {
        value: "psychoeducation-groups",
        label: "Psychoeducation Groups"
    },
    {
        value: "process-groups",
        label: "Process Groups"
    },
    {
        value: "skills-groups",
        label: "Skills Training Groups"
    },
    {
        value: "family-systems",
        label: "Family Systems Therapy"
    },
    {
        value: "structural-family",
        label: "Structural Family Therapy"
    },
    {
        value: "strategic-family",
        label: "Strategic Family Therapy"
    },
    {
        value: "multisystemic-therapy",
        label: "Multisystemic Therapy (MST)"
    },
    // Medication-Assisted & Neuromodulation Therapies
    {
        value: "esketamine-therapy",
        label: "Esketamine Therapy"
    },
    {
        value: "tms",
        label: "Transcranial Magnetic Stimulation (TMS)"
    },
    {
        value: "ect",
        label: "Electroconvulsive Therapy (ECT)"
    },
    {
        value: "lai-antipsychotic",
        label: "Long-Acting Injectable Antipsychotics"
    },
    {
        value: "buprenorphine-therapy",
        label: "Buprenorphine Treatment"
    },
    {
        value: "methadone-therapy",
        label: "Methadone Treatment"
    },
    {
        value: "naltrexone-therapy",
        label: "Naltrexone Treatment"
    },
    {
        value: "vivitrol",
        label: "Vivitrol (Injectable Naltrexone)"
    },
    // Psychedelic & Emerging Therapies
    {
        value: "psychedelic-assisted",
        label: "Psychedelic-Assisted Therapy"
    },
    {
        value: "ketamine-assisted",
        label: "Ketamine-Assisted Psychotherapy"
    },
    {
        value: "mdma-assisted",
        label: "MDMA-Assisted Therapy"
    },
    {
        value: "psilocybin-assisted",
        label: "Psilocybin-Assisted Therapy"
    },
    // Expressive & Alternative Therapies
    {
        value: "art-therapy",
        label: "Art Therapy"
    },
    {
        value: "music-therapy",
        label: "Music Therapy"
    },
    {
        value: "play-therapy",
        label: "Play Therapy"
    },
    {
        value: "hypnotherapy",
        label: "Hypnotherapy"
    },
    {
        value: "biofeedback",
        label: "Biofeedback"
    },
    {
        value: "neurofeedback",
        label: "Neurofeedback"
    },
    {
        value: "equine-therapy",
        label: "Equine-Assisted Therapy"
    },
    {
        value: "adventure-therapy",
        label: "Adventure/Wilderness Therapy"
    },
    {
        value: "horticultural-therapy",
        label: "Horticultural Therapy"
    },
    {
        value: "acupuncture",
        label: "Acupuncture"
    },
    {
        value: "somatic",
        label: "Somatic/Body-Based Therapy"
    },
    {
        value: "meditation-mindfulness",
        label: "Meditation & Mindfulness Training"
    },
    {
        value: "mindfulness",
        label: "Mindfulness-Based Therapy"
    },
    {
        value: "yoga-therapy",
        label: "Yoga Therapy"
    },
    {
        value: "wilderness-therapy",
        label: "Wilderness Therapy"
    },
    // Other Modalities
    {
        value: "motivational-interviewing",
        label: "Motivational Interviewing"
    }
];
/**
 * Specialty Treatment Condition options (service-specialty-treatment-condition CodeSystem)
 * Conditions and diagnoses the service treats
 */ const SERVICE_SPECIALTY_TREATMENT_CONDITION_OPTIONS = [
    {
        value: "adhd",
        label: "ADHD"
    },
    {
        value: "anxiety-disorders",
        label: "Anxiety Disorders"
    },
    {
        value: "autism-spectrum",
        label: "Autism Spectrum"
    },
    {
        value: "bipolar-disorder",
        label: "Bipolar Disorder"
    },
    {
        value: "chronic-health-conditions",
        label: "Chronic Health Conditions"
    },
    {
        value: "depression",
        label: "Depressive Disorders"
    },
    {
        value: "dual-diagnosis",
        label: "Dual Diagnosis (Comorbidity)"
    },
    {
        value: "eating-disorders",
        label: "Eating Disorders"
    },
    {
        value: "grief-loss",
        label: "Grief & Loss"
    },
    {
        value: "ocd",
        label: "OCD & Related Disorders"
    },
    {
        value: "personality-disorders",
        label: "Personality Disorders"
    },
    {
        value: "ptsd-trauma",
        label: "Trauma & Stressor-Related Disorders"
    },
    {
        value: "psychotic-disorders",
        label: "Psychotic Disorders"
    },
    {
        value: "self-harm",
        label: "Self-Harm & Suicidality"
    },
    {
        value: "sleep-disorders",
        label: "Sleep Disorders"
    },
    {
        value: "substance-use",
        label: "Substance Use Disorders"
    }
];
/**
 * Service Provision Code options (service-provision CodeSystem)
 * Payment and cost-related conditions under which service is offered
 *
 * Note: Access patterns (referral-required, walk-in-accepted, appointment-required)
 * have been moved to SERVICE_CHARACTERISTIC_ACCESS_OPTIONS.
 * Availability status (waitlist-active) has been moved to SERVICE_CHARACTERISTIC_AVAILABILITY_OPTIONS.
 */ const SERVICE_PROVISION_OPTIONS = [
    {
        value: "free-care",
        label: "Free"
    },
    {
        value: "sliding-scale",
        label: "Sliding Scale"
    },
    {
        value: "cash-pay-accepted",
        label: "Cash Pay Accepted"
    },
    {
        value: "insurance-accepted",
        label: "Insurance Accepted"
    },
    {
        value: "insurance-required",
        label: "Insurance Required"
    },
    {
        value: "medicaid-accepted",
        label: "Medicaid Accepted"
    },
    {
        value: "medicare-accepted",
        label: "Medicare Accepted"
    }
];
// ============================================================================
// ELIGIBILITY DIMENSION CONSTANTS
// ============================================================================
/**
 * Eligibility Dimension options (service-eligibility-dimension CodeSystem)
 */ const SERVICE_ELIGIBILITY_DIMENSION_OPTIONS = [
    {
        value: "age",
        label: "Age"
    },
    {
        value: "gender",
        label: "Gender"
    },
    {
        value: "veteranStatus",
        label: "Veteran Status"
    },
    {
        value: "studentStatus",
        label: "Student Status"
    },
    {
        value: "pregnancyStatus",
        label: "Pregnancy Status"
    },
    {
        value: "legalStatus",
        label: "Legal Status"
    },
    {
        value: "residence",
        label: "Residence"
    },
    {
        value: "language",
        label: "Language"
    },
    {
        value: "incomeBand",
        label: "Income Band"
    }
];
/**
 * Eligibility Age Group options (service-eligibility-age-group CodeSystem)
 */ const SERVICE_ELIGIBILITY_AGE_GROUP_OPTIONS = [
    {
        value: "0-12",
        label: "0-12 years"
    },
    {
        value: "13-17",
        label: "13-17 years"
    },
    {
        value: "18-64",
        label: "18-64 years"
    },
    {
        value: "65-plus",
        label: "65+ years"
    }
];
/**
 * Eligibility Gender options (service-eligibility-gender CodeSystem)
 */ const SERVICE_ELIGIBILITY_GENDER_OPTIONS = [
    {
        value: "female",
        label: "Female"
    },
    {
        value: "male",
        label: "Male"
    },
    {
        value: "nonbinary",
        label: "Non-binary"
    },
    {
        value: "any",
        label: "Any"
    }
];
/**
 * Eligibility Veteran Status options (service-eligibility-veteran-status CodeSystem)
 */ const SERVICE_ELIGIBILITY_VETERAN_STATUS_OPTIONS = [
    {
        value: "yes",
        label: "Yes"
    },
    {
        value: "no",
        label: "No"
    },
    {
        value: "any",
        label: "Any"
    }
];
/**
 * Eligibility Student Status options (service-eligibility-student-status CodeSystem)
 */ const SERVICE_ELIGIBILITY_STUDENT_STATUS_OPTIONS = [
    {
        value: "yes",
        label: "Yes"
    },
    {
        value: "no",
        label: "No"
    },
    {
        value: "part-time",
        label: "Part-time"
    },
    {
        value: "full-time",
        label: "Full-time"
    },
    {
        value: "any",
        label: "Any"
    }
];
/**
 * Eligibility Pregnancy Status options (service-eligibility-pregnancy-status CodeSystem)
 */ const SERVICE_ELIGIBILITY_PREGNANCY_STATUS_OPTIONS = [
    {
        value: "pregnant",
        label: "Pregnant"
    },
    {
        value: "postpartum",
        label: "Postpartum"
    },
    {
        value: "not-pregnant",
        label: "Not Pregnant"
    },
    {
        value: "any",
        label: "Any"
    }
];
/**
 * Eligibility Legal Status options (service-eligibility-legal-status CodeSystem)
 */ const SERVICE_ELIGIBILITY_LEGAL_STATUS_OPTIONS = [
    {
        value: "court-ordered",
        label: "Court Ordered"
    },
    {
        value: "voluntary",
        label: "Voluntary"
    },
    {
        value: "probation",
        label: "Probation"
    },
    {
        value: "parole",
        label: "Parole"
    },
    {
        value: "any",
        label: "Any"
    }
];
/**
 * Eligibility Income Band options (service-eligibility-income-band CodeSystem)
 */ const SERVICE_ELIGIBILITY_INCOME_BAND_OPTIONS = [
    {
        value: "low",
        label: "Low Income"
    },
    {
        value: "medium",
        label: "Medium Income"
    },
    {
        value: "high",
        label: "High Income"
    },
    {
        value: "any",
        label: "Any"
    }
];
// ============================================================================
// CHARACTERISTIC DIMENSION CONSTANTS
// ============================================================================
/**
 * Characteristic Dimension options (service-characteristic-dimension CodeSystem)
 */ const SERVICE_CHARACTERISTIC_DIMENSION_OPTIONS = [
    {
        value: "visitMode",
        label: "Visit Mode"
    },
    {
        value: "partnership",
        label: "Partnership"
    },
    {
        value: "accessibility",
        label: "Accessibility"
    },
    {
        value: "scheduling",
        label: "Scheduling"
    },
    {
        value: "waitTime",
        label: "Wait Time"
    },
    {
        value: "sessionLength",
        label: "Session Length"
    },
    {
        value: "evidenceTier",
        label: "Evidence Tier"
    },
    {
        value: "culturalFocus",
        label: "Cultural Focus"
    },
    {
        value: "clinicianCadence",
        label: "Clinician Cadence"
    },
    {
        value: "access",
        label: "Access"
    },
    {
        value: "availability",
        label: "Availability"
    },
    {
        value: "ageGroup",
        label: "Age Group"
    }
];
/**
 * Characteristic Visit Mode options (service-characteristic-visit-mode CodeSystem)
 * Comprehensive service delivery channels including modern text/app-based modalities
 */ const SERVICE_CHARACTERISTIC_VISIT_MODE_OPTIONS = [
    // Synchronous in-person
    {
        value: "in-person",
        label: "In-Person"
    },
    // Combinations
    {
        value: "hybrid",
        label: "Hybrid"
    },
    // Synchronous remote
    {
        value: "telehealth-video",
        label: "Telehealth (Video)"
    },
    {
        value: "telehealth-phone",
        label: "Telehealth (Phone)"
    },
    // Text-based
    {
        value: "text-sms",
        label: "Text/SMS"
    },
    {
        value: "chat",
        label: "Web Chat"
    },
    // Asynchronous
    {
        value: "async-messaging",
        label: "Asynchronous Messaging"
    },
    // Digital/self-guided
    {
        value: "digital-self-guided",
        label: "Digital Self-Guided"
    }
];
/**
 * Characteristic Partnership options (service-characteristic-partnership CodeSystem)
 * Defines partnership relationships for healthcare services
 */ const SERVICE_CHARACTERISTIC_PARTNERSHIP_OPTIONS = [
    {
        value: "primary",
        label: "Primary"
    },
    {
        value: "secondary",
        label: "Secondary"
    }
];
/**
 * Characteristic Accessibility options (service-characteristic-accessibility CodeSystem)
 */ const SERVICE_CHARACTERISTIC_ACCESSIBILITY_OPTIONS = [
    {
        value: "wheelchair",
        label: "Wheelchair Accessible"
    },
    {
        value: "asl",
        label: "American Sign Language (ASL)"
    },
    {
        value: "captions",
        label: "Real-time Captions"
    },
    {
        value: "service-animals-welcome",
        label: "Service Animals Welcome"
    },
    {
        value: "sensory-friendly",
        label: "Sensory Friendly"
    },
    {
        value: "low-stimulation",
        label: "Low Stimulation Environment"
    }
];
/**
 * Characteristic Scheduling options (service-characteristic-scheduling CodeSystem)
 */ const SERVICE_CHARACTERISTIC_SCHEDULING_OPTIONS = [
    {
        value: "same-day",
        label: "Same Day Scheduling"
    },
    {
        value: "flexible",
        label: "Flexible Scheduling"
    },
    {
        value: "advance-booking",
        label: "Advance Booking Required"
    },
    {
        value: "evening-hours",
        label: "Evening Hours Available"
    },
    {
        value: "weekend-hours",
        label: "Weekend Hours Available"
    },
    {
        value: "cancellation-fee",
        label: "Cancellation Fee"
    },
    {
        value: "no-show-fee",
        label: "No Show Fee"
    }
];
/**
 * Characteristic Wait Time options (service-characteristic-wait-time CodeSystem)
 */ const SERVICE_CHARACTERISTIC_WAIT_TIME_OPTIONS = [
    {
        value: "same-day",
        label: "Same Day"
    },
    {
        value: "1-3-days",
        label: "1-3 Days"
    },
    {
        value: "4-7-days",
        label: "4-7 Days"
    },
    {
        value: "8-14-days",
        label: "8-14 Days"
    },
    {
        value: "15-plus-days",
        label: "15+ Days"
    }
];
/**
 * Characteristic Session Length options (service-characteristic-session-length CodeSystem)
 */ const SERVICE_CHARACTERISTIC_SESSION_LENGTH_OPTIONS = [
    {
        value: "15-minutes",
        label: "15 Minutes"
    },
    {
        value: "30-minutes",
        label: "30 Minutes"
    },
    {
        value: "45-minutes",
        label: "45 Minutes"
    },
    {
        value: "60-minutes",
        label: "60 Minutes"
    },
    {
        value: "90-minutes",
        label: "90 Minutes"
    },
    {
        value: "120-minutes",
        label: "120 Minutes"
    }
];
/**
 * Characteristic Evidence Tier options (service-characteristic-evidence-tier CodeSystem)
 */ const SERVICE_CHARACTERISTIC_EVIDENCE_TIER_OPTIONS = [
    {
        value: "tier-1",
        label: "Tier 1 - Strong Evidence"
    },
    {
        value: "tier-2",
        label: "Tier 2 - Moderate Evidence"
    },
    {
        value: "tier-3",
        label: "Tier 3 - Promising Evidence"
    },
    {
        value: "tier-4",
        label: "Tier 4 - Emerging Evidence"
    }
];
/**
 * Characteristic Cultural Focus options (service-characteristic-cultural-focus CodeSystem)
 */ const SERVICE_CHARACTERISTIC_CULTURAL_FOCUS_OPTIONS = [
    {
        value: "lgbtq-affirming",
        label: "LGBTQ+ Affirming"
    },
    {
        value: "culturally-diverse",
        label: "Culturally Diverse Communities"
    },
    {
        value: "refugees-immigrants",
        label: "Refugees & Immigrants"
    },
    {
        value: "veterans-military",
        label: "Veterans & Military Families"
    },
    {
        value: "first-responders",
        label: "First Responders"
    },
    {
        value: "religious-spiritual",
        label: "Religious/Spiritual"
    },
    {
        value: "indigenous-communities",
        label: "Indigenous Communities"
    }
];
/**
 * Characteristic Clinician Cadence options (service-characteristic-clinician-cadence CodeSystem)
 */ const SERVICE_CHARACTERISTIC_CLINICIAN_CADENCE_OPTIONS = [
    {
        value: "weekly",
        label: "Weekly"
    },
    {
        value: "bi-weekly",
        label: "Bi-weekly"
    },
    {
        value: "monthly",
        label: "Monthly"
    },
    {
        value: "as-needed",
        label: "As Needed"
    },
    {
        value: "intensive",
        label: "Intensive (Multiple per week)"
    }
];
/**
 * Characteristic Access options (service-characteristic-access CodeSystem)
 * How patients access the service (referral requirements, appointment patterns)
 */ const SERVICE_CHARACTERISTIC_ACCESS_OPTIONS = [
    {
        value: "appointment-required",
        label: "Appointment Required"
    },
    {
        value: "referral-required",
        label: "Referral Required"
    },
    {
        value: "self-referral-allowed",
        label: "Self-Referral Allowed"
    },
    {
        value: "walk-in-accepted",
        label: "Walk-In Accepted"
    }
];
/**
 * Characteristic Availability options (service-characteristic-availability CodeSystem)
 * Current capacity and acceptance status (filterable for directory)
 */ const SERVICE_CHARACTERISTIC_AVAILABILITY_OPTIONS = [
    {
        value: "accepting-new-patients",
        label: "Accepting New Patients"
    },
    {
        value: "not-accepting",
        label: "Not Accepting New Patients"
    },
    {
        value: "limited-availability",
        label: "Limited Availability"
    },
    {
        value: "waitlist-open",
        label: "Waitlist Open"
    },
    {
        value: "waitlist-closed",
        label: "Waitlist Closed"
    }
];
/**
 * Characteristic Age Group options (service-characteristic-age-group CodeSystem)
 * Denormalized from eligibility for FHIR searchability via characteristic param.
 */ const SERVICE_CHARACTERISTIC_AGE_GROUP_OPTIONS = [
    {
        value: "0-12",
        label: "0-12 years"
    },
    {
        value: "13-17",
        label: "13-17 years"
    },
    {
        value: "18-64",
        label: "18-64 years"
    },
    {
        value: "65-plus",
        label: "65+ years"
    }
];
// ============================================================================
// PROGRAM DIMENSION CONSTANTS
// ============================================================================
/**
 * Program Dimension options (service-program-dimension CodeSystem)
 */ const SERVICE_PROGRAM_DIMENSION_OPTIONS = [
    {
        value: "insurancePlan",
        label: "Insurance Plan"
    },
    {
        value: "payerNetwork",
        label: "Payer Network"
    },
    {
        value: "stateProgram",
        label: "State Program"
    }
];
/**
 * Program Payer Network options (service-program-payer-network CodeSystem)
 * Stable payer network identifiers
 */ const SERVICE_PROGRAM_PAYER_NETWORK_OPTIONS = [
    {
        value: "masshealth-bh-network",
        label: "MassHealth Behavioral Health Network"
    },
    {
        value: "medi-cal-xyz",
        label: "Medi-Cal XYZ"
    }
];
/**
 * Program State options (service-program-state CodeSystem)
 * State-specific program identifiers
 */ const SERVICE_PROGRAM_STATE_OPTIONS = [
    {
        value: "masshealth-bh-cp",
        label: "MassHealth BH Care Partnership"
    },
    {
        value: "medi-cal-xyz",
        label: "Medi-Cal XYZ Program"
    }
];
/**
 * Program Insurance Plan options (service-program-insurance-plan CodeSystem)
 * Specific insurance plan identifiers (e.g., bcbs-ma-preferred, aetna-choice-pos-ii)
 */ const SERVICE_PROGRAM_INSURANCE_PLAN_OPTIONS = [
    {
        value: "bcbs-ma-preferred",
        label: "BlueCross BlueShield MA Preferred"
    },
    {
        value: "aetna-choice-pos-ii",
        label: "Aetna Choice POS II"
    },
    {
        value: "uhc-options-ppo",
        label: "UnitedHealthcare Options PPO"
    }
];
// ============================================================================
// REFERRAL METHOD DIMENSION CONSTANTS
// ============================================================================
/**
 * Referral Method Dimension options (service-referralMethod-dimension CodeSystem)
 */ const SERVICE_REFERRAL_METHOD_DIMENSION_OPTIONS = [
    {
        value: "intakeChannel",
        label: "Intake Channel"
    },
    {
        value: "referralType",
        label: "Referral Type"
    },
    {
        value: "referralFormat",
        label: "Referral Format"
    }
];
/**
 * Referral Method Intake Channel options (service-referralMethod-intakeChannel CodeSystem)
 */ const SERVICE_REFERRAL_METHOD_INTAKE_CHANNEL_OPTIONS = [
    {
        value: "fax",
        label: "Fax"
    },
    {
        value: "phone",
        label: "Phone"
    },
    {
        value: "email",
        label: "Email"
    },
    {
        value: "portal",
        label: "Portal"
    },
    {
        value: "ehr-to-ehr",
        label: "EHR-to-EHR"
    },
    {
        value: "api",
        label: "API"
    }
];
/**
 * Referral Method Referral Type options (service-referralMethod-referralType CodeSystem)
 */ const SERVICE_REFERRAL_METHOD_REFERRAL_TYPE_OPTIONS = [
    {
        value: "direct",
        label: "Direct Referral"
    },
    {
        value: "indirect",
        label: "Indirect Referral"
    },
    {
        value: "self",
        label: "Self-Referral"
    },
    {
        value: "warm",
        label: "Warm Handoff"
    },
    {
        value: "cold",
        label: "Cold Referral"
    }
];
/**
 * Referral Method Referral Format options (service-referralMethod-referralFormat CodeSystem)
 */ const SERVICE_REFERRAL_METHOD_REFERRAL_FORMAT_OPTIONS = [
    {
        value: "structured",
        label: "Structured"
    },
    {
        value: "unstructured",
        label: "Unstructured"
    },
    {
        value: "ccda",
        label: "CCDA"
    },
    {
        value: "pdf",
        label: "PDF"
    },
    {
        value: "fhir-bundle",
        label: "FHIR Bundle"
    }
];
// ============================================================================
// OTHER CONSTANTS
// ============================================================================
/**
 * Service status options
 */ const SERVICE_STATUS_OPTIONS = [
    {
        value: "true",
        label: "Active"
    },
    {
        value: "false",
        label: "Inactive"
    }
];
/**
 * Appointment requirement options
 */ const APPOINTMENT_REQUIRED_OPTIONS = [
    {
        value: "true",
        label: "Yes"
    },
    {
        value: "false",
        label: "No"
    }
];
/**
 * Telecom system options for contact information
 */ const TELECOM_SYSTEM_OPTIONS = [
    {
        value: "phone",
        label: "Phone"
    },
    {
        value: "email",
        label: "Email"
    },
    {
        value: "url",
        label: "Website"
    },
    {
        value: "fax",
        label: "Fax"
    }
];
/**
 * Telecom use options for contact information
 */ const TELECOM_USE_OPTIONS = [
    {
        value: "work",
        label: "Work"
    },
    {
        value: "home",
        label: "Home"
    },
    {
        value: "mobile",
        label: "Mobile"
    }
];
// ============================================================================
// HELPER FUNCTIONS
// ============================================================================
/**
 * Get value options for a specific eligibility dimension
 */ function getEligibilityValueOptionsByDimension(dimension) {
    const dimensionMap = {
        age: SERVICE_ELIGIBILITY_AGE_GROUP_OPTIONS,
        gender: SERVICE_ELIGIBILITY_GENDER_OPTIONS,
        veteranStatus: SERVICE_ELIGIBILITY_VETERAN_STATUS_OPTIONS,
        studentStatus: SERVICE_ELIGIBILITY_STUDENT_STATUS_OPTIONS,
        pregnancyStatus: SERVICE_ELIGIBILITY_PREGNANCY_STATUS_OPTIONS,
        legalStatus: SERVICE_ELIGIBILITY_LEGAL_STATUS_OPTIONS,
        incomeBand: SERVICE_ELIGIBILITY_INCOME_BAND_OPTIONS
    };
    return dimensionMap[dimension] || [];
}
/**
 * Get value options for a specific characteristic dimension
 */ function getCharacteristicValueOptionsByDimension(dimension) {
    const dimensionMap = {
        visitMode: SERVICE_CHARACTERISTIC_VISIT_MODE_OPTIONS,
        partnership: SERVICE_CHARACTERISTIC_PARTNERSHIP_OPTIONS,
        accessibility: SERVICE_CHARACTERISTIC_ACCESSIBILITY_OPTIONS,
        scheduling: SERVICE_CHARACTERISTIC_SCHEDULING_OPTIONS,
        waitTime: SERVICE_CHARACTERISTIC_WAIT_TIME_OPTIONS,
        sessionLength: SERVICE_CHARACTERISTIC_SESSION_LENGTH_OPTIONS,
        evidenceTier: SERVICE_CHARACTERISTIC_EVIDENCE_TIER_OPTIONS,
        culturalFocus: SERVICE_CHARACTERISTIC_CULTURAL_FOCUS_OPTIONS,
        clinicianCadence: SERVICE_CHARACTERISTIC_CLINICIAN_CADENCE_OPTIONS,
        access: SERVICE_CHARACTERISTIC_ACCESS_OPTIONS,
        availability: SERVICE_CHARACTERISTIC_AVAILABILITY_OPTIONS,
        ageGroup: SERVICE_CHARACTERISTIC_AGE_GROUP_OPTIONS
    };
    return dimensionMap[dimension] || [];
}
/**
 * Get value options for a specific specialty dimension
 */ function getSpecialtyValueOptionsByDimension(dimension) {
    const dimensionMap = {
        treatmentModality: SERVICE_SPECIALTY_TREATMENT_MODALITY_OPTIONS,
        treatmentCondition: SERVICE_SPECIALTY_TREATMENT_CONDITION_OPTIONS
    };
    return dimensionMap[dimension] || [];
}
/**
 * Get value options for a specific program dimension
 */ function getProgramValueOptionsByDimension(dimension) {
    const dimensionMap = {
        payerNetwork: SERVICE_PROGRAM_PAYER_NETWORK_OPTIONS,
        stateProgram: SERVICE_PROGRAM_STATE_OPTIONS,
        insurancePlan: SERVICE_PROGRAM_INSURANCE_PLAN_OPTIONS
    };
    return dimensionMap[dimension] || [];
}
/**
 * Get value options for a specific referral method dimension
 */ function getReferralMethodValueOptionsByDimension(dimension) {
    const dimensionMap = {
        intakeChannel: SERVICE_REFERRAL_METHOD_INTAKE_CHANNEL_OPTIONS,
        referralType: SERVICE_REFERRAL_METHOD_REFERRAL_TYPE_OPTIONS,
        referralFormat: SERVICE_REFERRAL_METHOD_REFERRAL_FORMAT_OPTIONS
    };
    return dimensionMap[dimension] || [];
}

/**
 * FHIR-Compliant Constants for Insurance Plan Domain
 *
 * This file contains all constants used for InsurancePlan resources,
 * including plan type classification and network type classification.
 * All codes follow FHIR R4 value sets and include proper system/code/display structures.
 */ // =============================================================================
// INSURANCE PLAN TYPE CONSTANTS
// =============================================================================
/**
 * Insurance Plan Type CodeSystem URL
 * Used in InsurancePlan.type for plan classification
 */ const INSURANCE_PLAN_TYPE_SYSTEM = "http://benchmarkhealth.com/fhir/CodeSystem/insurance-plan-type";
/**
 * Insurance Plan Type Options (alphabetical by code)
 * Defines the classification system for insurance plan types:
 * - commercial: Standard commercial plans
 * - commercial-epo: Commercial EPO plans
 * - commercial-hmo: Commercial HMO plans
 * - commercial-ppo: Commercial PPO plans
 * - d-snp: Dual Eligible Special Needs Plans
 * - dual-eligible: Integrated dual-eligible plans (Medicare/Medicaid)
 * - eap: Employee Assistance Program plans
 * - fep: Federal Employees Program plans
 * - fep-ppo: Federal Employees Program PPO plans
 * - marketplace: ACA Marketplace plans
 * - masshealth-aco: MassHealth ACO plans
 * - masshealth-mco: MassHealth MCO plans
 * - medicaid: Medicaid plans
 * - medicare-advantage: Medicare Advantage/Replacement plans
 * - medicare-advantage-hmo: Medicare Advantage HMO plans
 * - medicare-advantage-ppo: Medicare Advantage PPO plans
 * - medicare-supplement: Medicare Supplement (Medigap) plans
 * - tricare: Military TRICARE plans
 * - tricare-hmo: TRICARE HMO plans
 * - tricare-ppo: TRICARE PPO plans
 * - workers-comp: Workers' Compensation plans
 */ const INSURANCE_PLAN_TYPE_OPTIONS = [
    {
        code: "commercial",
        display: "Commercial",
        system: INSURANCE_PLAN_TYPE_SYSTEM,
        description: "Standard commercial insurance plans"
    },
    {
        code: "commercial-epo",
        display: "Commercial EPO",
        system: INSURANCE_PLAN_TYPE_SYSTEM,
        description: "Commercial Exclusive Provider Organization plans"
    },
    {
        code: "commercial-hmo",
        display: "Commercial HMO",
        system: INSURANCE_PLAN_TYPE_SYSTEM,
        description: "Commercial Health Maintenance Organization plans"
    },
    {
        code: "commercial-ppo",
        display: "Commercial PPO",
        system: INSURANCE_PLAN_TYPE_SYSTEM,
        description: "Commercial Preferred Provider Organization plans"
    },
    {
        code: "d-snp",
        display: "D-SNP",
        system: INSURANCE_PLAN_TYPE_SYSTEM,
        description: "Dual Eligible Special Needs Plan"
    },
    {
        code: "dual-eligible",
        display: "Dual Eligible (Medicare/Medicaid)",
        system: INSURANCE_PLAN_TYPE_SYSTEM,
        description: "Integrated dual-eligible plans for Medicare and Medicaid beneficiaries"
    },
    {
        code: "eap",
        display: "Employee Assistance Program",
        system: INSURANCE_PLAN_TYPE_SYSTEM,
        description: "Employee Assistance Program plans"
    },
    {
        code: "fep",
        display: "FEP",
        system: INSURANCE_PLAN_TYPE_SYSTEM,
        description: "Federal Employees Program plans"
    },
    {
        code: "fep-ppo",
        display: "FEP PPO",
        system: INSURANCE_PLAN_TYPE_SYSTEM,
        description: "Federal Employees Program PPO plans"
    },
    {
        code: "marketplace",
        display: "ACA Marketplace",
        system: INSURANCE_PLAN_TYPE_SYSTEM,
        description: "ACA Marketplace plans"
    },
    {
        code: "masshealth-aco",
        display: "MassHealth ACO",
        system: INSURANCE_PLAN_TYPE_SYSTEM,
        description: "MassHealth Accountable Care Organization plans"
    },
    {
        code: "masshealth-mco",
        display: "MassHealth MCO",
        system: INSURANCE_PLAN_TYPE_SYSTEM,
        description: "MassHealth Managed Care Organization plans"
    },
    {
        code: "medicaid",
        display: "Medicaid",
        system: INSURANCE_PLAN_TYPE_SYSTEM,
        description: "Medicaid plans"
    },
    {
        code: "medicare-advantage",
        display: "Medicare Advantage",
        system: INSURANCE_PLAN_TYPE_SYSTEM,
        description: "Medicare Advantage/Replacement plans"
    },
    {
        code: "medicare-advantage-hmo",
        display: "Medicare Advantage HMO",
        system: INSURANCE_PLAN_TYPE_SYSTEM,
        description: "Medicare Advantage Health Maintenance Organization plans"
    },
    {
        code: "medicare-advantage-ppo",
        display: "Medicare Advantage PPO",
        system: INSURANCE_PLAN_TYPE_SYSTEM,
        description: "Medicare Advantage Preferred Provider Organization plans"
    },
    {
        code: "medicare-supplement",
        display: "Medicare Supplement",
        system: INSURANCE_PLAN_TYPE_SYSTEM,
        description: "Medicare Supplement (Medigap) plans"
    },
    {
        code: "tricare",
        display: "TRICARE",
        system: INSURANCE_PLAN_TYPE_SYSTEM,
        description: "Military TRICARE plans"
    },
    {
        code: "tricare-hmo",
        display: "TRICARE HMO",
        system: INSURANCE_PLAN_TYPE_SYSTEM,
        description: "Military TRICARE HMO plans"
    },
    {
        code: "tricare-ppo",
        display: "TRICARE PPO",
        system: INSURANCE_PLAN_TYPE_SYSTEM,
        description: "Military TRICARE PPO plans"
    },
    {
        code: "workers-comp",
        display: "Workers' Compensation",
        system: INSURANCE_PLAN_TYPE_SYSTEM,
        description: "Workers' Compensation plans"
    }
];
/**
 * Get display name for insurance plan type code
 */ const getInsurancePlanTypeDisplay = (code)=>{
    const type = INSURANCE_PLAN_TYPE_OPTIONS.find((t)=>t.code === code);
    return type?.display || code;
};
/**
 * Infer granular plan type from base plan type and network type
 * Returns the most specific plan type code available
 * Examples:
 * - medicare-advantage + ppo -> medicare-advantage-ppo
 * - medicare-advantage + hmo -> medicare-advantage-hmo
 * - commercial + ppo -> commercial-ppo
 * - medicaid + null -> medicaid (no granular type available)
 */ const inferGranularPlanType = (basePlanType, networkType)=>{
    // If no network type, return base plan type
    if (!networkType) {
        // Check if base plan type is already granular
        const exists = INSURANCE_PLAN_TYPE_OPTIONS.some((t)=>t.code === basePlanType);
        return exists ? basePlanType : "commercial";
    }
    // Try to find granular type: {basePlanType}-{networkType}
    const granularType = `${basePlanType}-${networkType}`;
    const granularExists = INSURANCE_PLAN_TYPE_OPTIONS.some((t)=>t.code === granularType);
    if (granularExists) {
        return granularType;
    }
    // Fall back to base plan type if granular doesn't exist
    const baseExists = INSURANCE_PLAN_TYPE_OPTIONS.some((t)=>t.code === basePlanType);
    return baseExists ? basePlanType : "commercial";
};
/**
 * UI-ready select options for insurance plan type
 */ const INSURANCE_PLAN_TYPE_SELECT_OPTIONS = INSURANCE_PLAN_TYPE_OPTIONS.map((type)=>({
        value: type.code,
        label: type.display
    }));
// =============================================================================
// NETWORK TYPE CONSTANTS
// =============================================================================
/**
 * Network Type CodeSystem URL
 * Used in InsurancePlan.network for network classification
 */ const NETWORK_TYPE_SYSTEM = "http://benchmarkhealth.com/fhir/CodeSystem/insurance-network-type";
/**
 * Network Type Options
 * Defines the classification system for insurance network types:
 * - hmo: Health Maintenance Organization
 * - ppo: Preferred Provider Organization
 * - epo: Exclusive Provider Organization
 * - pos: Point of Service
 * - hdhp: High Deductible Health Plan
 */ const NETWORK_TYPE_OPTIONS = [
    {
        code: "hmo",
        display: "HMO",
        system: NETWORK_TYPE_SYSTEM,
        description: "Health Maintenance Organization"
    },
    {
        code: "ppo",
        display: "PPO",
        system: NETWORK_TYPE_SYSTEM,
        description: "Preferred Provider Organization"
    },
    {
        code: "epo",
        display: "EPO",
        system: NETWORK_TYPE_SYSTEM,
        description: "Exclusive Provider Organization"
    },
    {
        code: "pos",
        display: "POS",
        system: NETWORK_TYPE_SYSTEM,
        description: "Point of Service"
    },
    {
        code: "hdhp",
        display: "HDHP",
        system: NETWORK_TYPE_SYSTEM,
        description: "High Deductible Health Plan"
    }
];
/**
 * Get display name for network type code
 */ const getNetworkTypeDisplay = (code)=>{
    const type = NETWORK_TYPE_OPTIONS.find((t)=>t.code === code);
    return type?.display || code;
};
/**
 * UI-ready select options for network type
 */ const NETWORK_TYPE_SELECT_OPTIONS = NETWORK_TYPE_OPTIONS.map((type)=>({
        value: type.code,
        label: type.display
    }));

/**
 * Intake billing eligibility + flag constants.
 *
 * The Schedule Intake step records a patient-level billing ELIGIBILITY
 * (`CoCM` / `Wellness eligible` / `No bill`) as a searchable `Patient.meta.tag`
 * (see `PATIENT_INTAKE_ELIGIBILITY_SYSTEM`). At `complete-intake` that eligibility
 * PREFILLS the clinician's billing-reality selection — it is not read-only. The
 * three flag options below are shared by the schedule-intake cards and the
 * complete-intake prefill.
 *
 * @agentic @benchmark/core
 */ /**
 * `Patient.meta.tag` system storing the intake billing ELIGIBILITY chosen at the
 * Schedule Intake step. A patient-level, `_tag`-searchable determination; absence
 * of the tag means "no eligibility". Codes reuse the billing-flag values below.
 * @agentic @benchmark/core
 */ const PATIENT_INTAKE_ELIGIBILITY_SYSTEM = "http://benchmarkhealth.com/fhir/CodeSystem/patient-intake-eligibility";
/**
 * CodeSystem URL for the selected billing-flag value (eligibility + reality prefill).
 * @agentic @benchmark/core
 */ const INTAKE_BILLING_FLAG_SYSTEM = "http://benchmarkhealth.com/fhir/CodeSystem/intake-billing-flag";
/**
 * Identifier system for the one-time Wellness-intake `ChargeItem`. The value is
 * the intake Encounter id, giving one idempotent charge per intake encounter.
 * @agentic @benchmark/core
 */ const INTAKE_CHARGE_ITEM_IDENTIFIER_SYSTEM = "http://benchmarkhealth.com/fhir/identifier/intake-charge-item";
/**
 * CPT code billed for a Wellness (clinical) intake — psychiatric diagnostic
 * evaluation. CoCM intakes (99492) bill only through the month-end flow.
 * @agentic @benchmark/core
 */ const INTAKE_CHARGE_CPT_90791 = "90791";
/**
 * Billing-flag options shown as cards on the Schedule Intake step.
 * @agentic @benchmark/core
 */ const INTAKE_BILLING_FLAG_OPTIONS = [
    {
        code: "cocm",
        display: "CoCM-eligible",
        system: INTAKE_BILLING_FLAG_SYSTEM,
        description: "Patient is eligible for CoCM (99492)."
    },
    {
        code: "wellness",
        display: "Wellness-eligible",
        system: INTAKE_BILLING_FLAG_SYSTEM,
        description: "Patient is eligible for Wellness exam (90791)."
    },
    {
        code: "no-bill",
        display: "No Bill",
        system: INTAKE_BILLING_FLAG_SYSTEM,
        description: "Patient is not eligible for billing."
    }
];
/**
 * UI-ready select/card options for the intake billing flag.
 * @agentic @benchmark/core
 */ const INTAKE_BILLING_FLAG_SELECT_OPTIONS = INTAKE_BILLING_FLAG_OPTIONS.map((option)=>({
        value: option.code,
        label: option.display,
        description: option.description
    }));

/**
 * Library (clinical content management) domain constants.
 *
 * Models the FHIR R4 `Library` resource as the persistence container for
 * editable clinical documentation (Clinical SOPs, Conversation Guides). One
 * `Library` represents one document; the markdown body is stored as a single
 * embedded `content` Attachment. Provides the canonical system URLs, type /
 * topic / category codings, the document-category configuration map, and slug /
 * name helpers used by the builder and adapters.
 *
 * @agentic @benchmark/core
 */ /** Canonical URL prefix for Benchmark `Library` resources. @agentic @benchmark/core */ const LIBRARY_CANONICAL_URL_BASE = "https://benchmark.health/fhir/Library";
/** Business identifier system for `Library` resources. @agentic @benchmark/core */ const LIBRARY_IDENTIFIER_SYSTEM = "https://benchmark.health/library-id";
/** Benchmark document-category code system (carried in `useContext.value`). @agentic @benchmark/core */ const LIBRARY_CATEGORY_SYSTEM = "https://benchmark.health/library-category";
/** Benchmark usage-context-type system for the document-category context. @agentic @benchmark/core */ const LIBRARY_USE_CONTEXT_TYPE_SYSTEM = "https://benchmark.health/library-context-type";
/** `useContext.code` value marking the document-category context entry. @agentic @benchmark/core */ const LIBRARY_USE_CONTEXT_CATEGORY_CODE = "document-category";
/** HL7 R4 `library-type` CodeSystem URL (`Library.type`). @agentic @benchmark/core */ const LIBRARY_TYPE_SYSTEM = "http://terminology.hl7.org/CodeSystem/library-type";
/** HL7 R4 `definition-topic` CodeSystem URL (`Library.topic`). @agentic @benchmark/core */ const LIBRARY_TOPIC_SYSTEM = "http://terminology.hl7.org/CodeSystem/definition-topic";
/** Content type for the single embedded markdown body Attachment. @agentic @benchmark/core */ const LIBRARY_MARKDOWN_CONTENT_TYPE = "text/markdown";
/**
 * Maximum size of the markdown body before base64 encoding. Guards against
 * oversized payloads at the action layer.
 * @agentic @benchmark/core
 */ const MAX_LIBRARY_MARKDOWN_BYTES = 256 * 1024;
/** `library-type` codings used per document category. @agentic @benchmark/core */ const LIBRARY_TYPE_CODINGS = {
    "logic-library": {
        system: LIBRARY_TYPE_SYSTEM,
        code: "logic-library",
        display: "Logic Library"
    },
    "asset-collection": {
        system: LIBRARY_TYPE_SYSTEM,
        code: "asset-collection",
        display: "Asset Collection"
    }
};
/** `definition-topic` coding options selectable on the authoring form. @agentic @benchmark/core */ const LIBRARY_TOPIC_OPTIONS = [
    {
        code: "treatment",
        display: "Treatment"
    },
    {
        code: "education",
        display: "Education"
    },
    {
        code: "assessment",
        display: "Assessment"
    }
];
/** Document-category select options for the authoring form. @agentic @benchmark/core */ const LIBRARY_DOCUMENT_CATEGORY_OPTIONS = [
    {
        value: "clinical-sop",
        label: "Clinical SOP",
        description: "Standard operating procedure for clinical practice"
    },
    {
        value: "conversation-guide",
        label: "Conversation Guide",
        description: "Talking points and structure for member conversations"
    }
];
/**
 * Per-category FHIR coding configuration. `clinical-sop` is locked to
 * `type = logic-library` and `topic = treatment` (D10).
 * @agentic @benchmark/core
 */ const LIBRARY_CATEGORY_CONFIG = {
    "clinical-sop": {
        typeCoding: LIBRARY_TYPE_CODINGS["logic-library"],
        defaultTopicCodes: [
            "treatment"
        ],
        categoryCoding: {
            system: LIBRARY_CATEGORY_SYSTEM,
            code: "clinical-sop",
            display: "Clinical SOP"
        }
    },
    "conversation-guide": {
        typeCoding: LIBRARY_TYPE_CODINGS["asset-collection"],
        defaultTopicCodes: [],
        categoryCoding: {
            system: LIBRARY_CATEGORY_SYSTEM,
            code: "conversation-guide",
            display: "Conversation Guide"
        }
    }
};
/**
 * Type guard narrowing an arbitrary string to a known document category.
 * @agentic @benchmark/core
 */ function isLibraryDocumentCategory(value) {
    return value === "clinical-sop" || value === "conversation-guide";
}
/** Returns the FHIR coding configuration for a document category. @agentic @benchmark/core */ function getLibraryCategoryConfig(category) {
    return LIBRARY_CATEGORY_CONFIG[category];
}
/** Returns the human display label for a document category. @agentic @benchmark/core */ function getLibraryDocumentCategoryDisplay(category) {
    return LIBRARY_DOCUMENT_CATEGORY_OPTIONS.find((o)=>o.value === category)?.label ?? category;
}
/** Returns the display label for a `definition-topic` code. @agentic @benchmark/core */ function getLibraryTopicDisplay(code) {
    return LIBRARY_TOPIC_OPTIONS.find((o)=>o.code === code)?.display ?? code;
}
/**
 * Builds a Benchmark `Coding` for a `definition-topic` code, preserving the
 * known display where one exists.
 * @agentic @benchmark/core
 */ function buildLibraryTopicCoding(code) {
    return {
        system: LIBRARY_TOPIC_SYSTEM,
        code,
        display: getLibraryTopicDisplay(code)
    };
}
/**
 * Slugifies a title into a URL-safe stem (lowercase, hyphen-separated,
 * alphanumeric). Empty/symbol-only titles fall back to `document`.
 * @agentic @benchmark/core
 */ function slugifyLibraryTitle(title) {
    const stem = title.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-")// Runs of non-alphanumerics are already collapsed to a single "-" above,
    // so trimming a single leading/trailing hyphen is sufficient. A
    // quantifier-free pattern avoids the polynomial-ReDoS backtracking that
    // `/^-+|-+$/` exhibits on uncontrolled input (CodeQL js/polynomial-redos).
    .replace(/^-|-$/g, "").slice(0, 64);
    return stem.length > 0 ? stem : "document";
}
/**
 * Generates a short, lowercase alphanumeric suffix for slug uniqueness.
 * @agentic @benchmark/core
 */ function generateLibrarySlugSuffix(length = 6) {
    const alphabet = "abcdefghijklmnopqrstuvwxyz0123456789";
    let out = "";
    for(let i = 0; i < length; i++){
        out += alphabet[Math.floor(Math.random() * alphabet.length)];
    }
    return out;
}
/**
 * Builds a unique slug for a document from its title plus a random suffix.
 * Pass an explicit `suffix` for deterministic output (e.g. in tests).
 * @agentic @benchmark/core
 */ function buildLibrarySlug(title, suffix) {
    return `${slugifyLibraryTitle(title)}-${suffix ?? generateLibrarySlugSuffix()}`;
}
/** Builds the canonical `Library.url` for a slug. @agentic @benchmark/core */ function buildLibraryCanonicalUrl(slug) {
    return `${LIBRARY_CANONICAL_URL_BASE}/${slug}`;
}
/**
 * Builds a PascalCase machine `name` from a title (FHIR `Library.name`).
 * Falls back to `Document` for empty/symbol-only titles.
 * @agentic @benchmark/core
 */ function buildLibraryName(title) {
    const name = title.replace(/[^a-zA-Z0-9 ]+/g, " ").split(/\s+/).filter(Boolean).map((word)=>word.charAt(0).toUpperCase() + word.slice(1)).join("").slice(0, 64);
    return name.length > 0 ? name : "Document";
}

/**
 * Clinical Notification Task constants and predicates.
 *
 * Clinical notifications are a distinct domain backed by FHIR `Task`, kept
 * strictly separate from care-plan and operational Tasks. These constants
 * define the discriminating `code.system` and the per-recipient read/unread
 * `businessStatus`, plus predicates used to slice notification Tasks from
 * operational Tasks in both directions.
 *
 * @agentic @benchmark/core
 */ /**
 * Canonical system URL for clinical notification Task types. A Task carrying a
 * `code.coding` in this system is a clinical notification, never an operational
 * or care-plan Task.
 * @agentic @benchmark/core
 */ const NOTIFICATION_TASK_TYPE_SYSTEM = "http://benchmarkhealth.com/fhir/CodeSystem/notification-task-type";
/**
 * The centralized clinical notification dictionary. Keyed by a stable SCREAMING
 * key; every entry is validated against {@link NotificationTypeDefinition}. This
 * is the one place to register, inspect, or reason about notification types.
 * @agentic @benchmark/core
 */ const NOTIFICATION_TYPE_DEFINITIONS = {
    ACUTE_RISK_ESCALATION: {
        code: "acute-risk-escalation",
        display: "Acute Risk Escalation",
        system: NOTIFICATION_TASK_TYPE_SYSTEM,
        description: "A member survey response crossed an acute clinical-risk threshold and needs prompt clinician review.",
        category: "clinical-risk",
        defaultPriority: "urgent",
        prominenceTier: "urgent",
        triggerSummary: "PHQ-9 / PHQ-9A item 9 >= 1, C-SSRS >= 3, or a high-acuity questionnaire risk band.",
        userConfigurable: false,
        alwaysOnFloor: true
    },
    SERVICE_APPROVAL: {
        code: "service-approval",
        display: "Service approval",
        system: NOTIFICATION_TASK_TYPE_SYSTEM,
        description: "A member's selected services are ready for the advocate to approve or deny.",
        category: "service-approval",
        defaultPriority: "routine",
        prominenceTier: "routine",
        triggerSummary: "The member's approve-selected-services task transitioned to ready (actionable).",
        userConfigurable: false,
        alwaysOnFloor: false
    },
    APPOINTMENT_SCHEDULED: {
        code: "appointment-scheduled",
        display: "Appointment scheduled",
        system: NOTIFICATION_TASK_TYPE_SYSTEM,
        description: "A booked appointment with a future start was newly recorded for the advocate's member.",
        category: "appointment",
        defaultPriority: "routine",
        prominenceTier: "routine",
        triggerSummary: "An Appointment was created with status booked and a start in the future.",
        userConfigurable: false,
        alwaysOnFloor: false
    }
};
/**
 * All registered notification type definitions.
 * @agentic @benchmark/core
 */ function listNotificationTypeDefinitions() {
    return Object.values(NOTIFICATION_TYPE_DEFINITIONS);
}
/**
 * Look up a notification type definition by its code. Returns undefined for an
 * unknown code.
 * @agentic @benchmark/core
 */ function getNotificationTypeDefinition(code) {
    return listNotificationTypeDefinitions().find((def)=>def.code === code);
}
/**
 * Canonical system URL for the per-recipient read/unread state carried on
 * `Task.businessStatus`. Kept distinct from `Task.status` so "read" is a
 * business fact, not a workflow-lifecycle transition (read !== work done).
 * @agentic @benchmark/core
 */ const NOTIFICATION_READ_STATUS_SYSTEM = "http://benchmarkhealth.com/fhir/CodeSystem/notification-read-status";
/**
 * Read/unread codes stored on `Task.businessStatus.coding`.
 * @agentic @benchmark/core
 */ const NOTIFICATION_READ_STATUS = {
    UNREAD: {
        code: "unread",
        display: "Unread",
        system: NOTIFICATION_READ_STATUS_SYSTEM
    },
    READ: {
        code: "read",
        display: "Read",
        system: NOTIFICATION_READ_STATUS_SYSTEM
    }
};
/**
 * FHIR token that matches any notification Task by system (system-only token,
 * trailing pipe). Use with the `code` search param for the notification slice,
 * or with `code:not` to exclude notifications from operational searches.
 * @agentic @benchmark/core
 */ const NOTIFICATION_TASK_CODE_TOKEN = `${NOTIFICATION_TASK_TYPE_SYSTEM}|`;
/**
 * FHIR token matching unread notification Tasks via the `business-status`
 * search param.
 * @agentic @benchmark/core
 */ const NOTIFICATION_UNREAD_STATUS_TOKEN = `${NOTIFICATION_READ_STATUS_SYSTEM}|${NOTIFICATION_READ_STATUS.UNREAD.code}`;
/**
 * True when the Task is a clinical notification (carries a coding in the
 * notification code system).
 * @agentic @benchmark/core
 */ function isNotificationTask(task) {
    return (task.code?.coding ?? []).some((coding)=>coding.system === NOTIFICATION_TASK_TYPE_SYSTEM);
}
/**
 * True when the Task is an operational/care-plan Task (i.e. NOT a clinical
 * notification). Defensive client-side guard complementing server-side
 * `code:not` slicing.
 * @agentic @benchmark/core
 */ function isOperationalTask(task) {
    return !isNotificationTask(task);
}
/**
 * True when the notification Task carries the given notification code.
 * @agentic @benchmark/core
 */ function hasNotificationTaskCode(task, code) {
    return (task.code?.coding ?? []).some((coding)=>coding.system === NOTIFICATION_TASK_TYPE_SYSTEM && coding.code === code);
}
/**
 * Reads the per-recipient read/unread state from `Task.businessStatus`.
 * Returns undefined when absent.
 * @agentic @benchmark/core
 */ function getNotificationReadStatus(task) {
    const coding = task.businessStatus?.coding?.find((entry)=>entry.system === NOTIFICATION_READ_STATUS_SYSTEM);
    return coding?.code;
}
/**
 * True when the Task is an unread clinical notification.
 * @agentic @benchmark/core
 */ function isUnreadNotificationTask(task) {
    return isNotificationTask(task) && getNotificationReadStatus(task) === NOTIFICATION_READ_STATUS.UNREAD.code;
}

/**
 * FHIR-Compliant Constants for Organization Domain
 *
 * This file contains all constants used for Organization resources,
 * including business partnership tier classification and organization type classification.
 * All codes follow FHIR R4 value sets and include proper system/code/display structures.
 */ // =============================================================================
// ORGANIZATION TIER CONSTANTS
// =============================================================================
/**
 * Organization Tier CodeSystem URL
 * Used in meta.tag for Organization resources to track business partnership tiers
 */ const ORGANIZATION_TIER_SYSTEM = "https://benchmarkhealth.com/fhir/CodeSystem/organization-tier";
/**
 * Organization Tier Options
 * Defines the three-tier classification system for business partnerships:
 * - Tier 1: QuIN Member Provider
 * - Tier 2: QuIN Candidate or Preferred Provider
 * - Tier 3: Non-QuIN Provider
 */ const ORGANIZATION_TIER_OPTIONS = [
    {
        code: "tier-1",
        display: "Tier 1",
        system: ORGANIZATION_TIER_SYSTEM,
        description: "QuIN Member Provider"
    },
    {
        code: "tier-2",
        display: "Tier 2",
        system: ORGANIZATION_TIER_SYSTEM,
        description: "QuIN Candidate or Preferred Provider"
    },
    {
        code: "tier-3",
        display: "Tier 3",
        system: ORGANIZATION_TIER_SYSTEM,
        description: "Non-QuIN Provider"
    }
];
/**
 * Get display name for organization tier code
 */ const getOrganizationTierDisplay = (code)=>{
    const tier = ORGANIZATION_TIER_OPTIONS.find((t)=>t.code === code);
    return tier?.display || code;
};
/**
 * Get description for organization tier code
 */ const getOrganizationTierDescription = (code)=>{
    const tier = ORGANIZATION_TIER_OPTIONS.find((t)=>t.code === code);
    return tier?.description || "";
};
/**
 * UI-ready select options for organization tier
 */ const ORGANIZATION_TIER_SELECT_OPTIONS = ORGANIZATION_TIER_OPTIONS.map((tier)=>({
        value: tier.code,
        label: tier.display,
        description: tier.description
    }));
// =============================================================================
// ORGANIZATION EXTENSION URLS
// =============================================================================
/**
 * Extension URL for assigned LCSW (Licensed Clinical Social Worker)
 * Used to store the PractitionerRole reference for the LCSW assigned to handle
 * fax intake tasks for patients at this organization.
 *
 * Example usage:
 * ```json
 * {
 *   "url": "https://benchmark.health/fhir/StructureDefinition/assigned-lcsw",
 *   "valueReference": {
 *     "reference": "PractitionerRole/abc-123",
 *     "display": "Annie Williamson"
 *   }
 * }
 * ```
 */ const ASSIGNED_LCSW_EXTENSION_URL = "https://benchmark.health/fhir/StructureDefinition/assigned-lcsw";
// =============================================================================
// ORGANIZATION TYPE CONSTANTS
// =============================================================================
/**
 * FHIR R4 Standard Organization Type CodeSystem URL
 * Used in Organization.type for standard FHIR organization type classification
 */ const ORGANIZATION_TYPE_SYSTEM = "http://terminology.hl7.org/CodeSystem/organization-type";
/**
 * Custom Organization Type CodeSystem URL
 * Used in Organization.type for Benchmark-specific organization types
 */ const CUSTOM_ORGANIZATION_TYPE_SYSTEM = "https://benchmarkhealth.com/fhir/CodeSystem/organization-type";
/**
 * FHIR Standard Organization Type Options
 * Standard FHIR R4 organization type codes from HL7 terminology
 * Commented out codes are not used in the current application
 */ const FHIR_ORGANIZATION_TYPE_OPTIONS = [
    {
        code: "prov",
        display: "Healthcare Provider",
        system: ORGANIZATION_TYPE_SYSTEM
    },
    // {
    //   code: "dept",
    //   display: "Hospital Department",
    //   system: ORGANIZATION_TYPE_SYSTEM,
    // },
    // {
    //   code: "team",
    //   display: "Organizational Team",
    //   system: ORGANIZATION_TYPE_SYSTEM,
    // },
    // {
    //   code: "govt",
    //   display: "Government",
    //   system: ORGANIZATION_TYPE_SYSTEM,
    // },
    {
        code: "ins",
        display: "Insurance Company",
        system: ORGANIZATION_TYPE_SYSTEM
    },
    {
        code: "pay",
        display: "Payer",
        system: ORGANIZATION_TYPE_SYSTEM
    },
    // {
    //   code: "edu",
    //   display: "Educational Institute",
    //   system: ORGANIZATION_TYPE_SYSTEM,
    // },
    // {
    //   code: "reli",
    //   display: "Religious Institution",
    //   system: ORGANIZATION_TYPE_SYSTEM,
    // },
    // {
    //   code: "crs",
    //   display: "Clinical Research Sponsor",
    //   system: ORGANIZATION_TYPE_SYSTEM,
    // },
    {
        code: "cg",
        display: "Community Group",
        system: ORGANIZATION_TYPE_SYSTEM
    }
];
/**
 * Custom Organization Type Options
 * Benchmark-specific organization types not in the FHIR standard
 */ const CUSTOM_ORGANIZATION_TYPE_OPTIONS = [
    {
        code: "ehr",
        display: "Electronic Health Record Provider",
        system: CUSTOM_ORGANIZATION_TYPE_SYSTEM
    },
    {
        code: "referring-practice",
        display: "Referring Practice",
        system: CUSTOM_ORGANIZATION_TYPE_SYSTEM
    },
    {
        code: "private-practice",
        display: "Private Practice",
        system: CUSTOM_ORGANIZATION_TYPE_SYSTEM
    }
];
/**
 * Combined Organization Type Options
 * All organization types (FHIR standard + custom) for use in forms and UI
 */ const ORGANIZATION_TYPE_OPTIONS = [
    ...FHIR_ORGANIZATION_TYPE_OPTIONS,
    ...CUSTOM_ORGANIZATION_TYPE_OPTIONS
];
/**
 * Service Provider Type Options
 * Healthcare service provider organization types for UI distinction
 * Contains: Healthcare Provider, Community Group, and Private Practice
 */ const SERVICE_PROVIDER_TYPE_OPTIONS = [
    FHIR_ORGANIZATION_TYPE_OPTIONS.find((t)=>t.code === "prov"),
    FHIR_ORGANIZATION_TYPE_OPTIONS.find((t)=>t.code === "cg"),
    CUSTOM_ORGANIZATION_TYPE_OPTIONS.find((t)=>t.code === "private-practice")
];
/**
 * Insurance Provider Type Options
 * Insurance and payer organization types for UI distinction
 * Contains: Insurance Company and Payer
 */ const INSURANCE_PROVIDER_TYPE_OPTIONS = [
    FHIR_ORGANIZATION_TYPE_OPTIONS.find((t)=>t.code === "ins"),
    FHIR_ORGANIZATION_TYPE_OPTIONS.find((t)=>t.code === "pay")
];
/**
 * Get display name for organization type code
 */ const getOrganizationTypeDisplay = (code)=>{
    const type = ORGANIZATION_TYPE_OPTIONS.find((t)=>t.code === code);
    return type?.display || code;
};
/**
 * Get the correct system URL for an organization type code
 * Returns the FHIR standard system for standard codes, custom system for custom codes
 */ const getOrganizationTypeSystem = (code)=>{
    const type = ORGANIZATION_TYPE_OPTIONS.find((t)=>t.code === code);
    return type?.system || ORGANIZATION_TYPE_SYSTEM;
};
/**
 * UI-ready select options for organization type
 */ const ORGANIZATION_TYPE_SELECT_OPTIONS = ORGANIZATION_TYPE_OPTIONS.map((type)=>({
        value: type.code,
        label: type.display
    }));

/**
 * PlanDefinition Type System
 * Custom system for plan definition templates
 */ const PLAN_DEFINITION_TYPE_SYSTEM = "http://benchmarkhealth.com/fhir/CodeSystem/plan-type";
/**
 * PlanDefinition Canonical URLs
 * Standard URLs for referencing plan definition templates
 */ const INTAKE_PLAN_DEFINITION_URL = "http://benchmarkhealth.com/fhir/PlanDefinition/intake-protocol";
const TRIAGE_PLAN_DEFINITION_URL = "http://benchmarkhealth.com/fhir/PlanDefinition/triage-protocol";
const COCM_PLAN_DEFINITION_URL = "http://benchmarkhealth.com/fhir/PlanDefinition/cocm-protocol";
/**
 * PlanDefinition Type Options
 * Defines available plan definition templates
 */ const PLAN_DEFINITION_TYPE_OPTIONS = [
    {
        code: "intake-protocol",
        display: "Intake Protocol",
        system: PLAN_DEFINITION_TYPE_SYSTEM,
        description: "Standard intake protocol for new patients",
        canonical: "PlanDefinition/intake-protocol"
    },
    {
        code: "triage-protocol",
        display: "Triage Protocol",
        system: PLAN_DEFINITION_TYPE_SYSTEM,
        description: "Standard triage protocol for ongoing care coordination",
        canonical: "PlanDefinition/triage-protocol"
    },
    {
        code: "cocm-protocol",
        display: "CoCM Protocol",
        system: PLAN_DEFINITION_TYPE_SYSTEM,
        description: "Standard Collaborative Care Model protocol",
        canonical: "PlanDefinition/cocm-protocol"
    }
];
/**
 * Helper Functions
 */ const getPlanDefinitionDisplay = (code)=>{
    const option = PLAN_DEFINITION_TYPE_OPTIONS.find((o)=>o.code === code);
    return option?.display || code;
};
const getPlanDefinitionCanonical = (code)=>{
    const option = PLAN_DEFINITION_TYPE_OPTIONS.find((o)=>o.code === code);
    return option?.canonical || `PlanDefinition/${code}`;
};
const getPlanDefinitionByCanonical = (canonical)=>{
    return PLAN_DEFINITION_TYPE_OPTIONS.find((o)=>o.canonical === canonical);
};
/**
 * PlanDefinition Resources
 * FHIR PlanDefinition resources that define care protocols
 */ /**
 * Intake Protocol PlanDefinition
 *
 * Defines the standard intake workflow for new members.
 * Activities: Care Team Assignment → Initial Contact → Consent → Intake Assessment → Care Plan Selection
 * Uses ActivityDefinition constants as the single source of truth.
 */ const INTAKE_PLAN_DEFINITION = {
    resourceType: "PlanDefinition",
    id: "intake-protocol",
    url: INTAKE_PLAN_DEFINITION_URL,
    status: "active",
    type: {
        coding: [
            {
                system: PLAN_DEFINITION_TYPE_SYSTEM,
                code: "intake-protocol",
                display: "Intake Protocol"
            }
        ]
    },
    title: "Benchmark Intake Protocol",
    description: "Standard intake workflow for new members entering the Benchmark Health program",
    action: getActivityDefinitionsByProtocol("intake").map((activity, index)=>{
        const action = {
            id: activity.code,
            title: activity.display,
            description: activity.description,
            code: [
                {
                    coding: [
                        {
                            system: ACTIVITY_DEFINITION_TYPE_SYSTEM,
                            code: activity.code,
                            display: getActivityDefinitionDisplay(activity.code)
                        }
                    ],
                    text: activity.description
                }
            ]
        };
        // Add relationship chain: each activity comes after the previous one
        if (index > 0) {
            const previousActivity = getActivityDefinitionsByProtocol("intake")[index - 1];
            if (previousActivity) {
                action.relatedAction = [
                    {
                        actionId: previousActivity.code,
                        relationship: "after"
                    }
                ];
            }
        }
        return action;
    })
};
/**
 * Triage Protocol PlanDefinition
 *
 * Defines the standard triage workflow for members requiring additional care coordination.
 * Activities: Resource Offering → Resource Acceptance → Appointment Scheduling → Appointment Completion → Needs Evaluation
 * Uses ActivityDefinition constants as the single source of truth.
 */ const TRIAGE_PLAN_DEFINITION = {
    resourceType: "PlanDefinition",
    id: "triage-protocol",
    url: TRIAGE_PLAN_DEFINITION_URL,
    status: "active",
    type: {
        coding: [
            {
                system: PLAN_DEFINITION_TYPE_SYSTEM,
                code: "triage-protocol",
                display: "Triage Protocol"
            }
        ]
    },
    title: "Benchmark Triage Protocol",
    description: "Standard triage workflow for members requiring care coordination and external services",
    action: getActivityDefinitionsByProtocol("triage").map((activity, index)=>{
        const action = {
            id: activity.code,
            title: activity.display,
            description: activity.description,
            code: [
                {
                    coding: [
                        {
                            system: ACTIVITY_DEFINITION_TYPE_SYSTEM,
                            code: activity.code,
                            display: getActivityDefinitionDisplay(activity.code)
                        }
                    ],
                    text: activity.description
                }
            ]
        };
        // Add relationship chain: each activity comes after the previous one
        if (index > 0) {
            const previousActivity = getActivityDefinitionsByProtocol("triage")[index - 1];
            if (previousActivity) {
                action.relatedAction = [
                    {
                        actionId: previousActivity.code,
                        relationship: "after"
                    }
                ];
            }
        }
        return action;
    })
};
/**
 * CoCM Protocol PlanDefinition
 *
 * Defines the standard Collaborative Care Model workflow.
 * Sequential activities must happen in order; flexible activities can occur in any order and may repeat.
 * Uses ActivityDefinition constants as the single source of truth.
 */ const COCM_PLAN_DEFINITION = {
    resourceType: "PlanDefinition",
    id: "cocm-protocol",
    url: COCM_PLAN_DEFINITION_URL,
    status: "active",
    type: {
        coding: [
            {
                system: PLAN_DEFINITION_TYPE_SYSTEM,
                code: "cocm-protocol",
                display: "CoCM Protocol"
            }
        ]
    },
    title: "Benchmark CoCM Protocol",
    description: "Standard Collaborative Care Model workflow for integrated behavioral health care",
    action: getActivityDefinitionsByProtocol("cocm").map((activity, index)=>{
        const action = {
            id: activity.code,
            title: activity.display,
            description: activity.description,
            code: [
                {
                    coding: [
                        {
                            system: ACTIVITY_DEFINITION_TYPE_SYSTEM,
                            code: activity.code,
                            display: getActivityDefinitionDisplay(activity.code)
                        }
                    ],
                    text: activity.description
                }
            ]
        };
        // Add relationship chain: sequential activities come after the previous one
        // Flexible activities don't have strict ordering requirements
        const cocmActivities = getActivityDefinitionsByProtocol("cocm");
        if (index > 0) {
            const previousActivity = cocmActivities[index - 1];
            if (previousActivity) {
                action.relatedAction = [
                    {
                        actionId: previousActivity.code,
                        relationship: "after"
                    }
                ];
            }
        }
        return action;
    })
};
/**
 * UI-ready select options
 */ const PLAN_DEFINITION_TYPE_SELECT_OPTIONS = PLAN_DEFINITION_TYPE_OPTIONS.map((option)=>({
        value: option.code,
        label: option.display,
        description: option.description
    }));

/**
 * Program Selection Observation Constants
 *
 * Defines codes, systems, and value sets for the Program Selection Observation
 * used to record the deterministic program enrollment decision during intake completion.
 * @agentic @benchmark/core
 */ /**
 * CodeSystem URL for program selection observations.
 * @agentic @benchmark/core
 */ const PROGRAM_SELECTION_OBSERVATION_SYSTEM = "http://benchmarkhealth.com/fhir/CodeSystem/program-selection";
/**
 * Observation.code for program selection.
 * @agentic @benchmark/core
 */ const PROGRAM_SELECTION_OBSERVATION_CODE = {
    system: PROGRAM_SELECTION_OBSERVATION_SYSTEM,
    code: "program-selection",
    display: "Program Selection"
};
/**
 * Whether an Observation records the intake program enrollment decision.
 * Program selection is surfaced via care-plan enrollment timeline rows, not
 * as a standalone clinical observation.
 * @agentic @benchmark/core
 */ const isProgramSelectionObservation = (observation)=>(observation.code?.coding ?? []).some((coding)=>coding.system === PROGRAM_SELECTION_OBSERVATION_CODE.system && coding.code === PROGRAM_SELECTION_OBSERVATION_CODE.code);
/**
 * Identifier system for idempotent program selection upserts.
 * Value format: "{patientId}|{intakeCarePlanId}"
 * @agentic @benchmark/core
 */ const PROGRAM_SELECTION_IDENTIFIER_SYSTEM = "http://benchmarkhealth.com/fhir/identifier/program-selection";
/**
 * CodeSystem URL for the selected operational program value.
 * @agentic @benchmark/core
 */ const OPERATIONAL_PROGRAM_SYSTEM = "http://benchmarkhealth.com/fhir/CodeSystem/operational-program";
/**
 * Operational program options: the programs a patient can be enrolled into after intake.
 * @agentic @benchmark/core
 */ const OPERATIONAL_PROGRAM_OPTIONS = [
    {
        code: "triage",
        display: "Triage",
        system: OPERATIONAL_PROGRAM_SYSTEM,
        description: "Benchmark Care Triage & Navigation"
    },
    {
        code: "cocm",
        display: "CoCM",
        system: OPERATIONAL_PROGRAM_SYSTEM,
        description: "Benchmark Collaborative Care Model"
    }
];
/**
 * UI-ready select options for operational programs.
 * @agentic @benchmark/core
 */ const OPERATIONAL_PROGRAM_SELECT_OPTIONS = OPERATIONAL_PROGRAM_OPTIONS.map((option)=>({
        value: option.code,
        label: option.display,
        description: option.description
    }));
/**
 * CodeSystem URL for program selection reason codes.
 * @agentic @benchmark/core
 */ const PROGRAM_SELECTION_REASON_SYSTEM = "http://benchmarkhealth.com/fhir/CodeSystem/program-selection-reason";
/**
 * Reason code options for justifying program selection.
 * @agentic @benchmark/core
 */ const PROGRAM_SELECTION_REASON_OPTIONS = [
    {
        code: "clinical-need-acuity",
        display: "Clinical Need or Acuity",
        system: PROGRAM_SELECTION_REASON_SYSTEM
    },
    {
        code: "insurance-cost",
        display: "Insurance or Cost",
        system: PROGRAM_SELECTION_REASON_SYSTEM
    },
    {
        code: "patient-preference",
        display: "Patient Preference",
        system: PROGRAM_SELECTION_REASON_SYSTEM
    },
    {
        code: "pcp-recommendation",
        display: "PCP Recommendation",
        system: PROGRAM_SELECTION_REASON_SYSTEM
    }
];
/**
 * UI-ready select options for program selection reasons.
 * @agentic @benchmark/core
 */ const PROGRAM_SELECTION_REASON_SELECT_OPTIONS = PROGRAM_SELECTION_REASON_OPTIONS.map((option)=>({
        value: option.code,
        label: option.display
    }));
/**
 * CodeSystem URL for selected service needs (component coding).
 * @agentic @benchmark/core
 */ const SELECTED_NEEDS_COMPONENT_SYSTEM = "http://benchmarkhealth.com/fhir/CodeSystem/selected-service-need";
/**
 * Component code for the selected-needs slice within the Observation.
 * @agentic @benchmark/core
 */ const SELECTED_NEEDS_COMPONENT_CODE = {
    system: SELECTED_NEEDS_COMPONENT_SYSTEM,
    code: "selected-needs",
    display: "Selected Service Needs"
};

/**
 * FHIR-Compliant Constants for Practitioner Domain
 *
 * This file contains all constants used in Practitioner forms, utils, and related components.
 * All codes follow FHIR R4 value sets and include proper system/code/display structures.
 */ // FHIR R4-compliant specialty options from Practice Setting Code Value Set
// http://hl7.org/fhir/ValueSet/c80-practice-codes
// Based on SNOMED CT International edition
const PRACTITIONER_SPECIALTY_OPTIONS = [
    {
        code: "394576009",
        display: "Accident & emergency",
        system: "http://snomed.info/sct"
    },
    {
        code: "28351000087106",
        display: "Addiction specialty",
        system: "http://snomed.info/sct"
    },
    {
        code: "408467006",
        display: "Adult mental illness",
        system: "http://snomed.info/sct"
    },
    {
        code: "394577000",
        display: "Anesthetics",
        system: "http://snomed.info/sct"
    },
    {
        code: "394579002",
        display: "Cardiology",
        system: "http://snomed.info/sct"
    },
    {
        code: "394588006",
        display: "Child and adolescent psychiatry",
        system: "http://snomed.info/sct"
    },
    {
        code: "394803006",
        display: "Clinical hematology",
        system: "http://snomed.info/sct"
    },
    {
        code: "1255918004",
        display: "Clinical psychology",
        system: "http://snomed.info/sct"
    },
    {
        code: "408454008",
        display: "Clinical microbiology",
        system: "http://snomed.info/sct"
    },
    {
        code: "408478003",
        display: "Critical care medicine",
        system: "http://snomed.info/sct"
    },
    {
        code: "394582007",
        display: "Dermatology",
        system: "http://snomed.info/sct"
    },
    {
        code: "1255916000",
        display: "Educational psychology",
        system: "http://snomed.info/sct"
    },
    {
        code: "394583002",
        display: "Endocrinology",
        system: "http://snomed.info/sct"
    },
    {
        code: "419772000",
        display: "Family practice",
        system: "http://snomed.info/sct"
    },
    {
        code: "394584008",
        display: "Gastroenterology",
        system: "http://snomed.info/sct"
    },
    {
        code: "408444009",
        display: "General dental practice",
        system: "http://snomed.info/sct"
    },
    {
        code: "394802001",
        display: "General medicine",
        system: "http://snomed.info/sct"
    },
    {
        code: "394915009",
        display: "General pathology",
        system: "http://snomed.info/sct"
    },
    {
        code: "394814009",
        display: "General practice",
        system: "http://snomed.info/sct"
    },
    {
        code: "394811001",
        display: "Geriatric medicine",
        system: "http://snomed.info/sct"
    },
    {
        code: "394586005",
        display: "Gynecology",
        system: "http://snomed.info/sct"
    },
    {
        code: "394597005",
        display: "Histopathology",
        system: "http://snomed.info/sct"
    },
    {
        code: "394807007",
        display: "Infectious diseases",
        system: "http://snomed.info/sct"
    },
    {
        code: "419192003",
        display: "Internal medicine",
        system: "http://snomed.info/sct"
    },
    {
        code: "408468001",
        display: "Learning disability - specialty",
        system: "http://snomed.info/sct"
    },
    {
        code: "394593009",
        display: "Medical oncology",
        system: "http://snomed.info/sct"
    },
    {
        code: "394733009",
        display: "Medical specialty - Other",
        system: "http://snomed.info/sct"
    },
    {
        code: "394589003",
        display: "Nephrology",
        system: "http://snomed.info/sct"
    },
    {
        code: "394591006",
        display: "Neurology",
        system: "http://snomed.info/sct"
    },
    {
        code: "394649004",
        display: "Nuclear medicine",
        system: "http://snomed.info/sct"
    },
    {
        code: "394585009",
        display: "Obstetrics and gynecology",
        system: "http://snomed.info/sct"
    },
    {
        code: "408470005",
        display: "Obstetrics",
        system: "http://snomed.info/sct"
    },
    {
        code: "394821009",
        display: "Occupational medicine",
        system: "http://snomed.info/sct"
    },
    {
        code: "394594003",
        display: "Ophthalmology",
        system: "http://snomed.info/sct"
    },
    {
        code: "394605001",
        display: "Oral surgery",
        system: "http://snomed.info/sct"
    },
    {
        code: "394608004",
        display: "Orthodontics",
        system: "http://snomed.info/sct"
    },
    {
        code: "394882004",
        display: "Pain management",
        system: "http://snomed.info/sct"
    },
    {
        code: "394806003",
        display: "Palliative medicine",
        system: "http://snomed.info/sct"
    },
    {
        code: "408459003",
        display: "Pediatric cardiology",
        system: "http://snomed.info/sct"
    },
    {
        code: "394607009",
        display: "Pediatric dentistry",
        system: "http://snomed.info/sct"
    },
    {
        code: "419610006",
        display: "Pediatric endocrinology",
        system: "http://snomed.info/sct"
    },
    {
        code: "418058008",
        display: "Pediatric gastroenterology",
        system: "http://snomed.info/sct"
    },
    {
        code: "419365004",
        display: "Pediatric nephrology",
        system: "http://snomed.info/sct"
    },
    {
        code: "418002000",
        display: "Pediatric oncology",
        system: "http://snomed.info/sct"
    },
    {
        code: "394539006",
        display: "Pediatric surgery",
        system: "http://snomed.info/sct"
    },
    {
        code: "409968004",
        display: "Preventive medicine",
        system: "http://snomed.info/sct"
    },
    {
        code: "394587001",
        display: "Psychiatry",
        system: "http://snomed.info/sct"
    },
    {
        code: "394817002",
        display: "Forensic psychiatry",
        system: "http://snomed.info/sct"
    },
    {
        code: "1255881004",
        display: "Mental health nursing",
        system: "http://snomed.info/sct"
    },
    {
        code: "721961006",
        display: "Psychosomatic medicine",
        system: "http://snomed.info/sct"
    },
    {
        code: "394913002",
        display: "Psychotherapy",
        system: "http://snomed.info/sct"
    },
    {
        code: "722162001",
        display: "Psychology",
        system: "http://snomed.info/sct"
    },
    {
        code: "408440000",
        display: "Public health medicine",
        system: "http://snomed.info/sct"
    },
    {
        code: "419815003",
        display: "Radiation oncology",
        system: "http://snomed.info/sct"
    },
    {
        code: "394914008",
        display: "Radiology",
        system: "http://snomed.info/sct"
    },
    {
        code: "408455009",
        display: "Radiology - Interventional radiology",
        system: "http://snomed.info/sct"
    },
    {
        code: "394602003",
        display: "Rehabilitation",
        system: "http://snomed.info/sct"
    },
    {
        code: "394810000",
        display: "Rheumatology",
        system: "http://snomed.info/sct"
    },
    {
        code: "408466002",
        display: "Surgery - Cardiac surgery",
        system: "http://snomed.info/sct"
    },
    {
        code: "394609007",
        display: "Surgery - General",
        system: "http://snomed.info/sct"
    },
    {
        code: "394610002",
        display: "Surgery - Neurosurgery",
        system: "http://snomed.info/sct"
    },
    {
        code: "394611003",
        display: "Surgery - Plastic surgery",
        system: "http://snomed.info/sct"
    },
    {
        code: "394801008",
        display: "Surgery - Trauma and orthopedics",
        system: "http://snomed.info/sct"
    },
    {
        code: "408463005",
        display: "Surgery - Vascular",
        system: "http://snomed.info/sct"
    },
    {
        code: "1251536003",
        display: "Sport medicine/psychology",
        system: "http://snomed.info/sct"
    },
    {
        code: "394732004",
        display: "Surgical specialty - Other",
        system: "http://snomed.info/sct"
    },
    {
        code: "394590007",
        display: "Thoracic medicine",
        system: "http://snomed.info/sct"
    },
    {
        code: "394612005",
        display: "Urology",
        system: "http://snomed.info/sct"
    }
];
// UI-specific mappings for Select components
const PRACTITIONER_SPECIALTY_SELECT_OPTIONS = PRACTITIONER_SPECIALTY_OPTIONS.map((specialty)=>({
        value: specialty.code,
        label: specialty.display
    }));
// Helper function to get specialty display name from code
function getPractitionerSpecialtyDisplay(code) {
    const specialty = PRACTITIONER_SPECIALTY_OPTIONS.find((s)=>s.code === code);
    return specialty?.display || "Unknown specialty";
}

/** HL7 v2 Table 0360 — Degree/License/Certificate. */ const PRACTITIONER_QUALIFICATION_HL7_SYSTEM = `${HL7_TERMINOLOGY_BASE}/CodeSystem/v2-0360`;
/** Benchmark-owned CodeSystem for credentials HL7 v2-0360 does not define. */ const PRACTITIONER_QUALIFICATION_BENCHMARK_SYSTEM = `${ORGANIZATION_BASE}/CodeSystem/practitioner-qualification`;
/**
 * The complete v2-0360 value set (HL7 Terminology v7.3.0), with the published
 * casing — `PHD` not `PhD`, `RPH` not `RPh`, but `PharmD` genuinely mixed.
 */ const HL7_PRACTITIONER_QUALIFICATION_OPTIONS = [
    {
        code: "PN",
        display: "Advanced Practice Nurse"
    },
    {
        code: "AAS",
        display: "Associate of Applied Science"
    },
    {
        code: "AA",
        display: "Associate of Arts"
    },
    {
        code: "ABA",
        display: "Associate of Business Administration"
    },
    {
        code: "AE",
        display: "Associate of Engineering"
    },
    {
        code: "AS",
        display: "Associate of Science"
    },
    {
        code: "BA",
        display: "Bachelor of Arts"
    },
    {
        code: "BBA",
        display: "Bachelor of Business Administration"
    },
    {
        code: "BE",
        display: "Bachelor or Engineering"
    },
    {
        code: "BFA",
        display: "Bachelor of Fine Arts"
    },
    {
        code: "BN",
        display: "Bachelor of Nursing"
    },
    {
        code: "BS",
        display: "Bachelor of Science"
    },
    {
        code: "BSL",
        display: "Bachelor of Science - Law"
    },
    {
        code: "BSN",
        display: "Bachelor on Science - Nursing"
    },
    {
        code: "BT",
        display: "Bachelor of Theology"
    },
    {
        code: "CER",
        display: "Certificate"
    },
    {
        code: "CANP",
        display: "Certified Adult Nurse Practitioner"
    },
    {
        code: "CMA",
        display: "Certified Medical Assistant"
    },
    {
        code: "CNP",
        display: "Certified Nurse Practitioner"
    },
    {
        code: "CNM",
        display: "Certified Nurse Midwife"
    },
    {
        code: "CRN",
        display: "Certified Registered Nurse"
    },
    {
        code: "CNS",
        display: "Certified Nurse Specialist"
    },
    {
        code: "CPNP",
        display: "Certified Pediatric Nurse Practitioner"
    },
    {
        code: "CTR",
        display: "Certified Tumor Registrar"
    },
    {
        code: "DIP",
        display: "Diploma"
    },
    {
        code: "DBA",
        display: "Doctor of Business Administration"
    },
    {
        code: "DED",
        display: "Doctor of Education"
    },
    {
        code: "PharmD",
        display: "Doctor of Pharmacy"
    },
    {
        code: "PHE",
        display: "Doctor of Engineering"
    },
    {
        code: "PHD",
        display: "Doctor of Philosophy"
    },
    {
        code: "PHS",
        display: "Doctor of Science"
    },
    {
        code: "MD",
        display: "Doctor of Medicine"
    },
    {
        code: "DO",
        display: "Doctor of Osteopathy"
    },
    {
        code: "EMT",
        display: "Emergency Medical Technician"
    },
    {
        code: "EMTP",
        display: "Emergency Medical Technician - Paramedic"
    },
    {
        code: "FPNP",
        display: "Family Practice Nurse Practitioner"
    },
    {
        code: "HS",
        display: "High School Graduate"
    },
    {
        code: "JD",
        display: "Juris Doctor"
    },
    {
        code: "MA",
        display: "Master of Arts"
    },
    {
        code: "MBA",
        display: "Master of Business Administration"
    },
    {
        code: "MCE",
        display: "Master of Civil Engineering"
    },
    {
        code: "MDI",
        display: "Master of Divinity"
    },
    {
        code: "MED",
        display: "Master of Education"
    },
    {
        code: "MEE",
        display: "Master of Electrical Engineering"
    },
    {
        code: "ME",
        display: "Master of Engineering"
    },
    {
        code: "MFA",
        display: "Master of Fine Arts"
    },
    {
        code: "MME",
        display: "Master of Mechanical Engineering"
    },
    {
        code: "MS",
        display: "Master of Science"
    },
    {
        code: "MSL",
        display: "Master of Science - Law"
    },
    {
        code: "MSN",
        display: "Master of Science - Nursing"
    },
    {
        code: "MTH",
        display: "Master of Theology"
    },
    {
        code: "MDA",
        display: "Medical Assistant"
    },
    {
        code: "MT",
        display: "Medical Technician"
    },
    {
        code: "NG",
        display: "Non-Graduate"
    },
    {
        code: "NP",
        display: "Nurse Practitioner"
    },
    {
        code: "PA",
        display: "Physician Assistant"
    },
    {
        code: "RMA",
        display: "Registered Medical Assistant"
    },
    {
        code: "RN",
        display: "Registered Nurse"
    },
    {
        code: "RPH",
        display: "Registered Pharmacist"
    },
    {
        code: "SEC",
        display: "Secretarial Certificate"
    },
    {
        code: "TS",
        display: "Trade School Graduate"
    }
];
/**
 * Credentials Benchmark clinicians hold that v2-0360 does not define.
 *
 * Provenance: behavioral-health licenses referenced across this codebase
 * (`LCSW` in the CoCM billing packet and MCP directory; `PsyD`, `LMHC`,
 * `LICSW`, `LPC`, `LADC`, `MSW` in the practice-name credential parser) plus
 * the advanced-practice and referring-provider credentials the
 * `referring-practice` CLI has always accepted (`DNP`, `FNP`, `PNP`, `CRNA`,
 * `MBBS`, `DPM`, `DC`, `OD`, `DDS`, `DMD`), and the state-variant counseling
 * and psychiatric-nursing licenses that accompany them.
 */ const BENCHMARK_PRACTITIONER_QUALIFICATION_OPTIONS = [
    {
        code: "LCSW",
        display: "Licensed Clinical Social Worker"
    },
    {
        code: "LICSW",
        display: "Licensed Independent Clinical Social Worker"
    },
    {
        code: "LMSW",
        display: "Licensed Master Social Worker"
    },
    {
        code: "LSW",
        display: "Licensed Social Worker"
    },
    {
        code: "MSW",
        display: "Master of Social Work"
    },
    {
        code: "LPC",
        display: "Licensed Professional Counselor"
    },
    {
        code: "LPCC",
        display: "Licensed Professional Clinical Counselor"
    },
    {
        code: "LCPC",
        display: "Licensed Clinical Professional Counselor"
    },
    {
        code: "LMHC",
        display: "Licensed Mental Health Counselor"
    },
    {
        code: "LMFT",
        display: "Licensed Marriage and Family Therapist"
    },
    {
        code: "LADC",
        display: "Licensed Alcohol and Drug Counselor"
    },
    {
        code: "CADC",
        display: "Certified Alcohol and Drug Counselor"
    },
    {
        code: "PsyD",
        display: "Doctor of Psychology"
    },
    {
        code: "PMHNP",
        display: "Psychiatric-Mental Health Nurse Practitioner"
    },
    {
        code: "APRN",
        display: "Advanced Practice Registered Nurse"
    },
    {
        code: "DNP",
        display: "Doctor of Nursing Practice"
    },
    {
        code: "FNP",
        display: "Family Nurse Practitioner"
    },
    {
        code: "PNP",
        display: "Pediatric Nurse Practitioner"
    },
    {
        code: "CRNA",
        display: "Certified Registered Nurse Anesthetist"
    },
    {
        code: "MBBS",
        display: "Bachelor of Medicine, Bachelor of Surgery"
    },
    {
        code: "DPM",
        display: "Doctor of Podiatric Medicine"
    },
    {
        code: "DC",
        display: "Doctor of Chiropractic"
    },
    {
        code: "OD",
        display: "Doctor of Optometry"
    },
    {
        code: "DDS",
        display: "Doctor of Dental Surgery"
    },
    {
        code: "DMD",
        display: "Doctor of Dental Medicine"
    }
];
/** Every qualification concept, HL7 first, each tagged with its own system. */ const PRACTITIONER_QUALIFICATION_OPTIONS = [
    ...HL7_PRACTITIONER_QUALIFICATION_OPTIONS.map((option)=>({
            ...option,
            system: PRACTITIONER_QUALIFICATION_HL7_SYSTEM
        })),
    ...BENCHMARK_PRACTITIONER_QUALIFICATION_OPTIONS.map((option)=>({
            ...option,
            system: PRACTITIONER_QUALIFICATION_BENCHMARK_SYSTEM
        }))
];
/**
 * Case-folded lookup. Built once; the accompanying test asserts the two systems
 * never contribute the same folded key, so a lookup is never ambiguous.
 */ const QUALIFICATIONS_BY_FOLDED_CODE = new Map(PRACTITIONER_QUALIFICATION_OPTIONS.map((option)=>[
        option.code.toUpperCase(),
        option
    ]));
/** Trailing punctuation is common when credentials are pasted from a name. */ function foldQualificationInput(input) {
    return input.trim().replace(/[.,;]+$/, "").toUpperCase();
}
/**
 * Resolves operator input of any casing to the canonical qualification concept,
 * or `undefined` when no system defines it.
 * @agentic @benchmark/core
 */ function resolvePractitionerQualification(input) {
    if (typeof input !== "string") {
        return undefined;
    }
    return QUALIFICATIONS_BY_FOLDED_CODE.get(foldQualificationInput(input));
}
/**
 * Strict type guard for already-canonical data. Use
 * `resolvePractitionerQualification` for anything a human typed.
 * @agentic @benchmark/core
 */ function isPractitionerQualificationCode(code) {
    return typeof code === "string" && resolvePractitionerQualification(code)?.code === code;
}
/**
 * Builds the `Practitioner.qualification` entry for a credential, accepting any
 * casing. Returns `undefined` when no system defines the input.
 * @agentic @benchmark/core
 */ function buildPractitionerQualification(input) {
    const option = resolvePractitionerQualification(input);
    if (!option) {
        return undefined;
    }
    return {
        code: buildPractitionerQualificationConcept(option)
    };
}
/**
 * Builds the `CodeableConcept` for a resolved qualification concept.
 * @agentic @benchmark/core
 */ function buildPractitionerQualificationConcept(option) {
    return {
        coding: [
            {
                system: option.system,
                code: option.code,
                display: option.display
            }
        ]
    };
}
/**
 * True when the concept is defined by a qualification system this application
 * owns or recognizes, regardless of which of the two it is.
 * @agentic @benchmark/core
 */ function isPractitionerQualificationSystem(system) {
    return system === PRACTITIONER_QUALIFICATION_HL7_SYSTEM || system === PRACTITIONER_QUALIFICATION_BENCHMARK_SYSTEM;
}
/**
 * Reads the canonical qualification codes recorded on a Practitioner, ignoring
 * codings from systems this application does not recognize.
 * @agentic @benchmark/core
 */ function getPractitionerQualificationCodes(qualifications) {
    const found = [];
    const seen = new Set();
    for (const entry of qualifications ?? []){
        for (const coding of entry.code?.coding ?? []){
            if (!isPractitionerQualificationSystem(coding.system) || !coding.code) {
                continue;
            }
            const option = resolvePractitionerQualification(coding.code);
            if (option && option.system === coding.system && !seen.has(option.code)) {
                seen.add(option.code);
                found.push(option);
            }
        }
    }
    return found;
}

// Business role codes use HL7 practitioner-role CodeSystem with our organization-specific codes
const PRACTITIONER_ROLE_CODE_SYSTEM = `${HL7_TERMINOLOGY_BASE}/CodeSystem/practitioner-role`;
const PRACTITIONER_ROLE_CODE_OPTIONS = [
    {
        code: "clinical-director",
        display: "Clinical Director",
        system: PRACTITIONER_ROLE_CODE_SYSTEM,
        description: "Clinical leadership responsible for care program oversight"
    },
    {
        code: "lead-advocate",
        display: "Lead Advocate",
        system: PRACTITIONER_ROLE_CODE_SYSTEM,
        description: "Senior LCSW responsible for licensed member care"
    },
    {
        code: "care-manager",
        display: "Care Manager",
        system: PRACTITIONER_ROLE_CODE_SYSTEM,
        description: "Care coordination and ongoing member management"
    },
    {
        code: "intake-coordinator",
        display: "Intake Coordinator",
        system: PRACTITIONER_ROLE_CODE_SYSTEM,
        description: "Responsible for scheduling and completing member intake"
    },
    {
        code: "special-operations-director",
        display: "Special Ops Director",
        system: PRACTITIONER_ROLE_CODE_SYSTEM,
        description: "Operations leadership for non-licensed member programs"
    },
    {
        code: "special-operations-associate",
        display: "Special Ops Associate",
        system: PRACTITIONER_ROLE_CODE_SYSTEM,
        description: "Operations Specialist responsible for non-licensed member outreach and follow-up"
    },
    {
        code: "consulting-psychiatrist",
        display: "Consulting Psychiatrist",
        system: PRACTITIONER_ROLE_CODE_SYSTEM,
        description: "Psychiatric consultation for CoCM and complex cases"
    }
];
/** HL7 practitioner-role code for ICT (information and communications technology) staff. */ const ICT_PROFESSIONAL_ROLE_CODE = "ict";
const PRACTITIONER_ROLE_BUSINESS_CODES = new Set(PRACTITIONER_ROLE_CODE_OPTIONS.map((o)=>o.code));
/**
 * Type guard: narrows an arbitrary string to a known business role code.
 * @agentic @benchmark/core
 */ function isPractitionerRoleBusinessCode(code) {
    return typeof code === "string" && PRACTITIONER_ROLE_BUSINESS_CODES.has(code);
}
/**
 * Extracts the known business role codes from a single PractitionerRole's
 * `code` field. Only codings under the application's practitioner-role
 * CodeSystem that match a known business code are returned, de-duplicated and
 * in declaration order. Returns an empty array when the resource carries no
 * recognized business codes.
 * @agentic @benchmark/core
 */ function getPractitionerRoleBusinessCodes(role) {
    if (!role?.code) {
        return [];
    }
    const found = new Set();
    for (const concept of role.code){
        for (const coding of concept.coding ?? []){
            if (coding.system === PRACTITIONER_ROLE_CODE_SYSTEM && isPractitionerRoleBusinessCode(coding.code)) {
                found.add(coding.code);
            }
        }
    }
    // Preserve the canonical declaration order for stable, predictable output.
    return PRACTITIONER_ROLE_CODE_OPTIONS.filter((o)=>found.has(o.code)).map((o)=>o.code);
}
/**
 * Extracts the de-duplicated set of known business role codes across multiple
 * PractitionerRole resources (e.g. all of a user's active roles). Output is in
 * canonical declaration order.
 * @agentic @benchmark/core
 */ function getPractitionerRolesBusinessCodes(roles) {
    const found = new Set();
    for (const role of roles){
        for (const code of getPractitionerRoleBusinessCodes(role)){
            found.add(code);
        }
    }
    return PRACTITIONER_ROLE_CODE_OPTIONS.filter((o)=>found.has(o.code)).map((o)=>o.code);
}
/**
 * True when the role carries the standard HL7 `ict` practitioner-role code
 * (display: "ICT professional").
 * @agentic @benchmark/core
 */ function isIctProfessionalPractitionerRole(role) {
    if (!role?.code) {
        return false;
    }
    for (const concept of role.code){
        for (const coding of concept.coding ?? []){
            if (coding.system === PRACTITIONER_ROLE_CODE_SYSTEM && coding.code === ICT_PROFESSIONAL_ROLE_CODE) {
                return true;
            }
        }
    }
    return false;
}
/**
 * True when any role in the set is an ICT professional role.
 * @agentic @benchmark/core
 */ function practitionerRolesIncludeIctProfessional(roles) {
    return roles.some(isIctProfessionalPractitionerRole);
}
function getPractitionerRoleCodeDisplay(code) {
    return PRACTITIONER_ROLE_CODE_OPTIONS.find((o)=>o.code === code)?.display || code;
}
/**
 * Resolves a human-readable label for a PractitionerRole, for display purposes.
 * Unlike {@link getPractitionerRoleBusinessCodes}, this is NOT limited to the
 * curated business codes: it prefers a curated business-role display when the
 * role carries one, then falls back to the resource's own `code` text /
 * coding display, and finally a raw code. Returns undefined only when the role
 * has no usable label at all (e.g. no `code`). This lets the UI show standard
 * HL7 roles (e.g. "ICT professional") that are not in the business-code set.
 * @agentic @benchmark/core
 */ function getPractitionerRoleDisplayLabel(role) {
    if (!role?.code) {
        return undefined;
    }
    const businessCodes = getPractitionerRoleBusinessCodes(role);
    if (businessCodes.length > 0) {
        return getPractitionerRoleCodeDisplay(businessCodes[0]);
    }
    for (const concept of role.code){
        const text = concept.text?.trim();
        if (text) {
            return text;
        }
        for (const coding of concept.coding ?? []){
            const display = coding.display?.trim();
            if (display) {
                return display;
            }
        }
    }
    for (const concept of role.code){
        for (const coding of concept.coding ?? []){
            const code = coding.code?.trim();
            if (code) {
                return code;
            }
        }
    }
    return undefined;
}
function getPractitionerRoleCodingByCode(code) {
    const match = PRACTITIONER_ROLE_CODE_OPTIONS.find((o)=>o.code === code);
    if (match) {
        return {
            system: match.system,
            display: match.display
        };
    }
    return {
        system: PRACTITIONER_ROLE_CODE_SYSTEM,
        display: code
    };
}
// UI-ready options for Select/CardSelect components
const PRACTITIONER_ROLE_CODE_SELECT_OPTIONS = PRACTITIONER_ROLE_CODE_OPTIONS.map((o)=>({
        value: o.code,
        label: o.display,
        description: o.description
    }));
const PRACTITIONER_ROLE_CODE_CARD_OPTIONS = PRACTITIONER_ROLE_CODE_OPTIONS.map((o)=>({
        value: o.code,
        label: o.display,
        description: o.description
    }));
/**
 * HL7 v2 Table 0286 — Provider Role. Referring providers at partner practices
 * are typed with this system rather than the business-role CodeSystem, because
 * they are external to Benchmark.
 */ const REFERRING_PROVIDER_ROLE_SYSTEM = `${HL7_TERMINOLOGY_BASE}/CodeSystem/v2-0286`;
const REFERRING_PROVIDER_ROLE_CODE = "RP";
const REFERRING_PROVIDER_ROLE_DISPLAY = "Referring Provider";
/** `PractitionerRole.code` entry marking an external referring provider. */ const REFERRING_PROVIDER_ROLE_CONCEPT = {
    coding: [
        {
            system: REFERRING_PROVIDER_ROLE_SYSTEM,
            code: REFERRING_PROVIDER_ROLE_CODE,
            display: REFERRING_PROVIDER_ROLE_DISPLAY
        }
    ]
};
/** FHIR token for `PractitionerRole?role=` searches scoped to referring providers. */ const REFERRING_PROVIDER_ROLE_SEARCH_TOKEN = `${REFERRING_PROVIDER_ROLE_SYSTEM}|${REFERRING_PROVIDER_ROLE_CODE}`;
/**
 * True when the role carries the v2-0286 referring-provider code.
 * @agentic @benchmark/core
 */ function isReferringProviderPractitionerRole(role) {
    return role?.code?.some((concept)=>concept.coding?.some((coding)=>coding.system === REFERRING_PROVIDER_ROLE_SYSTEM && coding.code === REFERRING_PROVIDER_ROLE_CODE)) ?? false;
}
/**
 * Case-folded lookup keyed by both the code and the display label, so operator
 * input can be either (`Clinical Director` or `clinical-director`).
 */ const ROLE_OPTIONS_BY_FOLDED_KEY = new Map(PRACTITIONER_ROLE_CODE_OPTIONS.flatMap((option)=>[
        [
            foldRoleInput(option.code),
            option
        ],
        [
            foldRoleInput(option.display),
            option
        ]
    ]));
function foldRoleInput(input) {
    return input.trim().toLowerCase().replace(/[\s_]+/g, "-");
}
/**
 * Resolves operator input of any casing — and either the code or the display
 * label — to the canonical business role, or `undefined` when unrecognized.
 * @agentic @benchmark/core
 */ function resolvePractitionerRoleBusinessCode(input) {
    if (typeof input !== "string") {
        return undefined;
    }
    return ROLE_OPTIONS_BY_FOLDED_KEY.get(foldRoleInput(input));
}
/**
 * Builds the `PractitionerRole.code` entry for a business role.
 * @agentic @benchmark/core
 */ function buildPractitionerRoleConcept(code) {
    const { system, display } = getPractitionerRoleCodingByCode(code);
    return {
        coding: [
            {
                system,
                code,
                display
            }
        ]
    };
}

/**
 * PHQ-9 scoring configuration
 * CLINICALLY CONFIRMED
 */ const PHQ9_SCORING = {
    ranges: [
        {
            min: 0,
            max: 4,
            level: OBSERVATION_RISK_LEVELS.MINIMAL,
            interpretation: "Minimal depression"
        },
        {
            min: 5,
            max: 9,
            level: OBSERVATION_RISK_LEVELS.MILD,
            interpretation: "Mild depression"
        },
        {
            min: 10,
            max: 14,
            level: OBSERVATION_RISK_LEVELS.MODERATE,
            interpretation: "Moderate depression"
        },
        {
            min: 15,
            max: 19,
            level: OBSERVATION_RISK_LEVELS.MODERATELY_SEVERE,
            interpretation: "Moderately severe depression"
        },
        {
            min: 20,
            max: 27,
            level: OBSERVATION_RISK_LEVELS.SEVERE,
            interpretation: "Severe depression"
        }
    ],
    maxScore: 27,
    linkIds: [
        "phq9-1",
        "phq9-2",
        "phq9-3",
        "phq9-4",
        "phq9-5",
        "phq9-6",
        "phq9-7",
        "phq9-8",
        "phq9-9"
    ]
};
/**
 * PHQ-9A (Adolescent) scoring configuration
 * Modified version of PHQ-9 for adolescents ages 12-18
 * CLINICALLY CONFIRMED - Same scoring bands as PHQ-9
 * IMPORTANT: Q9 (suicidal ideation) >= 1 triggers high-priority alert
 */ const PHQ9A_SCORING = {
    ranges: [
        {
            min: 0,
            max: 4,
            level: OBSERVATION_RISK_LEVELS.MINIMAL,
            interpretation: "Minimal depression"
        },
        {
            min: 5,
            max: 9,
            level: OBSERVATION_RISK_LEVELS.MILD,
            interpretation: "Mild depression"
        },
        {
            min: 10,
            max: 14,
            level: OBSERVATION_RISK_LEVELS.MODERATE,
            interpretation: "Moderate depression"
        },
        {
            min: 15,
            max: 19,
            level: OBSERVATION_RISK_LEVELS.MODERATELY_SEVERE,
            interpretation: "Moderately severe depression"
        },
        {
            min: 20,
            max: 27,
            level: OBSERVATION_RISK_LEVELS.SEVERE,
            interpretation: "Severe depression"
        }
    ],
    maxScore: 27,
    linkIds: [
        "phq9a-1",
        "phq9a-2",
        "phq9a-3",
        "phq9a-4",
        "phq9a-5",
        "phq9a-6",
        "phq9a-7",
        "phq9a-8",
        "phq9a-9"
    ]
};
/**
 * PHQ-4 scoring configuration
 * Combined depression and anxiety screening (first 2 items from PHQ-9, first 2 from GAD-7)
 * VALIDATED SCORING: Depression subscale ≥3 OR Anxiety subscale ≥3 OR Total ≥6 = positive screen
 */ const PHQ4_SCORING = {
    ranges: [
        {
            min: 0,
            max: 2,
            level: OBSERVATION_RISK_LEVELS.MINIMAL,
            interpretation: "Minimal depression/anxiety symptoms"
        },
        {
            min: 3,
            max: 5,
            level: OBSERVATION_RISK_LEVELS.MILD,
            interpretation: "Mild depression/anxiety symptoms"
        },
        {
            min: 6,
            max: 8,
            level: OBSERVATION_RISK_LEVELS.MODERATE,
            interpretation: "Moderate depression/anxiety symptoms - positive screen"
        },
        {
            min: 9,
            max: 12,
            level: OBSERVATION_RISK_LEVELS.SEVERE,
            interpretation: "Severe depression/anxiety symptoms - positive screen"
        }
    ],
    maxScore: 12,
    screeningCutoff: 6,
    linkIds: [
        "phq4-1",
        "phq4-2",
        "phq4-3",
        "phq4-4"
    ],
    subscales: {
        depression: {
            linkIds: [
                "phq4-1",
                "phq4-2"
            ],
            cutoff: 3,
            range: [
                0,
                6
            ]
        },
        anxiety: {
            linkIds: [
                "phq4-3",
                "phq4-4"
            ],
            cutoff: 3,
            range: [
                0,
                6
            ]
        }
    }
};
/**
 * GAD-7 scoring configuration
 */ const GAD7_SCORING = {
    ranges: [
        {
            min: 0,
            max: 4,
            level: OBSERVATION_RISK_LEVELS.MINIMAL,
            interpretation: "Minimal anxiety"
        },
        {
            min: 5,
            max: 9,
            level: OBSERVATION_RISK_LEVELS.MILD,
            interpretation: "Mild anxiety"
        },
        {
            min: 10,
            max: 14,
            level: OBSERVATION_RISK_LEVELS.MODERATE,
            interpretation: "Moderate anxiety"
        },
        {
            min: 15,
            max: 21,
            level: OBSERVATION_RISK_LEVELS.SEVERE,
            interpretation: "Severe anxiety"
        }
    ],
    maxScore: 21,
    linkIds: [
        "gad7-1",
        "gad7-2",
        "gad7-3",
        "gad7-4",
        "gad7-5",
        "gad7-6",
        "gad7-7"
    ]
};
/**
 * AUDIT-C scoring configuration
 */ const AUDIT_C_SCORING = {
    ranges: [
        {
            min: 0,
            max: 2,
            level: OBSERVATION_RISK_LEVELS.MINIMAL,
            interpretation: "Low risk"
        },
        {
            min: 3,
            max: 7,
            level: OBSERVATION_RISK_LEVELS.MODERATE,
            interpretation: "Moderate risk"
        },
        {
            min: 8,
            max: 12,
            level: OBSERVATION_RISK_LEVELS.SEVERE,
            interpretation: "High risk"
        }
    ],
    maxScore: 12,
    linkIds: [
        "audit-c-1",
        "audit-c-2",
        "audit-c-3"
    ],
    // Different scoring thresholds by gender
    thresholds: {
        male: 4,
        female: 3,
        other: 3
    }
};
/**
 * Drug-use screen scoring (Benchmark house-modified — NOT a validated instrument).
 *
 * D16 / D7: a single categorical question reported as positive/negative plus the
 * frequency the patient gave — never a score out of a total. Positive is any
 * answer other than "Never". Because the stem is reworded (destigmatizing
 * "recreational" wording) to match a frequency ladder, this carries no
 * validated-sensitivity claim; see PRD §8.8 and clarifications brief D16.
 */ const DRUG_USE_SCORING = {
    ranges: [
        {
            min: 0,
            max: 0,
            level: OBSERVATION_RISK_LEVELS.NEGATIVE,
            interpretation: "No recreational drug use reported"
        },
        {
            min: 1,
            max: 4,
            level: OBSERVATION_RISK_LEVELS.POSITIVE,
            interpretation: "Reported recreational drug use"
        }
    ],
    linkIds: [
        "drug-use-1"
    ]
};
/**
 * GAD-2 anxiety short-check scoring (D17). Two items, 0–3 each, total 0–6.
 * Published two-item cut-point is ≥3 (opens the full GAD-7 in the adaptive
 * flow). Recorded on its own 0–6 scale, never as a GAD-7 severity.
 */ const GAD2_SCORING = {
    ranges: [
        {
            min: 0,
            max: 2,
            level: OBSERVATION_RISK_LEVELS.NEGATIVE,
            interpretation: "Anxiety short check negative"
        },
        {
            min: 3,
            max: 6,
            level: OBSERVATION_RISK_LEVELS.POSITIVE,
            interpretation: "Anxiety short check positive — full GAD-7 recommended"
        }
    ],
    maxScore: 6,
    screeningCutoff: 3,
    linkIds: [
        "gad2-1",
        "gad2-2"
    ]
};
/**
 * UCLA Three-Item Loneliness Scale scoring. Each item 1–3; total 3–9 (floor is
 * 3, not 0). Scores of 6 or higher are labelled as elevated loneliness.
 */ const UCLA3_SCORING = {
    ranges: [
        {
            min: 3,
            max: 5,
            level: OBSERVATION_RISK_LEVELS.NEGATIVE,
            interpretation: "No elevated loneliness"
        },
        {
            min: 6,
            max: 9,
            level: OBSERVATION_RISK_LEVELS.POSITIVE,
            interpretation: "Elevated loneliness"
        }
    ],
    maxScore: 9,
    screeningCutoff: 6,
    linkIds: [
        "ucla3-1",
        "ucla3-2",
        "ucla3-3"
    ]
};
/**
 * Patient Global Impression of Severity (PGI-S). Single 7-point item reported as
 * a category — the chosen point IS the result, never averaged or shown as a
 * number out of a total (D7). Levels drive display color only; the interpretation
 * carries the exact point label.
 */ const PGI_S_SCORING = {
    ranges: [
        {
            min: 1,
            max: 1,
            level: OBSERVATION_RISK_LEVELS.MINIMAL,
            interpretation: "Not present"
        },
        {
            min: 2,
            max: 2,
            level: OBSERVATION_RISK_LEVELS.MINIMAL,
            interpretation: "Very mild"
        },
        {
            min: 3,
            max: 3,
            level: OBSERVATION_RISK_LEVELS.MILD,
            interpretation: "Mild"
        },
        {
            min: 4,
            max: 4,
            level: OBSERVATION_RISK_LEVELS.MODERATE,
            interpretation: "Moderate"
        },
        {
            min: 5,
            max: 5,
            level: OBSERVATION_RISK_LEVELS.MODERATE,
            interpretation: "Moderately severe"
        },
        {
            min: 6,
            max: 6,
            level: OBSERVATION_RISK_LEVELS.SEVERE,
            interpretation: "Severe"
        },
        {
            min: 7,
            max: 7,
            level: OBSERVATION_RISK_LEVELS.SEVERE,
            interpretation: "Extremely severe"
        }
    ],
    linkIds: [
        "pgi-s-1"
    ]
};
/**
 * Patient Global Impression of Change (PGI-C). Single 7-point item, always vs.
 * the beginning of treatment. Reported as a category (D7); "improved" is 1 or 2.
 */ const PGI_C_SCORING = {
    ranges: [
        {
            min: 1,
            max: 1,
            level: OBSERVATION_RISK_LEVELS.MINIMAL,
            interpretation: "Very much improved"
        },
        {
            min: 2,
            max: 2,
            level: OBSERVATION_RISK_LEVELS.MINIMAL,
            interpretation: "Much improved"
        },
        {
            min: 3,
            max: 3,
            level: OBSERVATION_RISK_LEVELS.LOW,
            interpretation: "Minimally improved"
        },
        {
            min: 4,
            max: 4,
            level: OBSERVATION_RISK_LEVELS.MILD,
            interpretation: "No change"
        },
        {
            min: 5,
            max: 5,
            level: OBSERVATION_RISK_LEVELS.MODERATE,
            interpretation: "Minimally worse"
        },
        {
            min: 6,
            max: 6,
            level: OBSERVATION_RISK_LEVELS.SEVERE,
            interpretation: "Much worse"
        },
        {
            min: 7,
            max: 7,
            level: OBSERVATION_RISK_LEVELS.SEVERE,
            interpretation: "Very much worse"
        }
    ],
    linkIds: [
        "pgi-c-1"
    ]
};
/**
 * Work and Social Adjustment Scale (WSAS) scoring. Five domains, each 0–8; total
 * 0–40, using the paper's own bands. When the work item is N/A, the four
 * remaining items are summed and rescaled ×5/4 (PRD §8.8).
 */ const WSAS_SCORING = {
    method: "wsas-summation",
    ranges: [
        {
            min: 0,
            max: 9,
            level: OBSERVATION_RISK_LEVELS.SUBCLINICAL,
            interpretation: "Below clinical concern"
        },
        {
            min: 10,
            max: 20,
            level: OBSERVATION_RISK_LEVELS.MODERATE,
            interpretation: "Significant impairment"
        },
        {
            min: 21,
            max: 40,
            level: OBSERVATION_RISK_LEVELS.SEVERE,
            interpretation: "Moderately severe or worse impairment"
        }
    ],
    maxScore: 40,
    linkIds: [
        "wsas-1",
        "wsas-2",
        "wsas-3",
        "wsas-4",
        "wsas-5"
    ]
};
/**
 * PC-PTSD-5 scoring configuration
 * Primary Care PTSD Screen for DSM-5
 */ const PC_PTSD_5_SCORING = {
    ranges: [
        {
            min: 0,
            max: 2,
            level: OBSERVATION_RISK_LEVELS.NEGATIVE,
            interpretation: "PTSD unlikely"
        },
        {
            min: 3,
            max: 5,
            level: OBSERVATION_RISK_LEVELS.POSITIVE,
            interpretation: "PTSD possible - further evaluation recommended"
        }
    ],
    maxScore: 5,
    linkIds: [
        "pc-ptsd-5-1",
        "pc-ptsd-5-2",
        "pc-ptsd-5-3",
        "pc-ptsd-5-4",
        "pc-ptsd-5-5"
    ],
    cutoff: 3
};
/**
 * PSC-17 scoring configuration
 * Pediatric Symptom Checklist-17 (parent-report)
 * Screens for psychosocial dysfunction in children ages 4-18
 * VALIDATED SCORING: Total ≥15 OR any subscale above cutoff = positive screen
 */ const PSC_17_SCORING = {
    ranges: [
        {
            min: 0,
            max: 14,
            level: OBSERVATION_RISK_LEVELS.NEGATIVE,
            interpretation: "Screen negative"
        },
        {
            min: 15,
            max: 34,
            level: OBSERVATION_RISK_LEVELS.POSITIVE,
            interpretation: "Screen positive - further evaluation recommended"
        }
    ],
    maxScore: 34,
    screeningCutoff: 15,
    linkIds: [
        "psc17-1",
        "psc17-2",
        "psc17-3",
        "psc17-4",
        "psc17-5",
        "psc17-6",
        "psc17-7",
        "psc17-8",
        "psc17-9",
        "psc17-10",
        "psc17-11",
        "psc17-12",
        "psc17-13",
        "psc17-14",
        "psc17-15",
        "psc17-16",
        "psc17-17"
    ],
    subscales: {
        attention: {
            linkIds: [
                "psc17-1",
                "psc17-2",
                "psc17-3",
                "psc17-4",
                "psc17-7"
            ],
            cutoff: 7,
            range: [
                0,
                10
            ],
            description: "Attention problems (ADHD)"
        },
        internalizing: {
            linkIds: [
                "psc17-5",
                "psc17-6",
                "psc17-9",
                "psc17-10",
                "psc17-11"
            ],
            cutoff: 5,
            range: [
                0,
                10
            ],
            description: "Internalizing problems (anxiety/depression)"
        },
        externalizing: {
            linkIds: [
                "psc17-8",
                "psc17-12",
                "psc17-13",
                "psc17-14",
                "psc17-15",
                "psc17-16",
                "psc17-17"
            ],
            cutoff: 7,
            range: [
                0,
                14
            ],
            description: "Externalizing problems (conduct)"
        }
    }
};
/**
 * C-SSRS scoring configuration
 * Columbia Suicide Severity Rating Scale — Screener (Recent, with Triage)
 *
 * IMPORTANT: C-SSRS uses ORDINAL SEVERITY LEVELS (0-5), not a total score. Risk
 * is the highest level reached. Scored via `method: "c-ssrs-ordinal"`
 * (see `calculateCSSRSScore`). Risk tiers follow the certified Columbia triage:
 * Q1/Q2 → Low, Q3 → Moderate, Q4/Q5 → High; suicidal behavior floors risk at
 * Moderate (lifetime) or High (within the past 3 months).
 */ const C_SSRS_SCORING = {
    method: "c-ssrs-ordinal",
    // Highest ordinal level reached (0-5); not a summed total.
    maxScore: 5,
    // Severity levels (ordinal scale - highest level reached determines risk).
    // `riskLevel` is the field the ordinal scorer reads.
    severityLevels: [
        {
            level: 0,
            riskLevel: OBSERVATION_RISK_LEVELS.MINIMAL,
            interpretation: "No suicidal ideation",
            description: "No thoughts of death or suicide"
        },
        {
            level: 1,
            riskLevel: OBSERVATION_RISK_LEVELS.LOW,
            interpretation: "Wish to be dead",
            description: "Endorses thoughts about death or dying"
        },
        {
            level: 2,
            riskLevel: OBSERVATION_RISK_LEVELS.LOW,
            interpretation: "Active suicidal thoughts",
            description: "Non-specific active suicidal thoughts of killing oneself without method, intent, or plan"
        },
        {
            level: 3,
            riskLevel: OBSERVATION_RISK_LEVELS.MODERATE,
            interpretation: "Active suicidal ideation with method",
            description: "Thoughts of suicide with consideration of method, without intent to act"
        },
        {
            level: 4,
            riskLevel: OBSERVATION_RISK_LEVELS.HIGH,
            interpretation: "Active suicidal ideation with intent",
            description: "Active suicidal thoughts with some intent to act, without specific plan"
        },
        {
            level: 5,
            riskLevel: OBSERVATION_RISK_LEVELS.HIGH,
            interpretation: "Active suicidal ideation with specific plan and intent",
            description: "Active suicidal ideation with plan and intent to carry it out"
        }
    ],
    linkIds: [
        "c-ssrs-ideation-1",
        "c-ssrs-ideation-2",
        "c-ssrs-ideation-3",
        "c-ssrs-ideation-4",
        "c-ssrs-ideation-5",
        "c-ssrs-behavior-1",
        "c-ssrs-behavior-recency"
    ]
};
/**
 * ASRS scoring configuration
 * Adult ADHD Self-Report Scale v1.1
 * VALIDATED SCORING: Part A (6 items) summed 0-24 scale, cutpoint ≥14 (Harvard NCS scoring update Feb 2024)
 *
 * - Part A items (asrs-1 through asrs-6): 0-4 per item (Never=0, Rarely=1, Sometimes=2, Often=3, Very Often=4)
 * - Score = sum of Part A only (0-24). Part B is not included in total score.
 * - Cutpoint ≥14 = positive screen for ADHD
 * - Four strata: 0-9 low negative, 10-13 high negative, 14-17 low positive, 18-24 high positive
 */ const ASRS_SCORING = {
    method: "asrs-threshold",
    ranges: [
        {
            min: 0,
            max: 9,
            level: OBSERVATION_RISK_LEVELS.NEGATIVE,
            interpretation: "ADHD unlikely – low negative range"
        },
        {
            min: 10,
            max: 13,
            level: OBSERVATION_RISK_LEVELS.NEGATIVE,
            interpretation: "ADHD unlikely – high negative range"
        },
        {
            min: 14,
            max: 17,
            level: OBSERVATION_RISK_LEVELS.POSITIVE,
            interpretation: "ADHD likely – low positive range; further evaluation recommended"
        },
        {
            min: 18,
            max: 24,
            level: OBSERVATION_RISK_LEVELS.POSITIVE,
            interpretation: "ADHD likely – high positive range; further evaluation recommended"
        }
    ],
    maxScore: 24,
    // All ASRS linkIds for complete scoring (required by QuestionnaireScoring interface)
    linkIds: [
        "asrs-1",
        "asrs-2",
        "asrs-3",
        "asrs-4",
        "asrs-5",
        "asrs-6",
        "asrs-7",
        "asrs-8",
        "asrs-9",
        "asrs-10",
        "asrs-11",
        "asrs-12",
        "asrs-13",
        "asrs-14",
        "asrs-15",
        "asrs-16",
        "asrs-17",
        "asrs-18"
    ],
    partA: {
        // Part A (6 items) – summed for total score 0-24; cutpoint ≥14 (Harvard NCS 2024)
        linkIds: [
            "asrs-1",
            "asrs-2",
            "asrs-3",
            "asrs-4",
            "asrs-5",
            "asrs-6"
        ],
        cutoff: 14
    },
    partB: {
        // Additional symptom questions
        linkIds: [
            "asrs-7",
            "asrs-8",
            "asrs-9",
            "asrs-10",
            "asrs-11",
            "asrs-12",
            "asrs-13",
            "asrs-14",
            "asrs-15",
            "asrs-16",
            "asrs-17",
            "asrs-18"
        ]
    }
};
/**
 * OCI-4 scoring configuration
 * 4-Item Obsessive-Compulsive Inventory (ultra-brief OCD screener)
 * VALIDATED SCORING: Research suggests cutoff around 8 for optimal sensitivity/specificity
 */ const OCI_4_SCORING = {
    ranges: [
        {
            min: 0,
            max: 4,
            level: OBSERVATION_RISK_LEVELS.MINIMAL,
            interpretation: "Low probability of OCD"
        },
        {
            min: 5,
            max: 7,
            level: OBSERVATION_RISK_LEVELS.MILD,
            interpretation: "Mild OCD symptoms - monitor"
        },
        {
            min: 8,
            max: 12,
            level: OBSERVATION_RISK_LEVELS.MODERATE,
            interpretation: "Moderate OCD symptoms - positive screen"
        },
        {
            min: 13,
            max: 16,
            level: OBSERVATION_RISK_LEVELS.SEVERE,
            interpretation: "Severe OCD symptoms - positive screen"
        }
    ],
    maxScore: 16,
    screeningCutoff: 8,
    linkIds: [
        "oci-4-1",
        "oci-4-2",
        "oci-4-3",
        "oci-4-4"
    ],
    subscales: {
        ordering: {
            linkIds: [
                "oci-4-1"
            ],
            description: "Arranging/ordering compulsions"
        },
        checking: {
            linkIds: [
                "oci-4-2"
            ],
            description: "Checking compulsions"
        },
        washing: {
            linkIds: [
                "oci-4-3"
            ],
            description: "Washing/cleaning compulsions"
        },
        obsessing: {
            linkIds: [
                "oci-4-4"
            ],
            description: "Unacceptable thoughts/obsessions"
        }
    },
    clinicalCutoff: 3
};
/**
 * TAPS-1 scoring configuration
 * TAPS Screener (Part 1)
 */ const TAPS_1_SCORING = {
    ranges: [
        {
            min: 0,
            max: 0,
            level: OBSERVATION_RISK_LEVELS.NEGATIVE,
            interpretation: "No substance use"
        },
        {
            min: 1,
            max: 16,
            level: OBSERVATION_RISK_LEVELS.POSITIVE,
            interpretation: "Substance use detected - proceed to TAPS-2"
        }
    ],
    maxScore: 16,
    linkIds: [
        "taps-1-1",
        "taps-1-2",
        "taps-1-3",
        "taps-1-4"
    ],
    substances: {
        tobacco: "taps-1-1",
        alcohol: "taps-1-2",
        prescription: "taps-1-3",
        illicit: "taps-1-4"
    }
};
/**
 * TAPS-2 scoring configuration
 * TAPS Assessment (Part 2)
 */ const TAPS_2_SCORING = {
    ranges: [
        {
            min: 0,
            max: 0,
            level: OBSERVATION_RISK_LEVELS.MINIMAL,
            interpretation: "No substance use problems"
        },
        {
            min: 1,
            max: 1,
            level: OBSERVATION_RISK_LEVELS.MILD,
            interpretation: "Suggested problem use"
        },
        {
            min: 2,
            max: 28,
            level: OBSERVATION_RISK_LEVELS.MODERATE,
            interpretation: "Higher risk - potential SUD diagnosis"
        }
    ],
    maxScore: 28,
    linkIds: [
        // Questions asked for each substance that screened positive in TAPS-1
        "taps-2-1",
        "taps-2-2",
        "taps-2-3",
        "taps-2-4",
        "taps-2-5",
        "taps-2-6",
        "taps-2-7"
    ],
    cutoffs: {
        tobacco: 2,
        alcohol: 2,
        marijuana: 2,
        cocaine: 1,
        heroin: 1,
        prescription: 1,
        other: 1
    }
};
/**
 * SBIRT-ED scoring configuration
 * SBIRT Eating Disorder Screener
 */ const SBIRT_ED_SCORING = {
    ranges: [
        {
            min: 0,
            max: 0,
            level: OBSERVATION_RISK_LEVELS.NEGATIVE,
            interpretation: "No eating disorder symptoms"
        },
        {
            min: 1,
            max: 1,
            level: OBSERVATION_RISK_LEVELS.POSITIVE,
            interpretation: "Eating disorder possible"
        },
        {
            min: 2,
            max: 5,
            level: OBSERVATION_RISK_LEVELS.POSITIVE,
            interpretation: "Eating disorder likely"
        }
    ],
    maxScore: 5,
    linkIds: [
        "sbirt-ed-1",
        "sbirt-ed-2",
        "sbirt-ed-3",
        "sbirt-ed-4",
        "sbirt-ed-5"
    ],
    cutoff: 2
};
/**
 * WPAI scoring configuration
 * Work Productivity and Activity Impairment
 */ const WPAI_SCORING = {
    ranges: [
        {
            min: 0,
            max: 25,
            level: OBSERVATION_RISK_LEVELS.MINIMAL,
            interpretation: "Minimal impairment"
        },
        {
            min: 26,
            max: 50,
            level: OBSERVATION_RISK_LEVELS.MILD,
            interpretation: "Mild impairment"
        },
        {
            min: 51,
            max: 75,
            level: OBSERVATION_RISK_LEVELS.MODERATE,
            interpretation: "Moderate impairment"
        },
        {
            min: 76,
            max: 100,
            level: OBSERVATION_RISK_LEVELS.SEVERE,
            interpretation: "Severe impairment"
        }
    ],
    maxScore: 100,
    linkIds: [
        "wpai-1",
        "wpai-2",
        "wpai-3",
        "wpai-4",
        "wpai-5",
        "wpai-6"
    ],
    calculations: {
        absenteeism: "Hours missed due to health / (Hours missed + Hours worked) × 100",
        presenteeism: "Impairment while working score × 100",
        workProductivityLoss: "Absenteeism + Presenteeism × (1 - Absenteeism)",
        activityImpairment: "Activity impairment score × 100"
    }
};
/**
 * PRAPARE scoring configuration
 * Protocol for Responding to and Assessing Patients' Assets, Risks, and Experiences
 */ const PRAPARE_SCORING = {
    ranges: [
        {
            min: 0,
            max: 25,
            level: OBSERVATION_RISK_LEVELS.LOW,
            interpretation: "Low social risk"
        },
        {
            min: 26,
            max: 50,
            level: OBSERVATION_RISK_LEVELS.MODERATE,
            interpretation: "Moderate social risk"
        },
        {
            min: 51,
            max: 100,
            level: OBSERVATION_RISK_LEVELS.HIGH,
            interpretation: "High social risk"
        }
    ],
    maxScore: 100,
    domains: {
        housing: [
            "prapare-1",
            "prapare-2",
            "prapare-3"
        ],
        food: [
            "prapare-4",
            "prapare-5"
        ],
        transportation: [
            "prapare-6"
        ],
        utilities: [
            "prapare-7"
        ],
        childcare: [
            "prapare-8"
        ],
        employment: [
            "prapare-9",
            "prapare-10"
        ],
        education: [
            "prapare-11"
        ],
        finances: [
            "prapare-12",
            "prapare-13"
        ],
        personalSafety: [
            "prapare-14"
        ],
        legalStatus: [
            "prapare-15"
        ]
    }
};
/**
 * WHO-5 Well-Being Index scoring configuration.
 * 5 items scored 0-5 (Likert), raw total 0-25, percentage = raw * 4.
 * Standard cutoffs: raw ≤7 (≤28%) likely depression, raw ≤12 (≤50%) poor well-being.
 * Mirah returns the raw score (0-25).
 */ const WHO5_SCORING = {
    ranges: [
        {
            min: 0,
            max: 7,
            level: OBSERVATION_RISK_LEVELS.SEVERE,
            interpretation: "Likely depression (WHO-5 ≤28%)"
        },
        {
            min: 8,
            max: 12,
            level: OBSERVATION_RISK_LEVELS.MODERATE,
            interpretation: "Poor well-being (WHO-5 ≤50%)"
        },
        {
            min: 13,
            max: 25,
            level: OBSERVATION_RISK_LEVELS.MINIMAL,
            interpretation: "Adequate well-being (WHO-5 >50%)"
        }
    ],
    maxScore: 25,
    linkIds: [
        "who5-1",
        "who5-2",
        "who5-3",
        "who5-4",
        "who5-5"
    ]
};
/**
 * SDOH Health Leads Social Needs Screening Toolkit scoring configuration.
 * Screens across social need domains (housing, food, utilities, etc.).
 * Scoring varies by implementation; Mirah provides a computed score.
 */ const SDOH_SCORING = {
    maxScore: 0,
    ranges: [],
    linkIds: []
};
/**
 * No-scoring configuration for questionnaires that don't produce numeric scores
 * Used for safety plans and other non-scored assessments
 */ const NO_SCORING = {
    maxScore: 0,
    ranges: [],
    linkIds: []
};
/**
 * NPS scoring: single 0–10 item; bands match promoter / passive / detractor.
 * @agentic @benchmark/core
 */ const NPS_SCORING = {
    maxScore: 10,
    linkIds: [
        "nps-score"
    ],
    ranges: [
        {
            min: 0,
            max: 6,
            level: OBSERVATION_RISK_LEVELS.MODERATE,
            interpretation: "Detractor"
        },
        {
            min: 7,
            max: 8,
            level: OBSERVATION_RISK_LEVELS.SUBCLINICAL,
            interpretation: "Passive"
        },
        {
            min: 9,
            max: 10,
            level: OBSERVATION_RISK_LEVELS.MINIMAL,
            interpretation: "Promoter"
        }
    ]
};
/**
 * Standard questionnaire response status options
 */ const QUESTIONNAIRE_RESPONSE_STATUS_OPTIONS = [
    {
        value: "in-progress",
        display: "In Progress"
    },
    {
        value: "completed",
        display: "Completed"
    },
    {
        value: "amended",
        display: "Amended"
    },
    {
        value: "entered-in-error",
        display: "Entered in Error"
    },
    {
        value: "stopped",
        display: "Stopped"
    }
];
/**
 * Common answer value sets for Likert-style questions
 */ const LIKERT_FREQUENCY_OPTIONS = [
    {
        value: 0,
        coding: {
            code: "LA6568-5",
            display: "Not at all"
        }
    },
    {
        value: 1,
        coding: {
            code: "LA6569-3",
            display: "Several days"
        }
    },
    {
        value: 2,
        coding: {
            code: "LA6570-1",
            display: "More than half the days"
        }
    },
    {
        value: 3,
        coding: {
            code: "LA6571-9",
            display: "Nearly every day"
        }
    }
];
/**
 * Extract scoring information from NIH FHIR questionnaire items
 * Uses itemWeight extensions for accurate scoring
 */ function extractQuestionnaireScoringInfo(questionnaire) {
    const linkIds = [];
    const answerOptions = [];
    questionnaire.item?.forEach((item)=>{
        if (item.type === "choice" && item.answerOption) {
            linkIds.push(item.linkId);
            const options = item.answerOption.map((option)=>{
                const weight = option.extension?.find((ext)=>ext.url === "http://hl7.org/fhir/StructureDefinition/itemWeight")?.valueDecimal || 0;
                return {
                    value: weight,
                    code: option.valueCoding?.code,
                    display: option.valueCoding?.display
                };
            });
            answerOptions.push({
                linkId: item.linkId,
                options
            });
        }
    });
    return {
        linkIds,
        answerOptions
    };
}
/**
 * Type guard to validate QuestionnaireScoring configuration at runtime
 * Ensures all required properties are present and properly typed
 */ function isValidQuestionnaireScoring(scoring) {
    if (!scoring || typeof scoring !== "object") {
        return false;
    }
    // Check required linkIds property
    if (!Array.isArray(scoring.linkIds) || scoring.linkIds.length === 0) {
        return false;
    }
    // Validate linkIds are all strings
    if (!scoring.linkIds.every((id)=>typeof id === "string")) {
        return false;
    }
    // Validate optional ranges property if present
    if (scoring.ranges && !Array.isArray(scoring.ranges)) {
        return false;
    }
    // Validate optional maxScore property if present
    if (scoring.maxScore !== undefined && typeof scoring.maxScore !== "number") {
        return false;
    }
    return true;
}
/**
 * Helper function to get display name for questionnaire response status
 */ function getQuestionnaireResponseStatusDisplay(status) {
    const option = QUESTIONNAIRE_RESPONSE_STATUS_OPTIONS.find((opt)=>opt.value === status);
    return option?.display || status;
}
 // Note: Validation type moved to questionnaire.ts to avoid circular dependency

/**
 * Questionnaire types for categorization
 */ const QUESTIONNAIRE_SUBJECTS = {
    ADHD: "adhd",
    ANXIETY: "anxiety",
    DEPRESSION: "depression",
    EATING_DISORDERS: "eating-disorders",
    FUNCTIONAL_ASSESSMENT: "functional-assessment",
    GENERAL_HEALTH: "general-health",
    MEMBER_FEEDBACK: "member-feedback",
    OBSESSIVE_COMPULSIVE: "obsessive-compulsive",
    PTSD: "ptsd",
    SAFETY_PLANNING: "safety-planning",
    SOCIAL_DETERMINANTS: "social-determinants",
    SUBSTANCE_USE: "substance-use",
    SUICIDALITY: "suicidality"
};
/**
 * LOINC codes for questionnaire panels/forms (used for search/discovery and canonical URL construction)
 * These identify the questionnaire definition itself, NOT the scores/observations
 *
 * NOTE: These are used for:
 * 1. Searching/discovering questionnaires in FHIR servers
 * 2. Constructing canonical URLs for Questionnaire.url
 * 3. Initial questionnaire creation
 *
 * IMPORTANT: Once a questionnaire is fetched, prefer extracting LOINC codes
 * dynamically from the actual FHIR resource rather than using these static values.
 *
 * Only includes assessments with official LOINC questionnaire panel codes.
 * Questionnaires without official LOINC panel codes use organizational URLs.
 *
 * IMPORTANT: These codes should ONLY be used for Questionnaire and QuestionnaireResponse
 * resources. For total score observations, use OBSERVATION_LOINC_CODES from
 * observation.ts which contains the score codes.
 *
 * Validation: ✅ All codes verified against official LOINC database (2025-01-08)
 * See: packages/medplum/LOINC_CODE_MAPPINGS.md for detailed mapping documentation
 */ const QUESTIONNAIRE_LOINC_CODES = {
    /** ASRS Adult ADHD Self-Report Scale - LOINC 62750-5 ✅ VALIDATED */ ASRS: "62750-5",
    /** AUDIT-C Alcohol Screening Panel - LOINC 72109-2 ✅ VALIDATED */ AUDIT_C: "72109-2",
    /** C-SSRS Columbia Suicide Severity Rating Scale - LOINC 93245-9 ✅ VALIDATED */ C_SSRS: "93245-9",
    /** GAD-7 Anxiety Assessment Panel - LOINC 69737-5 ✅ VALIDATED */ GAD7: "69737-5",
    /** PC-PTSD-5 PTSD Screening Panel - LOINC 102010-6 ✅ VALIDATED */ PC_PTSD_5: "102010-6",
    /** PHQ-9 Depression Assessment Panel - LOINC 44249-1 ✅ VALIDATED */ PHQ9: "44249-1",
    /** PHQ-9A (Adolescent) Depression Assessment Panel - LOINC 89206-7 ✅ VALIDATED */ PHQ9A: "89206-7",
    /** PHQ-4 Combined Depression/Anxiety Panel - LOINC 69724-3 ✅ VALIDATED */ PHQ4: "69724-3",
    /** Health Leads Screening Panel (SDOH) - LOINC 99549-8 ✅ VALIDATED */ SDOH: "99549-8"
};
/**
 * Common questionnaire URLs used in FHIR Questionnaire.url
 * Uses official LOINC canonical URLs where available, organizational URLs otherwise
 */ const QUESTIONNAIRE_URLS = {
    ASRS: `http://loinc.org/q/${QUESTIONNAIRE_LOINC_CODES.ASRS}`,
    C_SSRS: `http://loinc.org/q/${QUESTIONNAIRE_LOINC_CODES.C_SSRS}`,
    GAD7: `http://loinc.org/q/${QUESTIONNAIRE_LOINC_CODES.GAD7}`,
    OCI_4: "https://benchmarkhealth.org/fhir/Questionnaire/oci-4",
    PC_PTSD_5: `http://loinc.org/q/${QUESTIONNAIRE_LOINC_CODES.PC_PTSD_5}`,
    PSC_17: "https://benchmarkhealth.org/fhir/Questionnaire/psc-17",
    PHQ9: `http://loinc.org/q/${QUESTIONNAIRE_LOINC_CODES.PHQ9}`,
    PHQ9A: `http://loinc.org/q/${QUESTIONNAIRE_LOINC_CODES.PHQ9A}`,
    PHQ4: `http://loinc.org/q/${QUESTIONNAIRE_LOINC_CODES.PHQ4}`,
    SBIRT_ED: "https://benchmarkhealth.org/fhir/Questionnaire/sbirt-ed",
    SAFETY_PLAN: "https://benchmarkhealth.org/fhir/Questionnaire/safety-plan",
    TAPS_1: "https://benchmarkhealth.org/fhir/Questionnaire/taps-1",
    TAPS_2: "https://benchmarkhealth.org/fhir/Questionnaire/taps-2",
    WPAI: "https://benchmarkhealth.org/fhir/Questionnaire/wpai-functional",
    WHO5: "https://benchmarkhealth.org/fhir/Questionnaire/who-5",
    SDOH: `http://loinc.org/q/${QUESTIONNAIRE_LOINC_CODES.SDOH}`,
    MIRAH_COMPLEXITY: "https://benchmarkhealth.org/fhir/Questionnaire/mirah-complexity",
    MIRAH_SEVERITY: "https://benchmarkhealth.org/fhir/Questionnaire/mirah-severity",
    NPS: "https://benchmarkhealth.org/fhir/Questionnaire/nps-member-feedback",
    DRUG_USE: "https://benchmarkhealth.org/fhir/Questionnaire/drug-use-screen",
    GAD2: "https://benchmarkhealth.org/fhir/Questionnaire/gad-2",
    AUDIT_C: `http://loinc.org/q/${QUESTIONNAIRE_LOINC_CODES.AUDIT_C}`,
    UCLA3: "https://benchmarkhealth.org/fhir/Questionnaire/ucla-3",
    PGI_S: "https://benchmarkhealth.org/fhir/Questionnaire/pgi-s",
    PGI_C: "https://benchmarkhealth.org/fhir/Questionnaire/pgi-c",
    WSAS: "https://benchmarkhealth.org/fhir/Questionnaire/wsas"
};
/**
 * Standard questionnaire status options
 */ const QUESTIONNAIRE_STATUS_OPTIONS = [
    {
        value: "draft",
        display: "Draft"
    },
    {
        value: "active",
        display: "Active"
    },
    {
        value: "retired",
        display: "Retired"
    },
    {
        value: "unknown",
        display: "Unknown"
    }
];
/**
 * Questionnaire configurations for common standardized tools
 * Strongly typed with FHIR-compliant structure
 */ const STANDARD_QUESTIONNAIRES = {
    PHQ4: {
        url: QUESTIONNAIRE_URLS.PHQ4,
        name: "PHQ-4",
        title: "Depression & Anxiety Screener",
        description: "Patient Health Questionnaire | 4 Item",
        instructions: "Over the last two weeks, how often have you been bothered by the following problems?",
        type: QUESTIONNAIRE_SUBJECTS.DEPRESSION,
        scoring: PHQ4_SCORING,
        totalScoreLoincCode: OBSERVATION_LOINC_CODES.PHQ4_TOTAL_SCORE
    },
    PHQ9: {
        url: QUESTIONNAIRE_URLS.PHQ9,
        name: "PHQ-9",
        title: "Depression Screener (Adult)",
        description: "Patient Health Questionnaire | 9 Item",
        instructions: "Over the last two weeks, how often have you been bothered by the following problems?",
        type: QUESTIONNAIRE_SUBJECTS.DEPRESSION,
        scoring: PHQ9_SCORING,
        totalScoreLoincCode: OBSERVATION_LOINC_CODES.PHQ9_TOTAL_SCORE
    },
    PHQ9A: {
        url: QUESTIONNAIRE_URLS.PHQ9A,
        name: "PHQ-9A",
        title: "Depression Screener (Ages 12-18)",
        description: "Patient Health Questionnaire - Adolescent | 10 Item",
        instructions: "Over the last two weeks, how often have you been bothered by any of the following problems?",
        type: QUESTIONNAIRE_SUBJECTS.DEPRESSION,
        scoring: PHQ9A_SCORING,
        totalScoreLoincCode: OBSERVATION_LOINC_CODES.PHQ9A_TOTAL_SCORE
    },
    GAD7: {
        url: QUESTIONNAIRE_URLS.GAD7,
        name: "GAD-7",
        title: "Anxiety Screener",
        description: "Generalized Anxiety Disorder | 7 Item",
        instructions: "Over the last two weeks, how often have you been bothered by the following problems?",
        type: QUESTIONNAIRE_SUBJECTS.ANXIETY,
        scoring: GAD7_SCORING,
        totalScoreLoincCode: OBSERVATION_LOINC_CODES.GAD7_TOTAL_SCORE
    },
    C_SSRS: {
        url: QUESTIONNAIRE_URLS.C_SSRS,
        name: "C-SSRS",
        title: "Suicide Risk Assessment",
        description: "Columbia Suicide Severity Rating Scale | Screening Version",
        instructions: "Please answer about the past month. Always ask questions 1 and 2. If YES to 2, ask questions 3, 4, 5 and 6. If NO to 2, skip to question 6. Always ask question 6.",
        type: QUESTIONNAIRE_SUBJECTS.SUICIDALITY,
        scoring: C_SSRS_SCORING,
        totalScoreLoincCode: OBSERVATION_LOINC_CODES.C_SSRS_SEVERITY
    },
    PC_PTSD_5: {
        url: QUESTIONNAIRE_URLS.PC_PTSD_5,
        name: "PC-PTSD-5",
        title: "PTSD Screener",
        description: "Primary Care Posttraumatic Stress Disorder Screen for DSM-5 | 6 Item",
        instructions: "Sometimes things happen to people that are unusually or especially frightening, horrible, or traumatic. For example: a serious accident or fire; a physical or sexual assault or abuse; an earthquake or flood; a war; seeing someone be killed or seriously injured; or having a loved one die through homicide or suicide. In the past month, have you...",
        type: QUESTIONNAIRE_SUBJECTS.PTSD,
        scoring: PC_PTSD_5_SCORING,
        totalScoreLoincCode: OBSERVATION_LOINC_CODES.PC_PTSD_5_TOTAL_SCORE
    },
    PSC_17: {
        url: QUESTIONNAIRE_URLS.PSC_17,
        name: "PSC-17",
        title: "Pediatric Symptom Checklist (Ages 4-18)",
        description: "Pediatric Symptom Checklist | 17 Item",
        instructions: "Please mark under the heading that best describes your child.",
        type: QUESTIONNAIRE_SUBJECTS.GENERAL_HEALTH,
        scoring: PSC_17_SCORING,
        totalScoreLoincCode: OBSERVATION_LOINC_CODES.PSC_17_TOTAL_SCORE
    },
    ASRS: {
        url: QUESTIONNAIRE_URLS.ASRS,
        name: "ASRS",
        title: "ADHD Screener (Adult)",
        description: "Adult ADHD Self-Report Scale | 18 Item (Part A: 6, Part B: 12)",
        instructions: "Please answer the questions below, rating yourself over the past 6 months.",
        type: QUESTIONNAIRE_SUBJECTS.ADHD,
        scoring: ASRS_SCORING,
        totalScoreLoincCode: OBSERVATION_LOINC_CODES.ASRS_TOTAL_SCORE
    },
    OCI_4: {
        url: QUESTIONNAIRE_URLS.OCI_4,
        name: "OCI-4",
        title: "OCD Screener",
        description: "Obsessive Compulsive Inventory | 4 Item",
        instructions: "Please rate the extent you have been bothered or distressed by the following during the past month.",
        type: QUESTIONNAIRE_SUBJECTS.OBSESSIVE_COMPULSIVE,
        scoring: OCI_4_SCORING,
        totalScoreLoincCode: OBSERVATION_LOINC_CODES.OCI_4_TOTAL_SCORE
    },
    SBIRT_ED: {
        url: QUESTIONNAIRE_URLS.SBIRT_ED,
        name: "SBIRT-ED",
        title: "Disordered Eating Screener",
        description: "Screening, Brief Intervention, and Referral to Treatment for Eating Disorders | 5 Item",
        instructions: "Please answer the following questions.",
        type: QUESTIONNAIRE_SUBJECTS.EATING_DISORDERS,
        scoring: SBIRT_ED_SCORING,
        totalScoreLoincCode: OBSERVATION_LOINC_CODES.SBIRT_ED_TOTAL_SCORE
    },
    TAPS_1: {
        url: QUESTIONNAIRE_URLS.TAPS_1,
        name: "TAPS-1",
        title: "Substance Use (Part 1)",
        description: "Tobacco, Alcohol, Prescription medication, and other Substance Use Screener | 5 Item",
        instructions: "The next questions ask about your use of tobacco, alcohol, and other substances in the past 12 months.",
        type: QUESTIONNAIRE_SUBJECTS.SUBSTANCE_USE,
        scoring: TAPS_1_SCORING,
        totalScoreLoincCode: OBSERVATION_LOINC_CODES.TAPS_1_TOTAL_SCORE
    },
    TAPS_2: {
        url: QUESTIONNAIRE_URLS.TAPS_2,
        name: "TAPS-2",
        title: "Substance Use (Part 2)",
        description: "Tobacco, Alcohol, Prescription medication, and other Substance Use Assessment | 9 Item",
        instructions: "The next questions ask about your use over the past 3 months.",
        type: QUESTIONNAIRE_SUBJECTS.SUBSTANCE_USE,
        scoring: TAPS_2_SCORING,
        totalScoreLoincCode: OBSERVATION_LOINC_CODES.TAPS_2_TOTAL_SCORE
    },
    WPAI: {
        url: QUESTIONNAIRE_URLS.WPAI,
        name: "WPAI",
        title: "Functional Assessment",
        description: "Work Productivity and Activity Impairment | 6 Item",
        instructions: "The following questions refer to the past seven days.",
        type: QUESTIONNAIRE_SUBJECTS.FUNCTIONAL_ASSESSMENT,
        scoring: WPAI_SCORING,
        totalScoreLoincCode: OBSERVATION_LOINC_CODES.WPAI_TOTAL_SCORE
    },
    WHO5: {
        url: QUESTIONNAIRE_URLS.WHO5,
        name: "WHO-5",
        title: "Well-Being Index",
        description: "World Health Organization Well-Being Index | 5 Item",
        instructions: "Please indicate for each of the five statements which is closest to how you have been feeling over the last two weeks.",
        type: QUESTIONNAIRE_SUBJECTS.GENERAL_HEALTH,
        scoring: WHO5_SCORING,
        totalScoreLoincCode: OBSERVATION_LOINC_CODES.WHO5_TOTAL_SCORE
    },
    SDOH: {
        url: QUESTIONNAIRE_URLS.SDOH,
        name: "SDOH",
        title: "Social Needs Screening",
        description: "Social Needs Screening Toolkit (Health Leads)",
        instructions: "The following questions relate to conditions that may affect your health and well-being.",
        type: QUESTIONNAIRE_SUBJECTS.SOCIAL_DETERMINANTS,
        scoring: SDOH_SCORING,
        totalScoreLoincCode: OBSERVATION_LOINC_CODES.SDOH_TOTAL_SCORE
    },
    MIRAH_COMPLEXITY: {
        url: QUESTIONNAIRE_URLS.MIRAH_COMPLEXITY,
        name: "Mirah Complexity",
        title: "Mirah Complexity Score",
        description: "Mirah composite complexity score across clinical domains",
        type: QUESTIONNAIRE_SUBJECTS.GENERAL_HEALTH,
        scoring: NO_SCORING,
        totalScoreLoincCode: OBSERVATION_LOINC_CODES.MIRAH_COMPLEXITY_SCORE
    },
    MIRAH_SEVERITY: {
        url: QUESTIONNAIRE_URLS.MIRAH_SEVERITY,
        name: "Mirah Severity",
        title: "Mirah Severity Score",
        description: "Mirah composite severity score across clinical domains",
        type: QUESTIONNAIRE_SUBJECTS.GENERAL_HEALTH,
        scoring: NO_SCORING,
        totalScoreLoincCode: OBSERVATION_LOINC_CODES.MIRAH_SEVERITY_SCORE
    },
    SAFETY_PLAN: {
        url: QUESTIONNAIRE_URLS.SAFETY_PLAN,
        name: "Safety Plan",
        title: "Safety Plan",
        description: "Collaborative safety plan",
        instructions: "Complete the safety plan collaboratively with the patient.",
        type: QUESTIONNAIRE_SUBJECTS.SAFETY_PLANNING,
        scoring: NO_SCORING,
        totalScoreLoincCode: ""
    },
    // Benchmark house-modified single-question drug screen (NOT a validated
    // instrument). Categorical: reported positive/negative + frequency, never a
    // score out of a total (D7/D16). No score-observation LOINC. Not administrable
    // through the manual picker or survey link — surfaced only by the adaptive
    // screener flow (feature-flagged); see PRD §8.8 and clarifications brief D16.
    DRUG_USE: {
        url: QUESTIONNAIRE_URLS.DRUG_USE,
        name: "Drug Use Screen",
        title: "Drug Use Screen",
        description: "Single-question recreational drug use screen (Benchmark house-modified; not a validated instrument).",
        instructions: "Ask the patient the question as written and record the response.",
        type: QUESTIONNAIRE_SUBJECTS.SUBSTANCE_USE,
        scoring: DRUG_USE_SCORING,
        totalScoreLoincCode: ""
    },
    // Adaptive-screener anxiety short check (D17). Separate 0–6 instrument that
    // opens GAD-7 when positive; not administrable via the manual picker/survey
    // link. Organizational score code (the legacy GAD-2 LOINC is deprecated).
    GAD2: {
        url: QUESTIONNAIRE_URLS.GAD2,
        name: "GAD-2",
        title: "Anxiety Short Check (GAD-2)",
        description: "Two-item anxiety short check (first two GAD-7 items); opens the full GAD-7 when positive.",
        instructions: "Over the last 2 weeks, how often have you been bothered by…",
        type: QUESTIONNAIRE_SUBJECTS.ANXIETY,
        scoring: GAD2_SCORING,
        totalScoreLoincCode: OBSERVATION_LOINC_CODES.GAD2_TOTAL_SCORE
    },
    // Adaptive-screener alcohol section (D19). Re-enabled; the first item gates
    // the other two (D17). Not administrable via the manual picker/survey link.
    AUDIT_C: {
        url: QUESTIONNAIRE_URLS.AUDIT_C,
        name: "AUDIT-C",
        title: "Alcohol Use (AUDIT-C)",
        description: "Alcohol Use Disorders Identification Test – Consumption. The first question gates the remaining two.",
        instructions: "Answer for your alcohol use over the past year.",
        type: QUESTIONNAIRE_SUBJECTS.SUBSTANCE_USE,
        scoring: AUDIT_C_SCORING,
        totalScoreLoincCode: OBSERVATION_LOINC_CODES.AUDIT_C_TOTAL_SCORE
    },
    // Adaptive-screener loneliness section. Public-domain three-item scale; total
    // 3–9. Not administrable via the manual picker or survey link.
    UCLA3: {
        url: QUESTIONNAIRE_URLS.UCLA3,
        name: "UCLA-3",
        title: "Loneliness (UCLA-3)",
        description: "Three-Item Loneliness Scale (UCLA). Total 3–9; scores of 6 or higher indicate elevated loneliness.",
        instructions: "Choose how often each statement applies to you.",
        type: QUESTIONNAIRE_SUBJECTS.GENERAL_HEALTH,
        scoring: UCLA3_SCORING,
        totalScoreLoincCode: OBSERVATION_LOINC_CODES.UCLA3_TOTAL_SCORE
    },
    // Adaptive-screener patient self-rating of severity (first visit). Single
    // 7-point item reported as a category (D7); no numeric score observation.
    PGI_S: {
        url: QUESTIONNAIRE_URLS.PGI_S,
        name: "PGI-S",
        title: "Patient Global Impression of Severity",
        description: "Patient self-rating of overall severity (adapted from the public-domain CGI). Reported as a category.",
        type: QUESTIONNAIRE_SUBJECTS.GENERAL_HEALTH,
        scoring: PGI_S_SCORING,
        totalScoreLoincCode: ""
    },
    // Adaptive-screener patient self-rating of change (follow-up visits), always
    // vs. the beginning of treatment. Single 7-point categorical item (D7).
    PGI_C: {
        url: QUESTIONNAIRE_URLS.PGI_C,
        name: "PGI-C",
        title: "Patient Global Impression of Change",
        description: "Patient self-rating of change vs. the beginning of treatment (adapted from the public-domain CGI). Reported as a category.",
        type: QUESTIONNAIRE_SUBJECTS.GENERAL_HEALTH,
        scoring: PGI_C_SCORING,
        totalScoreLoincCode: ""
    },
    // Adaptive-screener everyday-functioning section (D20). Licensed for electronic
    // administration with on-page Mundt et al. (2002) attribution (D2).
    WSAS: {
        url: QUESTIONNAIRE_URLS.WSAS,
        name: "WSAS",
        title: "Everyday Functioning (WSAS)",
        description: "Work and Social Adjustment Scale — five domains, total 0–40. Developed by Mundt et al. (2002).",
        instructions: "Rate each of the following questions on a 0 to 8 scale: 0 indicates no impairment at all and 8 indicates very severe impairment.",
        type: QUESTIONNAIRE_SUBJECTS.FUNCTIONAL_ASSESSMENT,
        scoring: WSAS_SCORING,
        totalScoreLoincCode: OBSERVATION_LOINC_CODES.WSAS_TOTAL_SCORE
    },
    NPS: {
        url: QUESTIONNAIRE_URLS.NPS,
        name: "NPS",
        title: "Member Feedback Survey",
        description: "Net Promoter Score — mental health concierge experience",
        instructions: "Let us know about your recent experience with Benchmark Health. All responses are anonymous.",
        type: QUESTIONNAIRE_SUBJECTS.MEMBER_FEEDBACK,
        scoring: NPS_SCORING,
        totalScoreLoincCode: OBSERVATION_LOINC_CODES.NPS_SCORE
    }
};

// PHQ-4 Questions (first 2 from PHQ-9, first 2 from GAD-7)
const PHQ4_QUESTIONS = [
    {
        linkId: "phq4-1",
        text: "Little interest or pleasure in doing things",
        type: "choice",
        scorable: true,
        answerOptions: LIKERT_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                code: opt.coding.code,
                display: opt.coding.display
            }))
    },
    {
        linkId: "phq4-2",
        text: "Feeling down, depressed, or hopeless",
        type: "choice",
        scorable: true,
        answerOptions: LIKERT_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                code: opt.coding.code,
                display: opt.coding.display
            }))
    },
    {
        linkId: "phq4-3",
        text: "Feeling nervous, anxious or on edge",
        type: "choice",
        scorable: true,
        answerOptions: LIKERT_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                code: opt.coding.code,
                display: opt.coding.display
            }))
    },
    {
        linkId: "phq4-4",
        text: "Not being able to stop or control worrying",
        type: "choice",
        scorable: true,
        answerOptions: LIKERT_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                code: opt.coding.code,
                display: opt.coding.display
            }))
    }
];
// PHQ-9 Questions
const PHQ9_QUESTIONS = [
    {
        linkId: "phq9-1",
        text: "Little interest or pleasure in doing things",
        type: "choice",
        scorable: true,
        answerOptions: LIKERT_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                code: opt.coding.code,
                display: opt.coding.display
            }))
    },
    {
        linkId: "phq9-2",
        text: "Feeling down, depressed, or hopeless",
        type: "choice",
        scorable: true,
        answerOptions: LIKERT_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                code: opt.coding.code,
                display: opt.coding.display
            }))
    },
    {
        linkId: "phq9-3",
        text: "Trouble falling or staying asleep, or sleeping too much",
        type: "choice",
        scorable: true,
        answerOptions: LIKERT_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                code: opt.coding.code,
                display: opt.coding.display
            }))
    },
    {
        linkId: "phq9-4",
        text: "Feeling tired or having little energy",
        type: "choice",
        scorable: true,
        answerOptions: LIKERT_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                code: opt.coding.code,
                display: opt.coding.display
            }))
    },
    {
        linkId: "phq9-5",
        text: "Poor appetite or overeating",
        type: "choice",
        scorable: true,
        answerOptions: LIKERT_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                code: opt.coding.code,
                display: opt.coding.display
            }))
    },
    {
        linkId: "phq9-6",
        text: "Feeling bad about yourself - or that you are a failure or have let yourself or your family down",
        type: "choice",
        scorable: true,
        answerOptions: LIKERT_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                code: opt.coding.code,
                display: opt.coding.display
            }))
    },
    {
        linkId: "phq9-7",
        text: "Trouble concentrating on things, such as reading the newspaper or watching television",
        type: "choice",
        scorable: true,
        answerOptions: LIKERT_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                code: opt.coding.code,
                display: opt.coding.display
            }))
    },
    {
        linkId: "phq9-8",
        text: "Moving or speaking so slowly that other people could have noticed? Or the opposite - being so fidgety or restless that you have been moving around a lot more than usual",
        type: "choice",
        scorable: true,
        answerOptions: LIKERT_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                code: opt.coding.code,
                display: opt.coding.display
            }))
    },
    {
        linkId: "phq9-9",
        text: "Thoughts that you would be better off dead, or of hurting yourself in some way",
        type: "choice",
        scorable: true,
        answerOptions: LIKERT_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                code: opt.coding.code,
                display: opt.coding.display
            }))
    },
    // Functional impairment item (not scored in PHQ-9 total)
    // TODO(#989): Add LOINC item coding for PHQ-9 items, including impairment, to align with FHIR best practices
    {
        linkId: "phq9-10",
        text: "If you checked off any problems, how difficult have these problems made it for you to do your work, take care of things at home, or get along with other people?",
        type: "choice",
        required: false,
        scorable: false,
        options: [
            {
                value: 0,
                display: "Not difficult at all"
            },
            {
                value: 1,
                display: "Somewhat difficult"
            },
            {
                value: 2,
                display: "Very difficult"
            },
            {
                value: 3,
                display: "Extremely difficult"
            }
        ]
    }
];
// PHQ-9A Questions (Adolescent version, ages 12-18)
// Same core questions as PHQ-9, with adolescent-appropriate language
const PHQ9A_QUESTIONS = [
    {
        linkId: "phq9a-1",
        text: "Little interest or pleasure in doing things",
        type: "choice",
        scorable: true,
        answerOptions: LIKERT_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                code: opt.coding.code,
                display: opt.coding.display
            }))
    },
    {
        linkId: "phq9a-2",
        text: "Feeling down, depressed, irritable, or hopeless",
        type: "choice",
        scorable: true,
        answerOptions: LIKERT_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                code: opt.coding.code,
                display: opt.coding.display
            }))
    },
    {
        linkId: "phq9a-3",
        text: "Trouble falling asleep, staying asleep, or sleeping too much",
        type: "choice",
        scorable: true,
        answerOptions: LIKERT_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                code: opt.coding.code,
                display: opt.coding.display
            }))
    },
    {
        linkId: "phq9a-4",
        text: "Feeling tired or having little energy",
        type: "choice",
        scorable: true,
        answerOptions: LIKERT_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                code: opt.coding.code,
                display: opt.coding.display
            }))
    },
    {
        linkId: "phq9a-5",
        text: "Poor appetite, weight loss, or overeating",
        type: "choice",
        scorable: true,
        answerOptions: LIKERT_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                code: opt.coding.code,
                display: opt.coding.display
            }))
    },
    {
        linkId: "phq9a-6",
        text: "Feeling bad about yourself - or feeling that you are a failure, or that you have let yourself or your family down",
        type: "choice",
        scorable: true,
        answerOptions: LIKERT_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                code: opt.coding.code,
                display: opt.coding.display
            }))
    },
    {
        linkId: "phq9a-7",
        text: "Trouble concentrating on things like school work, reading, or watching TV",
        type: "choice",
        scorable: true,
        answerOptions: LIKERT_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                code: opt.coding.code,
                display: opt.coding.display
            }))
    },
    {
        linkId: "phq9a-8",
        text: "Moving or speaking so slowly that other people could have noticed? Or the opposite - being so fidgety or restless that you were moving around a lot more than usual",
        type: "choice",
        scorable: true,
        answerOptions: LIKERT_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                code: opt.coding.code,
                display: opt.coding.display
            }))
    },
    {
        linkId: "phq9a-9",
        text: "Thoughts that you would be better off dead, or of hurting yourself in some way",
        type: "choice",
        scorable: true,
        answerOptions: LIKERT_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                code: opt.coding.code,
                display: opt.coding.display
            }))
    },
    // Functional impairment item (not scored in PHQ-9A total)
    {
        linkId: "phq9a-10",
        text: "If you checked off any problems, how difficult have these problems made it for you to do your work, take care of things at home, or get along with other people?",
        type: "choice",
        required: false,
        scorable: false,
        options: [
            {
                value: 0,
                display: "Not difficult at all"
            },
            {
                value: 1,
                display: "Somewhat difficult"
            },
            {
                value: 2,
                display: "Very difficult"
            },
            {
                value: 3,
                display: "Extremely difficult"
            }
        ]
    }
];
// GAD-7 Questions
const GAD7_QUESTIONS = [
    {
        linkId: "gad7-1",
        text: "Feeling nervous, anxious or on edge",
        type: "choice",
        scorable: true,
        answerOptions: LIKERT_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                code: opt.coding.code,
                display: opt.coding.display
            }))
    },
    {
        linkId: "gad7-2",
        text: "Not being able to stop or control worrying",
        type: "choice",
        scorable: true,
        answerOptions: LIKERT_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                code: opt.coding.code,
                display: opt.coding.display
            }))
    },
    {
        linkId: "gad7-3",
        text: "Worrying too much about different things",
        type: "choice",
        scorable: true,
        answerOptions: LIKERT_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                code: opt.coding.code,
                display: opt.coding.display
            }))
    },
    {
        linkId: "gad7-4",
        text: "Trouble relaxing",
        type: "choice",
        scorable: true,
        answerOptions: LIKERT_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                code: opt.coding.code,
                display: opt.coding.display
            }))
    },
    {
        linkId: "gad7-5",
        text: "Being so restless that it is hard to sit still",
        type: "choice",
        scorable: true,
        answerOptions: LIKERT_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                code: opt.coding.code,
                display: opt.coding.display
            }))
    },
    {
        linkId: "gad7-6",
        text: "Becoming easily annoyed or irritable",
        type: "choice",
        scorable: true,
        answerOptions: LIKERT_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                code: opt.coding.code,
                display: opt.coding.display
            }))
    },
    {
        linkId: "gad7-7",
        text: "Feeling afraid as if something awful might happen",
        type: "choice",
        scorable: true,
        answerOptions: LIKERT_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                code: opt.coding.code,
                display: opt.coding.display
            }))
    },
    // Functional impairment item (not scored in GAD-7 total)
    // TODO(#989): Add LOINC item coding for GAD-7 items, including impairment, to align with FHIR best practices
    {
        linkId: "gad7-8",
        text: "If you checked off any problems, how difficult have these problems made it for you to do your work, take care of things at home, or get along with other people?",
        type: "choice",
        required: false,
        scorable: false,
        options: [
            {
                value: 0,
                display: "Not difficult at all"
            },
            {
                value: 1,
                display: "Somewhat difficult"
            },
            {
                value: 2,
                display: "Very difficult"
            },
            {
                value: 3,
                display: "Extremely difficult"
            }
        ]
    }
];
/**
 * GAD-2 anxiety short check (D17). Its two items are, verbatim, the first two
 * GAD-7 items; the instrument is scored 0–6 on its own scale and recorded under
 * the GAD-2 name — never as a GAD-7 severity. A positive GAD-2 (published cut
 * ≥3) opens GAD-7 as a separate instrument in the adaptive flow.
 */ const GAD2_QUESTIONS = [
    {
        linkId: "gad2-1",
        text: "Feeling nervous, anxious or on edge",
        type: "choice",
        scorable: true,
        answerOptions: LIKERT_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                code: opt.coding.code,
                display: opt.coding.display
            }))
    },
    {
        linkId: "gad2-2",
        text: "Not being able to stop or control worrying",
        type: "choice",
        scorable: true,
        answerOptions: LIKERT_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                code: opt.coding.code,
                display: opt.coding.display
            }))
    }
];
// AUDIT-C Questions (Alcohol Use Disorders Identification Test - Consumption)
/**
 * AUDIT-C alcohol consumption short check. The first item gates the other two
 * (D17/D19): a "Never" answer (value 0) completes the instrument at a genuine
 * 0/12 without asking items 2–3; any other answer reveals them for the full
 * 0–12 score. Recorded as AUDIT-C throughout — there is no separate gate
 * instrument. Positive-screen thresholds are sex-dependent (see AUDIT_C_SCORING).
 */ const AUDIT_C_QUESTIONS = [
    {
        linkId: "audit-c-1",
        text: "How often do you have a drink containing alcohol?",
        type: "choice",
        scorable: true,
        required: true,
        gateFor: [
            "audit-c-2",
            "audit-c-3"
        ],
        options: [
            {
                value: 0,
                display: "Never"
            },
            {
                value: 1,
                display: "Monthly or less"
            },
            {
                value: 2,
                display: "2-4 times a month"
            },
            {
                value: 3,
                display: "2-3 times a week"
            },
            {
                value: 4,
                display: "4 or more times a week"
            }
        ]
    },
    {
        linkId: "audit-c-2",
        text: "How many drinks containing alcohol do you have on a typical day when you are drinking?",
        type: "choice",
        scorable: true,
        required: true,
        options: [
            {
                value: 0,
                display: "1 or 2"
            },
            {
                value: 1,
                display: "3 or 4"
            },
            {
                value: 2,
                display: "5 or 6"
            },
            {
                value: 3,
                display: "7 to 9"
            },
            {
                value: 4,
                display: "10 or more"
            }
        ]
    },
    {
        linkId: "audit-c-3",
        text: "How often do you have six or more drinks on one occasion?",
        type: "choice",
        scorable: true,
        required: true,
        options: [
            {
                value: 0,
                display: "Never"
            },
            {
                value: 1,
                display: "Less than monthly"
            },
            {
                value: 2,
                display: "Monthly"
            },
            {
                value: 3,
                display: "Weekly"
            },
            {
                value: 4,
                display: "Daily or almost daily"
            }
        ]
    }
];
/**
 * Drug-use screen (Benchmark house-modified — NOT a validated instrument).
 *
 * A single categorical question. The stem intentionally reworks the validated
 * single-question drug screen: it uses destigmatizing "recreational" wording (a
 * deliberate clinical-team choice, not transcription drift) and a frequency
 * ladder, so it makes no validated-sensitivity claim and carries no third-party
 * citation. Reported as positive/negative + frequency, never a score out of a
 * total (D7). See PRD §8.8 and clarifications brief D16.
 */ const DRUG_USE_QUESTIONS = [
    {
        linkId: "drug-use-1",
        text: "In the past year, how often have you used a recreational drug or used a prescription medication for non-medical reasons?",
        type: "choice",
        scorable: true,
        required: true,
        options: [
            {
                value: 0,
                display: "Never"
            },
            {
                value: 1,
                display: "Once or twice"
            },
            {
                value: 2,
                display: "Monthly"
            },
            {
                value: 3,
                display: "Weekly"
            },
            {
                value: 4,
                display: "Daily"
            }
        ]
    }
];
/**
 * UCLA Three-Item Loneliness Scale (Hughes et al. 2004). Public-domain / freely
 * available (PhenX). Three items, each 1–3; total 3–9.
 * TODO(D22): verify wording + credit line against the Hughes 2004 source before
 * launch (Clinical/Legal).
 */ const UCLA3_OPTIONS = [
    {
        value: 1,
        display: "Hardly ever"
    },
    {
        value: 2,
        display: "Some of the time"
    },
    {
        value: 3,
        display: "Often"
    }
];
const UCLA3_QUESTIONS = [
    {
        linkId: "ucla3-1",
        text: "How often do you feel that you lack companionship?",
        type: "choice",
        scorable: true,
        required: true,
        options: UCLA3_OPTIONS
    },
    {
        linkId: "ucla3-2",
        text: "How often do you feel left out?",
        type: "choice",
        scorable: true,
        required: true,
        options: UCLA3_OPTIONS
    },
    {
        linkId: "ucla3-3",
        text: "How often do you feel isolated from others?",
        type: "choice",
        scorable: true,
        required: true,
        options: UCLA3_OPTIONS
    }
];
/**
 * Patient Global Impression of Severity (PGI-S). Patient-report adaptation of
 * the public-domain CGI (Guy 1976). Single 7-point item; the answer is the
 * result, reported as a category (D7). This is the wording we lock under D7.
 * TODO(D22): confirm the locked wording with Clinical before launch.
 */ const PGI_S_QUESTIONS = [
    {
        linkId: "pgi-s-1",
        text: "Overall, how would you rate the severity of your current condition?",
        type: "choice",
        scorable: true,
        required: true,
        options: [
            {
                value: 1,
                display: "Not present"
            },
            {
                value: 2,
                display: "Very mild"
            },
            {
                value: 3,
                display: "Mild"
            },
            {
                value: 4,
                display: "Moderate"
            },
            {
                value: 5,
                display: "Moderately severe"
            },
            {
                value: 6,
                display: "Severe"
            },
            {
                value: 7,
                display: "Extremely severe"
            }
        ]
    }
];
/**
 * Patient Global Impression of Change (PGI-C). Patient-report adaptation of the
 * public-domain CGI (Guy 1976). Single 7-point item, ALWAYS compared with the
 * beginning of treatment (never the last visit). The answer is the result,
 * reported as a category (D7); "improved" is 1 or 2.
 * TODO(D22): confirm the locked wording with Clinical before launch.
 */ const PGI_C_QUESTIONS = [
    {
        linkId: "pgi-c-1",
        text: "Since the beginning of treatment, how would you describe the change (if any) in your overall mental health status?",
        type: "choice",
        scorable: true,
        required: true,
        options: [
            {
                value: 1,
                display: "Very much improved"
            },
            {
                value: 2,
                display: "Much improved"
            },
            {
                value: 3,
                display: "Minimally improved"
            },
            {
                value: 4,
                display: "No change"
            },
            {
                value: 5,
                display: "Minimally worse"
            },
            {
                value: 6,
                display: "Much worse"
            },
            {
                value: 7,
                display: "Very much worse"
            }
        ]
    }
];
/**
 * Work and Social Adjustment Scale (WSAS; Mundt et al. 2002). Electronic
 * administration is permitted with on-page Mundt et al. (2002) attribution (D2).
 * Verbatim item and anchor wording per NovoPsych / Mundt et al. (2002).
 */ /** Sentinel for the work-item N/A option (not impaired — unrelated to difficulties). */ const WSAS_WORK_NOT_APPLICABLE_VALUE = -1;
const WSAS_SCALE_OPTIONS = [
    {
        value: 0,
        display: "Not at all impaired"
    },
    {
        value: 1,
        display: "1"
    },
    {
        value: 2,
        display: "2"
    },
    {
        value: 3,
        display: "3"
    },
    {
        value: 4,
        display: "4"
    },
    {
        value: 5,
        display: "5"
    },
    {
        value: 6,
        display: "6"
    },
    {
        value: 7,
        display: "7"
    },
    {
        value: 8,
        display: "Very severely impaired"
    }
];
const WSAS_WORK_OPTIONS = [
    {
        value: WSAS_WORK_NOT_APPLICABLE_VALUE,
        display: "N/A — I am not working for reasons unrelated to my difficulties"
    },
    {
        value: 0,
        display: "Not at all impaired"
    },
    {
        value: 1,
        display: "1"
    },
    {
        value: 2,
        display: "2"
    },
    {
        value: 3,
        display: "3"
    },
    {
        value: 4,
        display: "4"
    },
    {
        value: 5,
        display: "5"
    },
    {
        value: 6,
        display: "6"
    },
    {
        value: 7,
        display: "7"
    },
    {
        value: 8,
        display: "Very severely impaired to the point I can't work"
    }
];
const WSAS_QUESTIONS = [
    {
        linkId: "wsas-1",
        text: "Because of my difficulties, my ability to work is impaired.",
        type: "choice",
        scorable: true,
        required: true,
        options: [
            ...WSAS_WORK_OPTIONS
        ]
    },
    {
        linkId: "wsas-2",
        text: "Because of my difficulties, my home management (cleaning, tidying, shopping, cooking, looking after home or children, paying bills) is impaired.",
        type: "choice",
        scorable: true,
        required: true,
        options: [
            ...WSAS_SCALE_OPTIONS
        ]
    },
    {
        linkId: "wsas-3",
        text: "Because of my difficulties, my social leisure activities (with other people, such as parties, bars, clubs, outings, visits, dating, home entertainment) are impaired.",
        type: "choice",
        scorable: true,
        required: true,
        options: [
            ...WSAS_SCALE_OPTIONS
        ]
    },
    {
        linkId: "wsas-4",
        text: "Because of my difficulties, my private leisure activities (done alone, such as reading, gardening, collecting, sewing, walking alone) are impaired.",
        type: "choice",
        scorable: true,
        required: true,
        options: [
            ...WSAS_SCALE_OPTIONS
        ]
    },
    {
        linkId: "wsas-5",
        text: "Because of my difficulties, my ability to form and maintain close relationships with others, including those I live with, is impaired.",
        type: "choice",
        scorable: true,
        required: true,
        options: [
            ...WSAS_SCALE_OPTIONS
        ]
    }
];
// PC-PTSD-5 Questions (Primary Care PTSD Screen for DSM-5)
const PC_PTSD_5_QUESTIONS = [
    // Screening (gate) question – required, not scored, and not numbered
    {
        linkId: "pc-ptsd-5-exposure",
        text: "Have you ever experienced this kind of event?",
        type: "choice",
        required: true,
        scorable: false,
        suppressNumbering: true,
        gateFor: [
            "pc-ptsd-5-1",
            "pc-ptsd-5-2",
            "pc-ptsd-5-3",
            "pc-ptsd-5-4",
            "pc-ptsd-5-5"
        ],
        options: [
            {
                value: 1,
                display: "Yes"
            },
            {
                value: 0,
                display: "No"
            }
        ]
    },
    {
        linkId: "pc-ptsd-5-1",
        text: "Had nightmares about the event(s) or thought about the event(s) when you did not want to?",
        type: "choice",
        scorable: true,
        required: true,
        options: [
            {
                value: 0,
                display: "No"
            },
            {
                value: 1,
                display: "Yes"
            }
        ]
    },
    {
        linkId: "pc-ptsd-5-2",
        text: "Tried hard not to think about the event(s) or went out of your way to avoid situations that reminded you of the event(s)?",
        type: "choice",
        scorable: true,
        required: true,
        options: [
            {
                value: 0,
                display: "No"
            },
            {
                value: 1,
                display: "Yes"
            }
        ]
    },
    {
        linkId: "pc-ptsd-5-3",
        text: "Been constantly on guard, watchful, or easily startled?",
        type: "choice",
        scorable: true,
        required: true,
        options: [
            {
                value: 0,
                display: "No"
            },
            {
                value: 1,
                display: "Yes"
            }
        ]
    },
    {
        linkId: "pc-ptsd-5-4",
        text: "Felt numb or detached from people, activities, or your surroundings?",
        type: "choice",
        scorable: true,
        required: true,
        options: [
            {
                value: 0,
                display: "No"
            },
            {
                value: 1,
                display: "Yes"
            }
        ]
    },
    {
        linkId: "pc-ptsd-5-5",
        text: "Felt guilty or unable to stop blaming yourself or others for the event(s) or any problems the event(s) may have caused?",
        type: "choice",
        scorable: true,
        required: true,
        options: [
            {
                value: 0,
                display: "No"
            },
            {
                value: 1,
                display: "Yes"
            }
        ]
    }
];
// PSC-17 Answer Options (3-point scale: Never, Sometimes, Often)
const PSC_17_FREQUENCY_OPTIONS = [
    {
        value: 0,
        coding: {
            code: "LA6270-8",
            display: "Never"
        }
    },
    {
        value: 1,
        coding: {
            code: "LA10082-8",
            display: "Sometimes"
        }
    },
    {
        value: 2,
        coding: {
            code: "LA10044-8",
            display: "Often"
        }
    }
];
// PSC-17 Questions (Pediatric Symptom Checklist-17)
// Parent-report screening for psychosocial dysfunction in children ages 4-18
// Subscales: Attention (1,2,3,4,7), Internalizing (5,6,9,10,11), Externalizing (8,12,13,14,15,16,17)
const PSC_17_QUESTIONS = [
    // Attention subscale items: 1, 2, 3, 4, 7
    {
        linkId: "psc17-1",
        text: "Fidgety, unable to sit still",
        type: "choice",
        scorable: true,
        required: true,
        options: PSC_17_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                display: opt.coding.display
            }))
    },
    {
        linkId: "psc17-2",
        text: "Acts as if driven by a motor",
        type: "choice",
        scorable: true,
        required: true,
        options: PSC_17_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                display: opt.coding.display
            }))
    },
    {
        linkId: "psc17-3",
        text: "Daydreams too much",
        type: "choice",
        scorable: true,
        required: true,
        options: PSC_17_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                display: opt.coding.display
            }))
    },
    {
        linkId: "psc17-4",
        text: "Distracted easily",
        type: "choice",
        scorable: true,
        required: true,
        options: PSC_17_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                display: opt.coding.display
            }))
    },
    // Internalizing subscale items: 5, 6, 9, 10, 11
    {
        linkId: "psc17-5",
        text: "Feels sad, unhappy",
        type: "choice",
        scorable: true,
        required: true,
        options: PSC_17_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                display: opt.coding.display
            }))
    },
    {
        linkId: "psc17-6",
        text: "Feels hopeless",
        type: "choice",
        scorable: true,
        required: true,
        options: PSC_17_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                display: opt.coding.display
            }))
    },
    // Attention subscale item: 7
    {
        linkId: "psc17-7",
        text: "Has trouble concentrating",
        type: "choice",
        scorable: true,
        required: true,
        options: PSC_17_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                display: opt.coding.display
            }))
    },
    // Externalizing subscale items: 8, 12, 13, 14, 15, 16, 17
    {
        linkId: "psc17-8",
        text: "Fights with other children",
        type: "choice",
        scorable: true,
        required: true,
        options: PSC_17_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                display: opt.coding.display
            }))
    },
    // Internalizing subscale items: 9, 10, 11
    {
        linkId: "psc17-9",
        text: "Is down on him or herself",
        type: "choice",
        scorable: true,
        required: true,
        options: PSC_17_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                display: opt.coding.display
            }))
    },
    {
        linkId: "psc17-10",
        text: "Blames self for problems",
        type: "choice",
        scorable: true,
        required: true,
        options: PSC_17_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                display: opt.coding.display
            }))
    },
    {
        linkId: "psc17-11",
        text: "Seems to be having less fun",
        type: "choice",
        scorable: true,
        required: true,
        options: PSC_17_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                display: opt.coding.display
            }))
    },
    // Externalizing subscale items: 12, 13, 14, 15, 16, 17
    {
        linkId: "psc17-12",
        text: "Does not listen to rules",
        type: "choice",
        scorable: true,
        required: true,
        options: PSC_17_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                display: opt.coding.display
            }))
    },
    {
        linkId: "psc17-13",
        text: "Does not understand other people's feelings",
        type: "choice",
        scorable: true,
        required: true,
        options: PSC_17_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                display: opt.coding.display
            }))
    },
    {
        linkId: "psc17-14",
        text: "Teases others",
        type: "choice",
        scorable: true,
        required: true,
        options: PSC_17_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                display: opt.coding.display
            }))
    },
    {
        linkId: "psc17-15",
        text: "Blames others for his or her troubles",
        type: "choice",
        scorable: true,
        required: true,
        options: PSC_17_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                display: opt.coding.display
            }))
    },
    {
        linkId: "psc17-16",
        text: "Takes things that do not belong to him or her",
        type: "choice",
        scorable: true,
        required: true,
        options: PSC_17_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                display: opt.coding.display
            }))
    },
    {
        linkId: "psc17-17",
        text: "Refuses to share",
        type: "choice",
        scorable: true,
        required: true,
        options: PSC_17_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                display: opt.coding.display
            }))
    }
];
// OCI-4 Questions (4-Item Obsessive-Compulsive Inventory)
const OCI_4_QUESTIONS = [
    {
        linkId: "oci-4-1",
        text: "I get upset if objects are not arranged properly.",
        type: "choice",
        scorable: true,
        required: true,
        options: [
            {
                value: 0,
                display: "Not at all"
            },
            {
                value: 1,
                display: "A little"
            },
            {
                value: 2,
                display: "Moderately"
            },
            {
                value: 3,
                display: "A lot"
            },
            {
                value: 4,
                display: "Extremely"
            }
        ]
    },
    {
        linkId: "oci-4-2",
        text: "I repeatedly check doors, windows, drawers, etc.",
        type: "choice",
        scorable: true,
        required: true,
        options: [
            {
                value: 0,
                display: "Not at all"
            },
            {
                value: 1,
                display: "A little"
            },
            {
                value: 2,
                display: "Moderately"
            },
            {
                value: 3,
                display: "A lot"
            },
            {
                value: 4,
                display: "Extremely"
            }
        ]
    },
    {
        linkId: "oci-4-3",
        text: "I sometimes have to wash or clean myself simply because I feel contaminated.",
        type: "choice",
        scorable: true,
        required: true,
        options: [
            {
                value: 0,
                display: "Not at all"
            },
            {
                value: 1,
                display: "A little"
            },
            {
                value: 2,
                display: "Moderately"
            },
            {
                value: 3,
                display: "A lot"
            },
            {
                value: 4,
                display: "Extremely"
            }
        ]
    },
    {
        linkId: "oci-4-4",
        text: "I frequently get nasty thoughts and have difficulty getting rid of them.",
        type: "choice",
        scorable: true,
        required: true,
        options: [
            {
                value: 0,
                display: "Not at all"
            },
            {
                value: 1,
                display: "A little"
            },
            {
                value: 2,
                display: "Moderately"
            },
            {
                value: 3,
                display: "A lot"
            },
            {
                value: 4,
                display: "Extremely"
            }
        ]
    }
];
// C-SSRS Questions (Columbia Suicide Severity Rating Scale - Screening Version)
const C_SSRS_QUESTIONS = [
    {
        linkId: "c-ssrs-ideation-1",
        text: "Have you wished you were dead or wished you could go to sleep and not wake up?",
        type: "choice",
        scorable: true,
        required: true,
        options: [
            {
                value: 0,
                display: "No"
            },
            {
                value: 1,
                display: "Yes"
            }
        ]
    },
    {
        linkId: "c-ssrs-ideation-2",
        text: "Have you actually had any thoughts of killing yourself?",
        type: "choice",
        scorable: true,
        required: true,
        // Certified branching: if NO to Q2, skip Q3-Q5 and go directly to behavior.
        gateFor: [
            "c-ssrs-ideation-3",
            "c-ssrs-ideation-4",
            "c-ssrs-ideation-5"
        ],
        options: [
            {
                value: 0,
                display: "No"
            },
            {
                value: 1,
                display: "Yes"
            }
        ]
    },
    {
        linkId: "c-ssrs-ideation-3",
        text: "Have you been thinking about how you might do this?",
        type: "choice",
        scorable: true,
        required: false,
        options: [
            {
                value: 0,
                display: "No"
            },
            {
                value: 1,
                display: "Yes"
            }
        ]
    },
    {
        linkId: "c-ssrs-ideation-4",
        text: "Have you had these thoughts and had some intention of acting on them?",
        type: "choice",
        scorable: true,
        required: false,
        options: [
            {
                value: 0,
                display: "No"
            },
            {
                value: 1,
                display: "Yes"
            }
        ]
    },
    {
        linkId: "c-ssrs-ideation-5",
        text: "Have you started to work out or worked out the details of how to kill yourself? Do you intend to carry out this plan?",
        type: "choice",
        scorable: true,
        required: false,
        options: [
            {
                value: 0,
                display: "No"
            },
            {
                value: 1,
                display: "Yes"
            }
        ]
    },
    // Behavior question (always asked), per the certified screener.
    {
        linkId: "c-ssrs-behavior-1",
        text: "Have you ever done anything, started to do anything, or prepared to do anything to end your life? Examples: Collected pills, obtained a gun, gave away valuables, wrote a will or suicide note, took out pills but didn't swallow any, held a gun but changed your mind or it was grabbed from your hand, went to the roof but didn't jump; or actually took pills, tried to shoot yourself, cut yourself, tried to hang yourself, etc.",
        type: "choice",
        scorable: true,
        required: true,
        // If YES, ask the recency follow-up; if NO, hide it.
        gateFor: [
            "c-ssrs-behavior-recency"
        ],
        options: [
            {
                value: 0,
                display: "No"
            },
            {
                value: 1,
                display: "Yes"
            }
        ]
    },
    {
        linkId: "c-ssrs-behavior-recency",
        text: "Was this within the past 3 months?",
        type: "choice",
        scorable: true,
        required: false,
        options: [
            {
                value: 0,
                display: "No"
            },
            {
                value: 1,
                display: "Yes"
            }
        ]
    }
];
// ASRS Questions (Adult ADHD Self-Report Scale v1.1)
const ASRS_QUESTIONS = [
    // Part A (Most predictive questions)
    {
        linkId: "asrs-1",
        text: "How often do you have trouble wrapping up the final details of a project, once the challenging parts have been done?",
        type: "choice",
        scorable: true,
        required: true,
        options: [
            {
                value: 0,
                display: "Never"
            },
            {
                value: 1,
                display: "Rarely"
            },
            {
                value: 2,
                display: "Sometimes"
            },
            {
                value: 3,
                display: "Often"
            },
            {
                value: 4,
                display: "Very Often"
            }
        ]
    },
    {
        linkId: "asrs-2",
        text: "How often do you have difficulty getting things in order when you have to do a task that requires organization?",
        type: "choice",
        scorable: true,
        required: true,
        options: [
            {
                value: 0,
                display: "Never"
            },
            {
                value: 1,
                display: "Rarely"
            },
            {
                value: 2,
                display: "Sometimes"
            },
            {
                value: 3,
                display: "Often"
            },
            {
                value: 4,
                display: "Very Often"
            }
        ]
    },
    {
        linkId: "asrs-3",
        text: "How often do you have problems remembering appointments or obligations?",
        type: "choice",
        scorable: true,
        required: true,
        options: [
            {
                value: 0,
                display: "Never"
            },
            {
                value: 1,
                display: "Rarely"
            },
            {
                value: 2,
                display: "Sometimes"
            },
            {
                value: 3,
                display: "Often"
            },
            {
                value: 4,
                display: "Very Often"
            }
        ]
    },
    {
        linkId: "asrs-4",
        text: "When you have a task that requires a lot of thought, how often do you avoid or delay getting started?",
        type: "choice",
        scorable: true,
        required: true,
        options: [
            {
                value: 0,
                display: "Never"
            },
            {
                value: 1,
                display: "Rarely"
            },
            {
                value: 2,
                display: "Sometimes"
            },
            {
                value: 3,
                display: "Often"
            },
            {
                value: 4,
                display: "Very Often"
            }
        ]
    },
    {
        linkId: "asrs-5",
        text: "How often do you fidget or squirm with your hands or feet when you have to sit down for a long time?",
        type: "choice",
        scorable: true,
        required: true,
        options: [
            {
                value: 0,
                display: "Never"
            },
            {
                value: 1,
                display: "Rarely"
            },
            {
                value: 2,
                display: "Sometimes"
            },
            {
                value: 3,
                display: "Often"
            },
            {
                value: 4,
                display: "Very Often"
            }
        ]
    },
    {
        linkId: "asrs-6",
        text: "How often do you feel overly active and compelled to do things, like you were driven by a motor?",
        type: "choice",
        scorable: true,
        required: true,
        options: [
            {
                value: 0,
                display: "Never"
            },
            {
                value: 1,
                display: "Rarely"
            },
            {
                value: 2,
                display: "Sometimes"
            },
            {
                value: 3,
                display: "Often"
            },
            {
                value: 4,
                display: "Very Often"
            }
        ]
    },
    // Part B (Additional symptoms)
    {
        linkId: "asrs-7",
        text: "How often do you make careless mistakes when you have to work on a boring or difficult project?",
        type: "choice",
        scorable: true,
        required: true,
        options: [
            {
                value: 0,
                display: "Never"
            },
            {
                value: 1,
                display: "Rarely"
            },
            {
                value: 2,
                display: "Sometimes"
            },
            {
                value: 3,
                display: "Often"
            },
            {
                value: 4,
                display: "Very Often"
            }
        ]
    },
    {
        linkId: "asrs-8",
        text: "How often do you have difficulty keeping your attention when you are doing boring or repetitive work?",
        type: "choice",
        scorable: true,
        required: true,
        options: [
            {
                value: 0,
                display: "Never"
            },
            {
                value: 1,
                display: "Rarely"
            },
            {
                value: 2,
                display: "Sometimes"
            },
            {
                value: 3,
                display: "Often"
            },
            {
                value: 4,
                display: "Very Often"
            }
        ]
    },
    {
        linkId: "asrs-9",
        text: "How often do you have difficulty concentrating on what people say to you, even when they are speaking to you directly?",
        type: "choice",
        scorable: true,
        required: true,
        options: [
            {
                value: 0,
                display: "Never"
            },
            {
                value: 1,
                display: "Rarely"
            },
            {
                value: 2,
                display: "Sometimes"
            },
            {
                value: 3,
                display: "Often"
            },
            {
                value: 4,
                display: "Very Often"
            }
        ]
    },
    {
        linkId: "asrs-10",
        text: "How often do you misplace or have difficulty finding things at home or at work?",
        type: "choice",
        scorable: true,
        required: true,
        options: [
            {
                value: 0,
                display: "Never"
            },
            {
                value: 1,
                display: "Rarely"
            },
            {
                value: 2,
                display: "Sometimes"
            },
            {
                value: 3,
                display: "Often"
            },
            {
                value: 4,
                display: "Very Often"
            }
        ]
    },
    {
        linkId: "asrs-11",
        text: "How often are you distracted by activity or noise around you?",
        type: "choice",
        scorable: true,
        required: true,
        options: [
            {
                value: 0,
                display: "Never"
            },
            {
                value: 1,
                display: "Rarely"
            },
            {
                value: 2,
                display: "Sometimes"
            },
            {
                value: 3,
                display: "Often"
            },
            {
                value: 4,
                display: "Very Often"
            }
        ]
    },
    {
        linkId: "asrs-12",
        text: "How often do you leave your seat in meetings or other situations in which you are expected to remain seated?",
        type: "choice",
        scorable: true,
        required: true,
        options: [
            {
                value: 0,
                display: "Never"
            },
            {
                value: 1,
                display: "Rarely"
            },
            {
                value: 2,
                display: "Sometimes"
            },
            {
                value: 3,
                display: "Often"
            },
            {
                value: 4,
                display: "Very Often"
            }
        ]
    },
    {
        linkId: "asrs-13",
        text: "How often do you feel restless or fidgety?",
        type: "choice",
        scorable: true,
        required: true,
        options: [
            {
                value: 0,
                display: "Never"
            },
            {
                value: 1,
                display: "Rarely"
            },
            {
                value: 2,
                display: "Sometimes"
            },
            {
                value: 3,
                display: "Often"
            },
            {
                value: 4,
                display: "Very Often"
            }
        ]
    },
    {
        linkId: "asrs-14",
        text: "How often do you have difficulty unwinding and relaxing when you have time to yourself?",
        type: "choice",
        scorable: true,
        required: true,
        options: [
            {
                value: 0,
                display: "Never"
            },
            {
                value: 1,
                display: "Rarely"
            },
            {
                value: 2,
                display: "Sometimes"
            },
            {
                value: 3,
                display: "Often"
            },
            {
                value: 4,
                display: "Very Often"
            }
        ]
    },
    {
        linkId: "asrs-15",
        text: "How often do you find yourself talking too much when you are in social situations?",
        type: "choice",
        scorable: true,
        required: true,
        options: [
            {
                value: 0,
                display: "Never"
            },
            {
                value: 1,
                display: "Rarely"
            },
            {
                value: 2,
                display: "Sometimes"
            },
            {
                value: 3,
                display: "Often"
            },
            {
                value: 4,
                display: "Very Often"
            }
        ]
    },
    {
        linkId: "asrs-16",
        text: "When you're in a conversation, how often do you find yourself finishing the sentences of the people you are talking to, before they can finish them themselves?",
        type: "choice",
        scorable: true,
        required: true,
        options: [
            {
                value: 0,
                display: "Never"
            },
            {
                value: 1,
                display: "Rarely"
            },
            {
                value: 2,
                display: "Sometimes"
            },
            {
                value: 3,
                display: "Often"
            },
            {
                value: 4,
                display: "Very Often"
            }
        ]
    },
    {
        linkId: "asrs-17",
        text: "How often do you have difficulty waiting your turn in situations when turn taking is required?",
        type: "choice",
        scorable: true,
        required: true,
        options: [
            {
                value: 0,
                display: "Never"
            },
            {
                value: 1,
                display: "Rarely"
            },
            {
                value: 2,
                display: "Sometimes"
            },
            {
                value: 3,
                display: "Often"
            },
            {
                value: 4,
                display: "Very Often"
            }
        ]
    },
    {
        linkId: "asrs-18",
        text: "How often do you interrupt others when they are busy?",
        type: "choice",
        scorable: true,
        required: true,
        options: [
            {
                value: 0,
                display: "Never"
            },
            {
                value: 1,
                display: "Rarely"
            },
            {
                value: 2,
                display: "Sometimes"
            },
            {
                value: 3,
                display: "Often"
            },
            {
                value: 4,
                display: "Very Often"
            }
        ]
    }
];
// SBIRT-ED Questions (Eating Disorder Screener)
const SBIRT_ED_QUESTIONS = [
    {
        linkId: "sbirt-ed-1",
        text: "Do you make yourself throw up because you feel uncomfortably full?",
        type: "choice",
        scorable: true,
        required: true,
        options: [
            {
                value: 0,
                display: "No"
            },
            {
                value: 1,
                display: "Yes"
            }
        ]
    },
    {
        linkId: "sbirt-ed-2",
        text: "Do you worry you have lost control over how much you eat?",
        type: "choice",
        scorable: true,
        required: true,
        options: [
            {
                value: 0,
                display: "No"
            },
            {
                value: 1,
                display: "Yes"
            }
        ]
    },
    {
        linkId: "sbirt-ed-3",
        text: "Have you recently lost more than 15 pounds in a 3-month period?",
        type: "choice",
        scorable: true,
        required: true,
        options: [
            {
                value: 0,
                display: "No"
            },
            {
                value: 1,
                display: "Yes"
            }
        ]
    },
    {
        linkId: "sbirt-ed-4",
        text: "Do you think you are fat even though others say you are too thin?",
        type: "choice",
        scorable: true,
        required: true,
        options: [
            {
                value: 0,
                display: "No"
            },
            {
                value: 1,
                display: "Yes"
            }
        ]
    },
    {
        linkId: "sbirt-ed-5",
        text: "Would you say that food dominates your life?",
        type: "choice",
        scorable: true,
        required: true,
        options: [
            {
                value: 0,
                display: "No"
            },
            {
                value: 1,
                display: "Yes"
            }
        ]
    }
];
// WPAI Questions (Work Productivity and Activity Impairment Questionnaire)
const WPAI_QUESTIONS = [
    {
        linkId: "wpai-1",
        text: "Are you currently employed (working for pay)?",
        type: "choice",
        scorable: true,
        required: true,
        options: [
            {
                value: 0,
                display: "No"
            },
            {
                value: 1,
                display: "Yes"
            }
        ]
    },
    {
        linkId: "wpai-2",
        text: "During the past seven days, how many hours did you miss from work because of problems associated with your condition? (Include hours you missed on sick days, times you went in late, left early, etc.)",
        type: "choice",
        scorable: true,
        required: true,
        options: [
            {
                value: 0,
                display: "0 hours"
            },
            {
                value: 1,
                display: "1-8 hours"
            },
            {
                value: 2,
                display: "9-16 hours"
            },
            {
                value: 3,
                display: "17-24 hours"
            },
            {
                value: 4,
                display: "25-32 hours"
            },
            {
                value: 5,
                display: "33-40 hours"
            },
            {
                value: 6,
                display: "More than 40 hours"
            }
        ]
    },
    {
        linkId: "wpai-3",
        text: "During the past seven days, how many hours did you miss from work because of any other reason, such as vacation, holidays, time off to participate in this study?",
        type: "choice",
        scorable: true,
        required: true,
        options: [
            {
                value: 0,
                display: "0 hours"
            },
            {
                value: 1,
                display: "1-8 hours"
            },
            {
                value: 2,
                display: "9-16 hours"
            },
            {
                value: 3,
                display: "17-24 hours"
            },
            {
                value: 4,
                display: "25-32 hours"
            },
            {
                value: 5,
                display: "33-40 hours"
            },
            {
                value: 6,
                display: "More than 40 hours"
            }
        ]
    },
    {
        linkId: "wpai-4",
        text: "During the past seven days, how many hours did you actually work?",
        type: "choice",
        scorable: true,
        required: true,
        options: [
            {
                value: 0,
                display: "0 hours"
            },
            {
                value: 1,
                display: "1-10 hours"
            },
            {
                value: 2,
                display: "11-20 hours"
            },
            {
                value: 3,
                display: "21-30 hours"
            },
            {
                value: 4,
                display: "31-40 hours"
            },
            {
                value: 5,
                display: "More than 40 hours"
            }
        ]
    },
    {
        linkId: "wpai-5",
        text: "During the past seven days, how much did your condition affect your productivity while you were working? (0 = no effect, 10 = completely prevented me from working)",
        type: "choice",
        scorable: true,
        required: true,
        options: [
            {
                value: 0,
                display: "0 - No effect on my work"
            },
            {
                value: 1,
                display: "1"
            },
            {
                value: 2,
                display: "2"
            },
            {
                value: 3,
                display: "3"
            },
            {
                value: 4,
                display: "4"
            },
            {
                value: 5,
                display: "5"
            },
            {
                value: 6,
                display: "6"
            },
            {
                value: 7,
                display: "7"
            },
            {
                value: 8,
                display: "8"
            },
            {
                value: 9,
                display: "9"
            },
            {
                value: 10,
                display: "10 - Completely prevented me from working"
            }
        ]
    },
    {
        linkId: "wpai-6",
        text: "During the past seven days, how much did your condition affect your ability to do your regular daily activities, other than work at a job? (0 = no effect, 10 = completely prevented me from doing my daily activities)",
        type: "choice",
        scorable: true,
        required: true,
        options: [
            {
                value: 0,
                display: "0 - No effect on my daily activities"
            },
            {
                value: 1,
                display: "1"
            },
            {
                value: 2,
                display: "2"
            },
            {
                value: 3,
                display: "3"
            },
            {
                value: 4,
                display: "4"
            },
            {
                value: 5,
                display: "5"
            },
            {
                value: 6,
                display: "6"
            },
            {
                value: 7,
                display: "7"
            },
            {
                value: 8,
                display: "8"
            },
            {
                value: 9,
                display: "9"
            },
            {
                value: 10,
                display: "10 - Completely prevented me from doing my daily activities"
            }
        ]
    }
];
// TAPS-1 Questions (Tobacco, Alcohol, Prescription medication, and other Substance use - Screener)
const TAPS_1_QUESTIONS = [
    {
        linkId: "taps-1-1",
        text: "In the PAST 12 MONTHS, how often have you used any tobacco product (for example, cigarettes, e-cigarettes, cigars, pipes, or smokeless tobacco)?",
        type: "choice",
        scorable: true,
        required: true,
        options: [
            {
                value: 0,
                display: "Never"
            },
            {
                value: 1,
                display: "Less Than Monthly"
            },
            {
                value: 2,
                display: "Monthly"
            },
            {
                value: 3,
                display: "Weekly"
            },
            {
                value: 4,
                display: "Daily or Almost Daily"
            }
        ]
    },
    {
        linkId: "taps-1-2",
        text: "In the PAST 12 MONTHS, how often have you had 5 or more drinks (men) / 4 or more drinks (women) containing alcohol in one day? One standard drink is about 1 small glass of wine (5 oz), 1 beer (12 oz), or 1 single shot of liquor.",
        type: "choice",
        scorable: true,
        required: true,
        options: [
            {
                value: 0,
                display: "Never"
            },
            {
                value: 1,
                display: "Less Than Monthly"
            },
            {
                value: 2,
                display: "Monthly"
            },
            {
                value: 3,
                display: "Weekly"
            },
            {
                value: 4,
                display: "Daily or Almost Daily"
            }
        ]
    },
    // Removed taps-1-3 (combined into taps-1-2)
    {
        linkId: "taps-1-4",
        text: "In the PAST 12 MONTHS, how often have you used any drugs including marijuana, cocaine or crack, heroin, methamphetamine (crystal meth), hallucinogens, ecstasy/MDMA?",
        type: "choice",
        scorable: true,
        required: true,
        options: [
            {
                value: 0,
                display: "Never"
            },
            {
                value: 1,
                display: "Less Than Monthly"
            },
            {
                value: 2,
                display: "Monthly"
            },
            {
                value: 3,
                display: "Weekly"
            },
            {
                value: 4,
                display: "Daily or Almost Daily"
            }
        ]
    },
    {
        linkId: "taps-1-5",
        text: "In the PAST 12 MONTHS, how often have you used any prescription medications just for the feeling, more than prescribed or that were not prescribed for you? Prescription medications that may be used this way include: Opiate pain relievers (for example, OxyContin, Vicodin, Percocet, Methadone) Medications for anxiety or sleeping (for example, Xanax, Ativan, Klonopin) Medications for ADHD (for example, Adderall or Ritalin)",
        type: "choice",
        scorable: true,
        required: true,
        options: [
            {
                value: 0,
                display: "Never"
            },
            {
                value: 1,
                display: "Less Than Monthly"
            },
            {
                value: 2,
                display: "Monthly"
            },
            {
                value: 3,
                display: "Weekly"
            },
            {
                value: 4,
                display: "Daily or Almost Daily"
            }
        ]
    }
];
// TAPS-2 Questions (Tobacco, Alcohol, Prescription medication, and other Substance use - Assessment)
const TAPS_2_QUESTIONS = [
    // 1. Tobacco
    {
        linkId: "taps-2-1",
        text: "In the PAST 3 MONTHS, did you smoke a cigarette containing tobacco?",
        type: "choice",
        scorable: true,
        required: true,
        options: [
            {
                value: 0,
                display: "No"
            },
            {
                value: 1,
                display: "Yes"
            }
        ]
    },
    {
        linkId: "taps-2-1a",
        text: "In the PAST 3 MONTHS, did you usually smoke more than 10 cigarettes each day?",
        type: "choice",
        scorable: true,
        required: false,
        options: [
            {
                value: 0,
                display: "No"
            },
            {
                value: 1,
                display: "Yes"
            }
        ]
    },
    {
        linkId: "taps-2-1b",
        text: "In the PAST 3 MONTHS, did you usually smoke within 30 minutes after waking?",
        type: "choice",
        scorable: true,
        required: false,
        options: [
            {
                value: 0,
                display: "No"
            },
            {
                value: 1,
                display: "Yes"
            }
        ]
    },
    // 2. Alcohol
    {
        linkId: "taps-2-2",
        text: "In the PAST 3 MONTHS, did you have a drink containing alcohol?",
        type: "choice",
        scorable: true,
        required: true,
        options: [
            {
                value: 0,
                display: "No"
            },
            {
                value: 1,
                display: "Yes"
            }
        ]
    },
    {
        linkId: "taps-2-2a",
        text: "In the PAST 3 MONTHS, did you have 4 or more drinks containing alcohol in a day? (Note: This question should only be answered by females.)",
        type: "choice",
        scorable: true,
        required: false,
        options: [
            {
                value: 0,
                display: "No"
            },
            {
                value: 1,
                display: "Yes"
            }
        ]
    },
    {
        linkId: "taps-2-2b",
        text: "In the PAST 3 MONTHS, did you have 5 or more drinks containing alcohol in a day? (Note: This question should only be answered by males.)",
        type: "choice",
        scorable: true,
        required: false,
        options: [
            {
                value: 0,
                display: "No"
            },
            {
                value: 1,
                display: "Yes"
            }
        ]
    },
    {
        linkId: "taps-2-2c",
        text: "In the PAST 3 MONTHS, have you tried and failed to control, cut down or stop drinking?",
        type: "choice",
        scorable: true,
        required: false,
        options: [
            {
                value: 0,
                display: "No"
            },
            {
                value: 1,
                display: "Yes"
            }
        ]
    },
    {
        linkId: "taps-2-2d",
        text: "In the PAST 3 MONTHS, has anyone expressed concern about your drinking?",
        type: "choice",
        scorable: true,
        required: false,
        options: [
            {
                value: 0,
                display: "No"
            },
            {
                value: 1,
                display: "Yes"
            }
        ]
    },
    // 3. Marijuana
    {
        linkId: "taps-2-3",
        text: "In the PAST 3 MONTHS, did you use marijuana (hash, weed)?",
        type: "choice",
        scorable: true,
        required: true,
        options: [
            {
                value: 0,
                display: "No"
            },
            {
                value: 1,
                display: "Yes"
            }
        ]
    },
    {
        linkId: "taps-2-3a",
        text: "In the PAST 3 MONTHS, have you had a strong desire or urge to use marijuana at least once a week or more often?",
        type: "choice",
        scorable: true,
        required: false,
        options: [
            {
                value: 0,
                display: "No"
            },
            {
                value: 1,
                display: "Yes"
            }
        ]
    },
    {
        linkId: "taps-2-3b",
        text: "In the PAST 3 MONTHS, has anyone expressed concern about your use of marijuana?",
        type: "choice",
        scorable: true,
        required: false,
        options: [
            {
                value: 0,
                display: "No"
            },
            {
                value: 1,
                display: "Yes"
            }
        ]
    },
    // 4. Cocaine/Crack/Methamphetamine
    {
        linkId: "taps-2-4",
        text: "In the PAST 3 MONTHS, did you use cocaine, crack, or methamphetamine (crystal meth)?",
        type: "choice",
        scorable: true,
        required: true,
        options: [
            {
                value: 0,
                display: "No"
            },
            {
                value: 1,
                display: "Yes"
            }
        ]
    },
    {
        linkId: "taps-2-4a",
        text: "In the PAST 3 MONTHS, did you use cocaine, crack, or methamphetamine (crystal meth) at least once a week or more often?",
        type: "choice",
        scorable: true,
        required: false,
        options: [
            {
                value: 0,
                display: "No"
            },
            {
                value: 1,
                display: "Yes"
            }
        ]
    },
    {
        linkId: "taps-2-4b",
        text: "In the PAST 3 MONTHS, has anyone expressed concern about your use of cocaine, crack, or methamphetamine (crystal meth)?",
        type: "choice",
        scorable: true,
        required: false,
        options: [
            {
                value: 0,
                display: "No"
            },
            {
                value: 1,
                display: "Yes"
            }
        ]
    },
    // 5. Heroin
    {
        linkId: "taps-2-5",
        text: "In the PAST 3 MONTHS, did you use heroin?",
        type: "choice",
        scorable: true,
        required: true,
        options: [
            {
                value: 0,
                display: "No"
            },
            {
                value: 1,
                display: "Yes"
            }
        ]
    },
    {
        linkId: "taps-2-5a",
        text: "In the PAST 3 MONTHS, have you tried and failed to control, cut down or stop using heroin?",
        type: "choice",
        scorable: true,
        required: false,
        options: [
            {
                value: 0,
                display: "No"
            },
            {
                value: 1,
                display: "Yes"
            }
        ]
    },
    {
        linkId: "taps-2-5b",
        text: "In the PAST 3 MONTHS, has anyone expressed concern about your use of heroin?",
        type: "choice",
        scorable: true,
        required: false,
        options: [
            {
                value: 0,
                display: "No"
            },
            {
                value: 1,
                display: "Yes"
            }
        ]
    },
    // 6. Prescription Opiate Pain Reliever
    {
        linkId: "taps-2-6",
        text: "In the PAST 3 MONTHS, did you use a prescription opiate pain reliever (for example, Percocet, Vicodin) not as prescribed or that was not prescribed for you?",
        type: "choice",
        scorable: true,
        required: true,
        options: [
            {
                value: 0,
                display: "No"
            },
            {
                value: 1,
                display: "Yes"
            }
        ]
    },
    {
        linkId: "taps-2-6a",
        text: "In the PAST 3 MONTHS, have you tried and failed to control, cut down or stop using an opiate pain reliever?",
        type: "choice",
        scorable: true,
        required: false,
        options: [
            {
                value: 0,
                display: "No"
            },
            {
                value: 1,
                display: "Yes"
            }
        ]
    },
    {
        linkId: "taps-2-6b",
        text: "In the PAST 3 MONTHS, has anyone expressed concern about your use of an opiate pain reliever?",
        type: "choice",
        scorable: true,
        required: false,
        options: [
            {
                value: 0,
                display: "No"
            },
            {
                value: 1,
                display: "Yes"
            }
        ]
    },
    // 7. Medication for Anxiety or Sleep
    {
        linkId: "taps-2-7",
        text: "In the PAST 3 MONTHS, did you use a medication for anxiety or sleep (for example, Xanax, Ativan, or Klonopin) not as prescribed or that was not prescribed for you?",
        type: "choice",
        scorable: true,
        required: true,
        options: [
            {
                value: 0,
                display: "No"
            },
            {
                value: 1,
                display: "Yes"
            }
        ]
    },
    {
        linkId: "taps-2-7a",
        text: "In the PAST 3 MONTHS, have you had a strong desire or urge to use medications for anxiety or sleep at least once a week or more often?",
        type: "choice",
        scorable: true,
        required: false,
        options: [
            {
                value: 0,
                display: "No"
            },
            {
                value: 1,
                display: "Yes"
            }
        ]
    },
    {
        linkId: "taps-2-7b",
        text: "In the PAST 3 MONTHS, has anyone expressed concern about your use of medication for anxiety or sleep?",
        type: "choice",
        scorable: true,
        required: false,
        options: [
            {
                value: 0,
                display: "No"
            },
            {
                value: 1,
                display: "Yes"
            }
        ]
    },
    // 8. Medication for ADHD
    {
        linkId: "taps-2-8",
        text: "In the PAST 3 MONTHS, did you use a medication for ADHD (for example, Adderall, Ritalin) not as prescribed or that was not prescribed for you?",
        type: "choice",
        scorable: true,
        required: true,
        options: [
            {
                value: 0,
                display: "No"
            },
            {
                value: 1,
                display: "Yes"
            }
        ]
    },
    {
        linkId: "taps-2-8a",
        text: "In the PAST 3 MONTHS, did you use a medication for ADHD (for example, Adderall, Ritalin) at least once a week or more often?",
        type: "choice",
        scorable: true,
        required: false,
        options: [
            {
                value: 0,
                display: "No"
            },
            {
                value: 1,
                display: "Yes"
            }
        ]
    },
    {
        linkId: "taps-2-8b",
        text: "In the PAST 3 MONTHS, has anyone expressed concern about your use of a medication for ADHD (for example, Adderall or Ritalin)?",
        type: "choice",
        scorable: true,
        required: false,
        options: [
            {
                value: 0,
                display: "No"
            },
            {
                value: 1,
                display: "Yes"
            }
        ]
    },
    // 9. Other Illegal or Recreational Drugs
    {
        linkId: "taps-2-9",
        text: "In the PAST 3 MONTHS, did you use any other illegal or recreational drug (for example, ecstasy/molly, GHB, poppers, LSD, mushrooms, special K, bath salts, synthetic marijuana ('spice'), whip-its, etc.)?",
        type: "choice",
        scorable: true,
        required: true,
        options: [
            {
                value: 0,
                display: "No"
            },
            {
                value: 1,
                display: "Yes"
            }
        ]
    }
];
// PRAPARE Questions (Protocol for Responding to and Assessing Patient Assets, Risks, and Experiences)
/**
 * @deprecated
 */ const PRAPARE_QUESTIONS = [
    // Personal Characteristics
    {
        linkId: "prapare-1",
        text: "Are you Hispanic or Latino?",
        type: "choice",
        scorable: true,
        required: true,
        options: [
            {
                value: 0,
                display: "No"
            },
            {
                value: 1,
                display: "Yes"
            },
            {
                value: 2,
                display: "I choose not to answer this question"
            }
        ]
    },
    {
        linkId: "prapare-2",
        text: "Which race(s) are you? Check all that apply",
        type: "choice",
        scorable: true,
        required: true,
        options: [
            {
                value: 1,
                display: "Asian"
            },
            {
                value: 2,
                display: "Native Hawaiian"
            },
            {
                value: 3,
                display: "Pacific Islander"
            },
            {
                value: 4,
                display: "Black/African American"
            },
            {
                value: 5,
                display: "White"
            },
            {
                value: 6,
                display: "American Indian/Alaskan Native"
            },
            {
                value: 7,
                display: "Other"
            },
            {
                value: 8,
                display: "I choose not to answer this question"
            }
        ]
    },
    {
        linkId: "prapare-3",
        text: "At any point in the past 2 years, has season or migrant farm work been your or your family's main source of income?",
        type: "choice",
        scorable: true,
        required: true,
        options: [
            {
                value: 0,
                display: "No"
            },
            {
                value: 1,
                display: "Yes"
            },
            {
                value: 2,
                display: "I choose not to answer this question"
            }
        ]
    },
    {
        linkId: "prapare-4",
        text: "Have you been discharged from the armed forces of the United States?",
        type: "choice",
        scorable: true,
        required: true,
        options: [
            {
                value: 0,
                display: "No"
            },
            {
                value: 1,
                display: "Yes"
            },
            {
                value: 2,
                display: "I choose not to answer this question"
            }
        ]
    },
    {
        linkId: "prapare-5",
        text: "What language are you most comfortable speaking?",
        type: "choice",
        scorable: true,
        required: true,
        options: [
            {
                value: 1,
                display: "English"
            },
            {
                value: 2,
                display: "Spanish"
            },
            {
                value: 3,
                display: "Other"
            },
            {
                value: 4,
                display: "I choose not to answer this question"
            }
        ]
    },
    // Family & Home
    {
        linkId: "prapare-6",
        text: "How many family members, including yourself, do you currently live with?",
        type: "choice",
        scorable: true,
        required: true,
        options: [
            {
                value: 1,
                display: "1 (just me)"
            },
            {
                value: 2,
                display: "2"
            },
            {
                value: 3,
                display: "3"
            },
            {
                value: 4,
                display: "4"
            },
            {
                value: 5,
                display: "5"
            },
            {
                value: 6,
                display: "6 or more"
            },
            {
                value: 7,
                display: "I choose not to answer this question"
            }
        ]
    },
    {
        linkId: "prapare-7",
        text: "What is your housing situation today?",
        type: "choice",
        scorable: true,
        required: true,
        options: [
            {
                value: 1,
                display: "I have housing"
            },
            {
                value: 2,
                display: "I do not have housing (staying with others, in a hotel, in a shelter, living outside on the street, on a beach, in a car, or in a park)"
            },
            {
                value: 3,
                display: "I choose not to answer this question"
            }
        ]
    },
    {
        linkId: "prapare-8",
        text: "Are you worried about losing your housing?",
        type: "choice",
        scorable: true,
        required: true,
        options: [
            {
                value: 0,
                display: "No"
            },
            {
                value: 1,
                display: "Yes"
            },
            {
                value: 2,
                display: "I choose not to answer this question"
            }
        ]
    },
    // Money & Resources
    {
        linkId: "prapare-10",
        text: "What is the highest level of school that you have finished?",
        type: "choice",
        scorable: true,
        required: true,
        options: [
            {
                value: 1,
                display: "Less than high school degree"
            },
            {
                value: 2,
                display: "High school diploma or GED"
            },
            {
                value: 3,
                display: "More than high school"
            },
            {
                value: 4,
                display: "I choose not to answer this question"
            }
        ]
    },
    {
        linkId: "prapare-11",
        text: "What is your current work situation?",
        type: "choice",
        scorable: true,
        required: true,
        options: [
            {
                value: 1,
                display: "Unemployed"
            },
            {
                value: 2,
                display: "Part-time or temporary work"
            },
            {
                value: 3,
                display: "Full-time work"
            },
            {
                value: 4,
                display: "Otherwise unemployed but not seeking work (ex: student, retired, disabled, unpaid primary care giver)"
            },
            {
                value: 5,
                display: "I choose not to answer this question"
            }
        ]
    },
    {
        linkId: "prapare-12",
        text: "What is your main insurance?",
        type: "choice",
        scorable: true,
        required: true,
        options: [
            {
                value: 1,
                display: "None/uninsured"
            },
            {
                value: 2,
                display: "Medicaid"
            },
            {
                value: 3,
                display: "CHIP Medicaid"
            },
            {
                value: 4,
                display: "Medicare"
            },
            {
                value: 5,
                display: "Other public insurance (not CHIP)"
            },
            {
                value: 6,
                display: "Other Public Insurance (CHIP)"
            },
            {
                value: 7,
                display: "Private Insurance"
            },
            {
                value: 8,
                display: "I choose not to answer this question"
            }
        ]
    },
    {
        linkId: "prapare-13",
        text: "During the past year, what was the total combined income for you and the family members you live with? This information will help us determine if you are eligible for any benefits.",
        type: "choice",
        scorable: true,
        required: true,
        options: [
            {
                value: 1,
                display: "Less than $10,000"
            },
            {
                value: 2,
                display: "$10,000 - $19,999"
            },
            {
                value: 3,
                display: "$20,000 - $29,999"
            },
            {
                value: 4,
                display: "$30,000 - $39,999"
            },
            {
                value: 5,
                display: "$40,000 - $49,999"
            },
            {
                value: 6,
                display: "$50,000 or more"
            },
            {
                value: 7,
                display: "I choose not to answer this question"
            }
        ]
    },
    {
        linkId: "prapare-14",
        text: "In the past year, have you or any family members you live with been unable to get any of the following when it was really needed? (Check all that apply: Food, Utilities, Medicine or Any Health Care, Phone, Clothing, Child Care, Other)",
        type: "choice",
        scorable: true,
        required: true,
        options: [
            {
                value: 0,
                display: "No, we have been able to get everything we needed"
            },
            {
                value: 1,
                display: "Yes, we have been unable to get some things we needed"
            },
            {
                value: 2,
                display: "I choose not to answer this question"
            }
        ]
    },
    {
        linkId: "prapare-15",
        text: "Has lack of transportation kept you from medical appointments, meetings, work, or from getting things needed for daily living?",
        type: "choice",
        scorable: true,
        required: true,
        options: [
            {
                value: 0,
                display: "No"
            },
            {
                value: 1,
                display: "Yes, it has kept me from medical appointments"
            },
            {
                value: 2,
                display: "Yes, it has kept me from non-medical meetings, appointments, work, or from getting things that I need"
            },
            {
                value: 3,
                display: "I choose not to answer this question"
            }
        ]
    },
    // Social and Emotional Health
    {
        linkId: "prapare-16",
        text: "How often do you see or talk to people that you care about and feel close to? (For example: talking to friends on the phone, visiting friends or family, going to church or club meetings)",
        type: "choice",
        scorable: true,
        required: true,
        options: [
            {
                value: 1,
                display: "Less than once a week"
            },
            {
                value: 2,
                display: "1 or 2 times a week"
            },
            {
                value: 3,
                display: "3 to 5 times a week"
            },
            {
                value: 4,
                display: "5 or more times a week"
            },
            {
                value: 5,
                display: "I choose not to answer this question"
            }
        ]
    },
    {
        linkId: "prapare-17",
        text: "Stress is when someone feels tense, nervous, anxious, or can't sleep at night because their mind is troubled. How stressed are you?",
        type: "choice",
        scorable: true,
        required: true,
        options: [
            {
                value: 1,
                display: "Not at all"
            },
            {
                value: 2,
                display: "A little bit"
            },
            {
                value: 3,
                display: "Somewhat"
            },
            {
                value: 4,
                display: "Quite a bit"
            },
            {
                value: 5,
                display: "Very much"
            },
            {
                value: 6,
                display: "I choose not to answer this question"
            }
        ]
    },
    // Optional Additional Questions
    {
        linkId: "prapare-18",
        text: "In the past year, have you spent more than 2 nights in a row in a jail, prison, detention center, or juvenile correctional facility?",
        type: "choice",
        scorable: true,
        required: false,
        options: [
            {
                value: 0,
                display: "No"
            },
            {
                value: 1,
                display: "Yes"
            },
            {
                value: 2,
                display: "I choose not to answer this question"
            }
        ]
    },
    {
        linkId: "prapare-19",
        text: "Are you a refugee?",
        type: "choice",
        scorable: true,
        required: false,
        options: [
            {
                value: 0,
                display: "No"
            },
            {
                value: 1,
                display: "Yes"
            },
            {
                value: 2,
                display: "I choose not to answer this question"
            }
        ]
    },
    {
        linkId: "prapare-20",
        text: "Do you feel physically and emotionally safe where you currently live?",
        type: "choice",
        scorable: true,
        required: false,
        options: [
            {
                value: 1,
                display: "Yes"
            },
            {
                value: 0,
                display: "No"
            },
            {
                value: 2,
                display: "Unsure"
            },
            {
                value: 3,
                display: "I choose not to answer this question"
            }
        ]
    },
    {
        linkId: "prapare-21",
        text: "In the past year, have you been afraid of your partner or ex-partner?",
        type: "choice",
        scorable: true,
        required: false,
        options: [
            {
                value: 0,
                display: "No"
            },
            {
                value: 1,
                display: "Yes"
            },
            {
                value: 2,
                display: "Unsure"
            },
            {
                value: 3,
                display: "I have not had a partner in the past year"
            },
            {
                value: 4,
                display: "I choose not to answer this question"
            }
        ]
    }
];
// Safety Plan Questions
//
// Benchmark's own collaborative safety plan template. Clinician-administered,
// unscored, and available to all users. Content is authored by Benchmark and is
// not derived from any third-party (trademarked) safety-planning instrument.
const SAFETY_PLAN_QUESTIONS = [
    // Warning signs
    {
        linkId: "safety-plan-warning-signs",
        text: "Warning signs that I am feeling worse",
        type: "group",
        instruction: "Thoughts, feelings, body sensations, situations, or behaviors that signal things are getting harder.",
        item: [
            {
                linkId: "safety-plan-warning-signs-1",
                text: "1.",
                type: "string",
                required: false,
                placeholder: "e.g., I stop reaching out to my friends",
                scorable: false
            },
            {
                linkId: "safety-plan-warning-signs-2",
                text: "2.",
                type: "string",
                required: false,
                placeholder: "Enter a warning sign...",
                scorable: false
            },
            {
                linkId: "safety-plan-warning-signs-3",
                text: "3.",
                type: "string",
                required: false,
                placeholder: "Enter a warning sign...",
                scorable: false
            }
        ]
    },
    // Coping strategies I can try on my own
    {
        linkId: "safety-plan-coping-strategies",
        text: "Coping strategies I can try to use on my own",
        type: "group",
        instruction: "Things I can do by myself to feel steadier.",
        item: [
            {
                linkId: "safety-plan-coping-strategies-1",
                text: "1.",
                type: "string",
                required: false,
                placeholder: "Enter a coping strategy...",
                scorable: false
            },
            {
                linkId: "safety-plan-coping-strategies-2",
                text: "2.",
                type: "string",
                required: false,
                placeholder: "Enter a coping strategy...",
                scorable: false
            },
            {
                linkId: "safety-plan-coping-strategies-3",
                text: "3.",
                type: "string",
                required: false,
                placeholder: "Enter a coping strategy...",
                scorable: false
            }
        ]
    },
    // Things that can distract me
    {
        linkId: "safety-plan-distractions",
        text: "Things that can distract me from what is bothering me",
        type: "group",
        instruction: "Grounding techniques, sensory tools, activities, or people that help take my mind off things.",
        item: [
            {
                linkId: "safety-plan-distractions-1",
                text: "1.",
                type: "string",
                required: false,
                placeholder: "Enter a distraction...",
                scorable: false
            },
            {
                linkId: "safety-plan-distractions-2",
                text: "2.",
                type: "string",
                required: false,
                placeholder: "Enter a distraction...",
                scorable: false
            },
            {
                linkId: "safety-plan-distractions-3",
                text: "3.",
                type: "string",
                required: false,
                placeholder: "Enter a distraction...",
                scorable: false
            }
        ]
    },
    // Spaces I can go to that make me feel better
    {
        linkId: "safety-plan-safe-spaces",
        text: "Spaces I can go to that make me feel better",
        type: "group",
        item: [
            {
                linkId: "safety-plan-safe-spaces-1",
                text: "1.",
                type: "string",
                required: false,
                placeholder: "Enter a place...",
                scorable: false
            },
            {
                linkId: "safety-plan-safe-spaces-2",
                text: "2.",
                type: "string",
                required: false,
                placeholder: "Enter a place...",
                scorable: false
            }
        ]
    },
    // Ways to make my space safer (means restriction)
    {
        linkId: "safety-plan-means-safety",
        text: "Ways to make my space safer",
        type: "group",
        instruction: "Steps to reduce access to means of harm — for example, securing firearms or medications.",
        item: [
            {
                linkId: "safety-plan-means-safety-1",
                text: "1.",
                type: "string",
                required: false,
                placeholder: "Enter a way to make my space safer...",
                scorable: false
            },
            {
                linkId: "safety-plan-means-safety-2",
                text: "2.",
                type: "string",
                required: false,
                placeholder: "Enter a way to make my space safer...",
                scorable: false
            }
        ]
    },
    // People I can reach out to when I am not feeling safe
    {
        linkId: "safety-plan-support-people",
        text: "People I can reach out to when I am not feeling safe",
        type: "group",
        instruction: "Family, friends, or others who can help.",
        item: [
            {
                linkId: "safety-plan-support-person-1",
                text: "1. Person",
                type: "group",
                item: [
                    {
                        linkId: "safety-plan-support-person-1-name",
                        text: "Name",
                        type: "string",
                        required: false,
                        placeholder: "Name",
                        scorable: false
                    },
                    {
                        linkId: "safety-plan-support-person-1-contact",
                        text: "Contact",
                        type: "string",
                        required: false,
                        placeholder: "Phone number",
                        scorable: false
                    }
                ]
            },
            {
                linkId: "safety-plan-support-person-2",
                text: "2. Person",
                type: "group",
                item: [
                    {
                        linkId: "safety-plan-support-person-2-name",
                        text: "Name",
                        type: "string",
                        required: false,
                        placeholder: "Name",
                        scorable: false
                    },
                    {
                        linkId: "safety-plan-support-person-2-contact",
                        text: "Contact",
                        type: "string",
                        required: false,
                        placeholder: "Phone number",
                        scorable: false
                    }
                ]
            }
        ]
    },
    // Professionals I can contact
    {
        linkId: "safety-plan-professionals",
        text: "Professionals I can contact",
        type: "group",
        instruction: "Clinicians, agencies, or crisis services I can contact.",
        item: [
            {
                linkId: "safety-plan-professional-1",
                text: "1. Professional or service",
                type: "group",
                item: [
                    {
                        linkId: "safety-plan-professional-1-name",
                        text: "Name",
                        type: "string",
                        required: false,
                        placeholder: "Professional/service name",
                        scorable: false
                    },
                    {
                        linkId: "safety-plan-professional-1-contact",
                        text: "Contact",
                        type: "string",
                        required: false,
                        placeholder: "Phone number",
                        scorable: false
                    }
                ]
            },
            {
                linkId: "safety-plan-crisis-line-988",
                text: "988 Suicide & Crisis Lifeline",
                type: "string",
                required: false,
                placeholder: "Call or text 988",
                instruction: "Available 24/7 — call or text 988.",
                scorable: false
            },
            {
                linkId: "safety-plan-local-crisis-line",
                text: "Local crisis line",
                type: "string",
                required: false,
                placeholder: "Local crisis line (if available)",
                scorable: false
            }
        ]
    },
    // Reasons for living (optional)
    {
        linkId: "safety-plan-reasons-for-living",
        text: "Things worth living for",
        type: "group",
        instruction: "Optional — things I find meaningful, things I am looking forward to, and reasons worth living for.",
        item: [
            {
                linkId: "safety-plan-meaningful",
                text: "Things I find most meaningful",
                type: "string",
                required: false,
                placeholder: "Optional",
                scorable: false
            },
            {
                linkId: "safety-plan-looking-forward",
                text: "Things I am looking forward to",
                type: "string",
                required: false,
                placeholder: "Optional",
                scorable: false
            },
            {
                linkId: "safety-plan-worth-living",
                text: "One thing worth living for is…",
                type: "string",
                required: false,
                placeholder: "Optional",
                scorable: false
            }
        ]
    },
    // Follow-up support
    {
        linkId: "safety-plan-followup",
        text: "Follow-up support",
        type: "group",
        instruction: "Someone the advocate can follow up with to help support this plan.",
        item: [
            {
                linkId: "safety-plan-followup-name",
                text: "Name",
                type: "string",
                required: false,
                placeholder: "Name",
                scorable: false
            },
            {
                linkId: "safety-plan-followup-contact",
                text: "Contact",
                type: "string",
                required: false,
                placeholder: "Phone number",
                scorable: false
            }
        ]
    }
];

const SERVICE_MODE_SELECT_OPTION_VALUES = [
    "in-person",
    "telehealth-video",
    "telehealth-phone",
    "text-sms",
    "hybrid"
];
/**
 * Referral mode select options with shared display labels.
 * @agentic @benchmark/core
 */ const REFERRAL_MODE_OPTIONS = [
    {
        value: "in-person",
        label: "In-Person Preferred"
    },
    {
        value: "telehealth-video",
        label: "Telehealth Preferred"
    },
    {
        value: "hybrid",
        label: "Flexible"
    }
];
/**
 * Service mode select options used when choosing a concrete referral delivery mode.
 * @agentic @benchmark/core
 */ const SERVICE_MODE_SELECT_OPTIONS = SERVICE_MODE_SELECT_OPTION_VALUES.map((value)=>({
        value,
        label: SERVICE_CHARACTERISTIC_VISIT_MODE_OPTIONS.find((option)=>option.value === value)?.label ?? value
    }));
const REFERRAL_MODE_LABEL_MAP = new Map(REFERRAL_MODE_OPTIONS.map((option)=>[
        option.value,
        option.label
    ]));
const SERVICE_MODE_LABEL_MAP = new Map(SERVICE_MODE_SELECT_OPTIONS.map((option)=>[
        option.value,
        option.label
    ]));
/**
 * Gets the referral mode display for a code, defaulting to the code when unknown.
 * @agentic @benchmark/core
 */ const getReferralModeLabel = (modeCode)=>REFERRAL_MODE_LABEL_MAP.get(modeCode) ?? modeCode;
/**
 * Gets the selected service mode display for a code, defaulting to the code when unknown.
 * @agentic @benchmark/core
 */ const getServiceModeLabel = (modeCode)=>SERVICE_MODE_LABEL_MAP.get(modeCode) ?? modeCode;

/**
 * ServiceRequest Constants
 *
 * Constants for need-based ServiceRequests used in referral fulfillment workflows.
 * Includes SNOMED CT category/code constants for inbound and outbound referrals.
 * @agentic @benchmark/core
 */ /**
 * Identifier system for need-based ServiceRequest idempotency.
 * Value format: `{patientId}|{carePlanId}|{serviceTypeCode}`
 * @agentic @benchmark/core
 */ const SERVICE_REQUEST_NEED_IDENTIFIER_SYSTEM = "https://benchmarkhealth.com/fhir/identifier/service-request-need";
/**
 * SNOMED CT category codes for ServiceRequest classification.
 * Separates inbound referrals (from PCP) from outbound referrals (to external services).
 * @agentic @benchmark/core
 */ const SERVICE_REQUEST_CATEGORY = {
    INBOUND_REFERRAL: {
        system: "http://snomed.info/sct",
        code: "305932003",
        display: "Referral by own general practitioner"
    },
    OUTBOUND_REFERRAL: {
        system: "http://snomed.info/sct",
        code: "306090001",
        display: "Referral by social worker"
    }
};
/**
 * SNOMED CT code for the inbound patient navigation referral.
 * @agentic @benchmark/core
 */ const SERVICE_REQUEST_CODE = {
    INBOUND_NAVIGATION: {
        system: "http://snomed.info/sct",
        code: "1003472007",
        display: "Referral to patient navigation service"
    }
};
/**
 * Maps operational service-type codes to SNOMED CT referral procedure codes.
 * Uses codes from the `3457005 | Patient referral` hierarchy.
 * Not every service type has a SNOMED match; unmapped types use only the operational coding.
 * @agentic @benchmark/core
 */ const SERVICE_TYPE_SNOMED_MAP = {
    "individual-therapy": {
        system: "http://snomed.info/sct",
        code: "183583007",
        display: "Refer to psychologist"
    },
    "psychiatry-medication-management": {
        system: "http://snomed.info/sct",
        code: "183524004",
        display: "Referral to psychiatrist"
    },
    "family-couples-therapy": {
        system: "http://snomed.info/sct",
        code: "183583007",
        display: "Refer to psychologist"
    },
    "collaborative-care": {
        system: "http://snomed.info/sct",
        code: "306136006",
        display: "Referral to liaison psychiatry service"
    },
    "intensive-outpatient-program": {
        system: "http://snomed.info/sct",
        code: "183561008",
        display: "Referral to mental health team"
    },
    "partial-hospitalization": {
        system: "http://snomed.info/sct",
        code: "183561008",
        display: "Referral to mental health team"
    },
    "crisis-stabilization": {
        system: "http://snomed.info/sct",
        code: "183561008",
        display: "Referral to mental health team"
    },
    inpatient: {
        system: "http://snomed.info/sct",
        code: "183561008",
        display: "Referral to mental health team"
    },
    residential: {
        system: "http://snomed.info/sct",
        code: "183561008",
        display: "Referral to mental health team"
    },
    "medication-assisted-treatment": {
        system: "http://snomed.info/sct",
        code: "306163007",
        display: "Referral to substance misuse service"
    },
    detoxification: {
        system: "http://snomed.info/sct",
        code: "306163007",
        display: "Referral to substance misuse service"
    },
    "individual-substance-use-counseling": {
        system: "http://snomed.info/sct",
        code: "306163007",
        display: "Referral to substance misuse service"
    },
    "residential-rehabilitation": {
        system: "http://snomed.info/sct",
        code: "306163007",
        display: "Referral to substance misuse service"
    },
    "intensive-outpatient-rehabilitation": {
        system: "http://snomed.info/sct",
        code: "306163007",
        display: "Referral to substance misuse service"
    },
    "housing-assistance": {
        system: "http://snomed.info/sct",
        code: "306227006",
        display: "Referral to social services"
    },
    "food-nutrition-assistance": {
        system: "http://snomed.info/sct",
        code: "306227006",
        display: "Referral to social services"
    },
    "employment-vocational-support": {
        system: "http://snomed.info/sct",
        code: "306227006",
        display: "Referral to social services"
    },
    "financial-benefits-counseling": {
        system: "http://snomed.info/sct",
        code: "306227006",
        display: "Referral to social services"
    },
    "legal-aid-advocacy": {
        system: "http://snomed.info/sct",
        code: "306227006",
        display: "Referral to social services"
    },
    "homeless-outreach-case-management": {
        system: "http://snomed.info/sct",
        code: "306227006",
        display: "Referral to social services"
    }
};
/**
 * ServiceRequest status options for the fulfillment lifecycle.
 * Maps FHIR R4 ServiceRequest.status values to operational meanings.
 * @agentic @benchmark/core
 */ const SERVICE_REQUEST_STATUS_OPTIONS = [
    {
        value: "draft",
        label: "Draft",
        description: "Being researched"
    },
    {
        value: "active",
        label: "Active",
        description: "Referral being offered"
    },
    {
        value: "on-hold",
        label: "On Hold",
        description: "Paused per clinical request"
    },
    {
        value: "completed",
        label: "Fulfilled",
        description: "Referral scheduled or completed"
    },
    {
        value: "revoked",
        label: "Closed",
        description: "No longer needed"
    }
];
/**
 * Get display label for a ServiceRequest status.
 * @agentic @benchmark/core
 */ const getServiceRequestStatusDisplay = (status)=>{
    const option = SERVICE_REQUEST_STATUS_OPTIONS.find((o)=>o.value === status);
    return option?.label ?? status;
};
/**
 * Get Mantine badge color for a ServiceRequest status.
 * @agentic @benchmark/core
 */ const getServiceRequestStatusColor = (status)=>{
    switch(status){
        case "draft":
            return "gray";
        case "active":
            return "blue";
        case "on-hold":
            return "orange";
        case "completed":
            return "green";
        case "revoked":
            return "red";
        default:
            return "gray";
    }
};

/**
 * Canonical system URL for Benchmark Health task types.
 * Use this constant instead of hardcoding the URL in your code.
 */ const TASK_TYPE_SYSTEM = "http://www.benchmarkhealth.com/fhir/ValueSet/task-type";
/**
 * Canonical system URL for `Task.output.type` codings.
 * @agentic @benchmark/core
 */ const TASK_OUTPUT_TYPE_SYSTEM = "http://benchmarkhealth.com/fhir/CodeSystem/task-output-type";
/**
 * `Task.output` type code marking the Encounter produced by completing an
 * encounter-documenting care-plan activity — the durable, queryable
 * Task → Encounter link. Mixed-era contract: absence on a completed Task means
 * a legacy completion (encounter unknown), never "undocumented".
 * @agentic @benchmark/core
 */ const TASK_OUTPUT_DOCUMENTED_ENCOUNTER_CODE = "documented-encounter";
/**
 * Extension URL for a saved, not-yet-submitted completion form (JSON
 * `valueString`). Removed when the Task's activity completes.
 * @agentic @benchmark/core
 */ const TASK_DRAFT_FORM_STATE_EXTENSION_URL = "http://www.benchmarkhealth.com/fhir/StructureDefinition/task-draft-form-state";
/**
 * Task statuses that represent a terminal (finished/abandoned) state.
 * @agentic @benchmark/core
 */ const TERMINAL_TASK_STATUSES = new Set([
    "completed",
    "cancelled",
    "failed",
    "entered-in-error"
]);
/**
 * Predefined Task types for healthcare workflows.
 * Using custom system for business-specific task categories.
 *
 * OCPE Phase 5: Several standalone task types are deprecated and replaced by
 * Task-backed CarePlan activities (v2 taxonomy). Deprecated types are excluded
 * from the task creation UI via `isDeprecatedStandaloneTaskType`, but remain
 * here for display/lookup of existing tasks in production.
 */ const TASK_TYPE_OPTIONS = [
    {
        /** @deprecated Replaced by intake plan activities: schedule-intake, complete-intake, finalize-care-plan-enrollment */ code: "new-member-intake",
        display: "Complete New Member Intake",
        description: "Finish new member intake with initial assessment",
        instructions: "Either complete 90791 intake for the individual or document that the individual has accepted Benchmark Health's services.",
        system: TASK_TYPE_SYSTEM,
        defaultPriority: "routine",
        estimatedDuration: 30,
        isRecurring: false,
        cadenceWeeks: []
    },
    {
        /** @deprecated Replaced by triage plan activity: offer-services */ code: "offer-member-resources",
        display: "Offer Resources to Member",
        description: "Find appropriate healthcare services and facilitate connection for member",
        instructions: "Find free or clinical resource options and share them with the patient. Document the interaction or communication.",
        system: TASK_TYPE_SYSTEM,
        defaultPriority: "routine",
        estimatedDuration: 20,
        isRecurring: false,
        cadenceWeeks: []
    },
    {
        /** @deprecated Replaced by triage plan activity: schedule-services */ code: "schedule-member-appointment",
        display: "Schedule Appointment for Member",
        description: "Schedule an appointment for the member to receive care",
        instructions: "Schedule or confirm a member's appointment. Log the appointment in connections.",
        system: TASK_TYPE_SYSTEM,
        defaultPriority: "urgent",
        estimatedDuration: 20,
        isRecurring: false,
        cadenceWeeks: []
    },
    {
        /** @deprecated Replaced by plan activity: check-in-on-member (intake as-needed) */ code: "member-check-in",
        display: "Check In on Member",
        description: "General check-in on member to assess progress or needs",
        instructions: "General check in with member. Log the interaction.",
        system: TASK_TYPE_SYSTEM,
        defaultPriority: "routine",
        estimatedDuration: 15,
        isRecurring: false,
        cadenceWeeks: []
    },
    {
        /** @deprecated NPS surveys are now handled by automation, no longer a plan activity */ code: "send-nps-survey",
        display: "Send NPS Survey",
        description: "Send Net Promoter Score survey to assess member satisfaction with Benchmark",
        instructions: "Send Member NPS survey and log the communication.",
        system: TASK_TYPE_SYSTEM,
        defaultPriority: "routine",
        estimatedDuration: 5,
        isRecurring: false,
        cadenceWeeks: []
    },
    {
        /** @deprecated Replaced by triage plan activity: follow-up-on-services */ code: "post-care-followup",
        display: "Complete Post-Care Followup",
        description: "Check-in with member after appointment completion to assess progress or needs",
        instructions: "Check in with the member after their appointment. Log the interaction and update the status of the appointment.",
        system: TASK_TYPE_SYSTEM,
        defaultPriority: "routine",
        estimatedDuration: 20,
        isRecurring: false,
        cadenceWeeks: []
    },
    {
        /** @deprecated Replaced by triage plan activity: complete-cadenced-followup */ code: "cadenced-followup",
        display: "Complete Cadenced Followup",
        description: "Check-in at 6 week intervals to assess progress or needs",
        instructions: "Send member 6 week outcome questionnaire and log the communication. Log results in an encounter if the member responds.",
        system: TASK_TYPE_SYSTEM,
        defaultPriority: "routine",
        estimatedDuration: 15,
        isRecurring: true,
        cadenceWeeks: [
            6,
            12,
            18
        ]
    }
];
const CUSTOM_TASK_TYPE = {
    code: "custom",
    display: "Custom Task",
    description: "Define a task with custom instructions and cadence.",
    system: "http://www.benchmarkhealth.com/fhir/ValueSet/custom-task-type"
};
// Define groupings so multiple underlying task codes can share one tab.
// These groups are used for the TasksPage segmented filter control.
// Each group includes both legacy standalone codes (task-type system) and
// v2 plan-backed activity codes (care-plan-activity system) so the filter
// works for both old standalone tasks and new Task-backed CarePlan activities.
const TASK_TYPE_GROUPS = [
    {
        id: "intake",
        label: "Intake",
        codes: [
            "new-member-intake",
            "schedule-intake",
            "complete-intake",
            "finalize-care-plan-enrollment",
            "check-in-on-member"
        ]
    },
    {
        id: "triage",
        label: "Triage",
        codes: [
            "offer-member-resources",
            "schedule-member-appointment",
            "connect-member-care",
            "member-check-in",
            "post-care-followup",
            "cadenced-followup",
            "select-services",
            "offer-services",
            "schedule-services",
            "follow-up-on-services",
            "approve-selected-services",
            "complete-cadenced-followup"
        ]
    },
    {
        id: "cocm",
        label: "CoCM",
        codes: [
            // Note: `enroll-in-mirah` (v2 cocm sequential, retired with the
            // Mirah partnership wind-down) is intentionally absent; legacy task
            // displays still resolve via taxonomy-mapping for backwards
            // compatibility.
            "build-cocm-care-plan",
            "follow-up-on-cocm",
            "consult-psych",
            "consult-pcp",
            "discharge-member"
        ]
    },
    {
        id: "other",
        label: "Other",
        codes: [
            "custom-task",
            "send-nps-survey",
            "review-new-referral",
            "potential-duplicate-review",
            "manual-review-fax",
            "review-unmatched-fax"
        ]
    }
];
/**
 * FHIR R4 Task status options
 * http://hl7.org/fhir/task-status
 */ const TASK_STATUS_OPTIONS = [
    {
        code: "draft",
        display: "Draft",
        system: "http://hl7.org/fhir/task-status"
    },
    {
        code: "requested",
        display: "Requested",
        system: "http://hl7.org/fhir/task-status"
    },
    {
        code: "received",
        display: "Received",
        system: "http://hl7.org/fhir/task-status"
    },
    {
        code: "accepted",
        display: "Accepted",
        system: "http://hl7.org/fhir/task-status"
    },
    {
        code: "rejected",
        display: "Rejected",
        system: "http://hl7.org/fhir/task-status"
    },
    {
        code: "ready",
        display: "Ready",
        system: "http://hl7.org/fhir/task-status"
    },
    {
        code: "cancelled",
        display: "Cancelled",
        system: "http://hl7.org/fhir/task-status"
    },
    {
        code: "in-progress",
        display: "In Progress",
        system: "http://hl7.org/fhir/task-status"
    },
    {
        code: "on-hold",
        display: "On Hold",
        system: "http://hl7.org/fhir/task-status"
    },
    {
        code: "failed",
        display: "Failed",
        system: "http://hl7.org/fhir/task-status"
    },
    {
        code: "completed",
        display: "Completed",
        system: "http://hl7.org/fhir/task-status"
    },
    {
        code: "entered-in-error",
        display: "Entered in Error",
        system: "http://hl7.org/fhir/task-status"
    }
];
/**
 * FHIR R4 Task priority options
 * http://hl7.org/fhir/request-priority
 */ const TASK_PRIORITY_OPTIONS = [
    {
        code: "routine",
        display: "Routine",
        system: "http://hl7.org/fhir/request-priority"
    },
    {
        code: "urgent",
        display: "Urgent",
        system: "http://hl7.org/fhir/request-priority"
    },
    {
        code: "asap",
        display: "ASAP",
        system: "http://hl7.org/fhir/request-priority"
    },
    {
        code: "stat",
        display: "STAT",
        system: "http://hl7.org/fhir/request-priority"
    }
];
/**
 * Task intent options for workflow context
 * http://hl7.org/fhir/task-intent
 */ const TASK_INTENT_OPTIONS = [
    {
        code: "proposal",
        display: "Proposal",
        system: "http://hl7.org/fhir/task-intent"
    },
    {
        code: "plan",
        display: "Plan",
        system: "http://hl7.org/fhir/task-intent"
    },
    {
        code: "order",
        display: "Order",
        system: "http://hl7.org/fhir/task-intent"
    },
    {
        code: "original-order",
        display: "Original Order",
        system: "http://hl7.org/fhir/task-intent"
    }
];
// UI-specific mappings for Select components
// OCPE Phase 5: Exclude deprecated standalone task types from creation UI
const BASE_TASK_TYPE_SELECT_OPTIONS = TASK_TYPE_OPTIONS.filter((type)=>!isDeprecatedStandaloneTaskType(type.code)).map((type)=>({
        value: type.code,
        label: type.display,
        description: type.description,
        isRecurring: type.isRecurring || false
    }));
const TASK_TYPE_SELECT_OPTIONS = [
    ...BASE_TASK_TYPE_SELECT_OPTIONS,
    {
        value: CUSTOM_TASK_TYPE.code,
        label: CUSTOM_TASK_TYPE.display,
        description: CUSTOM_TASK_TYPE.description,
        isRecurring: false
    }
];
const TASK_TYPE_GROUP_OPTIONS = TASK_TYPE_GROUPS.map((group)=>({
        value: group.id,
        label: group.label,
        codes: group.codes
    }));
/**
 * Set of all known v2 plan-backed activity codes (CARE_PLAN_ACTIVITY_SYSTEM).
 * @agentic @benchmark/core
 */ const V2_ACTIVITY_CODE_SET = new Set(getAllCarePlanActivityOptions().map((a)=>a.code));
/**
 * True when `code` is a v2 CarePlan-backed activity used on Task.code (care-plan-activity system).
 * @agentic @benchmark/core
 */ function isV2CarePlanActivityTaskCode(code) {
    return V2_ACTIVITY_CODE_SET.has(code);
}
/**
 * Set of all known standalone task type codes (TASK_TYPE_SYSTEM).
 * @agentic @benchmark/core
 */ const STANDALONE_TASK_CODE_SET = new Set(TASK_TYPE_OPTIONS.map((t)=>t.code));
/**
 * Qualify a task code with its FHIR system(s) for deterministic search.
 * Codes that exist in both systems (e.g. send-nps-survey) produce two entries.
 * Unknown codes default to TASK_TYPE_SYSTEM.
 * @agentic @benchmark/core
 */ function qualifyTaskCode(code) {
    const pairs = [];
    if (code === "custom-task") {
        pairs.push(`${CUSTOM_TASK_TYPE.system}|custom-task`);
    }
    if (STANDALONE_TASK_CODE_SET.has(code)) {
        pairs.push(`${TASK_TYPE_SYSTEM}|${code}`);
    }
    if (V2_ACTIVITY_CODE_SET.has(code)) {
        pairs.push(`${CARE_PLAN_ACTIVITY_SYSTEM}|${code}`);
    }
    if (pairs.length === 0) {
        pairs.push(`${TASK_TYPE_SYSTEM}|${code}`);
    }
    return pairs;
}
/**
 * Qualify an array of task codes with FHIR systems for deterministic search.
 * @agentic @benchmark/core
 */ function qualifyTaskCodes(codes) {
    return codes.flatMap(qualifyTaskCode);
}
/** Codes excluded from the "Other" filter group (deprecated / automated). @agentic connections */ const OTHER_GROUP_EXCLUDED_CODES = new Set([
    "send-nps-survey"
]);
/** Short display overrides for filter dropdown labels. @agentic connections */ const FILTER_DISPLAY_OVERRIDES = {
    "custom-task": CUSTOM_TASK_TYPE.display,
    "review-new-referral": "Fax | New Referral",
    "potential-duplicate-review": "Fax | Potential Duplicate",
    "manual-review-fax": "Fax | Manual Review",
    "review-unmatched-fax": "Fax | Unmatched"
};
/** Resolve display name for a code in the filter dropdown. @agentic connections */ const getFilterActivityDisplay = (code)=>FILTER_DISPLAY_OVERRIDES[code] ?? resolveActivityDisplay(code);
/**
 * Displayable activities per task type group for the filter dropdown.
 * For intake/triage/cocm: only v2 activity codes are shown as individual items.
 * For "other": all codes are shown. Legacy codes are included implicitly
 * via full-group selection when resolving FHIR queries.
 * @agentic connections
 */ const TASK_TYPE_FILTER_OPTIONS = TASK_TYPE_GROUPS.map((group)=>({
        id: group.id,
        label: group.label,
        activities: group.codes.filter((code)=>{
            if (group.id === "other") return !OTHER_GROUP_EXCLUDED_CODES.has(code);
            return V2_ACTIVITY_CODE_SET.has(code);
        }).map((code)=>({
                code,
                display: getFilterActivityDisplay(code)
            }))
    }));
/** Set of all individually-selectable activity codes in the filter. @agentic connections */ const ALL_FILTER_ACTIVITY_CODES = new Set(TASK_TYPE_FILTER_OPTIONS.flatMap((g)=>g.activities.map((a)=>a.code)));
/** Group ID lookup for fast deserialize. @agentic connections */ const FILTER_GROUP_BY_ID = new Map(TASK_TYPE_FILTER_OPTIONS.map((g)=>[
        g.id,
        g
    ]));
/**
 * Deserialize URL param value to a set of activity codes.
 * Expands group IDs to individual v2 codes. Unknown tokens are ignored.
 * Backward-compatible: existing `?taskType=intake` URLs work correctly.
 * @agentic connections
 */ function deserializeTaskTypeFilter(param) {
    if (!param) return new Set();
    const codes = new Set();
    for (const token of param.split(",")){
        const trimmed = token.trim();
        if (!trimmed) continue;
        const group = FILTER_GROUP_BY_ID.get(trimmed);
        if (group) {
            group.activities.forEach((a)=>codes.add(a.code));
        } else if (ALL_FILTER_ACTIVITY_CODES.has(trimmed)) {
            codes.add(trimmed);
        }
    }
    return codes;
}
/**
 * Serialize selected codes to URL param value.
 * Collapses fully-selected groups to group IDs for compact URLs.
 * Returns null when no filter is active (= "all tasks").
 * @agentic connections
 */ function serializeTaskTypeFilter(selectedCodes) {
    if (selectedCodes.size === 0) return null;
    const tokens = [];
    const accounted = new Set();
    for (const group of TASK_TYPE_FILTER_OPTIONS){
        const groupCodes = group.activities.map((a)=>a.code);
        if (groupCodes.length > 0 && groupCodes.every((c)=>selectedCodes.has(c))) {
            tokens.push(group.id);
            groupCodes.forEach((c)=>accounted.add(c));
        }
    }
    for (const code of selectedCodes){
        if (!accounted.has(code)) {
            tokens.push(code);
        }
    }
    return tokens.length > 0 ? tokens.join(",") : null;
}
/**
 * Resolve selected activity codes into FHIR system|code search tokens.
 * When all v2 activities for a group are selected, includes legacy codes
 * from that group so older tasks still appear in filtered results.
 * @agentic connections
 */ function resolveTaskTypeFilterCodes(selectedCodes) {
    if (selectedCodes.size === 0) return [];
    const allCodes = [];
    for (const group of TASK_TYPE_FILTER_OPTIONS){
        const groupActivityCodes = group.activities.map((a)=>a.code);
        const allSelected = groupActivityCodes.length > 0 && groupActivityCodes.every((c)=>selectedCodes.has(c));
        if (allSelected) {
            const rawGroup = TASK_TYPE_GROUPS.find((g)=>g.id === group.id);
            if (rawGroup) {
                allCodes.push(...rawGroup.codes);
            }
        } else {
            groupActivityCodes.filter((c)=>selectedCodes.has(c)).forEach((c)=>allCodes.push(c));
        }
    }
    return qualifyTaskCodes(allCodes);
}
const TASK_STATUS_SELECT_OPTIONS = TASK_STATUS_OPTIONS.map((status)=>({
        value: status.code,
        label: status.display
    }));
const TASK_PRIORITY_SELECT_OPTIONS = TASK_PRIORITY_OPTIONS.map((priority)=>({
        value: priority.code,
        label: priority.display
    }));
const TASK_INTENT_SELECT_OPTIONS = TASK_INTENT_OPTIONS.map((intent)=>({
        value: intent.code,
        label: intent.display
    }));
// Helper functions for display lookups
function getTaskTypeDisplay(code) {
    // Prefer v2 labels via taxonomy mapping (handles legacy activity codes and standalone task types)
    const resolvedDisplay = resolveActivityDisplay(code);
    if (resolvedDisplay !== code) {
        return resolvedDisplay;
    }
    // Fallback: match concrete task type
    const directMatch = TASK_TYPE_OPTIONS.find((type)=>type.code === code);
    if (directMatch) {
        return directMatch.display;
    }
    // Fallback: look within task-type group
    const groupMatch = TASK_TYPE_GROUPS.find((group)=>group.codes.some((c)=>c === code));
    if (groupMatch) {
        return groupMatch.label;
    }
    return code;
}
function getTaskStatusDisplay(code) {
    return TASK_STATUS_OPTIONS.find((status)=>status.code === code)?.display || code;
}
function getTaskPriorityDisplay(code) {
    return TASK_PRIORITY_OPTIONS.find((priority)=>priority.code === code)?.display || code;
}
function getTaskIntentDisplay(code) {
    return TASK_INTENT_OPTIONS.find((intent)=>intent.code === code)?.display || code;
}
// Helper function to get task type configuration
function getTaskTypeConfig(code) {
    return TASK_TYPE_OPTIONS.find((type)=>type.code === code);
}
// Helper function to get default priority for task type
function getDefaultPriorityForTaskType(taskTypeCode) {
    const taskType = getTaskTypeConfig(taskTypeCode);
    return taskType?.defaultPriority || "normal";
}
// Helper function to get estimated duration for task type
function getEstimatedDurationForTaskType(taskTypeCode) {
    const taskType = getTaskTypeConfig(taskTypeCode);
    return taskType?.estimatedDuration || 15;
}
// Helper function to check if task type is recurring
function isRecurringTaskType(taskTypeCode) {
    const taskType = getTaskTypeConfig(taskTypeCode);
    return taskType?.isRecurring || false;
}
// Helper function to get cadence weeks for recurring tasks
function getCadenceWeeksForTaskType(taskTypeCode) {
    const taskType = getTaskTypeConfig(taskTypeCode);
    return taskType?.cadenceWeeks || [];
}

/**
 * Domain Constants Index
 *
 * Central export point for all healthcare domain constants and types.
 * This file serves as the single source of truth for domain-related constants.
 */ // Export all domain-specific constants
// Healthcare domain names - single source of truth
const HEALTHCARE_DOMAINS = [
    "appointment",
    "clinical-impression",
    "communication",
    "coverage",
    "encounter",
    "healthcare-service",
    "location",
    "observation",
    "organization",
    "patient",
    "practitioner-role",
    "practitioner",
    "questionnaire",
    "questionnaire-response",
    "task",
    "flag"
];

/**
 * Medplum Query Count Constants
 *
 * Centralized documentation of all `_count` parameters used in Medplum FHIR queries.
 * This provides visibility into query limits that may unintentionally restrict
 * options or lists in the UI.
 *
 * Every query with a `_count` parameter should reference a constant from this file.
 *
 * Convention:
 * - Purpose-based constants: QUERY_LIMIT_SINGLE
 * - Resource-specific constants: <RESOURCE>_<PURPOSE>_COUNT
 *   - OPTIONS: dropdowns/selects
 *   - FORM: form searches
 *   - PAGINATION: paginated queries
 *   - BATCH_SMALL: small batch operations
 */ // ============================================================================
// Purpose-Based Limits
// ============================================================================
/** Single resource lookup (all single lookups use this). @agentic @benchmark/core */ const QUERY_LIMIT_SINGLE = 1;
// ============================================================================
// Resource-Specific Query Limits
// ============================================================================
// ----------------------------------------------------------------------------
// PractitionerRole
// ----------------------------------------------------------------------------
/**
 * PractitionerRole options for dropdowns/selects
 * @deprecated Use async search hooks (usePractitionerRoleSearch, useReferringProviderSearch, useBenchmarkStaffSearch) instead.
 * This limit is no longer the primary pattern - async search provides better scalability.
 * @agentic @benchmark/core
 */ const PRACTITIONER_ROLE_OPTIONS_COUNT = 200;
/** PractitionerRole paginated queries. @agentic @benchmark/core */ const PRACTITIONER_ROLE_PAGINATION_COUNT = 100;
/**
 * All active PractitionerRoles for the current signed-in user. A single user
 * holds at most a handful of roles; 20 is a generous upper bound that mirrors
 * the prior inline limit used by the tasks page.
 * @agentic @benchmark/core
 */ const PRACTITIONER_ROLE_CURRENT_USER_COUNT = 20;
// ----------------------------------------------------------------------------
// Patient
// ----------------------------------------------------------------------------
/** Patient paginated queries. @agentic @benchmark/core */ const PATIENT_PAGINATION_COUNT = 200;
// ----------------------------------------------------------------------------
// HealthcareService
// ----------------------------------------------------------------------------
/** HealthcareService sibling services query (batch edit mode). @agentic @benchmark/core */ const HEALTHCARE_SERVICE_SIBLINGS_COUNT = 100;
// ----------------------------------------------------------------------------
// Organization
// ----------------------------------------------------------------------------
/** Organization options for dropdowns/selects. @agentic @benchmark/core */ const ORGANIZATION_OPTIONS_COUNT = 1000;
// ----------------------------------------------------------------------------
// Location
// ----------------------------------------------------------------------------
/** Location options for dropdowns/selects. @agentic @benchmark/core */ const LOCATION_OPTIONS_COUNT = 1000;
// ----------------------------------------------------------------------------
// Encounter
// ----------------------------------------------------------------------------
/** Encounter recent/check queries (small batch). @agentic @benchmark/core */ const ENCOUNTER_BATCH_SMALL_COUNT = 10;
// ----------------------------------------------------------------------------
// CareTeam
// ----------------------------------------------------------------------------
/** CareTeam small batch queries. @agentic @benchmark/core */ const CARE_TEAM_BATCH_SMALL_COUNT = 2;
/** CareTeam timeline queries. @agentic @benchmark/core */ const CARE_TEAM_TIMELINE_COUNT = 5;
// ----------------------------------------------------------------------------
// ServiceRequest
// ----------------------------------------------------------------------------
/** ServiceRequest timeline queries. @agentic @benchmark/core */ const SERVICE_REQUEST_TIMELINE_COUNT = 10;
/** Paginated outbound referrals linked to a HealthcareService performer. @agentic @benchmark/core */ const HEALTHCARE_SERVICE_REFERRAL_HISTORY_COUNT = 25;
/** CarePlan timeline queries. @agentic @benchmark/core */ const CARE_PLAN_TIMELINE_COUNT = 10;
// ----------------------------------------------------------------------------
// Communication
// ----------------------------------------------------------------------------
/** SMS thread initial load - reasonable limit for most patients. @agentic @benchmark/core */ const SMS_THREAD_INITIAL_COUNT = 100;
/** SMS thread polling - only fetch recent messages for incremental updates. @agentic @benchmark/core */ const SMS_THREAD_POLL_COUNT = 20;
/** SMS thread reverse-pagination page size (older-history batches loaded on scroll-back). @agentic @benchmark/core */ const SMS_THREAD_PAGE_COUNT = 50;
// ----------------------------------------------------------------------------
// CarePlan
// ----------------------------------------------------------------------------
/** CarePlan paginated queries. @agentic @benchmark/core */ const CARE_PLAN_PAGINATION_COUNT = 50;
// ----------------------------------------------------------------------------
// Task
// ----------------------------------------------------------------------------
/** Task paginated queries (task list page) */ const TASK_PAGINATION_COUNT = 50;
/** Task history queries (completed/archived tasks in history drawer). @agentic @benchmark/core */ const TASK_HISTORY_COUNT = 100;
// ----------------------------------------------------------------------------
// Flag
// ----------------------------------------------------------------------------
/** Flag paginated queries (active flags). @agentic @benchmark/core */ const FLAG_PAGINATION_COUNT = 10;
// ----------------------------------------------------------------------------
// Timeline Resources (all resource types in timeline)
// ----------------------------------------------------------------------------
/** Paginated queries for patient timeline (all resource types). @agentic @benchmark/core */ const TIMELINE_PAGINATION_COUNT = 100;

/**
 * Mirah patient identifier system constants. Retained to read and preserve
 * historical Mirah identifiers on existing `Patient` resources; the Mirah
 * patient-sync integration that wrote them has been removed. Pure readers
 * over `Patient.identifier` live in
 * `@benchmark/core/adapters/mirah.patientIdentifier`.
 *
 * @agentic @benchmark/core
 */ /**
 * FHIR identifier system URL for Mirah patient ID.
 * @agentic @benchmark/core
 */ const MIRAH_PATIENT_ID_SYSTEM = "http://mirah.com/patient-id";
/**
 * FHIR identifier system URL for Mirah institute ID.
 * @agentic @benchmark/core
 */ const MIRAH_INSTITUTE_ID_SYSTEM = "http://mirah.com/institute";

/**
 * FHIR-aligned constants for activity progress logging.
 *
 * Defines coding systems, categories, outcome sets, and the activity-to-archetype
 * map used by the ActivityProgressWizard content components.
 */ // ── Coding Systems ─────────────────────────────────────────────────────
/** Custom system for progress log category coding on Communication.category. */ const PROGRESS_LOG_CATEGORY_SYSTEM = "http://benchmarkhealth.com/fhir/CodeSystem/progress-log-type";
/** Custom system for progress log outcome coding on Communication.reasonCode. */ const PROGRESS_LOG_OUTCOME_SYSTEM = "http://benchmarkhealth.com/fhir/CodeSystem/progress-log-outcome";
// ── Category Definitions ───────────────────────────────────────────────
const PROGRESS_LOG_CATEGORIES = [
    {
        code: "outreach-attempt",
        display: "Outreach Attempt"
    },
    {
        code: "provider-outreach",
        display: "Provider Outreach"
    },
    {
        code: "administrative-note",
        display: "Administrative Note"
    }
];
// ── Outreach Attempt Outcome Sets ──────────────────────────────────────
/** Standard outcomes for member-facing outreach attempts. */ const OUTREACH_STANDARD_OUTCOMES = [
    {
        value: "no-answer-left-voicemail",
        label: "No Answer – Left Voicemail",
        snoozeBehavior: {
            type: "auto",
            hours: 24
        }
    },
    {
        value: "no-answer-sent-text",
        label: "No Answer – Sent Text",
        snoozeBehavior: {
            type: "auto",
            hours: 24
        }
    },
    {
        value: "no-answer-sent-email",
        label: "No Answer – Sent Email",
        snoozeBehavior: {
            type: "auto",
            hours: 48
        }
    },
    {
        value: "answered-callback-requested",
        label: "Answered – Callback Requested",
        snoozeBehavior: {
            type: "prompt"
        }
    },
    {
        value: "answered-not-ready",
        label: "Answered – Not Ready",
        snoozeBehavior: {
            type: "prompt"
        }
    },
    {
        value: "answered-declined-services",
        label: "Answered – Declined Services",
        snoozeBehavior: {
            type: "none"
        }
    },
    {
        value: "problem-wrong-number",
        label: "Problem – Wrong Number",
        snoozeBehavior: {
            type: "none"
        }
    },
    {
        value: "problem-out-of-order",
        label: "Problem – Out of Order",
        snoozeBehavior: {
            type: "none"
        }
    }
];
/** Extended outcomes for encounter-based activities (adds no-show, rescheduled, etc.). */ const OUTREACH_ENCOUNTER_OUTCOMES = [
    ...OUTREACH_STANDARD_OUTCOMES,
    {
        value: "problem-no-show",
        label: "Problem – No Show",
        snoozeBehavior: {
            type: "auto",
            hours: 24
        }
    },
    {
        value: "answered-rescheduled",
        label: "Answered – Rescheduled",
        snoozeBehavior: {
            type: "prompt"
        }
    },
    {
        value: "problem-technical-issue",
        label: "Problem – Technical Issue",
        snoozeBehavior: {
            type: "auto",
            hours: 24
        }
    }
];
// ── Provider Outreach Outcome Set ──────────────────────────────────────
/** Outcomes for provider/facility-facing outreach. */ const PROVIDER_OUTREACH_OUTCOMES = [
    {
        value: "left-message",
        label: "Left Message",
        snoozeBehavior: {
            type: "auto",
            hours: 48
        }
    },
    {
        value: "fax-sent",
        label: "Fax Sent",
        snoozeBehavior: {
            type: "auto",
            hours: 72
        }
    },
    {
        value: "email-sent",
        label: "Email Sent",
        snoozeBehavior: {
            type: "auto",
            hours: 48
        }
    },
    {
        value: "spoke-with-staff",
        label: "Spoke with Staff — Pending",
        snoozeBehavior: {
            type: "prompt"
        }
    },
    {
        value: "awaiting-response",
        label: "Awaiting Response",
        snoozeBehavior: {
            type: "prompt"
        }
    },
    {
        value: "scheduling-in-progress",
        label: "Scheduling In Progress",
        snoozeBehavior: {
            type: "prompt"
        }
    }
];
// ── Activity → Progress Archetype Map ──────────────────────────────────
/**
 * Maps activity codes to their progress log category (archetype).
 * Used by the ActivityProgressWizard to select the correct content component.
 */ const ACTIVITY_PROGRESS_ARCHETYPE_MAP = {
    "schedule-intake": "outreach-attempt",
    "complete-intake": "outreach-attempt",
    "finalize-care-plan-enrollment": "administrative-note",
    "check-in-on-member": "outreach-attempt",
    "select-services": "administrative-note",
    "approve-selected-services": "administrative-note",
    "offer-services": "outreach-attempt",
    "confirm-services": "outreach-attempt",
    "schedule-services": "provider-outreach",
    "follow-up-on-services": "outreach-attempt",
    "complete-cadenced-followup": "outreach-attempt",
    "build-cocm-care-plan": "administrative-note",
    "follow-up-on-cocm": "outreach-attempt",
    "consult-psych": "provider-outreach",
    "consult-pcp": "provider-outreach",
    "discharge-member": "outreach-attempt"
};
/**
 * Activities that use the extended encounter outcome set (includes no-show, rescheduled).
 * All other outreach activities use the standard outcome set.
 */ const ENCOUNTER_OUTCOME_ACTIVITY_CODES = new Set([
    "complete-intake"
]);
/**
 * True when an activity code is "administrative" — its progress is an internal
 * note, not a member/provider interaction. Derived from the archetype map so
 * the set stays in lockstep with the progress classification (single source of
 * truth). Administrative activities do not generate a progress `Communication`.
 * @agentic @benchmark/core
 */ const isAdministrativeActivityCode = (code)=>!!code && ACTIVITY_PROGRESS_ARCHETYPE_MAP[code] === "administrative-note";

/**
 * FHIR-Compliant Contact Relationship Constants
 *
 * Based on FHIR R4 v2-0131 Contact Role code system
 * http://terminology.hl7.org/CodeSystem/v2-0131
 *
 * These constants define the types of relationships a patient contact can have.
 * Used in Patient.contact[].relationship field.
 */ // FHIR v2-0131 system URL
const CONTACT_RELATIONSHIP_SYSTEM = "http://terminology.hl7.org/CodeSystem/v2-0131";
const CONTACT_RELATIONSHIP_OPTIONS = [
    /*
  // Removed since covered elsewhere
   {
    code: "EP",
    display: "Emergency contact person",
    system: CONTACT_RELATIONSHIP_SYSTEM,
    description: "Emergency contact person",
  },

    code: "PR",
    display: "Person preparing referral",
    system: CONTACT_RELATIONSHIP_SYSTEM,
    description: "Person preparing referral",
  },
  {
    code: "C",
    display: "Emergency Contact",
    system: CONTACT_RELATIONSHIP_SYSTEM,
    description: "Emergency Contact",
  },
  */ // Deprecated codes (v2.9+) - included for backwards compatibility
    {
        code: "BP",
        display: "Billing contact person",
        system: CONTACT_RELATIONSHIP_SYSTEM,
        description: "Billing contact person"
    },
    {
        code: "CP",
        display: "Contact person",
        system: CONTACT_RELATIONSHIP_SYSTEM,
        description: "Contact person"
    },
    // Active codes
    {
        code: "E",
        display: "Employer",
        system: CONTACT_RELATIONSHIP_SYSTEM,
        description: "Employer"
    },
    {
        code: "F",
        display: "Federal Agency",
        system: CONTACT_RELATIONSHIP_SYSTEM,
        description: "Federal Agency"
    },
    {
        code: "I",
        display: "Insurance Company",
        system: CONTACT_RELATIONSHIP_SYSTEM,
        description: "Insurance Company"
    },
    {
        code: "N",
        display: "Next-of-Kin",
        system: CONTACT_RELATIONSHIP_SYSTEM,
        description: "Next-of-Kin"
    },
    {
        code: "O",
        display: "Other",
        system: CONTACT_RELATIONSHIP_SYSTEM,
        description: "Other"
    },
    {
        code: "S",
        display: "State Agency",
        system: CONTACT_RELATIONSHIP_SYSTEM,
        description: "State Agency"
    },
    {
        code: "U",
        display: "Unknown",
        system: CONTACT_RELATIONSHIP_SYSTEM,
        description: "Unknown"
    }
];
/**
 * Simplified options for Mantine Select components
 * Maps to { value, label } format expected by Select
 */ const CONTACT_RELATIONSHIP_SELECT_OPTIONS = CONTACT_RELATIONSHIP_OPTIONS.map((option)=>({
        value: option.code,
        label: option.display
    }));
/**
 * Helper function to get relationship display name from code
 */ function getContactRelationshipDisplay(code) {
    return CONTACT_RELATIONSHIP_OPTIONS.find((option)=>option.code === code)?.display;
}
/**
 * Helper function to get select options
 * Allows filtering or customization in the future
 */ function getContactRelationshipOptions() {
    return CONTACT_RELATIONSHIP_SELECT_OPTIONS;
}

/** Code system URL for family and caregiver patient contacts. @agentic @benchmark/core */ const PATIENT_FAMILY_CONTACT_RELATIONSHIP_SYSTEM = CODE_SYSTEM.V3_ROLE_CODE;
const PATIENT_FAMILY_CONTACT_RELATIONSHIP_OPTIONS = [
    {
        code: "MTH",
        display: "Mother",
        system: PATIENT_FAMILY_CONTACT_RELATIONSHIP_SYSTEM
    },
    {
        code: "FTH",
        display: "Father",
        system: PATIENT_FAMILY_CONTACT_RELATIONSHIP_SYSTEM
    },
    {
        code: "PRN",
        display: "Parent",
        system: PATIENT_FAMILY_CONTACT_RELATIONSHIP_SYSTEM
    },
    {
        code: "CHILD",
        display: "Child",
        system: PATIENT_FAMILY_CONTACT_RELATIONSHIP_SYSTEM
    },
    {
        code: "SONC",
        display: "Son",
        system: PATIENT_FAMILY_CONTACT_RELATIONSHIP_SYSTEM
    },
    {
        code: "DAUC",
        display: "Daughter",
        system: PATIENT_FAMILY_CONTACT_RELATIONSHIP_SYSTEM
    },
    {
        code: "SPS",
        display: "Spouse",
        system: PATIENT_FAMILY_CONTACT_RELATIONSHIP_SYSTEM
    },
    {
        code: "SIB",
        display: "Sibling",
        system: PATIENT_FAMILY_CONTACT_RELATIONSHIP_SYSTEM
    },
    {
        code: "FRND",
        display: "Friend",
        system: PATIENT_FAMILY_CONTACT_RELATIONSHIP_SYSTEM
    },
    {
        code: "GUARD",
        display: "Primary caregiver",
        system: PATIENT_FAMILY_CONTACT_RELATIONSHIP_SYSTEM
    }
];
/** Set of curated family and caregiver relationship codes. @agentic @benchmark/core */ const PATIENT_FAMILY_CONTACT_RELATIONSHIP_CODES = new Set(PATIENT_FAMILY_CONTACT_RELATIONSHIP_OPTIONS.map((option)=>option.code));
/** Select options for family and caregiver contact relationships. @agentic @benchmark/core */ const PATIENT_FAMILY_CONTACT_RELATIONSHIP_SELECT_OPTIONS = PATIENT_FAMILY_CONTACT_RELATIONSHIP_OPTIONS.map((option)=>({
        value: option.code,
        label: option.display
    }));
/**
 * Returns display text for a curated family contact relationship code.
 * @agentic @benchmark/core
 */ const getPatientFamilyContactRelationshipDisplay = (code)=>PATIENT_FAMILY_CONTACT_RELATIONSHIP_OPTIONS.find((option)=>option.code === code)?.display;
/**
 * Returns Mantine select options for family and caregiver contacts.
 * @agentic @benchmark/core
 */ const getPatientFamilyContactRelationshipOptions = ()=>PATIENT_FAMILY_CONTACT_RELATIONSHIP_SELECT_OPTIONS;
/**
 * Returns whether a contact relationship belongs to the family/caregiver set.
 * @agentic @benchmark/core
 */ const isPatientFamilyContactRelationship = (code, system)=>{
    if (system === PATIENT_FAMILY_CONTACT_RELATIONSHIP_SYSTEM) {
        return true;
    }
    if (system === CODE_SYSTEM.V2_0131) {
        return false;
    }
    if (!code) {
        return false;
    }
    return PATIENT_FAMILY_CONTACT_RELATIONSHIP_CODES.has(code);
};
/**
 * Resolves the relationship code system for a patient contact row.
 * @agentic @benchmark/core
 */ const inferPatientContactRelationshipSystem = (code, system)=>{
    if (system) {
        return system;
    }
    if (code && isPatientFamilyContactRelationship(code)) {
        return PATIENT_FAMILY_CONTACT_RELATIONSHIP_SYSTEM;
    }
    return CODE_SYSTEM.V2_0131;
};

/**
 * Condition Domain Constants (FHIR R4)
 *
 * Defines controlled value sets for Condition resources,
 * specifically for Problem List Items with ICD-10-CM codes.
 */ // ICD-10-CM Code System URL (official FHIR terminology)
const ICD10_CM_SYSTEM = "http://hl7.org/fhir/sid/icd-10-cm";
// ICD-10-CM ValueSet URL for expansion queries (Medplum terminology service)
const ICD10_CM_VALUESET_URL = "http://hl7.org/fhir/sid/icd-10-cm/vs";
// Condition Category System (FHIR R4)
const CONDITION_CATEGORY_SYSTEM = "http://terminology.hl7.org/CodeSystem/condition-category";
// Problem List Item category coding
const PROBLEM_LIST_ITEM_CATEGORY = {
    system: CONDITION_CATEGORY_SYSTEM,
    code: "problem-list-item",
    display: "Problem List Item"
};
// Condition Clinical Status System (FHIR R4)
const CONDITION_CLINICAL_STATUS_SYSTEM = "http://terminology.hl7.org/CodeSystem/condition-clinical";
// Clinical status options
const CONDITION_CLINICAL_STATUS_OPTIONS = [
    {
        code: "active",
        display: "Active",
        system: CONDITION_CLINICAL_STATUS_SYSTEM
    },
    {
        code: "recurrence",
        display: "Recurrence",
        system: CONDITION_CLINICAL_STATUS_SYSTEM
    },
    {
        code: "relapse",
        display: "Relapse",
        system: CONDITION_CLINICAL_STATUS_SYSTEM
    },
    {
        code: "inactive",
        display: "Inactive",
        system: CONDITION_CLINICAL_STATUS_SYSTEM
    },
    {
        code: "remission",
        display: "Remission",
        system: CONDITION_CLINICAL_STATUS_SYSTEM
    },
    {
        code: "resolved",
        display: "Resolved",
        system: CONDITION_CLINICAL_STATUS_SYSTEM
    }
];
// Helper to get clinical status display
function getConditionClinicalStatusDisplay(code) {
    return CONDITION_CLINICAL_STATUS_OPTIONS.find((o)=>o.code === code)?.display || code;
}
// Active clinical status coding (used when creating new conditions)
const CONDITION_ACTIVE_STATUS = {
    coding: [
        {
            system: CONDITION_CLINICAL_STATUS_SYSTEM,
            code: "active",
            display: "Active"
        }
    ]
};
// Inactive clinical status coding (used when removing conditions)
const CONDITION_INACTIVE_STATUS = {
    coding: [
        {
            system: CONDITION_CLINICAL_STATUS_SYSTEM,
            code: "inactive",
            display: "Inactive"
        }
    ]
};

/**
 * NPS member feedback survey item definitions (0–10 scale + optional comment).
 * @agentic @benchmark/core
 */ /** Answer options for the 0–10 NPS likelihood question. */ const NPS_SCALE_0_TO_10_OPTIONS = Array.from({
    length: 11
}, (_, value)=>({
        value,
        code: String(value),
        display: value === 0 ? "0 — Worst" : value === 10 ? "10 — Best" : String(value)
    }));
/**
 * NPS survey questions shown in the members app and API fallback Questionnaire.
 * @agentic @benchmark/core
 */ const NPS_QUESTIONS = [
    {
        linkId: "nps-score",
        text: "On a scale of 0–10, how likely are you to recommend our mental health concierge service to a friend or family member?",
        type: "choice",
        required: true,
        answerOptions: NPS_SCALE_0_TO_10_OPTIONS
    },
    {
        linkId: "nps-comment",
        text: "Could you share a bit about why you chose this score? What's one thing we can do better?",
        type: "string",
        required: false,
        placeholder: "Optional",
        maxLength: 4000
    }
];

/**
 * The canonical public-domain attribution for the PHQ / GAD family, which the
 * instruments require to be displayed.
 */ const PHQ_GAD_ATTRIBUTION = "Developed by Drs. Robert L. Spitzer, Janet B.W. Williams, Kurt Kroenke and colleagues, with an educational grant from Pfizer Inc. No permission required to reproduce, translate, display or distribute.";
/** WSAS credit line — Mundt et al. (2002); approved for electronic administration. */ const WSAS_ATTRIBUTION = "Work and Social Adjustment Scale (WSAS). Developed by Mundt et al. (2002).";
const ATTRIBUTION_BY_KEY = {
    PHQ9: PHQ_GAD_ATTRIBUTION,
    GAD7: PHQ_GAD_ATTRIBUTION,
    GAD2: PHQ_GAD_ATTRIBUTION,
    WSAS: WSAS_ATTRIBUTION
};
/**
 * Bundled item definitions per key (null for Mirah-ingested-only instruments
 * that are never administered in our apps). This is the single url→items source.
 */ const ITEMS_BY_KEY = {
    PHQ4: PHQ4_QUESTIONS,
    PHQ9: PHQ9_QUESTIONS,
    PHQ9A: PHQ9A_QUESTIONS,
    GAD7: GAD7_QUESTIONS,
    PC_PTSD_5: PC_PTSD_5_QUESTIONS,
    PSC_17: PSC_17_QUESTIONS,
    C_SSRS: C_SSRS_QUESTIONS,
    ASRS: ASRS_QUESTIONS,
    OCI_4: OCI_4_QUESTIONS,
    SBIRT_ED: SBIRT_ED_QUESTIONS,
    WPAI: WPAI_QUESTIONS,
    TAPS_1: TAPS_1_QUESTIONS,
    TAPS_2: TAPS_2_QUESTIONS,
    SAFETY_PLAN: SAFETY_PLAN_QUESTIONS,
    NPS: NPS_QUESTIONS,
    DRUG_USE: DRUG_USE_QUESTIONS,
    GAD2: GAD2_QUESTIONS,
    AUDIT_C: AUDIT_C_QUESTIONS,
    UCLA3: UCLA3_QUESTIONS,
    PGI_S: PGI_S_QUESTIONS,
    PGI_C: PGI_C_QUESTIONS,
    WSAS: WSAS_QUESTIONS,
    WHO5: null,
    SDOH: null,
    MIRAH_COMPLEXITY: null,
    MIRAH_SEVERITY: null
};
/** Explicit scoring method per key (the `*_SCORING` configs don't all carry it). */ const SCORING_METHOD_BY_KEY = {
    PHQ9: "likert",
    PHQ9A: "likert",
    GAD7: "likert",
    NPS: "likert",
    PHQ4: "summation",
    PC_PTSD_5: "summation",
    PSC_17: "summation",
    OCI_4: "summation",
    SBIRT_ED: "summation",
    TAPS_1: "summation",
    TAPS_2: "summation",
    WHO5: "summation",
    ASRS: "asrs-threshold",
    WPAI: "wpai",
    C_SSRS: "c-ssrs-ordinal",
    SDOH: "none",
    MIRAH_COMPLEXITY: "none",
    MIRAH_SEVERITY: "none",
    SAFETY_PLAN: "none",
    DRUG_USE: "category",
    GAD2: "summation",
    AUDIT_C: "summation",
    UCLA3: "summation",
    PGI_S: "category",
    PGI_C: "category",
    WSAS: "summation"
};
/**
 * Capability flags per key. This is the policy surface — kept explicit (not
 * clever) so the rules are auditable. See research report §7–§9 for rationale.
 */ const CAPABILITIES_BY_KEY = {
    // SENDABLE — self-report, non-branching, scorable; offered in the survey link.
    PHQ9: sendable("self"),
    PHQ9A: sendable("self"),
    GAD7: sendable("self"),
    OCI_4: sendable("self"),
    SBIRT_ED: sendable("self"),
    ASRS: sendable("self"),
    WPAI: sendable("self"),
    PSC_17: sendable("caregiver"),
    // DEFERRED — would be sendable once members-app tooling supports them.
    // PC-PTSD-5 / TAPS need `enableWhen` branching. See `getSurveyLinkDisposition`.
    PC_PTSD_5: branching("self"),
    TAPS_1: branching("self"),
    TAPS_2: branching("self"),
    // EXCLUDED — intentionally never offered in the survey link.
    PHQ4: encounterOnly("self"),
    C_SSRS: branching("none"),
    // Clinician-administered safety plan (available to all users).
    SAFETY_PLAN: {
        encounterAdministrable: true,
        selfReportMode: "none",
        surveyLinkAdministrable: false,
        hasBranching: false,
        mirahIngestedOnly: false
    },
    // Mirah-ingested-only: not administrable in our apps.
    WHO5: mirahOnly("self"),
    SDOH: mirahOnly("self"),
    MIRAH_COMPLEXITY: mirahOnly("none"),
    MIRAH_SEVERITY: mirahOnly("none"),
    // SEPARATE FLOW — anonymous member feedback, not a clinical questionnaire.
    NPS: {
        encounterAdministrable: false,
        selfReportMode: "self",
        surveyLinkAdministrable: false,
        hasBranching: false,
        mirahIngestedOnly: false
    },
    // ADAPTIVE + MANUAL — primarily surfaced by the adaptive screener flow, but
    // also `encounterAdministrable` so the manual/non-adaptive picker offers them
    // as a fallback (a questionnaire is a questionnaire). Not survey-link
    // sendable and not self-report (clinician-administered only). Persistence is
    // NOT gated on this flag — an answered response always saves; the flag only
    // controls manual-picker visibility.
    // Clinician-administered house-modified drug screen (D16), never self-report.
    DRUG_USE: {
        encounterAdministrable: true,
        selfReportMode: "none",
        surveyLinkAdministrable: false,
        hasBranching: false,
        mirahIngestedOnly: false
    },
    // Anxiety short check (D17): separate 0–6 instrument that opens GAD-7.
    GAD2: {
        encounterAdministrable: true,
        selfReportMode: "none",
        surveyLinkAdministrable: false,
        hasBranching: false,
        mirahIngestedOnly: false
    },
    // Alcohol section (D17/D19): the first item gates items 2–3.
    AUDIT_C: {
        encounterAdministrable: true,
        selfReportMode: "none",
        surveyLinkAdministrable: false,
        hasBranching: true,
        mirahIngestedOnly: false
    },
    // Loneliness (public domain), everyday functioning (WSAS, licence-gated D3),
    // and the patient self-ratings (PGI-S / PGI-C, categorical D7).
    UCLA3: {
        encounterAdministrable: true,
        selfReportMode: "none",
        surveyLinkAdministrable: false,
        hasBranching: false,
        mirahIngestedOnly: false
    },
    PGI_S: {
        encounterAdministrable: true,
        selfReportMode: "none",
        surveyLinkAdministrable: false,
        hasBranching: false,
        mirahIngestedOnly: false
    },
    PGI_C: {
        encounterAdministrable: true,
        selfReportMode: "none",
        surveyLinkAdministrable: false,
        hasBranching: false,
        mirahIngestedOnly: false
    },
    WSAS: {
        encounterAdministrable: true,
        selfReportMode: "none",
        surveyLinkAdministrable: false,
        hasBranching: false,
        mirahIngestedOnly: false
    }
};
/** Encounter- and survey-link-administrable self-report instrument (no branching). */ function sendable(selfReportMode) {
    return {
        encounterAdministrable: true,
        selfReportMode,
        surveyLinkAdministrable: true,
        hasBranching: false,
        mirahIngestedOnly: false
    };
}
/** Encounter-administrable only (not via survey link); no branching. */ function encounterOnly(selfReportMode) {
    return {
        encounterAdministrable: true,
        selfReportMode,
        surveyLinkAdministrable: false,
        hasBranching: false,
        mirahIngestedOnly: false
    };
}
/** Encounter-administrable, branching; not survey-link-administrable until enableWhen ships. */ function branching(selfReportMode) {
    return {
        encounterAdministrable: true,
        selfReportMode,
        surveyLinkAdministrable: false,
        hasBranching: true,
        mirahIngestedOnly: false
    };
}
/** Mirah-ingested-only: administrable through no channel. */ function mirahOnly(selfReportMode) {
    return {
        encounterAdministrable: false,
        selfReportMode,
        surveyLinkAdministrable: false,
        hasBranching: false,
        mirahIngestedOnly: true
    };
}
const PANEL_LOINC_BY_KEY = QUESTIONNAIRE_LOINC_CODES;
/**
 * The canonical registry. Composes config + items + scoring + capabilities for
 * every {@link QuestionnaireKey}.
 * @agentic @benchmark/core
 */ const QUESTIONNAIRE_REGISTRY = Object.fromEntries(Object.keys(STANDARD_QUESTIONNAIRES).map((key)=>{
    const config = STANDARD_QUESTIONNAIRES[key];
    const definition = {
        ...config,
        key,
        items: ITEMS_BY_KEY[key],
        scoringMethod: SCORING_METHOD_BY_KEY[key],
        capabilities: CAPABILITIES_BY_KEY[key],
        attribution: ATTRIBUTION_BY_KEY[key],
        loinc: {
            panel: PANEL_LOINC_BY_KEY[key],
            score: config.totalScoreLoincCode || undefined
        }
    };
    return [
        key,
        definition
    ];
}));
const REGISTRY_ENTRIES = Object.values(QUESTIONNAIRE_REGISTRY);
/**
 * Resolve a definition by canonical URL or {@link QuestionnaireKey}.
 * @agentic @benchmark/core
 */ const getQuestionnaireDefinition = (urlOrKey)=>QUESTIONNAIRE_REGISTRY[urlOrKey] ?? REGISTRY_ENTRIES.find((def)=>def.url === urlOrKey);
/**
 * The single url→items source (replaces the API `QUESTION_MAP` and the service
 * `questionsMap`). Returns null when the instrument has no bundled items.
 * @agentic @benchmark/core
 */ const getQuestionnaireItems = (url)=>getQuestionnaireDefinition(url)?.items ?? null;
/**
 * Keys whose definition matches a predicate (the basis for every derived list).
 * @agentic @benchmark/core
 */ const getQuestionnaireKeysWhere = (predicate)=>REGISTRY_ENTRIES.filter(predicate).map((def)=>def.key);
/**
 * Questionnaires the members app + survey API can serve from bundled item
 * definitions: clinical instruments (not NPS member feedback) that are
 * self/caregiver-reported. Derived — not hand-maintained.
 * @agentic @benchmark/core
 */ const MEMBERS_APP_SUPPORTED_QUESTIONNAIRE_KEYS = getQuestionnaireKeysWhere((def)=>def.items !== null && def.capabilities.selfReportMode !== "none" && def.type !== QUESTIONNAIRE_SUBJECTS.MEMBER_FEEDBACK);
/**
 * Canonical URLs for {@link MEMBERS_APP_SUPPORTED_QUESTIONNAIRE_KEYS}.
 * @agentic @benchmark/core
 */ const MEMBERS_APP_SUPPORTED_QUESTIONNAIRE_URLS = MEMBERS_APP_SUPPORTED_QUESTIONNAIRE_KEYS.map((key)=>QUESTIONNAIRE_REGISTRY[key].url);
/**
 * Questionnaires a clinician may send to a patient via the survey-link flow.
 * Derived from the `surveyLinkAdministrable` capability — adding/removing one is a
 * single registry edit, enforced by invariant tests.
 * @agentic @benchmark/core
 */ const SURVEY_LINK_SENDABLE_QUESTIONNAIRE_KEYS = getQuestionnaireKeysWhere((def)=>def.capabilities.surveyLinkAdministrable);
/**
 * Canonical URLs for {@link SURVEY_LINK_SENDABLE_QUESTIONNAIRE_KEYS}.
 * @agentic @benchmark/core
 */ const SURVEY_LINK_SENDABLE_QUESTIONNAIRE_URLS = SURVEY_LINK_SENDABLE_QUESTIONNAIRE_KEYS.map((key)=>QUESTIONNAIRE_REGISTRY[key].url);
/**
 * Canonical URLs for instruments that exist only to type Mirah-ingested data and
 * must never appear in the clinician assessment picker. Derived.
 * @agentic @benchmark/core
 */ const MIRAH_INGESTED_ONLY_QUESTIONNAIRE_URLS = getQuestionnaireKeysWhere((def)=>def.capabilities.mirahIngestedOnly).map((key)=>QUESTIONNAIRE_REGISTRY[key].url);
/**
 * Classify a questionnaire's survey-link disposition from its orthogonal
 * capability facts (single source of truth — see
 * {@link QuestionnaireSurveyLinkDisposition}). Returns undefined for non-clinical
 * instruments (NPS member feedback).
 * @agentic @benchmark/core
 */ const getSurveyLinkDisposition = (def)=>{
    if (def.type === QUESTIONNAIRE_SUBJECTS.MEMBER_FEEDBACK) {
        return undefined;
    }
    if (def.capabilities.surveyLinkAdministrable) {
        return "sendable";
    }
    // Blocked only by a members-app tooling gap → deferred: a self-report
    // instrument awaiting branching support, or a caregiver-report instrument
    // awaiting a caregiver flow.
    const branchingGated = def.capabilities.selfReportMode === "self" && def.capabilities.hasBranching;
    const caregiverGated = def.capabilities.selfReportMode === "caregiver";
    if (branchingGated || caregiverGated) {
        return "deferred";
    }
    return "excluded";
};
/**
 * Clinical questionnaires deferred from the survey link pending members-app
 * tooling (branching / caregiver flow). Derived.
 * @agentic @benchmark/core
 */ const SURVEY_LINK_DEFERRED_QUESTIONNAIRE_KEYS = getQuestionnaireKeysWhere((def)=>getSurveyLinkDisposition(def) === "deferred");
/**
 * Clinical questionnaires intentionally never offered in the survey link. Derived.
 * @agentic @benchmark/core
 */ const SURVEY_LINK_EXCLUDED_QUESTIONNAIRE_KEYS = getQuestionnaireKeysWhere((def)=>getSurveyLinkDisposition(def) === "excluded");
/**
 * True when the instrument is completed by a parent/guardian about the patient
 * (e.g. PSC-17), not by the patient themselves.
 * @agentic @benchmark/core
 */ const isCaregiverReportQuestionnaireUrl = (url)=>getQuestionnaireDefinition(url)?.capabilities.selfReportMode === "caregiver";
/**
 * True when any bundled questionnaire URL is caregiver-report.
 * @agentic @benchmark/core
 */ const hasCaregiverReportQuestionnaire = (urls)=>urls.some(isCaregiverReportQuestionnaireUrl);

/**
 * FHIR R4 Goal Domain Constants
 *
 * Defines codes and value sets for Goal resources including:
 * - Goal categories (access-supports, caregiver-needs, emotional-mental-health, etc.)
 * - Goal Attainment Scale (GAS) scores (-2 to +2)
 * - Goal lifecycle and achievement statuses
 * - Helper functions for display and validation
 */ /**
 * Goal Category Code System
 * Custom Benchmark Health system for goal categorization
 */ const GOAL_CATEGORY_SYSTEM = "http://benchmarkhealth.com/fhir/CodeSystem/goal-categories";
/**
 * Goal Category Options
 * Defines the categories of goals available
 */ const GOAL_CATEGORIES = [
    {
        code: "access-supports",
        display: "Access to services and supports",
        system: GOAL_CATEGORY_SYSTEM
    },
    {
        code: "caregiver-needs",
        display: "Caregiver needs and concerns",
        system: GOAL_CATEGORY_SYSTEM
    },
    {
        code: "emotional-mental-health",
        display: "Emotional and mental health",
        system: GOAL_CATEGORY_SYSTEM
    },
    {
        code: "end-of-life",
        display: "End of life",
        system: GOAL_CATEGORY_SYSTEM
    },
    {
        code: "housing",
        display: "Housing",
        system: GOAL_CATEGORY_SYSTEM
    },
    {
        code: "health-wellness",
        display: "Improving health and wellness",
        system: GOAL_CATEGORY_SYSTEM
    },
    {
        code: "independence",
        display: "Independence",
        system: GOAL_CATEGORY_SYSTEM
    },
    {
        code: "legal",
        display: "Legal",
        system: GOAL_CATEGORY_SYSTEM
    },
    {
        code: "managing-conditions",
        display: "Managing conditions and symptoms",
        system: GOAL_CATEGORY_SYSTEM
    },
    {
        code: "medication-management",
        display: "Medication management",
        system: GOAL_CATEGORY_SYSTEM
    },
    {
        code: "physical-function",
        display: "Physical function",
        system: GOAL_CATEGORY_SYSTEM
    },
    {
        code: "social-role",
        display: "Social role and functioning",
        system: GOAL_CATEGORY_SYSTEM
    }
];
/**
 * Goal Attainment Scale (GAS) Code System
 * Custom Benchmark Health system for GAS scoring
 */ const GAS_CODE_SYSTEM = "http://benchmarkhealth.com/fhir/CodeSystem/goal-attainment-scale";
/**
 * Goal Attainment Scale Assessment Code
 * Single code representing the GAS assessment (not the individual scores)
 */ const GAS_ASSESSMENT_CODE = {
    system: "http://benchmarkhealth.com/fhir/CodeSystem/assessments",
    code: "goal-attainment-scale",
    display: "Goal Attainment Scale (GAS)"
};
/**
 * Goal Attainment Scale (GAS) Scores
 * Defines the GAS score options (-2 to +2) with descriptions
 */ const GAS_SCORES = [
    {
        value: 2,
        code: "2",
        display: "Much better than expected",
        description: "The patient's progress significantly exceeds the expected outcome",
        system: GAS_CODE_SYSTEM
    },
    {
        value: 1,
        code: "1",
        display: "Better than expected",
        description: "The patient's progress exceeds the expected outcome",
        system: GAS_CODE_SYSTEM
    },
    {
        value: 0,
        code: "0",
        display: "Expected outcome achieved",
        description: "The patient's progress matches the expected outcome",
        system: GAS_CODE_SYSTEM
    },
    {
        value: -1,
        code: "-1",
        display: "Less than expected",
        description: "The patient's progress falls short of the expected outcome",
        system: GAS_CODE_SYSTEM
    },
    {
        value: -2,
        code: "-2",
        display: "Much less than expected",
        description: "The patient's progress significantly falls short of the expected outcome",
        system: GAS_CODE_SYSTEM
    }
];
/**
 * Goal Lifecycle Status Values
 * FHIR R4 Goal.lifecycleStatus value set
 */ const GOAL_LIFECYCLE_STATUSES = [
    "proposed",
    "planned",
    "accepted",
    "active",
    "on-hold",
    "completed",
    "cancelled",
    "entered-in-error"
];
/**
 * Goal Achievement Status Values
 * FHIR R4 Goal.achievementStatus value set
 */ const GOAL_ACHIEVEMENT_STATUSES = [
    "in-progress",
    "improving",
    "worsening",
    "no-change",
    "achieved",
    "sustained",
    "not-achieved",
    "no-progress"
];
/**
 * Helper Functions
 */ /**
 * Get goal category display text
 * @param code - Goal category code
 * @returns Display text or code if not found
 */ const getGoalCategoryDisplay = (code)=>{
    const option = GOAL_CATEGORIES.find((o)=>o.code === code);
    return option?.display || code;
};
/**
 * Get GAS score display text
 * @param value - GAS score value (-2 to +2)
 * @returns Display text or value if not found
 */ const getGASScoreDisplay = (value)=>{
    const option = GAS_SCORES.find((s)=>s.value === value);
    return option?.display || String(value);
};
/**
 * Get GAS score description
 * @param value - GAS score value (-2 to +2)
 * @returns Description text or empty string if not found
 */ const getGASScoreDescription = (value)=>{
    const option = GAS_SCORES.find((s)=>s.value === value);
    return option?.description || "";
};
/**
 * Validate GAS score value
 * @param value - GAS score value to validate
 * @returns true if value is valid (-2 to +2)
 */ const isValidGASScore = (value)=>{
    return GAS_SCORES.some((s)=>s.value === value);
};
/**
 * UI-ready select options
 */ const GOAL_CATEGORY_SELECT_OPTIONS = GOAL_CATEGORIES.map((option)=>({
        value: option.code,
        label: option.display
    }));
const GAS_SCORE_SELECT_OPTIONS = GAS_SCORES.map((score)=>({
        value: score.value,
        label: score.display,
        description: score.description
    }));

/**
 * FHIR-aligned constants for availability update logging on HealthcareService resources.
 *
 * Defines coding systems, finding options, verification methods, and the
 * finding-to-characteristic map used by the LogAvailabilityUpdateModal.
 * @agentic @benchmark/core
 */ // ── Extension URLs ─────────────────────────────────────────────────────
/** Complex extension storing the most recent availability verification snapshot on a HealthcareService. */ const AVAILABILITY_LAST_VERIFIED_EXTENSION_URL = "http://benchmarkhealth.com/fhir/StructureDefinition/availability-last-verified";
// ── Coding Systems ─────────────────────────────────────────────────────
/** Category system distinguishing availability-update Communications from progress-log Communications. */ const AVAILABILITY_UPDATE_CATEGORY_SYSTEM = "http://benchmarkhealth.com/fhir/CodeSystem/availability-update-category";
/** System for the availability finding (what status was discovered). */ const AVAILABILITY_FINDING_SYSTEM = "http://benchmarkhealth.com/fhir/CodeSystem/availability-finding";
/** System for how the verification was performed. */ const AVAILABILITY_VERIFICATION_METHOD_SYSTEM = "http://benchmarkhealth.com/fhir/CodeSystem/availability-verification-method";
// ── Finding Options ────────────────────────────────────────────────────
const AVAILABILITY_FINDING_OPTIONS = [
    {
        value: "accepting",
        label: "Accepting"
    },
    {
        value: "waitlist-only",
        label: "Waitlist Only"
    },
    {
        value: "no-availability",
        label: "No Availability"
    },
    {
        value: "couldnt-reach",
        label: "Couldn't Reach"
    }
];
// ── Verification Method Options ────────────────────────────────────────
const AVAILABILITY_VERIFICATION_METHOD_OPTIONS = [
    {
        value: "called",
        label: "Called"
    },
    {
        value: "emailed",
        label: "Emailed"
    },
    {
        value: "checked-website",
        label: "Checked website"
    },
    {
        value: "provider-directory",
        label: "Provider directory"
    }
];
/**
 * Maps an availability verification method to its `v3-ParticipationMode`
 * channel code, or `null` when the method has no person-to-person channel
 * (e.g. checking a website or provider directory). Single source of truth for
 * both the creation-path bundle builder and the standardization backfill.
 * @agentic @benchmark/core
 */ const AVAILABILITY_VERIFICATION_METHOD_TO_CHANNEL = {
    called: "phone",
    emailed: "email",
    "checked-website": null,
    "provider-directory": null
};
// ── Finding → HealthcareService Characteristic Map ─────────────────────
/**
 * Maps a Communication finding code to the HealthcareService characteristic
 * availability value it should set, or `null` when the status is unknown
 * (e.g. "couldnt-reach") and the characteristic should not be updated.
 */ const FINDING_TO_CHARACTERISTIC_MAP = {
    accepting: "accepting-new-patients",
    "waitlist-only": "waitlist-open",
    "no-availability": "not-accepting",
    "couldnt-reach": null
};

export { ACTIVITY_CODE_MAP, ACTIVITY_DEFINITION_OPTIONS, ACTIVITY_DEFINITION_SELECT_OPTIONS, ACTIVITY_DEFINITION_TYPE_SYSTEM, ACTIVITY_ENCOUNTER_TYPE_MAP, ACTIVITY_OBSERVATION_CATEGORY, ACTIVITY_PROGRESS_ARCHETYPE_MAP, AI_GENERATED_TAG_CODES, AI_GENERATED_TAG_SYSTEM, ALL_ASSESSMENTS, ALL_ENCOUNTER_TYPE_OPTIONS, ALL_ENCOUNTER_TYPE_SELECT_OPTIONS, ALL_PATIENT_ENGAGEMENT_STATUS_OPTIONS, ALL_PATIENT_ENGAGEMENT_STATUS_SELECT_OPTIONS, APPOINTMENT_REQUIRED_OPTIONS, APPOINTMENT_SPECIALTY_OPTIONS, APPOINTMENT_SPECIALTY_SELECT_OPTIONS, APPOINTMENT_STATUS_OPTIONS, APPOINTMENT_STATUS_SELECT_OPTIONS, ASRS_QUESTIONS, ASRS_SCORING, ASSIGNED_LCSW_EXTENSION_URL, ASSIGNMENT_MODE_OPTIONS, AUDIT_C_QUESTIONS, AUDIT_C_SCORING, AVAILABILITY_FINDING_OPTIONS, AVAILABILITY_FINDING_SYSTEM, AVAILABILITY_LAST_VERIFIED_EXTENSION_URL, AVAILABILITY_UPDATE_CATEGORY_SYSTEM, AVAILABILITY_VERIFICATION_METHOD_OPTIONS, AVAILABILITY_VERIFICATION_METHOD_SYSTEM, AVAILABILITY_VERIFICATION_METHOD_TO_CHANNEL, BENCHMARK_PRACTITIONER_QUALIFICATION_OPTIONS, BILLING_ELIGIBLE_ASSESSMENTS, BILLING_INELIGIBLE_ASSESSMENTS, CALL_STATE_OPTIONS, CALL_STATE_SYSTEM, CARE_PLAN_ACTIVITY_SYSTEM, CARE_PLAN_CATEGORY_OPTIONS, CARE_PLAN_CATEGORY_SELECT_OPTIONS, CARE_PLAN_CATEGORY_SYSTEM, CARE_PLAN_PAGINATION_COUNT, CARE_PLAN_PRE_PAUSE_STATUS_EXTENSION_URL, CARE_PLAN_TIMELINE_COUNT, CARE_TEAM_BATCH_SMALL_COUNT, CARE_TEAM_TIMELINE_COUNT, CARE_TIME_CATEGORY_SYSTEM, CARE_TIME_CATEGORY_TOKEN, CARE_TIME_CODE_SYSTEM, CARE_TIME_METHOD_CODES, CARE_TIME_METHOD_OPTIONS, CARE_TIME_METHOD_SYSTEM, CARE_TIME_NON_BILLABLE_EXTENSION_URL, CARE_TIME_OBSERVATION_CATEGORY, CARE_TIME_OBSERVATION_CODE, CARE_TIME_OBSERVATION_CODE_VALUE, CLINICAL_FOLLOW_UP_ENCOUNTER_TYPE_CODE, CLINICAL_IMPRESSION_CODE_MAPPINGS, CLINICAL_IMPRESSION_FORM_FIELDS, CLINICAL_IMPRESSION_STATUS_OPTIONS, CLINICAL_IMPRESSION_STATUS_SELECT_OPTIONS, CLINICAL_IMPRESSION_TYPES, CLINICAL_IMPRESSION_TYPE_OPTIONS, CLINICAL_IMPRESSION_TYPE_SELECT_OPTIONS, CLINICAL_INTAKE_ENCOUNTER_TYPE_CODE, COCM_ACTIVITY_DEFINITION_SELECT_OPTIONS, COCM_ACTIVITY_OPTIONS, COCM_ACTIVITY_SELECT_OPTIONS, COCM_ASSESSMENT_FRESHNESS_WEEKS, COCM_BILLING_PACKET_CATEGORY, COCM_BILLING_PACKET_CODE_SYSTEM, COCM_BILLING_PACKET_IDENTIFIER_SYSTEM, COCM_BILLING_PACKET_SNAPSHOT_EXTENSION_URL, COCM_BILLING_PACKET_STATUS, COCM_BILLING_PACKET_TYPE, COCM_BILLING_PACKET_TYPE_CODE, COCM_BILLING_PACKET_TYPE_TOKEN, COCM_BILLING_PACKET_VERSION_EXTENSION_URL, COCM_BILLING_SUMMARY_CATEGORY, COCM_BILLING_SUMMARY_CATEGORY_TOKEN, COCM_BILLING_SUMMARY_CODE, COCM_BILLING_SUMMARY_CODE_TOKEN, COCM_BILLING_SUMMARY_CODE_VALUE, COCM_BILLING_SUMMARY_EXT, COCM_BILLING_SUMMARY_EXTENSION_URL, COCM_BILLING_SUMMARY_IDENTIFIER_SYSTEM, COCM_BILLING_SUMMARY_STATUS, COCM_CHARGE_ITEM_IDENTIFIER_SYSTEM, COCM_CONSENT_DATE_EXTENSION_URL, COCM_CONSENT_EXTENSION_URL, COCM_ELIGIBLE_ASSESSMENT_CODES, COCM_ELIGIBLE_ASSESSMENT_CODES_TOKEN, COCM_FOLLOW_UP_ENCOUNTER_TYPE_CODE, COCM_INTAKE_ENCOUNTER_TYPE_CODE, COCM_PLAN_DEFINITION, COCM_PLAN_DEFINITION_URL, CODE_SYSTEM, COMMUNICATION_MEDIUM_OPTIONS, COMMUNICATION_MEDIUM_SELECT_OPTIONS, COMMUNICATION_MEDIUM_SYSTEM, COMMUNICATION_TYPE_OPTIONS, COMMUNICATION_TYPE_SYSTEM, CONDITION_ACTIVE_STATUS, CONDITION_CATEGORY_SYSTEM, CONDITION_CLINICAL_STATUS_OPTIONS, CONDITION_CLINICAL_STATUS_SYSTEM, CONDITION_INACTIVE_STATUS, CONSENT, CONSENT_CATEGORY_MAPPINGS, CONSENT_POLICY_RULE_MAPPINGS, CONSENT_SCOPE_MAPPINGS, CONTACT_RELATIONSHIP_OPTIONS, CONTACT_RELATIONSHIP_SELECT_OPTIONS, COVERAGE_AREA_STATE_CODESYSTEM, COVERAGE_AREA_STATE_IDENTIFIER_SYSTEM, COVERAGE_RELATIONSHIP_OPTIONS, COVERAGE_RELATIONSHIP_SELECT_OPTIONS, COVERAGE_STATUS_OPTIONS, COVERAGE_STATUS_SELECT_OPTIONS, COVERAGE_TYPE_OPTIONS, COVERAGE_TYPE_SELECT_OPTIONS, CUSTOM_ORGANIZATION_TYPE_OPTIONS, CUSTOM_ORGANIZATION_TYPE_SYSTEM, CUSTOM_TASK_TYPE, C_SSRS_QUESTIONS, C_SSRS_SCORING, DEPRECATED_ACTIVITY_CODES, DEPRECATED_ACTIVITY_DISPLAYS, DEPRECATED_CUSTOM_CARE_PLAN_ACTIVITY_CODE, DRUG_USE_QUESTIONS, DRUG_USE_SCORING, EMR_SYSTEM_OPTIONS, ENCOUNTER_BATCH_SMALL_COUNT, ENCOUNTER_BILLABLE_CARD_OPTIONS, ENCOUNTER_BILLABLE_OPTIONS, ENCOUNTER_BILLABLE_SELECT_OPTIONS, ENCOUNTER_DIRECTION_CARD_OPTIONS, ENCOUNTER_DIRECTION_OPTIONS, ENCOUNTER_DIRECTION_SELECT_OPTIONS, ENCOUNTER_DRAFT_FORM_STATE_EXTENSION_URL, ENCOUNTER_METHOD_CARD_OPTIONS, ENCOUNTER_METHOD_OPTIONS, ENCOUNTER_METHOD_SELECT_OPTIONS, ENCOUNTER_OUTCOME_ACTIVITY_CODES, ENCOUNTER_SCRATCH_EXTENSION_URL, ENCOUNTER_SECTION_SCRATCH_EXTENSION_URL, ENCOUNTER_SUCCESS_OPTIONS, ENCOUNTER_SUCCESS_SELECT_OPTIONS, ENCOUNTER_TAG_MAPPINGS, ENCOUNTER_TYPE_OPTIONS, ENCOUNTER_TYPE_SELECT_OPTIONS, ETHNICITY_OPTIONS, EXAMPLE, EXTENSION_BASE, EXTENSION_URLS, FHIR_ORGANIZATION_TYPE_OPTIONS, FINDING_TO_CHARACTERISTIC_MAP, FLAG_CATEGORY_OPTIONS, FLAG_CATEGORY_SYSTEM, FLAG_CODE_CARD_OPTIONS, FLAG_CODE_OPTIONS, FLAG_CODE_SYSTEM, FLAG_LOCAL_CODE_SYSTEM, FLAG_PAGINATION_COUNT, FOLLOW_UP_ENCOUNTER_CODES, GAD2_QUESTIONS, GAD2_SCORING, GAD7_QUESTIONS, GAD7_SCORING, GAS_ASSESSMENT_CODE, GAS_CODE_SYSTEM, GAS_SCORES, GAS_SCORE_SELECT_OPTIONS, GENDER_IDENTITY_OPTIONS, GOAL_ACHIEVEMENT_STATUSES, GOAL_CATEGORIES, GOAL_CATEGORY_SELECT_OPTIONS, GOAL_CATEGORY_SYSTEM, GOAL_LIFECYCLE_STATUSES, GP_ROLE_TYPES, GP_ROLE_TYPE_EXTENSION, HCPCS_SYSTEM, HEALTHCARE_DOMAINS, HEALTHCARE_SERVICE_CODESYSTEMS, HEALTHCARE_SERVICE_REFERRAL_HISTORY_COUNT, HEALTHCARE_SERVICE_SIBLINGS_COUNT, HL7_FHIR_BASE, HL7_PRACTITIONER_QUALIFICATION_OPTIONS, HL7_TERMINOLOGY_BASE, HL7_US_CDMH_BASE, HL7_US_CORE_BASE, ICD10_CM_SYSTEM, ICD10_CM_VALUESET_URL, ICT_PROFESSIONAL_ROLE_CODE, IDENTIFIER_SYSTEM, INDIVIDUAL_PRONOUNS_EXTENSION_URL, INSURANCE_PLAN_TYPE_OPTIONS, INSURANCE_PLAN_TYPE_SELECT_OPTIONS, INSURANCE_PLAN_TYPE_SYSTEM, INSURANCE_PROVIDER_TYPE_OPTIONS, INTAKE_ACTIVITY_DEFINITION_SELECT_OPTIONS, INTAKE_ACTIVITY_OPTIONS, INTAKE_ACTIVITY_SELECT_OPTIONS, INTAKE_BILLING_FLAG_OPTIONS, INTAKE_BILLING_FLAG_SELECT_OPTIONS, INTAKE_BILLING_FLAG_SYSTEM, INTAKE_CHARGE_CPT_90791, INTAKE_CHARGE_ITEM_IDENTIFIER_SYSTEM, INTAKE_ENCOUNTER_CODES, INTAKE_PLAN_DEFINITION, INTAKE_PLAN_DEFINITION_URL, LANGUAGES, LEGACY_CALL_STATE_SYSTEM, LEGACY_COMMUNICATION_CHANNEL_SYSTEM, LEGACY_ENCOUNTER_CODES, LEGACY_ENCOUNTER_TYPE_OPTIONS, LEGACY_OBSERVATION_LOINC_CODES, LEGACY_OBSERVATION_TYPES, LEGACY_PATIENT_ENGAGEMENT_STATUS_OPTIONS, LEGACY_PATIENT_ENGAGEMENT_STATUS_SYSTEM, LIBRARY_CANONICAL_URL_BASE, LIBRARY_CATEGORY_CONFIG, LIBRARY_CATEGORY_SYSTEM, LIBRARY_DOCUMENT_CATEGORY_OPTIONS, LIBRARY_IDENTIFIER_SYSTEM, LIBRARY_MARKDOWN_CONTENT_TYPE, LIBRARY_TOPIC_OPTIONS, LIBRARY_TOPIC_SYSTEM, LIBRARY_TYPE_CODINGS, LIBRARY_TYPE_SYSTEM, LIBRARY_USE_CONTEXT_CATEGORY_CODE, LIBRARY_USE_CONTEXT_TYPE_SYSTEM, LIKERT_FREQUENCY_OPTIONS, LOCATION_OPTIONS_COUNT, MAX_LIBRARY_MARKDOWN_BYTES, MEDPLUM_BASE, MEMBERS_APP_SUPPORTED_QUESTIONNAIRE_KEYS, MEMBERS_APP_SUPPORTED_QUESTIONNAIRE_URLS, MIRAH_INGESTED_ONLY_QUESTIONNAIRE_URLS, MIRAH_INSTITUTE_ID_SYSTEM, MIRAH_PATIENT_ID_SYSTEM, MSE_PHONE_DEFAULTED_FIELD_KEYS, MSE_PHONE_NOT_INFERABLE_DEFAULT, NETWORK_TYPE_OPTIONS, NETWORK_TYPE_SELECT_OPTIONS, NETWORK_TYPE_SYSTEM, NOTIFICATION_READ_STATUS, NOTIFICATION_READ_STATUS_SYSTEM, NOTIFICATION_TASK_CODE_TOKEN, NOTIFICATION_TASK_TYPE_SYSTEM, NOTIFICATION_TYPE_DEFINITIONS, NOTIFICATION_UNREAD_STATUS_TOKEN, NO_SCORING, NPS_COMMUNICATION_CATEGORY_CODE, NPS_METADATA_EXTENSION_URL, NPS_QUESTIONS, NPS_READ_COMMUNICATION_CATEGORIES, NPS_READ_KEY_IDENTIFIER_SYSTEM, NPS_SCALE_0_TO_10_OPTIONS, NPS_SCORING, NPS_STATUS_REASON_SYSTEM, OBSERVATION_CATEGORIES, OBSERVATION_CODE_MAPPINGS, OBSERVATION_LOINC_CODES, OBSERVATION_RISK_LEVELS, OBSERVATION_TYPES, OBSERVATION_TYPE_SELECT_OPTIONS, OCI_4_QUESTIONS, OCI_4_SCORING, OPERATIONAL_PROGRAM_OPTIONS, OPERATIONAL_PROGRAM_SELECT_OPTIONS, OPERATIONAL_PROGRAM_SYSTEM, ORGANIZATION_BASE, ORGANIZATION_OPTIONS_COUNT, ORGANIZATION_TIER_OPTIONS, ORGANIZATION_TIER_SELECT_OPTIONS, ORGANIZATION_TIER_SYSTEM, ORGANIZATION_TYPE_OPTIONS, ORGANIZATION_TYPE_SELECT_OPTIONS, ORGANIZATION_TYPE_SYSTEM, OUTREACH_ENCOUNTER_OUTCOMES, OUTREACH_STANDARD_OUTCOMES, PATIENT_ENGAGEMENT_STATUS_MIGRATION_MAP, PATIENT_ENGAGEMENT_STATUS_OPTIONS, PATIENT_ENGAGEMENT_STATUS_SELECT_OPTIONS, PATIENT_ENGAGEMENT_STATUS_SYSTEM, PATIENT_FAMILY_CONTACT_RELATIONSHIP_CODES, PATIENT_FAMILY_CONTACT_RELATIONSHIP_OPTIONS, PATIENT_FAMILY_CONTACT_RELATIONSHIP_SELECT_OPTIONS, PATIENT_FAMILY_CONTACT_RELATIONSHIP_SYSTEM, PATIENT_INTAKE_ELIGIBILITY_SYSTEM, PATIENT_PAGINATION_COUNT, PATIENT_REFERENCE_ELEMENTS, PATIENT_SUMMARY_AUTHOR_URL, PATIENT_SUMMARY_EXTENSION_URL, PATIENT_SUMMARY_LAST_UPDATED_URL, PATIENT_SUMMARY_TEXT_URL, PATIENT_TAG_MAPPINGS, PAUSE_FLAG_REASON_EXTENSION_URL, PC_PTSD_5_QUESTIONS, PC_PTSD_5_SCORING, PGI_C_QUESTIONS, PGI_C_SCORING, PGI_S_QUESTIONS, PGI_S_SCORING, PHQ4_QUESTIONS, PHQ4_SCORING, PHQ9A_QUESTIONS, PHQ9A_SCORING, PHQ9_QUESTIONS, PHQ9_SCORING, PLAN_DEFINITION_TYPE_OPTIONS, PLAN_DEFINITION_TYPE_SELECT_OPTIONS, PLAN_DEFINITION_TYPE_SYSTEM, PRACTITIONER_QUALIFICATION_BENCHMARK_SYSTEM, PRACTITIONER_QUALIFICATION_HL7_SYSTEM, PRACTITIONER_QUALIFICATION_OPTIONS, PRACTITIONER_ROLE_CODE_CARD_OPTIONS, PRACTITIONER_ROLE_CODE_OPTIONS, PRACTITIONER_ROLE_CODE_SELECT_OPTIONS, PRACTITIONER_ROLE_CODE_SYSTEM, PRACTITIONER_ROLE_CURRENT_USER_COUNT, PRACTITIONER_ROLE_OPTIONS_COUNT, PRACTITIONER_ROLE_PAGINATION_COUNT, PRACTITIONER_SPECIALTY_OPTIONS, PRACTITIONER_SPECIALTY_SELECT_OPTIONS, PRAPARE_QUESTIONS, PRAPARE_SCORING, PROBLEM_LIST_ITEM_CATEGORY, PROFILE_URLS, PROGRAM_SELECTION_IDENTIFIER_SYSTEM, PROGRAM_SELECTION_OBSERVATION_CODE, PROGRAM_SELECTION_OBSERVATION_SYSTEM, PROGRAM_SELECTION_REASON_OPTIONS, PROGRAM_SELECTION_REASON_SELECT_OPTIONS, PROGRAM_SELECTION_REASON_SYSTEM, PROGRESS_LOG_CATEGORIES, PROGRESS_LOG_CATEGORY_SYSTEM, PROGRESS_LOG_OUTCOME_SYSTEM, PROVIDER_COMMUNICATION_MEDIUM_SELECT_OPTIONS, PROVIDER_OUTREACH_OUTCOMES, PSC_17_FREQUENCY_OPTIONS, PSC_17_QUESTIONS, PSC_17_SCORING, QUERY_LIMIT_SINGLE, QUESTIONNAIRE, QUESTIONNAIRE_LOINC_CODES, QUESTIONNAIRE_REGISTRY, QUESTIONNAIRE_RESPONSE_STATUS_OPTIONS, QUESTIONNAIRE_STATUS_OPTIONS, QUESTIONNAIRE_SUBJECTS, QUESTIONNAIRE_URLS, RACE_OPTIONS, REFERRAL_MODE_OPTIONS, REFERRING_PROVIDER_ROLE_CODE, REFERRING_PROVIDER_ROLE_CONCEPT, REFERRING_PROVIDER_ROLE_DISPLAY, REFERRING_PROVIDER_ROLE_SEARCH_TOKEN, REFERRING_PROVIDER_ROLE_SYSTEM, RELATIONSHIP_TO_SUBSCRIBER_OPTIONS, RESOURCE_PROFILE_URLS, RETIRED_CLINICAL_IMPRESSION_FIELD_KEYS, SAFETY_PLAN_QUESTIONS, SBIRT_ED_QUESTIONS, SBIRT_ED_SCORING, SDOH_SCORING, SELECTED_NEEDS_COMPONENT_CODE, SELECTED_NEEDS_COMPONENT_SYSTEM, SERVICE_CATEGORY_OPTIONS, SERVICE_CHARACTERISTIC_ACCESSIBILITY_OPTIONS, SERVICE_CHARACTERISTIC_ACCESS_OPTIONS, SERVICE_CHARACTERISTIC_AGE_GROUP_OPTIONS, SERVICE_CHARACTERISTIC_AVAILABILITY_OPTIONS, SERVICE_CHARACTERISTIC_CLINICIAN_CADENCE_OPTIONS, SERVICE_CHARACTERISTIC_CULTURAL_FOCUS_OPTIONS, SERVICE_CHARACTERISTIC_DIMENSION_OPTIONS, SERVICE_CHARACTERISTIC_EVIDENCE_TIER_OPTIONS, SERVICE_CHARACTERISTIC_PARTNERSHIP_OPTIONS, SERVICE_CHARACTERISTIC_SCHEDULING_OPTIONS, SERVICE_CHARACTERISTIC_SESSION_LENGTH_OPTIONS, SERVICE_CHARACTERISTIC_VISIT_MODE_OPTIONS, SERVICE_CHARACTERISTIC_WAIT_TIME_OPTIONS, SERVICE_ELIGIBILITY_AGE_GROUP_OPTIONS, SERVICE_ELIGIBILITY_DIMENSION_OPTIONS, SERVICE_ELIGIBILITY_GENDER_OPTIONS, SERVICE_ELIGIBILITY_INCOME_BAND_OPTIONS, SERVICE_ELIGIBILITY_LEGAL_STATUS_OPTIONS, SERVICE_ELIGIBILITY_PREGNANCY_STATUS_OPTIONS, SERVICE_ELIGIBILITY_STUDENT_STATUS_OPTIONS, SERVICE_ELIGIBILITY_VETERAN_STATUS_OPTIONS, SERVICE_MODE_SELECT_OPTIONS, SERVICE_PROGRAM_DIMENSION_OPTIONS, SERVICE_PROGRAM_INSURANCE_PLAN_OPTIONS, SERVICE_PROGRAM_PAYER_NETWORK_OPTIONS, SERVICE_PROGRAM_STATE_OPTIONS, SERVICE_PROVIDER_TYPE_OPTIONS, SERVICE_PROVISION_OPTIONS, SERVICE_REFERRAL_METHOD_DIMENSION_OPTIONS, SERVICE_REFERRAL_METHOD_INTAKE_CHANNEL_OPTIONS, SERVICE_REFERRAL_METHOD_REFERRAL_FORMAT_OPTIONS, SERVICE_REFERRAL_METHOD_REFERRAL_TYPE_OPTIONS, SERVICE_REQUEST_CATEGORY, SERVICE_REQUEST_CODE, SERVICE_REQUEST_NEED_IDENTIFIER_SYSTEM, SERVICE_REQUEST_STATUS_OPTIONS, SERVICE_REQUEST_TIMELINE_COUNT, SERVICE_SPECIALTY_DIMENSION_OPTIONS, SERVICE_SPECIALTY_TREATMENT_CONDITION_OPTIONS, SERVICE_SPECIALTY_TREATMENT_MODALITY_OPTIONS, SERVICE_STATUS_OPTIONS, SERVICE_TYPE_BY_CATEGORY, SERVICE_TYPE_OPTIONS, SERVICE_TYPE_SNOMED_MAP, SEXUAL_ORIENTATION_OPTIONS, SMS_THREAD_INITIAL_COUNT, SMS_THREAD_PAGE_COUNT, SMS_THREAD_POLL_COUNT, STANDALONE_TASK_MIGRATION_MAP, STANDARD_QUESTIONNAIRES, STRUCTURE_DEFINITION, SUPPORTED_COVERAGE_AREA_STATE_CODES, SURVEY_LINK_DEFERRED_QUESTIONNAIRE_KEYS, SURVEY_LINK_EXCLUDED_QUESTIONNAIRE_KEYS, SURVEY_LINK_SENDABLE_QUESTIONNAIRE_KEYS, SURVEY_LINK_SENDABLE_QUESTIONNAIRE_URLS, SURVEY_OBSERVATION_CATEGORY, TAPS_1_QUESTIONS, TAPS_1_SCORING, TAPS_2_QUESTIONS, TAPS_2_SCORING, TASK_DRAFT_FORM_STATE_EXTENSION_URL, TASK_HISTORY_COUNT, TASK_INTENT_OPTIONS, TASK_INTENT_SELECT_OPTIONS, TASK_OUTPUT_DOCUMENTED_ENCOUNTER_CODE, TASK_OUTPUT_TYPE_SYSTEM, TASK_PAGINATION_COUNT, TASK_PRIORITY_OPTIONS, TASK_PRIORITY_SELECT_OPTIONS, TASK_STATUS_OPTIONS, TASK_STATUS_SELECT_OPTIONS, TASK_TYPE_FILTER_OPTIONS, TASK_TYPE_GROUPS, TASK_TYPE_GROUP_OPTIONS, TASK_TYPE_OPTIONS, TASK_TYPE_SELECT_OPTIONS, TASK_TYPE_SYSTEM, TELECOM_SYSTEM_OPTIONS, TELECOM_USE_OPTIONS, TERMINAL_TASK_STATUSES, TERMINATING_ENGAGEMENT_STATUSES, TIMELINE_PAGINATION_COUNT, TRIAGE_ACTIVITY_CODES, TRIAGE_ACTIVITY_DEFINITION_SELECT_OPTIONS, TRIAGE_ACTIVITY_OPTIONS, TRIAGE_ACTIVITY_SELECT_OPTIONS, TRIAGE_PLAN_DEFINITION, TRIAGE_PLAN_DEFINITION_URL, UCLA3_QUESTIONS, UCLA3_SCORING, UCUM_SYSTEM, UNBILLED_FOLLOW_UP_ENCOUNTER_TYPE_CODE, UNBILLED_INTAKE_ENCOUNTER_TYPE_CODE, US_STATES, V2_ACTIVITY_DISPLAYS, VALUE_SET, WHO5_SCORING, WPAI_QUESTIONS, WPAI_SCORING, WSAS_QUESTIONS, WSAS_SCORING, WSAS_WORK_NOT_APPLICABLE_VALUE, billingCodeSystem, buildCareTimeMinutesQuantity, buildCoCMBillingPacketIdentifierToken, buildCoCMBillingPacketIdentifierValue, buildCoCMBillingSummaryIdentifierToken, buildCoCMBillingSummaryIdentifierValue, buildCoCMChargeItemIdentifierValue, buildCommunicationTypeCategory, buildLibraryCanonicalUrl, buildLibraryName, buildLibrarySlug, buildLibraryTopicCoding, buildMediumCoding, buildPractitionerQualification, buildPractitionerQualificationConcept, buildPractitionerRoleConcept, channelCategoryCodeToMediumCode, cocmChargeItemMonthPrefix, coverageAreaStateCodeToStateCode, deserializeTaskTypeFilter, extractQuestionnaireScoringInfo, generateLibrarySlugSuffix, getActivitiesForCarePlanType, getActivityAssignmentGPIndex, getActivityAssignmentMode, getActivityAssignmentRoleCode, getActivityCadenceWeeks, getActivityDefinitionByCanonical, getActivityDefinitionCanonical, getActivityDefinitionDescription, getActivityDefinitionDisplay, getActivityDefinitionsByProtocol, getActivityMaxOccurrences, getActivitySlaDurationHours, getAllCarePlanActivityOptions, getCadenceWeeksForTaskType, getCarePlanActivityDescription, getCarePlanActivityDisplay, getCarePlanCategoryDisplay, getCareTimeMethodCoding, getCharacteristicValueOptionsByDimension, getClinicalImpressionDisplayFromCoding, getClinicalImpressionStatusDisplay, getClinicalImpressionTypeDisplay, getCoCMFlexibleActivities, getCoCMSequentialActivities, getConditionClinicalStatusDisplay, getContactRelationshipDisplay, getContactRelationshipOptions, getCoverageAreaLocationName, getCoverageAreaStateDisplay, getCoverageAreaStateOptions, getCoverageRelationshipDisplay, getCoverageStatusDisplay, getCoverageTypeDisplay, getDefaultPriorityForTaskType, getEligibilityValueOptionsByDimension, getEmrSystemOptions, getEncounterBillableDisplay, getEncounterDirectionDisplay, getEncounterMethodDisplay, getEncounterSuccessDisplay, getEncounterTypeDisplay, getEncounterTypeVersion, getEstimatedDurationForTaskType, getEthnicityOptions, getFlagCategoryByCode, getFlagCategoryDisplay, getFlagCodeDisplay, getFlagCodingByCode, getFlexibleActivitiesForType, getFreeCreationEncounterTypes, getFreeCreationNonIntakeEncounterTypes, getGASScoreDescription, getGASScoreDisplay, getGenderIdentityOptions, getGoalCategoryDisplay, getInsurancePlanTypeDisplay, getLanguageOptions, getLibraryCategoryConfig, getLibraryDocumentCategoryDisplay, getLibraryTopicDisplay, getNetworkTypeDisplay, getNotificationReadStatus, getNotificationTypeDefinition, getObservationCoding, getObservationCodingForField, getObservationDisplayByLoincCode, getObservationTypeDisplay, getOrganizationTierDescription, getOrganizationTierDisplay, getOrganizationTypeDisplay, getOrganizationTypeSystem, getPatientEngagementStatusDescription, getPatientEngagementStatusDisplay, getPatientFamilyContactRelationshipDisplay, getPatientFamilyContactRelationshipOptions, getPlanDefinitionByCanonical, getPlanDefinitionCanonical, getPlanDefinitionDisplay, getPractitionerQualificationCodes, getPractitionerRoleBusinessCodes, getPractitionerRoleCodeDisplay, getPractitionerRoleCodingByCode, getPractitionerRoleDisplayLabel, getPractitionerRolesBusinessCodes, getPractitionerSpecialtyDisplay, getProgramValueOptionsByDimension, getQuestionnaireDefinition, getQuestionnaireItems, getQuestionnaireKeysWhere, getQuestionnaireResponseStatusDisplay, getQuestionnaireScaleInfoByField, getRaceOptions, getReferralMethodValueOptionsByDimension, getReferralModeLabel, getRelationshipToSubscriberOptions, getSequentialActivitiesForType, getServiceModeLabel, getServiceRequestStatusColor, getServiceRequestStatusDisplay, getServiceTypesByCategory, getSexualOrientationOptions, getSpecialtyDisplay, getSpecialtyValueOptionsByDimension, getStateOptions, getStatusDisplay, getSurveyLinkDisposition, getTaskIntentDisplay, getTaskPriorityDisplay, getTaskStatusDisplay, getTaskTypeConfig, getTaskTypeDisplay, hasCaregiverReportQuestionnaire, hasNotificationTaskCode, inferGranularPlanType, inferPatientContactRelationshipSystem, isActivityFlexible, isAdministrativeActivityCode, isCareTimeNonBillable, isCaregiverReportQuestionnaireUrl, isCoverageAreaStateCode, isDeprecatedActivityCode, isDeprecatedStandaloneTaskType, isEncounterTypeDeprecated, isFollowUpEncounterType, isIctProfessionalPractitionerRole, isIntakeEncounterType, isLegacyEncounterType, isLegacyPatientEngagementStatus, isLibraryDocumentCategory, isNotificationTask, isOperationalTask, isPatientFamilyContactRelationship, isPractitionerQualificationCode, isPractitionerQualificationSystem, isPractitionerRoleBusinessCode, isProgramSelectionObservation, isRecurringTaskType, isReferringProviderPractitionerRole, isTerminatingEngagementStatus, isUnreadNotificationTask, isV2CarePlanActivityTaskCode, isValidGASScore, isValidObservationLoincCode, isValidQuestionnaireScoring, listNotificationTypeDefinitions, mapLegacyEncounterType, migratePatientEngagementStatusCode, practitionerRolesIncludeIctProfessional, qualifyTaskCode, qualifyTaskCodes, requiresBillableStatus, resolveActivityCode, resolveActivityDisplay, resolveBillableFromBillingFlag, resolveFollowUpCodeFromIntakeCode, resolveFollowUpCodePrefill, resolveIntakeCodeFromBillingFlag, resolvePractitionerQualification, resolvePractitionerRoleBusinessCode, resolveTaskTypeFilterCodes, serializeTaskTypeFilter, showsClinicalNotes, showsGeneralNotes, slugifyLibraryTitle };
